/* Backstory SDK v0.1.0 — backstory.io */
/**
 * SDK remote configuration and sampling policy (PLAN §5.5, §7.9, §19.2).
 *
 *   GET /v1/config?key=<projectKey>&sdk=<version>   (public, CORS *, cached 60 s)
 *     -> SdkRemoteConfig
 *
 * The SDK fetches this once at init (with a 300 ms timeout and built-in
 * defaults), and re-reads `IngestResponse.config` nudges on every ack.
 */
const DEFAULT_SDK_LIMITS = {
    sessions: "ok",
    canvas: { allowed: true },
    live: { allowed: true },
    remoteControl: { allowed: true },
    supportCalling: { allowed: true },
    customEvents: { allowed: true },
    webrtc: { allowed: true },
    recheckSec: 900,
};
const DEFAULT_SDK_REMOTE_CONFIG = {
    recording: "on",
    sampling: { mode: "always", baselineSampleRate: 1, triggers: ["error", "rage_click"], bufferSeconds: 30 },
    privacy: { mode: "balanced", allowUnmask: false },
    canvas: false,
    memoryBudgetMB: 24,
    segmentHours: 4,
    checkpointMinutes: 5,
    monitorModeAfterMinutes: 0,
    flags: {},
    collectors: { console: true, network: true, vitals: true, longTasks: true, errors: true }};

/**
 * Signals: deterministic issue detectors (PLAN §12) and issue clusters.
 * Shared between the worker (detection), the api, the web app, and the SDK
 * (client-side triggers for conditional recording use the same thresholds).
 */
const DEFAULT_THRESHOLDS = {
    rageClick: { clicks: 3, windowMs: 1000, radiusPx: 30 },
    deadClick: { waitMs: 1000 },
    errorClick: { waitMs: 500 },
    thrashedCursor: { minMs: 3000, minSpeedPxPerSec: 800, maxDirectionCoherence: 0.3 },
    formAbandonment: { idleMs: 60000, minFieldsChanged: 1 },
    fieldStruggle: { editsPerField: 3 },
    slowRequest: { absoluteMs: 3000, p95Multiplier: 1 },
    frozenUi: { noPaintAfterInteractionMs: 2000, longTaskMs: 1000 },
    layoutInstability: { clsPage: 0.25, shiftBeforeClickMs: 500 },
    slowPage: { lcpMs: 4000, inpMs: 500 },
    navigationLoop: { windowMs: 30000 },
    backButtonBounce: { maxDwellMs: 3000 },
    blankPage: { minVisibleChars: 20, afterLoadMs: 3000 },
    consoleErrorBurst: { count: 10, windowMs: 5000 },
    accessibilityTrap: { stuckMs: 10000 },
    consentFriction: { interactions: 2 },
    webrtcConnectFailure: { setupTimeoutMs: 15000 },
    webrtcOneWayMedia: { windowMs: 6000 },
    webrtcFreeze: { singleFreezeMs: 2000, callSharePct: 5 },
    webrtcAudioDropout: { concealmentPct: 5, sustainedMs: 10000 },
    webrtcHighLatency: { rttMs: 300, sustainedMs: 15000 },
    webrtcPacketLoss: { lossPct: 5, sustainedMs: 10000 },
    webrtcCpuLimited: { callSharePct: 20 },
    webrtcReconnectStorm: { events: 3, windowMs: 60000 },
    webrtcLowResolution: { minHeight: 240, sustainedMs: 20000 },
    webrtcCallAbandoned: { maxDurationMs: 30000, qualityScoreBelow: 60 },
};

/**
 * Live Assist protocol (PLAN §14). Co-browse rides the recording stream; the
 * messages below travel over the SDK's WebSocket (`/v1/live`) for signaling,
 * consent, annotations, and control, and over LiveKit for audio.
 *
 *   SDK  -> WS  /v1/live?key=&session=      (upgrade from the recording connection or a second socket)
 *   Agent -> WS /v1/live/agent?session=     (authenticated, requires role support|admin|owner)
 *   Server relays AgentToUser / UserToAgent messages and enforces the consent ladder server-side too.
 */
const CONTROL_POLICY = {
    userPriorityResumeMs: 2000,
    blockedInputTypes: ["password"],
    blockedAutocomplete: ["cc-number", "cc-csc", "cc-exp", "one-time-code"],
    blockedSelectors: ["[data-backstory-mask]", "[data-backstory-block]"]};
const CONSENT_WORDING = {
    cobrowse: "Annotations and remote control.",
    voice: "Wants to start an audio-only call.",
    annotate: "{agent} from {org} would like to highlight and draw on this page. Drawings disappear after 10 seconds. They cannot click or type.",
    control: "Allow {agent} from {org} to click and type on this page for you? You can stop access at any time.",
    record: "Record this support session (screen view, drawings, and audio) for quality and training?",
};

/**
 * WebRTC call observability (PLAN §5.7).
 *
 * The SDK patches `RTCPeerConnection` and `navigator.mediaDevices` to capture
 * call lifecycle, media device problems, and sampled quality statistics. Media
 * frames are NEVER recorded: only metrics and state. That is what makes this
 * safe for telehealth, where the audio and video themselves are PHI.
 *
 * Two event types, for two very different cost profiles:
 *   `webrtc` — state transitions and errors. Rare, emitted immediately, P0/P1.
 *   `webrtc_stats` — sampled metrics computed from `getStats()`. Thinned by the
 *                    governor exactly like pointer moves and canvas frames.
 *
 * Privacy rules enforced at capture time:
 *   - ICE candidate addresses are dropped; only `candidateType`, `protocol`,
 *     `networkType`, and a salted hash of the address are kept.
 *   - SDP is never captured in `strict` mode and is redacted elsewhere
 *     (`a=candidate` and `c=` lines removed) unless `webrtc.captureSdp` is on.
 *   - Device labels are recorded as the browser supplies them.
 *     `deviceId` is always hashed because it is a fingerprinting surface.
 */
const GROUP_ID = /^[A-Za-z0-9][A-Za-z0-9._:-]{0,79}$/;
const GROUP_ROLE = /^[A-Za-z0-9][A-Za-z0-9 _.:-]{0,39}$/;
/** Returns a storable group identity, or null when either required id is unusable. */
function sanitizeCallGroup(input) {
    if (!input)
        return null;
    const callGroupId = input.callGroupId.trim();
    const participantId = input.participantId.trim();
    if (!GROUP_ID.test(callGroupId) || !GROUP_ID.test(participantId))
        return null;
    const out = { callGroupId, participantId };
    const role = input.role?.trim();
    if (role && GROUP_ROLE.test(role))
        out.role = role;
    if (input.topology === "peer" || input.topology === "sfu")
        out.topology = input.topology;
    const remote = input.remoteParticipantId?.trim();
    if (remote && GROUP_ID.test(remote))
        out.remoteParticipantId = remote;
    return out;
}
const DEFAULT_WEBRTC_VISUAL = {
    enabled: false,
    intervalMs: 3000,
    burstIntervalMs: 1000,
    maxEdge: 320,
    whenStrict: false,
};
const DEFAULT_WEBRTC_CONFIG = {
    enabled: false,
    sampleIntervalSec: 5,
    captureSdp: false,
    emitOnThresholdCross: true,
    maxPeerConnections: 8,
    visual: DEFAULT_WEBRTC_VISUAL,
};
/**
 * Per-governor-level sampling policy. Mirrors the canvas ladder: quality
 * degrades in steps rather than switching off, and threshold crossings still
 * get through until SURVIVAL.
 */
const WEBRTC_SAMPLE_POLICY = {
    HEALTHY: { intervalSec: 5, onThresholdOnly: false, statsEnabled: true },
    CONSTRAINED: { intervalSec: 15, onThresholdOnly: false, statsEnabled: true },
    DEGRADED: { intervalSec: 30, onThresholdOnly: true, statsEnabled: true },
    SURVIVAL: { intervalSec: 0, onThresholdOnly: true, statsEnabled: false },
    PAUSED: { intervalSec: 0, onThresholdOnly: true, statsEnabled: false },
};
/**
 * Composite quality score, computed identically in the SDK (for triggers) and
 * the worker (for the Calls page) so the two never disagree. 100 is perfect.
 */
function callQualityScore(m) {
    const dur = Math.max(m.durationMs ?? 1, 1);
    const penalties = [
        // Latency: nothing under 150 ms, full penalty at 600 ms.
        clamp01(((m.rttMs ?? 0) - 150) / 450) * 25,
        // Loss: nothing at 0, full penalty at 10%.
        clamp01((m.lossPct ?? 0) / 10) * 30,
        // Jitter: nothing under 20 ms, full penalty at 120 ms.
        clamp01(((m.jitterMs ?? 0) - 20) / 100) * 15,
        // Freeze share of the call.
        clamp01((m.freezeMs ?? 0) / dur / 0.05) * 20,
        // Audio concealment: nothing at 0, full penalty at 10%.
        clamp01((m.concealmentPct ?? 0) / 10) * 10,
    ];
    return Math.round(Math.max(0, 100 - penalties.reduce((a, b) => a + b, 0)));
}
function clamp01(n) {
    return n < 0 ? 0 : n > 1 ? 1 : n;
}

/**
 * Backstory wire protocol v1.
 *
 * This file is the contract between the browser SDK, the Player, and the Go
 * services. The Go mirror lives in services/internal/protocol/protocol.go and
 * must stay field-for-field identical. Changes require bumping PROTOCOL_VERSION
 * and a note in docs/PROTOCOL.md.
 *
 * Encoding v1: JSON, deflate-compressed ("json+deflate"). Protobuf + zstd is
 * planned as encoding v2 (TASKS T0.2.3) and is selected per chunk by the
 * X-Backstory-Encoding header, so both can coexist.
 */
const PROTOCOL_VERSION = 1;
const LEVELS$1 = ["HEALTHY", "CONSTRAINED", "DEGRADED", "SURVIVAL", "PAUSED"];
/** Hard input caps enforced by the gateway (PLAN §20.5). */
const LIMITS = {
    maxConsoleArgBytes: 1024};
// ---------------------------------------------------------------------------
// HTTP contract
// ---------------------------------------------------------------------------
const HEADERS = {
    projectKey: "x-backstory-project-key",
    encoding: "x-backstory-encoding",
    sdkVersion: "x-backstory-sdk",
    /** Server time in epoch ms, returned on every response for clock sync. */
    serverTime: "x-backstory-server-time",
    /** Correlation id for one recording. A UUID. The SDK creates it when the page does not already have one. */
    requestId: "x-request-id",
};
/** Stable id generation: 26 chars, time-prefixed for sortable object keys. */
function newId(now = Date.now()) {
    const alphabet = "0123456789abcdefghjkmnpqrstvwxyz"; // Crockford base32
    let ts = "";
    let n = now;
    for (let i = 0; i < 10; i++) {
        ts = alphabet[n % 32] + ts;
        n = Math.floor(n / 32);
    }
    let rand = "";
    const bytes = new Uint8Array(16);
    if (typeof crypto !== "undefined" && crypto.getRandomValues)
        crypto.getRandomValues(bytes);
    else
        for (let i = 0; i < 16; i++)
            bytes[i] = Math.floor(Math.random() * 256);
    for (let i = 0; i < 16; i++)
        rand += alphabet[bytes[i] % 32];
    return ts + rand;
}

const MASK_ATTR = "data-backstory-mask";
const BLOCK_ATTR = "data-backstory-block";
const UNMASK_ATTR = "data-backstory-unmask";
const BULLET = "•";
/** Replace every non-whitespace character, preserving length and layout. */
function maskString(s) {
    return s.replace(/\S/g, BULLET);
}
// --- PII detectors (pure) -------------------------------------------------
const RE_EMAIL = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi;
const RE_PHONE = /(?:\+?\d{1,3}[\s.-]?)?(?:\(\d{2,4}\)|\d{2,4})[\s.-]?\d{3,4}[\s.-]?\d{3,4}\b/g;
const RE_CARD = /\b(?:\d[ -]?){13,19}\b/g;
const RE_SSN = /\b\d{3}-\d{2}-\d{4}\b/g;
const RE_IBAN = /\b[A-Z]{2}\d{2}(?:[ ]?[A-Z0-9]{4}){3,7}(?:[ ]?[A-Z0-9]{1,4})?\b/g;
function luhnValid(digits) {
    let sum = 0;
    let alt = false;
    for (let i = digits.length - 1; i >= 0; i--) {
        let d = digits.charCodeAt(i) - 48;
        if (alt) {
            d *= 2;
            if (d > 9)
                d -= 9;
        }
        sum += d;
        alt = !alt;
    }
    return digits.length >= 13 && sum % 10 === 0;
}
function detectEmail(s) {
    RE_EMAIL.lastIndex = 0;
    return RE_EMAIL.test(s);
}
function detectCard(s) {
    RE_CARD.lastIndex = 0;
    let m;
    while ((m = RE_CARD.exec(s)))
        if (luhnValid(m[0].replace(/\D/g, "")))
            return true;
    return false;
}
function detectSSN(s) {
    RE_SSN.lastIndex = 0;
    return RE_SSN.test(s);
}
function detectPhone(s) {
    RE_PHONE.lastIndex = 0;
    let m;
    while ((m = RE_PHONE.exec(s))) {
        const digits = m[0].replace(/\D/g, "");
        if (digits.length >= 10 && digits.length <= 15)
            return true;
    }
    return false;
}
function detectIBAN(s) {
    RE_IBAN.lastIndex = 0;
    return RE_IBAN.test(s);
}
/** Mask card numbers only. This runs in every mode. Returns [text, changed]. */
function maskCards(s) {
    let changed = false;
    const out = s.replace(RE_CARD, (m) => (luhnValid(m.replace(/\D/g, "")) ? ((changed = true), maskString(m)) : m));
    return [out, changed];
}
/** Missing means inputs only. An unrecognized value stays on all text. */
function normalizeMode(mode) {
    if (mode === "strict" || mode === "permissive")
        return mode;
    if (mode === "balanced" || mode === undefined || mode === "")
        return "balanced";
    return "strict";
}
/** Mask the PII substrings in telemetry (console, network, errors). Returns [text, changed]. */
function maskPII(s) {
    let changed = false;
    let out = s.replace(RE_EMAIL, (m) => ((changed = true), maskString(m)));
    out = out.replace(RE_SSN, (m) => ((changed = true), maskString(m)));
    out = out.replace(RE_IBAN, (m) => ((changed = true), maskString(m)));
    out = out.replace(RE_CARD, (m) => (luhnValid(m.replace(/\D/g, "")) ? ((changed = true), maskString(m)) : m));
    out = out.replace(RE_PHONE, (m) => {
        const d = m.replace(/\D/g, "").length;
        return d >= 10 && d <= 15 ? ((changed = true), maskString(m)) : m;
    });
    return [out, changed];
}
/**
 * Apply mode rules to a text node's content. `masked` = an ancestor is masked.
 * Strict masks every character. Balanced and permissive keep page text while
 * still masking card numbers; balanced differs by masking typed input values.
 */
function maskText(text, mode, masked = false) {
    if (masked || normalizeMode(mode) === "strict")
        return [maskString(text), true];
    return maskCards(text);
}
/** Hide a value that is shown in privacy-off mode: card numbers are masked anyway. */
function maskShownValue(value) {
    return maskCards(value)[0];
}
const SENSITIVE_AUTOCOMPLETE = /^(cc-|new-password|current-password|one-time-code)/;
const SENSITIVE_NAME = /cvv|cvc|csc|card.?num|cc.?num|cc.?exp|card.?exp|security.?code|one.?time|otp/i;
const UNMASKED_TYPES = new Set(["submit", "button", "reset", "search", "range", "color", "hidden", "checkbox", "radio", "file", "image"]);
/** Passwords, card fields, and one-time codes are hidden in every mode and cannot be unmasked. */
function isAlwaysMasked(el) {
    const inp = el;
    const type = (inp.type || "").toLowerCase();
    const ac = (inp.autocomplete || el.getAttribute("autocomplete") || "").toLowerCase();
    if (type === "password" || SENSITIVE_AUTOCOMPLETE.test(ac))
        return true;
    const name = `${el.getAttribute("name") ?? ""} ${el.id ?? ""}`;
    return SENSITIVE_NAME.test(name);
}
/** Should an input's value be masked? */
function shouldMaskInput(el, mode, ancestorMasked, rules) {
    if (isAlwaysMasked(el))
        return true;
    if (ancestorMasked)
        return true;
    if (isUnmasked(el, rules))
        return false;
    if (normalizeMode(mode) === "permissive")
        return false;
    return !UNMASKED_TYPES.has((el.type || "").toLowerCase());
}
function isUnmasked(el, rules) {
    return rules.allowUnmask && el.hasAttribute(UNMASK_ATTR);
}
/** Classify an element from its own attributes/selectors (not ancestors). */
function classify(el, rules) {
    if (el.hasAttribute(BLOCK_ATTR))
        return "block";
    if (rules.blockSelector) {
        try {
            if (el.matches(rules.blockSelector))
                return "block";
        }
        catch {
            /* invalid selector */
        }
    }
    if (el.hasAttribute(MASK_ATTR))
        return "mask";
    if (rules.maskSelector) {
        try {
            if (el.matches(rules.maskSelector))
                return "mask";
        }
        catch {
            /* invalid selector */
        }
    }
    return "none";
}
/** Attributes whose values are hidden on masked elements. */
const MASKED_ATTRS = new Set(["value", "placeholder", "alt", "title", "aria-label", "data-value"]);
/** Should images be blocked (src dropped, box kept)? */
function blockImages(mode) {
    return normalizeMode(mode) === "strict";
}

const MODES = ["strict", "balanced", "permissive"];
function num$1(v, def, min, max, warn, name) {
    if (v === undefined)
        return def;
    if (typeof v !== "number" || !Number.isFinite(v) || v < min || v > max) {
        warn.push(`${name} must be a number in [${min}, ${max}]`);
        return def;
    }
    return v;
}
function bool(v, def, warn, name) {
    if (v === undefined)
        return def;
    if (typeof v !== "boolean") {
        warn.push(`${name} must be a boolean`);
        return def;
    }
    return v;
}
function str(v, def, warn, name) {
    if (v === undefined)
        return def;
    if (typeof v !== "string") {
        warn.push(`${name} must be a string`);
        return def;
    }
    return v;
}
/** Validates user config, applying defaults. Never throws; returns warnings. */
function resolveConfig(input) {
    const warn = [];
    const c = (input && typeof input === "object" ? input : {});
    if (typeof c.projectKey !== "string" || c.projectKey.length < 4) {
        return { config: null, warnings: ["projectKey is required"] };
    }
    const p = (c.privacy ?? {});
    const r = (c.recording ?? {});
    let mode = p.mode;
    if (mode !== undefined && !MODES.includes(mode)) {
        warn.push("privacy.mode invalid");
        mode = undefined;
    }
    const config = {
        projectKey: c.projectKey,
        ingestUrl: str(c.ingestUrl, "http://localhost:8080/v1/ingest", warn, "ingestUrl"),
        // --- core ---
        apiUrl: str(c.apiUrl, undefined, warn, "apiUrl"),
        spill: bool(c.spill, true, warn, "spill"),
        // --- end core ---
        privacy: {
            mode: normalizeMode(mode),
            maskSelector: str(p.maskSelector, "", warn, "privacy.maskSelector") ?? "",
            blockSelector: str(p.blockSelector, "", warn, "privacy.blockSelector") ?? "",
            allowUnmask: bool(p.allowUnmask, false, warn, "privacy.allowUnmask"),
        },
        consent: resolveConsent(c.consent, warn),
        recording: {
            sampling: resolveSampling(r.sampling, warn),
            memoryBudgetMB: num$1(r.memoryBudgetMB, 24, 4, 256, warn, "recording.memoryBudgetMB"),
            batchMs: num$1(r.batchMs, 1000, 100, 30000, warn, "recording.batchMs"),
            batchBytes: num$1(r.batchBytes, 65536, 4096, 1 << 20, warn, "recording.batchBytes"),
            pointerHz: num$1(r.pointerHz, 20, 1, 60, warn, "recording.pointerHz"),
            checkpointMinutes: num$1(r.checkpointMinutes, 5, 1, 120, warn, "recording.checkpointMinutes"),
            segmentHours: num$1(r.segmentHours, 4, 1, 24, warn, "recording.segmentHours"),
            inactivityMinutes: num$1(r.inactivityMinutes, 30, 1, 24 * 60, warn, "recording.inactivityMinutes"),
            // --- transport & monitor ---
            monitorModeAfterMinutes: num$1(r.monitorModeAfterMinutes, 0, 0, 24 * 60, warn, "recording.monitorModeAfterMinutes"),
            monitorTriggers: Array.isArray(r.monitorTriggers) ? r.monitorTriggers.filter((s) => typeof s === "string") : [],
            // --- end transport & monitor ---
        },
        // --- transport & monitor ---
        transport: resolveTransport(c.transport, warn),
        // --- end transport & monitor ---
        network: resolveNetwork(c.network, warn, normalizeMode(mode)),
        collectors: resolveCollectors(c.collectors, warn),
        // --- state & otel ---
        state: resolveState(c.state, warn),
        application: resolveApplication(c.application, warn),
        otel: resolveOtel(c.otel, warn),
        modulesUrl: str(c.modulesUrl, undefined, warn, "modulesUrl"),
        // --- end state & otel ---
        // --- canvas ---
        canvas: resolveCanvas(c.canvas, warn),
        // --- end canvas ---
        // --- webrtc ---
        webrtc: resolveWebRtc(c.webrtc, warn),
        // --- end webrtc ---
        // --- live ---
        live: resolveLive(c.live, warn),
        // --- end live ---
        release: str(c.release, undefined, warn, "release"),
        environment: str(c.environment, undefined, warn, "environment"),
        debug: bool(c.debug, false, warn, "debug"),
        workerUrl: str(c.workerUrl, undefined, warn, "workerUrl"),
        worker: bool(c.worker, true, warn, "worker"),
        endUserId: str(c.endUserId, undefined, warn, "endUserId"),
        endUserName: str(c.endUserName, undefined, warn, "endUserName"),
    };
    // Low-end devices get a smaller budget automatically (PLAN §7.9).
    const mem = (typeof navigator !== "undefined" && navigator.deviceMemory);
    if (mem && mem <= 2 && r.memoryBudgetMB === undefined)
        config.recording.memoryBudgetMB = 8;
    return { config, warnings: warn };
}
// --- collectors & consent: resolvers -----------------------------------------
const ADAPTERS = ["tcf", "onetrust", "cookiebot", "osano", "callback"];
const SAMPLING_MODES = ["always", "conditional", "triggers-only"];
function resolveConsent(v, warn) {
    const c = (v && typeof v === "object" ? v : {});
    let adapter = c.adapter;
    if (adapter !== undefined && !ADAPTERS.includes(adapter)) {
        warn.push("consent.adapter invalid");
        adapter = undefined;
    }
    const ids = Array.isArray(c.purposeIds) && c.purposeIds.every((n) => typeof n === "number") ? c.purposeIds : [1, 7, 8, 10];
    const callback = typeof c.callback === "function" ? c.callback : undefined;
    if (adapter === "callback" && !callback)
        warn.push("consent.callback is required for adapter \"callback\"");
    return {
        required: bool(c.required, adapter !== undefined, warn, "consent.required"),
        adapter,
        purposeIds: ids,
        callback,
        ignoreGPC: bool(c.ignoreGPC, false, warn, "consent.ignoreGPC"),
    };
}
function strList(v, warn, name) {
    if (v === undefined)
        return [];
    if (!Array.isArray(v) || !v.every((s) => typeof s === "string")) {
        warn.push(`${name} must be a string array`);
        return [];
    }
    return v.map((s) => s.toLowerCase());
}
function resolveNetwork(v, warn, privacyMode) {
    const n = (v && typeof v === "object" ? v : {});
    let captureWebSocketMessages = bool(n.captureWebSocketMessages, false, warn, "network.captureWebSocketMessages");
    const webSocketMessageAllowlistUrls = Array.isArray(n.webSocketMessageAllowlistUrls) ? n.webSocketMessageAllowlistUrls.filter((s) => typeof s === "string") : [];
    if (captureWebSocketMessages && privacyMode === "strict") {
        warn.push("network.captureWebSocketMessages is disabled while privacy mode is on");
        captureWebSocketMessages = false;
    }
    if (captureWebSocketMessages && webSocketMessageAllowlistUrls.length === 0) {
        warn.push("network.webSocketMessageAllowlistUrls is required when WebSocket message capture is enabled");
    }
    return {
        captureMeta: bool(n.captureMeta, true, warn, "network.captureMeta"),
        captureBodies: bool(n.captureBodies, false, warn, "network.captureBodies"),
        headerAllowlist: strList(n.headerAllowlist, warn, "network.headerAllowlist"),
        bodyAllowlistUrls: Array.isArray(n.bodyAllowlistUrls) ? n.bodyAllowlistUrls.filter((s) => typeof s === "string") : [],
        maxBodyBytes: num$1(n.maxBodyBytes, 65536, 256, 1 << 20, warn, "network.maxBodyBytes"),
        captureWebSocketMessages,
        webSocketMessageAllowlistUrls,
        maxWebSocketMessageBytes: num$1(n.maxWebSocketMessageBytes, 4096, 128, 65536, warn, "network.maxWebSocketMessageBytes"),
    };
}
function resolveCollectors(v, warn) {
    const c = (v && typeof v === "object" ? v : {});
    return {
        console: bool(c.console, true, warn, "collectors.console"),
        network: bool(c.network, true, warn, "collectors.network"),
        vitals: bool(c.vitals, true, warn, "collectors.vitals"),
        longTasks: bool(c.longTasks, true, warn, "collectors.longTasks"),
        errors: bool(c.errors, true, warn, "collectors.errors"),
    };
}
function resolveSampling(v, warn) {
    const s = (v && typeof v === "object" ? v : {});
    let mode = s.mode;
    if (mode !== undefined && !SAMPLING_MODES.includes(mode)) {
        warn.push("recording.sampling.mode invalid");
        mode = undefined;
    }
    const triggers = Array.isArray(s.triggers) ? s.triggers.filter((x) => typeof x === "string") : ["error", "rage_click"];
    const routes = Array.isArray(s.routes)
        ? s.routes.filter((r) => {
            const o = r;
            return !!o && typeof o.match === "string" && (o.action === "record" || o.action === "skip" || o.action === "triggers-only");
        })
        : undefined;
    return {
        mode: mode ?? "always",
        baselineSampleRate: num$1(s.baselineSampleRate, mode === "triggers-only" ? 0 : 1, 0, 1, warn, "recording.sampling.baselineSampleRate"),
        triggers: triggers,
        bufferSeconds: num$1(s.bufferSeconds, 30, 1, 300, warn, "recording.sampling.bufferSeconds"),
        routes,
        alwaysRecordUsers: strList(s.alwaysRecordUsers, warn, "recording.sampling.alwaysRecordUsers"),
    };
}
// --- transport & monitor: resolver ---------------------------------------------
const TRANSPORTS = ["auto", "ws", "fetch", "webtransport"];
const COMPRESSIONS = ["deflate", "zstd"];
function resolveTransport(v, warn) {
    const o = (typeof v === "string" ? { transport: v } : v && typeof v === "object" ? v : {});
    let transport = o.transport;
    if (transport !== undefined && !TRANSPORTS.includes(transport)) {
        warn.push("transport.transport invalid");
        transport = undefined;
    }
    let compression = o.compression;
    if (compression !== undefined && !COMPRESSIONS.includes(compression)) {
        warn.push("transport.compression invalid");
        compression = undefined;
    }
    return {
        transport: transport ?? "auto",
        compression: compression ?? "deflate",
        dictUrl: str(o.dictUrl, undefined, warn, "transport.dictUrl"),
        wsUrl: str(o.wsUrl, undefined, warn, "transport.wsUrl"),
        wtUrl: str(o.wtUrl, undefined, warn, "transport.wtUrl"),
    };
}
// -----------------------------------------------------------------------------
// --- state & otel: resolvers ---------------------------------------------------
function ptrList(v, warn, name) {
    if (v === undefined)
        return [];
    if (!Array.isArray(v) || !v.every((s) => typeof s === "string")) {
        warn.push(`${name} must be a string array`);
        return [];
    }
    return v.map((s) => (s.startsWith("/") ? s : "/" + s.replace(/\./g, "/")));
}
function resolveState(v, warn) {
    const s = (v && typeof v === "object" ? v : {});
    return {
        allowPaths: ptrList(s.allowPaths, warn, "state.allowPaths"),
        denyPaths: ptrList(s.denyPaths, warn, "state.denyPaths"),
        snapshotEverySec: num$1(s.snapshotEverySec, 60, 5, 3600, warn, "state.snapshotEverySec"),
        maxSnapshotBytes: num$1(s.maxSnapshotBytes, 32 * 1024, 1024, 256 * 1024, warn, "state.maxSnapshotBytes"),
        maxDiffBytes: num$1(s.maxDiffBytes, 8 * 1024, 512, 64 * 1024, warn, "state.maxDiffBytes"),
    };
}
function resolveApplication(v, warn) {
    const a = (v && typeof v === "object" ? v : {});
    return {
        enabled: bool(a.enabled, true, warn, "application.enabled"),
        storageAllowlist: strList(a.storageAllowlist, warn, "application.storageAllowlist"),
        intervalMinutes: num$1(a.intervalMinutes, 5, 1, 24 * 60, warn, "application.intervalMinutes"),
    };
}
function resolveOtel(v, warn) {
    const o = (v && typeof v === "object" ? v : {});
    const globs = Array.isArray(o.propagateTraceContext) ? o.propagateTraceContext.filter((s) => typeof s === "string") : [];
    return {
        enabled: bool(o.enabled, true, warn, "otel.enabled"),
        exportSpans: bool(o.exportSpans, o.enabled === true, warn, "otel.exportSpans"),
        endpoint: str(o.endpoint, undefined, warn, "otel.endpoint"),
        propagateTraceContext: globs,
        propagateSameOrigin: bool(o.propagateSameOrigin, true, warn, "otel.propagateSameOrigin"),
        serviceName: str(o.serviceName, "browser", warn, "otel.serviceName") ?? "browser",
        batchMs: num$1(o.batchMs, 5000, 500, 60000, warn, "otel.batchMs"),
        batchSize: num$1(o.batchSize, 50, 1, 500, warn, "otel.batchSize"),
    };
}
// --- canvas: resolver ---------------------------------------------------------
function resolveCanvas(v, warn) {
    const c = (v && typeof v === "object" ? v : {});
    return {
        enabled: bool(c.enabled, false, warn, "canvas.enabled"),
        allowlist: str(c.allowlist, "[data-backstory-canvas]", warn, "canvas.allowlist") ?? "[data-backstory-canvas]",
        autoDetect: bool(c.autoDetect, false, warn, "canvas.autoDetect"),
        maxFps: num$1(c.maxFps, 15, 1, 60, warn, "canvas.maxFps"),
        bestLevel: num$1(c.bestLevel, 1, 0, 6, warn, "canvas.bestLevel"),
        maxCallsPerSec: num$1(c.maxCallsPerSec, 2000, 100, 1e6, warn, "canvas.maxCallsPerSec"),
        maxSourceBytesPerMin: num$1(c.maxSourceBytesPerMin, 2 * 1024 * 1024, 65536, 1 << 30, warn, "canvas.maxSourceBytesPerMin"),
        bytesPerMinute: num$1(c.bytesPerMinute, 3 * 1024 * 1024, 65536, 1 << 30, warn, "canvas.bytesPerMinute"),
    };
}
// --- end canvas ---
// --- live: resolver -------------------------------------------------------------
function resolveLive(v, warn) {
    const l = (v && typeof v === "object" ? v : {});
    let rc = l.remoteControl;
    if (rc !== undefined && rc !== "off" && rc !== "consent-per-session") {
        warn.push("live.remoteControl invalid");
        rc = undefined;
    }
    let pa = l.pauseOnActivity;
    if (pa !== undefined && pa !== "pause" && pa !== "revoke") {
        warn.push("live.pauseOnActivity invalid");
        pa = undefined;
    }
    return {
        // On by default: nothing loads, shows, or leaves the page until an agent is
        // waiting and the user accepts a prompt. Turn it off per project with the
        // remote flag `live: false`, or here with `live: { enabled: false }`.
        enabled: bool(l.enabled, true, warn, "live.enabled"),
        showCode: bool(l.showCode, false, warn, "live.showCode"),
        remoteControl: rc ?? "consent-per-session",
        voice: bool(l.voice, true, warn, "live.voice"),
        annotations: bool(l.annotations, true, warn, "live.annotations"),
        pauseOnActivity: pa ?? "pause",
        livekitClientUrl: str(l.livekitClientUrl, undefined, warn, "live.livekitClientUrl"),
    };
}
// --- end live ---
// --- webrtc: resolver ---------------------------------------------------------
function resolveWebRtc(c = {}, warn) {
    const visual = c.visual ?? {};
    const d = DEFAULT_WEBRTC_CONFIG.visual;
    return {
        enabled: bool(c.enabled, DEFAULT_WEBRTC_CONFIG.enabled, warn, "webrtc.enabled"),
        sampleIntervalSec: num$1(c.sampleIntervalSec, DEFAULT_WEBRTC_CONFIG.sampleIntervalSec, 1, 120, warn, "webrtc.sampleIntervalSec"),
        captureSdp: bool(c.captureSdp, DEFAULT_WEBRTC_CONFIG.captureSdp, warn, "webrtc.captureSdp"),
        emitOnThresholdCross: bool(c.emitOnThresholdCross, DEFAULT_WEBRTC_CONFIG.emitOnThresholdCross, warn, "webrtc.emitOnThresholdCross"),
        maxPeerConnections: num$1(c.maxPeerConnections, DEFAULT_WEBRTC_CONFIG.maxPeerConnections, 1, 64, warn, "webrtc.maxPeerConnections"),
        visual: {
            enabled: bool(visual.enabled, d.enabled, warn, "webrtc.visual.enabled"),
            intervalMs: num$1(visual.intervalMs, d.intervalMs, 1000, 30000, warn, "webrtc.visual.intervalMs"),
            burstIntervalMs: num$1(visual.burstIntervalMs, d.burstIntervalMs, 500, 10000, warn, "webrtc.visual.burstIntervalMs"),
            maxEdge: num$1(visual.maxEdge, d.maxEdge, 160, 640, warn, "webrtc.visual.maxEdge"),
            whenStrict: bool(visual.whenStrict, d.whenStrict, warn, "webrtc.visual.whenStrict"),
        },
    };
}
// -----------------------------------------------------------------------------

const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
function isGuid(s) {
    return typeof s === "string" && GUID.test(s);
}
/** A UUID. Uses the platform generator when the page has one. */
function guid() {
    const c = globalThis.crypto;
    if (c && typeof c.randomUUID === "function")
        return c.randomUUID();
    const b = new Uint8Array(16);
    if (c && typeof c.getRandomValues === "function")
        c.getRandomValues(b);
    else
        for (let i = 0; i < 16; i++)
            b[i] = Math.floor(Math.random() * 256);
    b[6] = (b[6] & 0x0f) | 0x40;
    b[8] = (b[8] & 0x3f) | 0x80;
    const hex = Array.from(b, (x) => x.toString(16).padStart(2, "0")).join("");
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
/** Keep a UUID the caller already has. Otherwise create one. */
function guidOrNew(s) {
    return isGuid(s) ? s : guid();
}

/** Bidirectional Node ↔ id map. Ids are stable for the life of the node. */
class NodeMap {
    next = 1;
    byNode = new WeakMap();
    byId = new Map();
    id(n) {
        return this.byNode.get(n);
    }
    has(n) {
        return this.byNode.has(n);
    }
    node(id) {
        return this.byId.get(id);
    }
    /** Returns existing id or assigns a new one. */
    assign(n) {
        let id = this.byNode.get(n);
        if (id === undefined) {
            id = this.next++;
            this.byNode.set(n, id);
            this.byId.set(id, n);
        }
        return id;
    }
    /** Forget a node and its subtree (frees the id → node references). */
    forget(n) {
        const id = this.byNode.get(n);
        if (id !== undefined) {
            this.byNode.delete(n);
            this.byId.delete(id);
        }
        const kids = n.shadowRoot ? [...n.childNodes, ...n.shadowRoot.childNodes] : n.childNodes;
        for (let i = 0; i < kids.length; i++)
            this.forget(kids[i]);
    }
    get size() {
        return this.byId.size;
    }
    reset() {
        this.next = 1;
        this.byNode = new WeakMap();
        this.byId = new Map();
    }
}

const URL_ATTRS = new Set(["src", "href", "poster", "data", "action", "formaction", "xlink:href"]);
const BOX_TAGS = new Set(["canvas", "video", "img", "iframe", "object", "embed", "audio"]);
const SVG_NS = "http://www.w3.org/2000/svg";
/** Resolve the page scheme independently of the replay viewer's theme. */
function resolveReplayColorScheme(doc, el = doc.documentElement) {
    const theme = el.getAttribute("data-theme")?.toLowerCase();
    if (theme === "dark" || theme === "light")
        return theme;
    if (el.classList.contains("dark"))
        return "dark";
    try {
        const declared = doc.defaultView?.getComputedStyle(el).colorScheme ?? "";
        const supportsDark = /(^|\s)dark(\s|$)/.test(declared);
        const supportsLight = /(^|\s)light(\s|$)/.test(declared);
        const preferredDark = doc.defaultView?.matchMedia?.("(prefers-color-scheme: dark)").matches ?? false;
        if (supportsDark && !supportsLight)
            return "dark";
        if (supportsLight && !supportsDark)
            return "light";
        return preferredDark ? "dark" : "light";
    }
    catch {
        return "light";
    }
}
function absolutize(v, base) {
    if (!v || /^(data:|blob:|#|javascript:|about:)/i.test(v))
        return v;
    try {
        return new URL(v, base).href;
    }
    catch {
        return v;
    }
}
function absolutizeSrcset(v, base) {
    return v
        .split(",")
        .map((part) => {
        const t = part.trim();
        if (!t)
            return t;
        const sp = t.indexOf(" ");
        return sp === -1 ? absolutize(t, base) : absolutize(t.slice(0, sp), base) + t.slice(sp);
    })
        .join(", ");
}
function styleSheetText(sheet) {
    if (!sheet)
        return undefined;
    try {
        const rules = sheet.cssRules;
        let out = "";
        for (let i = 0; i < rules.length; i++)
            out += rules[i].cssText + "\n";
        return out;
    }
    catch {
        return undefined; // cross-origin
    }
}
function box(el) {
    try {
        const r = el.getBoundingClientRect();
        if (r.width || r.height)
            return { w: Math.round(r.width), h: Math.round(r.height) };
    }
    catch {
        /* detached */
    }
    const w = el.width;
    const h = el.height;
    return typeof w === "number" && typeof h === "number" ? { w, h } : undefined;
}
/** Serialize a single node without its children. Returns null to skip the node. */
function serializeShallow(n, ctx, inh) {
    const { map, rules, doc } = ctx;
    const mode = rules.mode;
    switch (n.nodeType) {
        case Node.DOCUMENT_NODE: {
            const id = map.assign(n);
            const d = n;
            const s = { id, type: "document", childNodes: [] };
            const adopted = d.adoptedStyleSheets;
            if (adopted && adopted.length) {
                const texts = adopted.map(styleSheetText).filter((t) => !!t);
                if (texts.length)
                    s.adoptedStyles = texts;
            }
            return { s, childInh: inh, recurse: true };
        }
        case Node.DOCUMENT_TYPE_NODE: {
            const dt = n;
            return { s: { id: map.assign(n), type: "doctype", name: dt.name, publicId: dt.publicId, systemId: dt.systemId }, childInh: inh, recurse: false };
        }
        case Node.ELEMENT_NODE: {
            const el = n;
            const tag = el.tagName.toLowerCase();
            if (tag === "noscript")
                return null;
            const id = map.assign(n);
            const cls = classify(el, rules);
            const isSVG = el.namespaceURI === SVG_NS;
            const s = { id, type: "element", tag, attrs: {}, childNodes: [] };
            if (isSVG)
                s.isSVG = true;
            if (cls === "block") {
                s.blocked = true;
                const b = box(el);
                if (b)
                    s.box = b;
                for (const a of ["id", "class"]) {
                    const v = el.getAttribute(a);
                    if (v)
                        s.attrs[a] = v;
                }
                return { s, childInh: { masked: true }, recurse: false };
            }
            const masked = inh.masked || cls === "mask" || (normalizeMode(mode) === "strict" && !isUnmasked(el, rules));
            const maskValue = tag === "input" || tag === "textarea" || tag === "select" ? shouldMaskInput(el, mode, inh.masked || cls === "mask", rules) : masked;
            const base = doc.baseURI;
            const attrs = el.attributes;
            for (let i = 0; i < attrs.length; i++) {
                const a = attrs[i];
                let name = a.name;
                let value = a.value;
                if (name.startsWith("on"))
                    continue; // inline handlers are never useful in replay
                if ((maskValue || masked) && MASKED_ATTRS.has(name))
                    value = maskString(value);
                else if (MASKED_ATTRS.has(name))
                    value = maskShownValue(value);
                else if (name === "srcset")
                    value = absolutizeSrcset(value, base);
                else if (URL_ATTRS.has(name))
                    value = absolutize(value, base);
                s.attrs[name] = value;
            }
            // An iframe normally resolves `color-scheme` and prefers-color-scheme
            // against the viewer's environment. Preserve the recorded page's resolved
            // scheme so native controls, canvas placeholders, and responsive themes do
            // not flip between light and dark during replay/live viewing.
            if (tag === "html") {
                s.attrs["data-backstory-replay-color-scheme"] = resolveReplayColorScheme(doc, el);
            }
            if (tag === "img" && (blockImages(mode) && !isUnmasked(el, rules) || masked && cls === "mask")) {
                delete s.attrs.src;
                delete s.attrs.srcset;
                s.blocked = true;
                const b = box(el);
                if (b)
                    s.box = b;
            }
            if (BOX_TAGS.has(tag)) {
                const b = box(el);
                if (b)
                    s.box = b;
            }
            if (tag === "input" || tag === "textarea" || tag === "select") {
                const inp = el;
                const type = (inp.type || "").toLowerCase();
                if (type === "checkbox" || type === "radio") {
                    if (inp.checked)
                        s.attrs.checked = "";
                    else
                        delete s.attrs.checked;
                }
                else if (tag === "select") {
                    const sel = el;
                    s.attrs.value = sel.value;
                }
                else {
                    const v = inp.value ?? "";
                    if (v)
                        s.attrs.value = maskValue ? maskString(v) : maskShownValue(v);
                    else
                        delete s.attrs.value;
                }
                if (maskValue)
                    s.masked = true;
            }
            if (tag === "style") {
                const t = styleSheetText(el.sheet);
                if (t !== undefined)
                    s.cssText = t;
            }
            else if (tag === "link" && /stylesheet/i.test(el.getAttribute("rel") || "")) {
                const t = styleSheetText(el.sheet);
                if (t !== undefined)
                    s.cssText = t;
            }
            if (masked && cls === "mask")
                s.masked = true;
            if (tag === "script")
                return { s, childInh: { masked }, recurse: false };
            if (tag === "style")
                return { s, childInh: { masked: false }, recurse: true };
            if (el.shadowRoot) {
                s.shadow = [];
                ctx.onShadowRoot?.(el.shadowRoot);
            }
            return { s, childInh: { masked }, recurse: true };
        }
        case Node.TEXT_NODE: {
            const id = map.assign(n);
            const parent = n.parentNode;
            const raw = n.textContent ?? "";
            const inStyle = !!parent && parent.nodeName.toLowerCase() === "style";
            if (inStyle)
                return { s: { id, type: "text", text: raw, isStyle: true }, childInh: inh, recurse: false };
            if (parent && parent.nodeName.toLowerCase() === "script")
                return { s: { id, type: "text", text: "" }, childInh: inh, recurse: false };
            if (!raw.trim())
                return { s: { id, type: "text", text: raw }, childInh: inh, recurse: false };
            const [text, m] = maskText(raw, mode, inh.masked);
            const t = { id, type: "text", text };
            if (m)
                t.masked = true;
            return { s: t, childInh: inh, recurse: false };
        }
        case Node.CDATA_SECTION_NODE:
            return { s: { id: map.assign(n), type: "cdata", text: n.textContent ?? "" }, childInh: inh, recurse: false };
        case Node.COMMENT_NODE:
            return { s: { id: map.assign(n), type: "comment", text: n.textContent ?? "" }, childInh: inh, recurse: false };
        default:
            return null;
    }
}
/** Synchronous full serialization of a subtree. */
function serializeSubtree(root, ctx, inh = { masked: false }) {
    const job = new SnapshotJob(root, ctx, inh);
    job.run(Infinity);
    return job.result;
}
/**
 * Incremental serializer: processes the DOM as an explicit stack so it can be
 * sliced across idle callbacks (PLAN §5.2). Order is preserved because children
 * are pushed in reverse.
 */
class SnapshotJob {
    ctx;
    result = null;
    stack = [];
    done = false;
    processed = 0;
    constructor(root, ctx, inh = { masked: false }) {
        this.ctx = ctx;
        const holder = [];
        this.stack.push({ n: root, into: holder, inh });
        this.holder = holder;
    }
    holder;
    get finished() {
        return this.done;
    }
    /** Process up to `budget` nodes (or until the stack is empty). Returns true when complete. */
    run(budget) {
        let count = 0;
        while (this.stack.length && count < budget) {
            const item = this.stack.pop();
            const r = serializeShallow(item.n, this.ctx, item.inh);
            count++;
            this.processed++;
            if (!r)
                continue;
            item.into.push(r.s);
            if (!r.recurse)
                continue;
            const el = item.n;
            if (r.s.type === "element" && r.s.shadow && el.shadowRoot) {
                const kids = el.shadowRoot.childNodes;
                for (let i = kids.length - 1; i >= 0; i--)
                    this.stack.push({ n: kids[i], into: r.s.shadow, inh: r.childInh });
            }
            const kids = item.n.childNodes;
            const into = r.s.childNodes;
            for (let i = kids.length - 1; i >= 0; i--)
                this.stack.push({ n: kids[i], into, inh: r.childInh });
        }
        if (!this.stack.length) {
            this.done = true;
            this.result = this.holder[0] ?? null;
        }
        return this.done;
    }
}
/**
 * Serialize the document in idle slices with a hard cap; if the DOM mutated
 * while slicing, redo synchronously so the snapshot is consistent.
 */
function snapshotDocument(ctx, opts = {}) {
    const capMs = opts.capMs ?? 2000;
    const slice = opts.sliceNodes ?? 400;
    const doc = ctx.doc;
    const ric = globalThis.requestIdleCallback;
    return new Promise((resolvePromise) => {
        const job = new SnapshotJob(doc, ctx);
        let dirty = false;
        let mo = null;
        try {
            mo = new MutationObserver(() => (dirty = true));
            mo.observe(doc, { childList: true, subtree: true, attributes: true, characterData: true });
        }
        catch {
            /* no MutationObserver */
        }
        const start = Date.now();
        const timed = (fn) => {
            const t0 = Date.now();
            try {
                return fn();
            }
            finally {
                opts.onWork?.(Date.now() - t0);
            }
        };
        const finish = () => {
            mo?.disconnect();
            if (dirty) {
                // Consistency over speed: re-serialize synchronously.
                ctx.map.reset();
                const sync = timed(() => serializeSubtree(doc, ctx));
                resolvePromise(sync);
            }
            else
                resolvePromise(job.result);
        };
        const step = (deadline) => {
            if (Date.now() - start > capMs) {
                timed(() => job.run(Infinity));
                return finish();
            }
            const budget = deadline ? Math.max(50, Math.floor(deadline.timeRemaining() * 40)) : slice;
            if (timed(() => job.run(budget)))
                return finish();
            if (ric)
                ric(step);
            else
                setTimeout(step, 0);
        };
        if (ric)
            ric(step);
        else {
            timed(() => job.run(Infinity));
            finish();
        }
    });
}

/** Is any ancestor (or the node itself) masked/blocked per rules or strict mode? */
function inheritedFor(node, ctx) {
    let masked = normalizeMode(ctx.rules.mode) === "strict";
    let el = node.nodeType === Node.ELEMENT_NODE ? node : node.parentElement;
    while (el) {
        const c = classify(el, ctx.rules);
        if (c === "mask" || c === "block") {
            masked = true;
            break;
        }
        el = el.parentElement ?? (el.getRootNode().host ?? null);
    }
    return { masked };
}
/**
 * Coalesces MutationRecords per animation frame into one MutationEvent.
 * Attaches to the document and to every shadow root reported via
 * ctx.onShadowRoot (both from the snapshot and from added subtrees).
 */
class MutationCollector {
    opts;
    observers = [];
    pending = [];
    scheduled = false;
    stopped = false;
    frame;
    /** Extra coalescing window in ms (0 = per animation frame); set from LEVEL_POLICY. */
    coalesceMs = 0;
    coalesceTimer = null;
    constructor(opts) {
        this.opts = opts;
        const raf = globalThis.requestAnimationFrame;
        this.frame = raf ? (cb) => raf(() => cb()) : (cb) => setTimeout(cb, 16);
        const prev = opts.ctx.onShadowRoot;
        opts.ctx.onShadowRoot = (root) => {
            prev?.(root);
            this.observe(root);
        };
    }
    observe(target) {
        if (this.stopped)
            return;
        try {
            const mo = new MutationObserver((records) => {
                for (let i = 0; i < records.length; i++)
                    this.pending.push(records[i]);
                this.schedule();
            });
            mo.observe(target, { childList: true, subtree: true, attributes: true, attributeOldValue: true, characterData: true, characterDataOldValue: true });
            this.observers.push(mo);
        }
        catch {
            /* unsupported */
        }
    }
    schedule() {
        if (this.scheduled)
            return;
        this.scheduled = true;
        if (this.coalesceMs > 0) {
            this.coalesceTimer = setTimeout(() => {
                this.coalesceTimer = null;
                this.scheduled = false;
                this.flush();
            }, this.coalesceMs);
            return;
        }
        this.frame(() => {
            this.scheduled = false;
            this.flush();
        });
    }
    /** Process pending records now (also called on stop). */
    flush() {
        const records = this.pending;
        if (!records.length)
            return;
        this.pending = [];
        const t0 = this.opts.now();
        const e = this.process(records);
        this.opts.onWork?.(this.opts.now() - t0);
        if (e)
            this.opts.emit(e);
    }
    process(records) {
        const { map, rules } = this.opts.ctx;
        const adds = [];
        const removes = [];
        const attrMap = new Map();
        const texts = new Map();
        const addedNodes = new Set();
        const removedNodes = new Set();
        const addedRootsInOrder = [];
        for (const r of records) {
            if (r.type === "childList") {
                r.removedNodes.forEach((n) => {
                    if (addedNodes.has(n)) {
                        // Added and removed within the same frame: cancel the add.
                        addedNodes.delete(n);
                        const idx = addedRootsInOrder.indexOf(n);
                        if (idx >= 0)
                            addedRootsInOrder.splice(idx, 1);
                        return;
                    }
                    removedNodes.add(n);
                });
                r.addedNodes.forEach((n) => {
                    if (removedNodes.has(n)) {
                        // Removed then re-added (moved): treat as add; drop the remove.
                        removedNodes.delete(n);
                    }
                    if (!addedNodes.has(n)) {
                        addedNodes.add(n);
                        addedRootsInOrder.push(n);
                    }
                });
            }
            else if (r.type === "attributes") {
                const el = r.target;
                const id = map.id(el);
                if (id === undefined || addedNodes.has(el))
                    continue;
                const name = r.attributeName;
                if (name.startsWith("on"))
                    continue;
                let value = el.getAttribute(name);
                if (value !== null) {
                    const inh = inheritedFor(el, this.opts.ctx);
                    const tag = el.tagName.toLowerCase();
                    const maskValue = tag === "input" || tag === "textarea" ? shouldMaskInput(el, rules.mode, inh.masked, rules) : inh.masked;
                    if (maskValue && MASKED_ATTRS.has(name))
                        value = maskString(value);
                    else if (MASKED_ATTRS.has(name))
                        value = maskShownValue(value);
                }
                let m = attrMap.get(id);
                if (!m)
                    attrMap.set(id, (m = {}));
                m[name] = value;
                // Theme toggles usually mutate class/data-theme/style after the opening
                // snapshot. Carry the resolved scheme with that mutation so the replay
                // iframe does not keep its old scheme or inherit the agent console's.
                if (el === this.opts.ctx.doc.documentElement && (name === "class" || name === "data-theme" || name === "style")) {
                    m["data-backstory-replay-color-scheme"] = resolveReplayColorScheme(this.opts.ctx.doc, el);
                }
            }
            else if (r.type === "characterData") {
                const n = r.target;
                const id = map.id(n);
                if (id === undefined || addedNodes.has(n))
                    continue;
                const parent = n.parentNode;
                const raw = n.textContent ?? "";
                if (parent && parent.nodeName.toLowerCase() === "style") {
                    texts.set(id, { id, value: raw });
                    continue;
                }
                const inh = inheritedFor(n, this.opts.ctx);
                const [value, masked] = maskText(raw, rules.mode, inh.masked);
                const t = { id, value };
                if (masked)
                    t.masked = true;
                texts.set(id, t);
            }
        }
        // Removes: only nodes we know, whose parent we know. Forget their ids.
        for (const n of removedNodes) {
            const id = map.id(n);
            if (id === undefined)
                continue;
            // parentNode is null after removal; find the record's target.
            const rec = records.find((r) => r.type === "childList" && Array.prototype.includes.call(r.removedNodes, n));
            const parentId = rec ? map.id(rec.target) : undefined;
            if (parentId === undefined) {
                map.forget(n);
                continue;
            }
            removes.push({ id, parentId });
            map.forget(n);
        }
        // Adds: skip nodes whose ancestor is also being added (subtree serialized with parent),
        // nodes no longer connected, and nodes already known (moved nodes get remove+add).
        for (const n of addedRootsInOrder) {
            if (!n.isConnected && !(n.getRootNode() instanceof ShadowRoot))
                continue;
            let anc = n.parentNode;
            let nested = false;
            while (anc) {
                if (addedNodes.has(anc)) {
                    nested = true;
                    break;
                }
                anc = anc.parentNode ?? (anc.host ?? null);
            }
            if (nested)
                continue;
            const parent = n.parentNode;
            if (!parent)
                continue;
            const parentId = map.id(parent instanceof ShadowRoot ? parent.host : parent);
            if (parentId === undefined)
                continue;
            if (map.has(n)) {
                // Moved node: emit a remove from wherever it was, then re-add fresh.
                const oldId = map.id(n);
                removes.push({ id: oldId, parentId });
                map.forget(n);
            }
            const inh = inheritedFor(parent instanceof ShadowRoot ? parent.host : parent, this.opts.ctx);
            const s = serializeSubtree(n, this.opts.ctx, inh);
            if (!s)
                continue;
            const next = n.nextSibling;
            let nextId = null;
            // Walk forward to the first sibling that is known (skips siblings added in the same batch that come later).
            let sib = next;
            while (sib) {
                const sid = map.id(sib);
                if (sid !== undefined && !addedNodes.has(sib)) {
                    nextId = sid;
                    break;
                }
                sib = sib.nextSibling;
            }
            adds.push({ parentId, nextId, node: s });
        }
        const attrs = [];
        for (const [id, a] of attrMap)
            attrs.push({ id, attrs: a });
        if (!adds.length && !removes.length && !attrs.length && !texts.size)
            return null;
        const e = { type: "mutation", t: this.opts.now() };
        if (adds.length)
            e.adds = adds;
        if (removes.length)
            e.removes = removes;
        if (attrs.length)
            e.attrs = attrs;
        if (texts.size)
            e.texts = [...texts.values()];
        return e;
    }
    stop() {
        this.stopped = true;
        if (this.coalesceTimer)
            clearTimeout(this.coalesceTimer);
        for (const mo of this.observers) {
            // Drain records delivered but not yet processed.
            const recs = mo.takeRecords();
            for (let i = 0; i < recs.length; i++)
                this.pending.push(recs[i]);
            mo.disconnect();
        }
        this.observers = [];
        this.flush();
    }
}

/** Ramer–Douglas–Peucker path simplification. */
function simplifyPath(pts, epsilon) {
    if (pts.length < 3)
        return pts;
    const sq = (a, b, p) => {
        const dx = b.x - a.x;
        const dy = b.y - a.y;
        const l2 = dx * dx + dy * dy;
        if (l2 === 0)
            return (p.x - a.x) ** 2 + (p.y - a.y) ** 2;
        let t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / l2;
        t = Math.max(0, Math.min(1, t));
        return (p.x - (a.x + t * dx)) ** 2 + (p.y - (a.y + t * dy)) ** 2;
    };
    const keep = new Array(pts.length).fill(false);
    keep[0] = keep[pts.length - 1] = true;
    const stack = [[0, pts.length - 1]];
    const e2 = epsilon * epsilon;
    while (stack.length) {
        const [s, e] = stack.pop();
        let maxD = 0;
        let idx = -1;
        for (let i = s + 1; i < e; i++) {
            const d = sq(pts[s], pts[e], pts[i]);
            if (d > maxD) {
                maxD = d;
                idx = i;
            }
        }
        if (maxD > e2 && idx > 0) {
            keep[idx] = true;
            stack.push([s, idx], [idx, e]);
        }
    }
    return pts.filter((_, i) => keep[i]);
}
/** Heuristic route pattern: numeric and uuid-ish segments become :id. */
function routePattern(pathname) {
    return pathname
        .split("/")
        .map((seg) => (/^\d+$/.test(seg) || /^[0-9a-f]{8}-[0-9a-f-]{27,}$/i.test(seg) || /^[0-9a-z]{20,}$/i.test(seg) ? ":id" : seg))
        .join("/");
}
class InteractionCollector {
    o;
    off = [];
    moves = [];
    moveTimer = null;
    lastMove = 0;
    lastScroll = new Map();
    pointerHz;
    scrollHz;
    origPush;
    origReplace;
    constructor(o) {
        this.o = o;
        this.pointerHz = o.pointerHz;
        this.scrollHz = o.scrollHz;
    }
    setRates(pointerHz, scrollHz) {
        this.pointerHz = pointerHz;
        this.scrollHz = scrollHz;
    }
    idOf(t) {
        return t && t.nodeType ? this.o.map.id(t) : undefined;
    }
    on(target, type, fn, opts = { capture: true, passive: true }) {
        target.addEventListener(type, fn, opts);
        this.off.push(() => target.removeEventListener(type, fn, opts));
    }
    start() {
        const { doc, win, emit, now } = this.o;
        const activity = () => this.o.onActivity?.();
        this.on(doc, "pointermove", (e) => {
            if (this.pointerHz <= 0)
                return;
            const t = now();
            const minGap = 1000 / this.pointerHz;
            if (this.moves.length && t - this.lastMove < minGap)
                return;
            this.lastMove = t;
            this.moves.push({ t, x: Math.round(e.clientX), y: Math.round(e.clientY) });
            if (!this.moveTimer)
                this.moveTimer = setTimeout(() => this.flushMoves(), 500);
            if (this.moves.length >= 50)
                this.flushMoves();
        });
        const ptr = (kind) => (e) => {
            this.flushMoves();
            activity();
            emit({ type: "pointer", kind, t: now(), x: Math.round(e.clientX), y: Math.round(e.clientY), id: this.idOf(e.target) });
        };
        this.on(doc, "pointerdown", ptr("down"));
        this.on(doc, "pointerup", ptr("up"));
        this.on(doc, "click", ptr("click"));
        this.on(doc, "dblclick", ptr("dblclick"));
        this.on(doc, "contextmenu", ptr("contextmenu"));
        const touch = (kind) => (e) => {
            const tp = e.changedTouches && e.changedTouches[0];
            if (!tp)
                return;
            activity();
            emit({ type: "pointer", kind, t: now(), x: Math.round(tp.clientX), y: Math.round(tp.clientY), id: this.idOf(e.target) });
        };
        this.on(doc, "touchstart", touch("touchstart"));
        this.on(doc, "touchmove", touch("touchmove"));
        this.on(doc, "touchend", touch("touchend"));
        this.on(doc, "scroll", (e) => {
            if (this.scrollHz <= 0)
                return;
            const target = e.target;
            const isDoc = target === doc || target === doc.documentElement || target === doc.body;
            const id = isDoc ? 0 : this.o.map.id(target);
            if (id === undefined)
                return;
            const t = now();
            const last = this.lastScroll.get(id);
            if (last !== undefined && t - last < 1000 / this.scrollHz)
                return;
            this.lastScroll.set(id, t);
            activity();
            if (isDoc)
                emit({ type: "scroll", t, id: 0, x: Math.round(win.scrollX || doc.documentElement.scrollLeft), y: Math.round(win.scrollY || doc.documentElement.scrollTop) });
            else
                emit({ type: "scroll", t, id, x: Math.round(target.scrollLeft), y: Math.round(target.scrollTop) });
        });
        const input = (e) => {
            const el = e.target;
            if (!el || !el.tagName)
                return;
            const id = this.o.map.id(el);
            if (id === undefined)
                return;
            activity();
            const tag = el.tagName.toLowerCase();
            const editable = tag !== "input" && tag !== "textarea" && tag !== "select" && el.isContentEditable;
            if (editable) {
                // Contenteditable changes arrive as mutations; only record the interaction.
                emit({ type: "input", t: now(), id, value: "", masked: true });
                return;
            }
            const inp = el;
            const type = (inp.type || "").toLowerCase();
            const ancestorMasked = this.ancestorMasked(el);
            const masked = shouldMaskInput(el, this.o.rules.mode, ancestorMasked, this.o.rules);
            if (type === "checkbox" || type === "radio") {
                emit({ type: "input", t: now(), id, value: "", checked: inp.checked, masked: false });
                return;
            }
            const v = inp.value ?? "";
            emit({ type: "input", t: now(), id, value: masked ? maskString(v) : maskShownValue(v), masked });
        };
        this.on(doc, "input", input);
        this.on(doc, "change", input);
        this.on(doc, "focusin", (e) => {
            const id = this.idOf(e.target);
            if (id !== undefined)
                emit({ type: "focus", t: now(), id });
        });
        this.on(doc, "focusout", (e) => {
            const id = this.idOf(e.target);
            if (id !== undefined)
                emit({ type: "blur", t: now(), id });
        });
        this.on(doc, "keydown", () => activity(), { capture: true, passive: true });
        this.on(win, "resize", () => emit({ type: "viewport", t: now(), w: win.innerWidth, h: win.innerHeight, dpr: win.devicePixelRatio || 1 }));
        // Surface visibility/focus is emitted by the recorder, which outlives this
        // collector and so can report state across segment boundaries.
        // Navigation.
        const nav = (kind) => {
            const url = win.location.href;
            emit({ type: "navigation", t: now(), url, title: doc.title, kind, route: routePattern(win.location.pathname) });
        };
        const h = win.history;
        this.origPush = h.pushState;
        this.origReplace = h.replaceState;
        const self = this;
        h.pushState = function (...args) {
            const r = self.origPush.apply(this, args);
            nav("push");
            return r;
        };
        h.replaceState = function (...args) {
            const r = self.origReplace.apply(this, args);
            nav("replace");
            return r;
        };
        this.on(win, "popstate", () => nav("pop"));
        this.on(win, "hashchange", () => nav("hash"));
    }
    ancestorMasked(el) {
        let cur = el;
        while (cur) {
            const c = classify(cur, this.o.rules);
            if (c !== "none")
                return true;
            cur = cur.parentElement;
        }
        return false;
    }
    flushMoves() {
        if (this.moveTimer) {
            clearTimeout(this.moveTimer);
            this.moveTimer = null;
        }
        if (!this.moves.length)
            return;
        const pts = simplifyPath(this.moves, 2);
        this.moves = [];
        const first = pts[0];
        this.o.onActivity?.();
        this.o.emit({
            type: "pointer",
            kind: "move",
            t: first.t,
            x: first.x,
            y: first.y,
            path: pts.length > 1 ? pts.slice(1).map((p) => ({ dt: p.t - first.t, x: p.x, y: p.y })) : undefined,
        });
    }
    stop() {
        this.flushMoves();
        for (const f of this.off)
            f();
        this.off = [];
        const h = this.o.win.history;
        if (this.origPush)
            h.pushState = this.origPush;
        if (this.origReplace)
            h.replaceState = this.origReplace;
    }
}

const MAX = 4096;
const cap$2 = (s) => (s.length > MAX ? s.slice(0, MAX) + "…" : s);
/** Remove query string and userinfo, keep origin + path. */
function stripUrl(u) {
    try {
        const url = new URL(u, typeof location !== "undefined" ? location.href : "http://localhost/");
        return url.origin + url.pathname;
    }
    catch {
        return u.split("?")[0].split("#")[0];
    }
}
/** FNV-1a 32-bit as 8 hex chars. */
function fnv1a$1(s) {
    let h = 0x811c9dc5;
    for (let i = 0; i < s.length; i++) {
        h ^= s.charCodeAt(i);
        h = Math.imul(h, 0x01000193) >>> 0;
    }
    return h.toString(16).padStart(8, "0");
}
/**
 * Client-side error fingerprint: message normalized (digits → N, urls stripped,
 * whitespace collapsed) plus the top 3 stack frames with columns/lines removed.
 * The server may recompute with source maps; this groups pre-symbolication.
 */
function fingerprint(message, stack, kind = "error") {
    const norm = message
        .replace(/https?:\/\/\S+/g, "<url>")
        .replace(/0x[0-9a-f]+/gi, "N")
        .replace(/\d+/g, "N")
        .replace(/\s+/g, " ")
        .trim()
        .slice(0, 200);
    const frames = (stack || "")
        .split("\n")
        .map((l) => l.trim())
        .filter((l) => /^at |@/.test(l))
        .slice(0, 3)
        .map((l) => l.replace(/:\d+:\d+\)?$/, "").replace(/\?\S+/g, "").replace(/https?:\/\/[^/]+/g, ""));
    return fnv1a$1(`${kind}|${norm}|${frames.join("|")}`);
}
/** window.onerror / unhandledrejection / resource load errors / CSP violations → ErrorEvent_. */
class ErrorCollector {
    win;
    emit;
    now;
    off = [];
    constructor(win, emit, now) {
        this.win = win;
        this.emit = emit;
        this.now = now;
    }
    start() {
        const onError = (e) => {
            const target = e.target;
            if (target && target !== this.win && target.tagName) {
                // Resource load failure (capture phase): img, script, link, video, audio, source.
                const el = target;
                const raw = el.currentSrc || el.src || el.href || "";
                const src = raw ? stripUrl(raw) : "";
                const msg = `Failed to load ${el.tagName.toLowerCase()}`;
                this.emit({ type: "error", t: this.now(), kind: "resource", message: msg, source: cap$2(src), fingerprint: fingerprint(msg + " " + src.replace(/[0-9a-f]{8,}/gi, "H"), undefined, "resource") });
                return;
            }
            const [msg] = maskPII(String(e.message ?? "Error"));
            const stack = e.error && e.error.stack ? cap$2(String(e.error.stack)) : undefined;
            this.emit({
                type: "error",
                t: this.now(),
                kind: "error",
                message: cap$2(msg),
                stack,
                source: e.filename ? stripUrl(e.filename) : undefined,
                lineno: e.lineno || undefined,
                colno: e.colno || undefined,
                fingerprint: fingerprint(msg, stack, "error"),
            });
        };
        const onRejection = (e) => {
            const r = e.reason;
            const raw = r && typeof r === "object" && "message" in r ? String(r.message) : String(r);
            const msg = cap$2(maskPII(raw)[0]);
            const stack = r && r.stack ? cap$2(String(r.stack)) : undefined;
            this.emit({ type: "error", t: this.now(), kind: "unhandledrejection", message: msg, stack, fingerprint: fingerprint(msg, stack, "unhandledrejection") });
        };
        const onCsp = (e) => {
            const msg = `CSP violation: ${e.violatedDirective} blocked ${e.blockedURI ? stripUrl(e.blockedURI) : "inline"}`;
            this.emit({
                type: "error",
                t: this.now(),
                kind: "csp",
                message: cap$2(msg),
                source: e.sourceFile ? stripUrl(e.sourceFile) : undefined,
                lineno: e.lineNumber || undefined,
                colno: e.columnNumber || undefined,
                fingerprint: fingerprint(msg, undefined, "csp"),
            });
        };
        this.win.addEventListener("error", onError, true);
        this.win.addEventListener("unhandledrejection", onRejection);
        this.win.addEventListener("securitypolicyviolation", onCsp);
        this.off.push(() => this.win.removeEventListener("error", onError, true), () => this.win.removeEventListener("unhandledrejection", onRejection), () => this.win.removeEventListener("securitypolicyviolation", onCsp));
    }
    stop() {
        for (const f of this.off)
            f();
        this.off = [];
    }
}

const VISIT_KEY$1 = "__backstory_visit";
class CrossTab {
    o;
    tab = newId();
    started;
    visitId = "";
    tabId = 0;
    leader = false;
    leaderTab = "";
    onLeaderChange = null;
    ch = null;
    peers = new Map();
    nextTabId = 1;
    hb = null;
    now;
    st;
    si;
    ci;
    stopped = false;
    constructor(o) {
        this.o = o;
        this.now = o.now ?? (() => Date.now());
        this.started = this.now();
        this.st = o.setTimeout ?? ((fn, ms) => setTimeout(fn, ms));
        this.si = o.setInterval ?? ((fn, ms) => setInterval(fn, ms));
        this.ci = o.clearInterval ?? ((h) => clearInterval(h));
    }
    storage() {
        if (this.o.storage !== undefined)
            return this.o.storage;
        try {
            return typeof localStorage !== "undefined" ? localStorage : null;
        }
        catch {
            return null;
        }
    }
    loadPersisted() {
        try {
            const raw = this.storage()?.getItem(VISIT_KEY$1);
            if (!raw)
                return null;
            const v = JSON.parse(raw);
            if (v && typeof v.id === "string" && typeof v.at === "number" && this.now() - v.at < this.o.inactivityMs) {
                // Records written before surface indexes were persisted only ever had surface 0.
                return { id: v.id, at: v.at, next: typeof v.next === "number" && v.next > 0 ? v.next : 1 };
            }
        }
        catch {
            /* ignore */
        }
        return null;
    }
    /** Persist last-activity time and the surface high-water mark (call on activity, throttled by caller). */
    touch() {
        try {
            this.storage()?.setItem(VISIT_KEY$1, JSON.stringify({ id: this.visitId, at: this.now(), next: this.nextTabId }));
        }
        catch {
            /* quota */
        }
    }
    /** Never let the next index fall back below one already handed out anywhere in the visit. */
    raiseMark(next) {
        if (Number.isFinite(next) && next > this.nextTabId)
            this.nextTabId = next;
    }
    /** Resolve the shared visit id and this tab's index. */
    start() {
        const factory = this.o.channelFactory ?? ((name) => (typeof BroadcastChannel !== "undefined" ? new BroadcastChannel(name) : null));
        try {
            this.ch = factory("backstory:" + this.o.projectKey);
        }
        catch {
            this.ch = null;
        }
        return new Promise((resolve) => {
            let settled = false;
            const finish = () => {
                if (settled)
                    return;
                settled = true;
                this.startHeartbeat();
                this.touch();
                resolve({ visitId: this.visitId, tabId: this.tabId, leader: this.leader });
            };
            if (!this.ch) {
                this.claimSurface();
                this.leader = true;
                this.leaderTab = this.tab;
                return finish();
            }
            this.ch.onmessage = (e) => {
                const m = e.data;
                if (!m || typeof m !== "object")
                    return;
                if (m.k === "visit" && m.to === this.tab && !settled) {
                    this.visitId = m.visitId;
                    this.tabId = m.tabId;
                    this.raiseMark(m.next);
                    this.leader = false;
                    this.leaderTab = m.leader;
                    this.peers.set(m.leader, { started: 0, last: this.now() });
                    finish();
                    return;
                }
                this.handle(m);
            };
            this.ch.postMessage({ k: "hello", tab: this.tab, started: this.started });
            this.st(() => {
                if (settled)
                    return;
                // No leader answered: become leader; reuse a recent persisted visit.
                this.claimSurface();
                this.leader = true;
                this.leaderTab = this.tab;
                finish();
            }, this.o.helloWaitMs ?? 150);
        });
    }
    /**
     * Leader path: continue a persisted visit's surface numbering, or open a new
     * visit at surface 0. Peers that were alive when this context started raise
     * the mark again through their heartbeats.
     */
    claimSurface() {
        const p = this.loadPersisted();
        if (p) {
            this.visitId = p.id;
            this.nextTabId = p.next;
            this.tabId = this.nextTabId++;
            return;
        }
        this.visitId = newId();
        this.tabId = 0;
        this.nextTabId = 1;
    }
    handle(m) {
        if (this.stopped)
            return;
        switch (m.k) {
            case "hello":
                if (m.tab === this.tab)
                    return;
                this.peers.set(m.tab, { started: m.started, last: this.now() });
                if (this.leader && this.visitId) {
                    const tabId = this.nextTabId++;
                    this.ch?.postMessage({ k: "visit", from: this.tab, to: m.tab, visitId: this.visitId, tabId, leader: this.tab, next: this.nextTabId });
                    this.touch();
                }
                return;
            case "hb":
                if (m.tab === this.tab)
                    return;
                this.peers.set(m.tab, { started: m.started, last: this.now() });
                this.raiseMark(m.next);
                if (m.leader) {
                    // Two leaders (race): the older tab wins.
                    if (this.leader && (m.started < this.started || (m.started === this.started && m.tab < this.tab)))
                        this.setLeader(false, m.tab);
                    else if (!this.leader)
                        this.leaderTab = m.tab;
                    if (!this.leader && m.visitId && m.visitId !== this.visitId)
                        this.visitId = m.visitId;
                }
                return;
            case "bye":
                this.peers.delete(m.tab);
                if (m.tab === this.leaderTab && !this.leader)
                    this.elect();
                return;
        }
    }
    setLeader(v, leaderTab) {
        const changed = this.leader !== v;
        this.leader = v;
        this.leaderTab = leaderTab;
        if (changed)
            this.onLeaderChange?.(v);
    }
    /** Bully election: the earliest-started live tab leads. */
    elect() {
        const now = this.now();
        let best = { tab: this.tab, started: this.started };
        for (const [tab, p] of this.peers) {
            if (now - p.last > (this.o.heartbeatMs ?? 2000) * 2.5) {
                this.peers.delete(tab);
                continue;
            }
            if (p.started < best.started || (p.started === best.started && tab < best.tab))
                best = { tab, started: p.started };
        }
        this.setLeader(best.tab === this.tab, best.tab);
    }
    startHeartbeat() {
        if (!this.ch)
            return;
        const hbMs = this.o.heartbeatMs ?? 2000;
        this.hb = this.si(() => {
            if (this.stopped)
                return;
            this.ch?.postMessage({ k: "hb", tab: this.tab, started: this.started, leader: this.leader, visitId: this.visitId, next: this.nextTabId });
            // Leader gone? (missed 2.5 heartbeats)
            const l = this.peers.get(this.leaderTab);
            if (!this.leader && (!l || this.now() - l.last > hbMs * 2.5))
                this.elect();
            if (this.leader)
                this.touch();
        }, hbMs);
    }
    stop() {
        this.stopped = true;
        if (this.hb)
            this.ci(this.hb);
        try {
            this.ch?.postMessage({ k: "bye", tab: this.tab });
            this.ch?.close();
        }
        catch {
            /* ignore */
        }
        this.ch = null;
    }
}

var workerSource$1 = "/* Backstory SDK v0.1.0 — backstory.io */\n!function(){\"use strict\";const t={HEALTHY:{pointerHz:20,scrollHz:10,canvasLevel:1,network:\"all\",networkBodies:!0,console:\"all\",attrCoalesceMs:0,checkpointIntervalMultiplier:1,allowDropP1:!1},CONSTRAINED:{pointerHz:8,scrollHz:4,canvasLevel:2,network:\"meta\",networkBodies:!1,console:\"warn\",attrCoalesceMs:0,checkpointIntervalMultiplier:1,allowDropP1:!1},DEGRADED:{pointerHz:2,scrollHz:2,canvasLevel:4,network:\"failures\",networkBodies:!1,console:\"error\",attrCoalesceMs:250,checkpointIntervalMultiplier:2,allowDropP1:!1},SURVIVAL:{pointerHz:0,scrollHz:1,canvasLevel:5,network:\"failures\",networkBodies:!1,console:\"error\",attrCoalesceMs:250,checkpointIntervalMultiplier:0,allowDropP1:!0},PAUSED:{pointerHz:0,scrollHz:0,canvasLevel:6,network:\"none\",networkBodies:!1,console:\"none\",attrCoalesceMs:0,checkpointIntervalMultiplier:0,allowDropP1:!0}};function e(t){switch(t.type){case\"control\":case\"snapshot\":case\"error\":case\"custom\":case\"assist\":return 0;case\"mutation\":case\"input\":case\"navigation\":case\"focus\":case\"blur\":case\"selection\":case\"viewport\":case\"visibility\":return 1;case\"pointer\":return\"move\"===t.kind?3:1;case\"console\":case\"network\":case\"vital\":case\"longtask\":case\"memory\":case\"media\":default:return 2;case\"scroll\":return 3;case\"canvas_cmds\":case\"canvas_frame\":return 4}}function s(t){const e=JSON.stringify(t);let s=e.length;for(let t=0;t<e.length;t++){const i=e.charCodeAt(t);i>127&&(s+=i>2047?2:1)}return s}class i{q=[[],[],[],[],[]];bytesBy=[0,0,0,0,0];droppedBytes=0;push(t,i=s(t)){const o=e(t);this.q[o].push({e:t,bytes:i}),this.bytesBy[o]+=i}get bytes(){return this.bytesBy[0]+this.bytesBy[1]+this.bytesBy[2]+this.bytesBy[3]+this.bytesBy[4]}bytesAt(t){return this.bytesBy[t]}get length(){return this.q.reduce((t,e)=>t+e.length,0)}shed(t,e=2){let s=0;for(let i=4;i>e&&s<t;i--){const t=this.bytesBy[i];t&&(s+=t,this.q[i]=[],this.bytesBy[i]=0)}return this.droppedBytes+=s,s}take(t){const e=[];for(let s=t;s<5;s++){const t=this.q[s];for(let s=0;s<t.length;s++)e.push(t[s]);this.q[s]=[],this.bytesBy[s]=0}return e}dropWhere(t){let e=0;for(let s=0;s<5;s++){const i=this.q[s],o=[];for(let s=0;s<i.length;s++){const n=i[s];t(n.e)?e+=n.bytes:o.push(n)}this.q[s]=o;let n=0;for(let t=0;t<o.length;t++)n+=o[t].bytes;this.bytesBy[s]=n}return this.droppedBytes+=e,e}drain(){const t=[];for(let e=0;e<5;e++){const s=this.q[e];for(let e=0;e<s.length;e++)t.push(s[e]);this.q[e]=[],this.bytesBy[e]=0}return t.sort((t,e)=>t.e.t-e.e.t),t}}const o=[\"HEALTHY\",\"CONSTRAINED\",\"DEGRADED\",\"SURVIVAL\",\"PAUSED\"],n=\"x-backstory-project-key\",r=\"x-backstory-encoding\",h=\"x-backstory-sdk\",l=\"x-backstory-server-time\",a=\"x-request-id\",c={HEALTHY:0,CONSTRAINED:2e4,DEGRADED:3e4,SURVIVAL:6e4,PAUSED:6e4},d=t=>o.indexOf(t),u=(t,e)=>o[Math.max(0,Math.min(o.length-1,d(t)+e))];class p{ev;level=\"HEALTHY\";forced=null;holdUntil=0;betterSince=0;lastCritical=-1/0;lastSelfDowngrade=-1/0;longTasks=[];constructor(t={},e=\"HEALTHY\"){this.ev=t,this.level=e}static target(t){const e=t.queuedBytes/Math.max(t.goodputBps,1),s=t.budgetBytes>0?t.queuedBytes/t.budgetBytes:0;let i;return i=!t.online&&t.spillFull?\"PAUSED\":e>60||s>.95||!t.online?\"SURVIVAL\":e>15||s>.85||t.ingest429?\"DEGRADED\":e>5||s>.6?\"CONSTRAINED\":\"HEALTHY\",{level:i,drainSec:e,memPct:s}}force(t,e,s){this.forced=t,t&&this.apply(t,e,s??{queuedBytes:0,budgetBytes:1,goodputBps:1,online:!0,spillFull:!1,ingest429:!1},0,0,\"forced\")}recordLongTask(t,e,s){this.longTasks.push({t:t,ms:e,self:s}),this.pruneSelf(t)}selfShare(t){this.pruneSelf(t);let e=0,s=0;for(const t of this.longTasks)e+=t.ms,t.self&&(s+=t.ms);return e>0?s/e:0}pruneSelf(t){const e=t-3e4;for(;this.longTasks.length&&this.longTasks[0].t<e;)this.longTasks.shift()}tick(t,e){const{level:s,drainSec:i,memPct:o}=p.target(e);return this.forced?this.level!==this.forced&&this.apply(this.forced,t,e,i,o,\"forced\"):d(s)>d(this.level)?this.apply(s,t,e,i,o,function(t,e,s,i){switch(t){case\"PAUSED\":return\"spill_full\";case\"SURVIVAL\":return e.online?i>.95?\"memory\":\"drain\":\"offline\";case\"DEGRADED\":return e.ingest429?\"rate_limited\":i>.85?\"memory\":\"drain\";case\"CONSTRAINED\":return i>.6?\"memory\":\"drain\";default:return\"recovery\"}}(s,e,0,o)):d(s)<d(this.level)?(this.betterSince||(this.betterSince=t),t>=this.holdUntil&&t-this.betterSince>=c[this.level]&&(this.apply(u(this.level,-1),t,e,i,o,\"recovery\"),this.betterSince=t)):this.betterSince=0,!this.forced&&\"PAUSED\"!==this.level&&this.selfShare(t)>.05&&this.longTasks.length>=3&&t-this.lastSelfDowngrade>3e4&&(this.lastSelfDowngrade=t,this.longTasks=[],this.apply(u(this.level,1),t,e,i,o,\"self_jank\")),o>.95&&t-this.lastCritical>5e3&&(this.lastCritical=t,this.ev.onCheckpointAndDrop?.()),this.level}apply(t,e,s,i,o,n){t!==this.level&&(this.level=t,this.holdUntil=e+c[t],this.betterSince=0,this.ev.onLevel?.(t,s,i,o,n))}}function f(t,e,s,i,o){if(!e.length)return null;const n=Math.round(o),r=new Array(e.length);let h=1/0,l=-1/0;for(let s=0;s<e.length;s++){const i=e[s].e,o=i.t+n;\"snapshot\"===i.type&&(t.checkpointId=i.checkpointId),r[s]=n?{...i,t:o}:i,o<h&&(h=o),o>l&&(l=o)}t.seq+=1;const a={v:1,sessionId:t.sessionId,visitId:t.visitId,segmentIndex:t.segmentIndex,seq:t.seq,checkpointId:t.checkpointId,firstTs:h,lastTs:l,level:s,droppedBytes:i,events:r};return t.gaps.length&&(a.gaps=t.gaps,t.gaps=[]),t.metaDirty&&t.meta&&(a.meta=t.meta,t.metaDirty=!1),a}let y=null;let g=null,m=null,b=0;function v(t,e=null){g=t,m=e,b=0}async function k(t,e=\"deflate\"){const s=function(t){if(\"undefined\"==typeof TextEncoder){const e=[];for(let s=0;s<t.length;s++){let i=t.codePointAt(s);i>65535&&s++,i<128?e.push(i):i<2048?e.push(192|i>>6,128|63&i):i<65536?e.push(224|i>>12,128|i>>6&63,128|63&i):e.push(240|i>>18,128|i>>12&63,128|i>>6&63,128|63&i)}return new Uint8Array(e)}return(y??=new TextEncoder).encode(t)}(t);if(\"zstd\"===e&&g&&b<3)try{const t=m&&g.compressUsingDict?g.compressUsingDict(s,m,3):g.compress(s,3);if(t&&t.byteLength>0)return{body:t,encoding:\"json+zstd\"};b++}catch{b++}const i=globalThis.CompressionStream;if(!i)return{body:s,encoding:\"json\"};try{const t=new Blob([s]).stream().pipeThrough(new i(\"deflate\")),e=await new Response(t).arrayBuffer();return{body:new Uint8Array(e),encoding:\"json+deflate\"}}catch{return{body:s,encoding:\"json\"}}}const w=/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;function q(t){return function(t){return\"string\"==typeof t&&w.test(t)}(t)?t:function(){const t=globalThis.crypto;if(t&&\"function\"==typeof t.randomUUID)return t.randomUUID();const e=new Uint8Array(16);if(t&&\"function\"==typeof t.getRandomValues)t.getRandomValues(e);else for(let t=0;t<16;t++)e[t]=Math.floor(256*Math.random());e[6]=15&e[6]|64,e[8]=63&e[8]|128;const s=Array.from(e,t=>t.toString(16).padStart(2,\"0\")).join(\"\");return`${s.slice(0,8)}-${s.slice(8,12)}-${s.slice(12,16)}-${s.slice(16,20)}-${s.slice(20)}`}()}class B{o;kind=\"fetch\";degraded=!1;constructor(t){this.o=t}async send(t){const e=this.o.now();let s;try{s=await this.o.fetch(this.o.url,{method:\"POST\",headers:{\"content-type\":\"application/octet-stream\",[n]:this.o.projectKey,[r]:t.encoding,[h]:this.o.sdkVersion,[a]:this.o.requestId},body:t.body})}catch{return{ok:!1,kind:\"network\",status:0,rttMs:this.o.now()-e,sentAt:e}}const i=this.o.now()-e,o=s.headers.get(l),c=o?Number(o):void 0;if(s.ok){let t=null;try{t=await s.json()}catch{}return{ok:!0,ack:t?.ack,credits:t?.credits,serverTime:c??t?.serverTime,config:t?.config,rttMs:i,sentAt:e}}if(401===s.status||402===s.status||403===s.status){let t=401===s.status?\"unauthorized\":402===s.status?\"subscription_ended\":\"origin_not_allowed\";if(402===s.status)try{const e=await s.json();d=e?.code,(\"unauthorized\"===d||\"origin_not_allowed\"===d||\"subscription_ended\"===d||\"limit_reached\"===d||\"feature_not_included\"===d)&&(t=e.code)}catch{}return{ok:!1,kind:\"fatal\",status:s.status,fatal:t,serverTime:c,rttMs:i,sentAt:e}}var d;if(400===s.status||413===s.status)return{ok:!1,kind:413===s.status?\"413\":\"400\",status:s.status,serverTime:c,rttMs:i,sentAt:e};const u=1e3*Number(s.headers.get(\"retry-after\")||0);return{ok:!1,kind:429===s.status?\"429\":\"5xx\",status:s.status,retryAfterMs:u,serverTime:c,rttMs:i,sentAt:e}}close(){}}class S{o;queue=[];inflight=new Map;inFlightBytes=0;credits;acked=new Map;goodputBps=0;ackedBytesWindow=[];stopped=!1;pumping=!1;clockOffsetMs=0;offsetSamples=0;lastRecoveredAt=-1/0;fetchLink;preferred=null;active;switchBackTimer=null;clearT;lossyDelivered=0;requestId;constructor(t){this.o=t,this.requestId=q(t.requestId),this.o.requestId=this.requestId,this.credits=t.initialCredits??262144,\"number\"==typeof t.initialClockOffsetMs&&Number.isFinite(t.initialClockOffsetMs)&&(this.clockOffsetMs=t.initialClockOffsetMs,this.offsetSamples=1),this.clearT=t.clearTimeout??(t=>clearTimeout(t)),this.fetchLink=new B(t),this.active=this.fetchLink,this.preferred=this.buildPreferred(),this.preferred&&(this.active=this.preferred)}buildPreferred(){return null}applyServerFrame(t){\"number\"==typeof t.credits&&t.credits>0&&(this.credits=t.credits),t.config&&this.o.onConfig?.(t.config)}get preference(){return this.o.transport??\"fetch\"}setPreferred(t){this.preferred?.close(),this.preferred=t,this.stopped||(this.switchBackTimer&&this.clearT(this.switchBackTimer),this.switchBackTimer=null,this.active=t,this.o.onTransportChange?.(t.kind),this.pump())}degradePreferred(){this.fallback()}get wtUrl(){return this.o.wtUrl??function(t){try{const e=new URL(t);return e.protocol=\"https:\",e.pathname=e.pathname.replace(/\\/+$/,\"\")+\"/wt\",e.toString()}catch{return t.replace(/\\/+$/,\"\")+\"/wt\"}}(this.o.url)}get wsUrl(){return this.o.wsUrl??function(t){try{const e=new URL(t);return e.protocol=\"https:\"===e.protocol?\"wss:\":\"ws:\",e.pathname=e.pathname.replace(/\\/+$/,\"\")+\"/ws\",e.toString()}catch{return t.replace(/^http/,\"ws\").replace(/\\/+$/,\"\")+\"/ws\"}}(this.o.url)}fallback(){if(this.active===this.fetchLink)return;this.active=this.fetchLink;const t=this.o.now();for(const e of this.queue)e.nextAt>t&&(e.nextAt=t);this.o.onTransportChange?.(\"fetch\"),this.switchBackTimer&&this.clearT(this.switchBackTimer),this.switchBackTimer=this.o.setTimeout(()=>this.switchBack(),this.o.switchBackMs??3e5),this.pump()}switchBack(){if(this.switchBackTimer=null,this.stopped||!this.preferred)return;const t=this.preferred.reset;if(\"function\"==typeof t)t.call(this.preferred);else if(this.preferred.degraded)return;this.active=this.preferred,this.o.onTransportChange?.(this.preferred.kind),this.pump()}get kind(){return this.active.kind}get activeLink(){return this.active}get preferredLink(){return this.preferred}get queuedBytes(){let t=0;for(const e of this.queue)t+=e.body.byteLength;return t}get pendingCount(){return this.queue.length+this.inflight.size}get livePending(){let t=this.inflight.size;for(const e of this.queue)e.recovered||t++;return t}get stoppedState(){return this.stopped}enqueue(t,e,s,i,o=!1){this.queue.push({sessionId:t,seq:e,body:s,encoding:i,attempts:0,nextAt:0,lossy:o||void 0}),this.pump()}enqueueRecovered(t,e,s,i){this.queue.push({sessionId:t,seq:e,body:s,encoding:i,attempts:0,nextAt:0,recovered:!0}),this.pump()}dropQueued(t){const e=[];return this.queue=this.queue.filter(s=>!(s.sessionId===t&&!s.recovered)||(e.push(s),!1)),e}drain(){const t=[...this.inflight.values(),...this.queue];return this.queue=[],t.sort((t,e)=>t.seq-e.seq)}stop(){this.stopped=!0,this.switchBackTimer&&this.clearT(this.switchBackTimer),this.preferred?.close()}key(t){return t.sessionId+\":\"+t.seq+(t.lossy?\":l\":\"\")}pump(){if(!this.stopped&&!this.pumping){this.pumping=!0;try{const t=this.o.now(),e=this.o.maxConcurrent??(\"fetch\"===this.active.kind?2:8);this.queue.sort((t,e)=>Number(!!t.recovered)-Number(!!e.recovered)||t.sessionId.localeCompare(e.sessionId)||t.seq-e.seq||t.nextAt-e.nextAt);let s=!1;for(let i=0;i<this.queue.length&&this.inflight.size<e;){const e=this.queue[i];if(e.nextAt>t)i++;else{if(e.recovered){if(this.livePending>0)break;const e=1e3-(t-this.lastRecoveredAt);if(e>0){s||(s=!0,this.o.setTimeout(()=>this.pump(),e));break}this.lastRecoveredAt=t}if(this.inFlightBytes+e.body.byteLength>this.credits&&this.inflight.size>0)break;this.queue.splice(i,1),this.send(e)}}const i=this.queue.find(e=>e.nextAt>t);i&&this.o.setTimeout(()=>this.pump(),Math.max(10,i.nextAt-t))}finally{this.pumping=!1}}}async send(t){this.inflight.set(this.key(t),t),this.inFlightBytes+=t.body.byteLength,t.attempts++;const e=t.recovered?this.fetchLink:this.active,s=await e.send(t);if(this.stopped&&!s.ok)return this.inflight.delete(this.key(t)),void(this.inFlightBytes-=t.body.byteLength);if(\"number\"==typeof s.serverTime&&this.sampleClock(s.serverTime,s.sentAt,s.rttMs),!s.ok)return\"fatal\"===s.kind?(this.inflight.delete(this.key(t)),this.inFlightBytes-=t.body.byteLength,this.stopped=!0,void this.o.onFatal(s.fatal??\"unauthorized\")):\"400\"===s.kind||\"413\"===s.kind?(this.inflight.delete(this.key(t)),this.inFlightBytes-=t.body.byteLength,this.o.onRejected?.(t.sessionId,t.seq,s.kind,!!t.lossy),t.lossy||this.o.onAck(t.sessionId,Math.max(this.acked.get(t.sessionId)??0,t.seq),0),void this.pump()):t.lossy&&\"network\"===s.kind&&\"webtransport\"===e.kind?(this.inflight.delete(this.key(t)),this.inFlightBytes-=t.body.byteLength,void this.pump()):void this.fail(t,s.kind,s.retryAfterMs??0,e!==this.active&&!t.recovered);if(this.inflight.delete(this.key(t)),this.inFlightBytes-=t.body.byteLength,this.recordGoodput(t.body.byteLength,s.rttMs),\"number\"==typeof s.credits&&s.credits>0&&(this.credits=s.credits),s.config&&this.o.onConfig?.(s.config),t.lossy)this.lossyDelivered++,this.o.onAck(t.sessionId,this.acked.get(t.sessionId)??0,t.body.byteLength);else{const e=this.acked.get(t.sessionId)??0;let i=Math.max(e,t.seq);\"number\"==typeof s.ack&&(i=Math.max(e,s.ack)),this.acked.set(t.sessionId,i),this.o.onAck(t.sessionId,i,t.body.byteLength)}this.pump()}fail(t,e,s,i=!1){if(this.inflight.delete(this.key(t)),this.inFlightBytes-=t.body.byteLength,i)return t.nextAt=this.o.now(),this.queue.push(t),this.o.onPressure(e,0),void this.o.setTimeout(()=>this.pump(),0);const o=Math.min(3e4,1e3*2**Math.min(t.attempts-1,5)),n=Math.random()*o*.3,r=\"network\"===e&&\"fetch\"!==this.active.kind?Math.min(o,250*t.attempts):null,h=Math.max(s,r??o+n);t.nextAt=this.o.now()+h,this.queue.push(t),this.o.onPressure(e,h),this.o.setTimeout(()=>this.pump(),h)}sampleClock(t,e,s){if(!Number.isFinite(t))return;const i=t-(e+s/2);if(this.offsetSamples++,1===this.offsetSamples)this.clockOffsetMs=i;else{const t=.3*(i-this.clockOffsetMs);this.clockOffsetMs+=Math.max(-500,Math.min(500,t))}this.o.onServerTime(this.clockOffsetMs)}recordGoodput(t,e){const s=this.o.now(),i=Number.isFinite(e)&&e>0?e:1;this.ackedBytesWindow.push({t:s,b:t,ms:i});const o=s-1e4;for(;this.ackedBytesWindow.length&&this.ackedBytesWindow[0].t<o;)this.ackedBytesWindow.shift();let n=0,r=0;for(const t of this.ackedBytesWindow)n+=t.b,r+=t.ms;const h=n/(Math.max(50,r)/1e3);this.goodputBps=this.goodputBps?this.goodputBps+.3*(h-this.goodputBps):h}}const T={ws:\"__BACKSTORY_WS__\",wt:\"__BACKSTORY_WT__\",zstd:\"__BACKSTORY_ZSTD__\",monitor:\"__BACKSTORY_MONITOR__\",inline:\"__BACKSTORY_INLINE__\"},M=new Map;function I(t,e,s){const i=globalThis,o=s?.[t];if(o)return Promise.resolve(o);const n=T[t];if(i[n])return Promise.resolve(i[n]);if(!e)return Promise.resolve(null);const r=t+\"|\"+e;let h=M.get(r);return h||(h=new Promise(t=>{try{if(\"function\"==typeof i.importScripts)return i.importScripts(e),void t(i[n]??null);if(i.document){const s=i.document.createElement(\"script\");return s.async=!0,s.src=e,s.onload=()=>t(i[n]??null),s.onerror=()=>t(null),void i.document.head.appendChild(s)}}catch{}t(null)}),M.set(r,h),h)}class A{available=!1;budgetBytes=0;bytes=0;count=0;full=!1;async open(){}async put(){return!1}async delete(){}async deleteThrough(){}stale(){return[]}async get(){return null}async wipe(){}}const L=\"chunks\",D=52428800;function C(t){return new Promise((e,s)=>{t.onsuccess=()=>e(t.result),t.onerror=()=>s(t.error)})}function x(t){return new Promise((e,s)=>{t.oncomplete=()=>e(),t.onerror=()=>s(t.error),t.onabort=()=>s(t.error??new Error(\"aborted\"))})}class U{o;available=!1;budgetBytes=D;bytes=0;count=0;db=null;index=new Map;opening=null;constructor(t={}){this.o=t}lastCurrent=\"\";get full(){if(!this.available||this.bytes<this.budgetBytes)return!1;for(const t of this.index.values())if(t.sessionId!==this.lastCurrent||0!==t.priorityHint)return!1;return!0}key(t,e){return t+\":\"+e}open(){return this.opening||(this.opening=(async()=>{const t=this.o.indexedDB??globalThis.indexedDB;if(t)try{const e=t.open(\"backstory-spill\",1);e.onupgradeneeded=()=>{const t=e.result;if(!t.objectStoreNames.contains(L)){const e=t.createObjectStore(L,{keyPath:[\"sessionId\",\"seq\"]});e.createIndex(\"bySession\",\"sessionId\"),e.createIndex(\"byCreated\",\"createdAt\")}};const s=await C(e);this.db=s;const i=this.o.budgetBytes;let o=i??104857600;try{const t=this.o.estimate?await this.o.estimate():await(globalThis.navigator?.storage?.estimate?.());t?.quota&&(o=Math.min(o,Math.floor(.2*t.quota)))}catch{o=Math.min(o,D)}this.budgetBytes=void 0!==i?o:Math.max(1048576,o);const n=s.transaction(L,\"readonly\").objectStore(L).openCursor();await new Promise((t,e)=>{n.onsuccess=()=>{const e=n.result;if(!e)return t();const s=e.value,i={sessionId:s.sessionId,seq:s.seq,bytes:s.bytes,createdAt:s.createdAt,priorityHint:s.priorityHint,project:s.project,encoding:s.encoding};this.index.set(this.key(i.sessionId,i.seq),i),this.bytes+=i.bytes,this.count++,e.continue()},n.onerror=()=>e(n.error)}),this.available=!0}catch{this.db=null,this.available=!1}})()),this.opening}evictionCandidates(t){const e=[...this.index.values()],s=e.filter(e=>e.sessionId!==t).sort((t,e)=>t.createdAt-e.createdAt),i=e.filter(e=>e.sessionId===t),o=t=>i.filter(e=>e.priorityHint===t).sort((t,e)=>t.createdAt-e.createdAt);return[...s,...o(2),...o(1),...o(0)]}async put(t,e){if(this.lastCurrent=e,!this.db)return!1;if(t.bytes>this.budgetBytes)return!1;if(this.bytes+t.bytes>this.budgetBytes){const s=this.evictionCandidates(e),i=[];let o=0;for(const n of s){if(this.bytes-o+t.bytes<=this.budgetBytes)break;n.sessionId===e&&0===n.priorityHint&&0!==t.priorityHint||(i.push(n),o+=n.bytes)}if(this.bytes-o+t.bytes>this.budgetBytes)return!1;const n=this.db.transaction(L,\"readwrite\"),r=n.objectStore(L);for(const t of i)r.delete([t.sessionId,t.seq]);await x(n);for(const t of i)this.forget(t.sessionId,t.seq)}try{const e=this.db.transaction(L,\"readwrite\");e.objectStore(L).put(t),await x(e)}catch{return!1}const s=this.key(t.sessionId,t.seq);this.index.has(s)||(this.bytes+=t.bytes,this.count++);const{body:i,...o}=t;return this.index.set(s,o),!0}forget(t,e){const s=this.key(t,e),i=this.index.get(s);i&&(this.index.delete(s),this.bytes-=i.bytes,this.count--)}async delete(t,e){if(this.db&&this.index.has(this.key(t,e))){try{const s=this.db.transaction(L,\"readwrite\");s.objectStore(L).delete([t,e]),await x(s)}catch{}this.forget(t,e)}}async deleteThrough(t,e){if(!this.db)return;const s=[...this.index.values()].filter(s=>s.sessionId===t&&s.seq<=e);if(s.length){try{const t=this.db.transaction(L,\"readwrite\"),e=t.objectStore(L);for(const t of s)e.delete([t.sessionId,t.seq]);await x(t)}catch{}for(const t of s)this.forget(t.sessionId,t.seq)}}stale(t,e){return[...this.index.values()].filter(s=>s.sessionId!==t&&s.project===e).sort((t,e)=>t.createdAt-e.createdAt||t.seq-e.seq)}async get(t,e){if(!this.db)return null;try{const s=this.db.transaction(L,\"readonly\");return await C(s.objectStore(L).get([t,e]))??null}catch{return null}}async wipe(){if(this.db){try{const t=this.db.transaction(L,\"readwrite\");t.objectStore(L).clear(),await x(t)}catch{}this.index.clear(),this.bytes=0,this.count=0}}}const P=[\"error\",\"network_failure\",\"offline\",\"long_task\"];function R(t){const e=\"canvas_frame\"===t.e.type;return Math.ceil(t.bytes*(e?.8:.35))}function E(t){let s=0;for(const i of t){if(e(i.e)<3)return!1;s+=i.bytes}return Math.ceil(.35*s)+120<=1195}const H=self,O=new class{d;cfg;q=new i;gov;tr;session=null;flushTimer=null;tickTimer=null;online=!0;ingest429Until=0;now;st;ct;started=!1;encoding=Promise.resolve();stopped=!1;lastPushed=null;spill;project=\"\";spillWrites=new Map;recovery=[];recoveryLoading=!1;recoveryInFlight=new Set;encodingBytes=0;abandoned=null;awaitingDropSnapshot=!1;throwStrikes=0;slowStrikes=0;watchdogTripped=!1;lastHealthSampleAt=-1/0;mon=null;monLoading=null;linkLoading=null;pendingMode=null;compression=\"deflate\";lossySeq=0;lossyDropped=0;sentContents=new Map;lastOversizeCheckpointAt=-1/0;constructor(t){this.d=t,this.now=t.now??(()=>Date.now()),this.st=t.setTimeout??((t,e)=>setTimeout(t,e)),this.ct=t.clearTimeout??(t=>clearTimeout(t)),this.spill=t.spill??new A}get level(){return this.gov?.level??\"HEALTHY\"}handle(t){try{this.dispatch(t),this.throwStrikes=0}catch(t){this.throwStrikes++,this.cfg?.debug&&console.warn(\"[backstory] worker error\",t),this.throwStrikes>=3&&this.tripWatchdog(\"exceptions\")}}dispatch(t){switch(t.type){case\"init\":return this.init(t.cfg);case\"session\":return this.flushNow(),this.session=(e=t.sessionId,s=t.visitId,i=t.segmentIndex,o=t.meta,{sessionId:e,visitId:s,segmentIndex:i,seq:0,checkpointId:0,meta:o,metaDirty:!0,gaps:[]}),void this.feedRecovery();case\"meta\":return void(this.session&&(this.session.meta=t.meta,this.session.metaDirty=!0));case\"event\":return this.push(t.e);case\"events\":for(const e of t.es)this.push(e);return;case\"flush\":return this.flushNow();case\"drain\":return this.drainToMain();case\"online\":if(this.online=t.online,t.online)this.tr.pump();else if(\"monitor\"===this.mon?.mode){const t=this.mon.offlineTrigger();t&&this.burst(t)}return;case\"mode\":return this.setMode(t.mode);case\"monitor-triggers\":return void this.mon?.setTriggers(t.triggers);case\"debug\":return this.d.post({type:\"debug\",state:this.debugState()});case\"longtask\":return void this.gov.recordLongTask(this.now(),t.ms,t.self);case\"force-level\":return void this.gov.force(t.level,this.now(),this.metrics());case\"handover\":return this.handover();case\"wipe\":return this.spill.wipe(),void(this.recovery=[]);case\"stop\":return this.stop();case\"drop-gated\":return this.dropGated(t)}var e,s,i,o}init(e){this.cfg=e,this.project=function(t){let e=2166136261;for(let s=0;s<t.length;s++)e^=t.charCodeAt(s),e=Math.imul(e,16777619);return(e>>>0).toString(36)}(e.projectKey),this.gov=new p({onLevel:(e,s,i,o,n)=>{this.q.push({type:\"control\",kind:\"level\",t:this.now(),level:e,drainSec:Math.round(i),memPct:Math.round(100*o)/100,reason:n}),this.sampleHealth(e),this.d.post({type:\"level\",level:e,collectors:t[e]})},onCheckpointAndDrop:()=>this.checkpointAndDrop()},e.initialLevel??\"HEALTHY\"),e.initialLevel&&\"HEALTHY\"!==e.initialLevel&&this.d.post({type:\"level\",level:e.initialLevel,collectors:t[e.initialLevel]}),this.tr=new S({url:e.ingestUrl,projectKey:e.projectKey,sdkVersion:e.sdkVersion,requestId:e.requestId,fetch:this.d.fetch,now:this.now,setTimeout:this.st,clearTimeout:this.ct,initialCredits:e.credits,initialClockOffsetMs:e.clockOffsetMs,onAck:(t,e)=>this.onAck(t,e),onFatal:t=>{this.stop(),this.d.post({type:\"fatal\",reason:t})},onPressure:(t,e)=>{\"429\"===t&&(this.ingest429Until=this.now()+Math.max(e,1e4)),\"network\"===t&&(this.online=!1)},onServerTime:()=>{},onConfig:t=>this.d.post({type:\"config\",remote:t}),transport:e.transport??\"fetch\",wsUrl:e.wsUrl,wtUrl:e.wtUrl,session:()=>this.session?{sessionId:this.session.sessionId,visitId:this.session.visitId}:null,socketFactory:this.d.socketFactory,webTransportFactory:this.d.webTransportFactory,switchBackMs:this.d.switchBackMs,heartbeatMs:this.d.heartbeatMs,onRejected:(t,e,s,i)=>this.onRejected(t,e,s,i),onTransportChange:t=>this.d.post({type:\"transport\",kind:t})}),(\"monitor\"===e.initialMode||e.preloadMonitor)&&this.ensureMonitor().then(()=>{e.initialMode&&this.mon&&this.mon.restore({mode:e.initialMode,burstUntil:e.burstUntil})}),this.linkLoading=this.installStreamingLink(e).catch(()=>{}),this.compression=e.compression??\"deflate\",\"zstd\"===this.compression&&this.installZstd(e),this.d.spill||!1===e.spill||void 0===globalThis.indexedDB||(this.spill=new U({budgetBytes:e.spillBudgetBytes})),this.spill.open().then(()=>this.feedRecovery()),this.started=!0,this.scheduleTick(),this.d.post({type:\"ready\"})}liveSession(){return this.session?{sessionId:this.session.sessionId,visitId:this.session.visitId}:null}async installStreamingLink(t){const e=t.transport??\"fetch\";if(\"fetch\"===e)return;let s=null;if(\"webtransport\"===e){const e=await I(\"wt\",t.wtJsUrl,this.d.modules);e&&(this.d.webTransportFactory||e.available())&&(s=e.create({url:this.tr.wtUrl,projectKey:t.projectKey,sdkVersion:t.sdkVersion,requestId:this.tr.requestId,session:()=>this.liveSession(),now:this.now,factory:this.d.webTransportFactory,onDegraded:()=>this.tr.degradePreferred()}))}else{const e=await I(\"ws\",t.wsJsUrl,this.d.modules);e&&(this.d.socketFactory||e.available())&&(s=e.create({url:this.tr.wsUrl,projectKey:t.projectKey,sdkVersion:t.sdkVersion,requestId:this.tr.requestId,session:()=>this.liveSession(),now:this.now,setTimeout:this.st,clearTimeout:this.ct,factory:this.d.socketFactory,heartbeatMs:this.d.heartbeatMs,onDegraded:()=>this.tr.degradePreferred(),onServerFrame:t=>this.tr.applyServerFrame(t)}))}this.stopped||(s?this.tr.setPreferred(s):t.debug&&console.info(\"[backstory] streaming transport unavailable; using fetch\"))}async installZstd(t){try{const e=await I(\"zstd\",t.zstdJsUrl,this.d.modules);if(!e)throw new Error(\"unavailable\");await e.init(t.zstdWasmUrl);let s=null;if(t.dictUrl)try{const e=await this.d.fetch(t.dictUrl);e.ok&&(s=new Uint8Array(await e.arrayBuffer()))}catch{s=null}const i={compress:(t,s)=>e.compress(t,s),compressUsingDict:e.compressUsingDict?(t,s,i)=>e.compressUsingDict(t,s,i):void 0};if(!i.compress(new Uint8Array([1,2,3,4]),1)?.byteLength)throw new Error(\"probe\");v(i,s),t.debug&&console.info(\"[backstory] zstd ready\")}catch{v(null),this.compression=\"deflate\",t.debug&&console.info(\"[backstory] zstd unavailable, using deflate\")}}ensureMonitor(){if(this.mon)return Promise.resolve();if(this.monLoading)return this.monLoading;const t=this.cfg,e=t.monitor??{triggers:[...P],preRollMs:6e4,preRollBytes:2097152,burstMs:6e4};return this.monLoading=I(\"monitor\",t.monitorJsUrl,this.d.modules).then(s=>{if(this.monLoading=null,s&&!this.stopped){if(this.mon=s.create({triggers:e.triggers,preRollMs:e.preRollMs,preRollBytes:e.preRollBytes,burstMs:e.burstMs,now:this.now}),this.pendingMode){const t=this.pendingMode;this.pendingMode=null,this.setMode(t)}}else t.debug&&!s&&console.info(\"[backstory] monitor module unavailable; monitor mode disabled\")}),this.monLoading}setMode(t){if(!this.mon)return this.pendingMode=t,void(\"monitor\"===t&&this.ensureMonitor());const e=this.mon.setMode(t);for(const t of e)this.enqueueEvent(t);e.length&&this.flushNow()}dropGated(t){const e=e=>!(!t.canvas||\"canvas_frame\"!==e.type&&\"canvas_cmds\"!==e.type)||(!(!t.webrtc||\"webrtc\"!==e.type&&\"webrtc_stats\"!==e.type)||(!(!t.webrtc||\"custom\"!==e.type||\"backstory.webrtc_trigger\"!==e.name)||(!(!t.live||\"assist\"!==e.type)||!(!t.custom||\"custom\"!==e.type||e.name.startsWith(\"backstory.\")))));this.q.dropWhere(e),this.mon?.dropWhere(e)}push(t){if(this.started&&!this.stopped&&this.session)if(\"PAUSED\"!==this.gov.level||\"control\"===t.type){if(this.mon&&\"monitor\"===this.mon.mode){const e=this.mon.intake(t);if(\"hold\"===e.disposition)return;if(e.burst){this.enqueueEvent(e.burst);for(const t of e.flush??[])this.enqueueEvent(t);this.d.post({type:\"burst\",trigger:String(e.burst.props?.trigger??\"\"),preRollEvents:e.flush?.length??0})}}this.enqueueEvent(t)}else this.q.droppedBytes+=s(t)}burst(t){if(!this.session||!this.mon)return;const{events:e,marker:s}=this.mon.startBurst(t);this.enqueueEvent(s);for(const t of e)this.enqueueEvent(t);this.d.post({type:\"burst\",trigger:t,preRollEvents:e.length}),this.flushNow()}enqueueEvent(t){if(!this.session)return;\"snapshot\"===t.type&&this.awaitingDropSnapshot&&this.freeAbandoned();const i=\"snapshot\"===t.type&&1===this.q.length&&\"control\"===this.lastPushed?.type&&\"checkpoint\"===this.lastPushed.kind;(function(t){return\"snapshot\"===t.type||\"control\"===t.type&&\"checkpoint\"===t.kind})(t)&&!i&&this.flushNow(),this.lastPushed=t;const o=s(t);this.totalBytes()+o>this.cfg.memoryBudgetBytes&&(this.abandoned&&this.freeAbandoned(),this.totalBytes()+o>this.cfg.memoryBudgetBytes&&this.q.shed(o,1),this.totalBytes()+o>this.cfg.memoryBudgetBytes&&0!==e(t))?this.q.droppedBytes+=o:(this.q.push(t,o),this.q.bytes>=this.cfg.batchBytes?this.flushNow():this.flushTimer||(this.flushTimer=this.st(()=>this.flushNow(),this.cfg.batchMs)))}totalBytes(){return this.q.bytes+this.encodingBytes+(this.abandoned?.bytes??0)+(this.tr?this.tr.queuedBytes+this.tr.inFlightBytes:0)+(this.mon?.ringBytes??0)}flushNow(){if(this.flushTimer&&(this.ct(this.flushTimer),this.flushTimer=null),!this.session||!this.q.length)return;const t=this.now(),e=this.q.drain(),s=this.q.droppedBytes;this.q.droppedBytes=0;const{groups:i,oversize:o}=function(t,e=7340032,s=921600){const i=[],o=[];let n=[],r=0,h=0;for(const l of t){const t=R(l);l.bytes>e||t>s?o.push(l):(n.length&&(r+l.bytes>e||h+t>s)&&(i.push(n),n=[],r=0,h=0),n.push(l),r+=l.bytes,h+=t)}n.length&&i.push(n);return{groups:i,oversize:o}}(e);for(const t of o)this.dropOversize(t);if(o.length&&this.q.length){const t=this.q.drain();i.length?i[0]=[...i[0],...t].sort((t,e)=>t.e.t-e.e.t):i.push(t)}let n=!0;for(const t of i){const e=\"webtransport\"===this.tr.kind&&E(t);this.emitChunk(t,n?s:0,e),n=!1}this.recordWork(this.now()-t)}emitChunk(t,s,i){if(!this.session||!t.length)return;const o=this.session;let n;if(i){const e=o.seq;if(n=f(o,t,this.gov.level,s,this.tr.clockOffsetMs),o.seq=e,!n)return;n.seq=++this.lossySeq,n.lossy=!0}else if(n=f(o,t,this.gov.level,s,this.tr.clockOffsetMs),!n)return;const r=n.sessionId,h=n.seq,l=JSON.stringify(n),a=function(t){let s=0,i=0;for(const o of t){if(\"snapshot\"===o.e.type)return 0;i+=o.bytes;e(o.e)>=3&&(s+=o.bytes)}return i>0&&s/i>.5?2:1}(t),c={snapshot:t.some(t=>\"snapshot\"===t.e.type),p1:t.some(t=>1===e(t.e))},d=l.length;this.encodingBytes+=d,this.encoding=this.encoding.then(async()=>{let t,e;try{({body:t,encoding:e}=await k(l,this.compression))}finally{this.encodingBytes-=d}if(this.stopped)return;if(i)return t.byteLength,void this.tr.enqueue(r,h,t,e,!0);const s=r+\":\"+h;this.sentContents.set(s,c);const o={sessionId:r,seq:h,body:t,encoding:e,bytes:t.byteLength,createdAt:this.now(),priorityHint:a,project:this.project},n=this.spill.put(o,this.session?.sessionId??r).catch(()=>!1);this.spillWrites.set(s,n),n.finally(()=>this.spillWrites.delete(s)),this.tr.enqueue(r,h,t,e)})}dropOversize(t){const s=this.session;if(!s)return;this.q.droppedBytes+=t.bytes;const i=e(t.e);if(\"snapshot\"===t.e.type||1===i){const e=s.seq+1,i={fromSeq:e,toSeq:e,ms:0,reason:\"quota\"};s.gaps.push(i),s.seq=e,this.q.push({type:\"control\",kind:\"gap\",t:this.now(),fromSeq:e,toSeq:e,ms:0,reason:\"quota\"});const o=this.now();o-this.lastOversizeCheckpointAt>6e4&&(this.lastOversizeCheckpointAt=o,this.q.push({type:\"custom\",t:o,name:\"backstory.oversize\",props:{type:t.e.type,bytes:t.bytes}}),this.d.post({type:\"checkpoint-request\",reason:\"drop\"}))}this.cfg.debug&&console.warn(\"[backstory] dropped oversize event\",t.e.type,t.bytes)}onRejected(t,e,s,i){if(i)return void this.lossyDropped++;const o=t+\":\"+e,n=this.sentContents.get(o);if(this.sentContents.delete(o),!this.session||this.session.sessionId!==t)return;const r=\"413\"===s?\"quota\":\"memory\";if(this.session.gaps.push({fromSeq:e,toSeq:e,ms:0,reason:r}),this.q.push({type:\"control\",kind:\"gap\",t:this.now(),fromSeq:e,toSeq:e,ms:0,reason:r}),n?.snapshot||n?.p1){const t=this.now();t-this.lastOversizeCheckpointAt>6e4&&(this.lastOversizeCheckpointAt=t,this.d.post({type:\"checkpoint-request\",reason:\"drop\"}))}}onAck(t,e){for(const s of this.sentContents.keys()){const[i,o]=s.split(\":\");i===t&&Number(o)<=e&&this.sentContents.delete(s)}const s=[...this.spillWrites.entries()].filter(([e])=>e.startsWith(t+\":\")).map(([,t])=>t);Promise.all(s).then(()=>this.spill.deleteThrough(t,e)).then(()=>{for(const s of this.recoveryInFlight){const[i,o]=s.split(\":\");i===t&&Number(o)<=e&&this.recoveryInFlight.delete(s)}this.feedRecovery()}),this.feedRecovery()}feedRecovery(){if(!this.started||this.stopped||!this.spill.available||!this.session)return;if(!this.recovery.length){const t=this.spill.stale(this.session.sessionId,this.project).filter(t=>!this.recoveryInFlight.has(t.sessionId+\":\"+t.seq));if(!t.length)return;this.recovery=t}if(this.recoveryLoading||this.tr.pendingCount>2)return;const t=this.recovery.shift();t&&(this.recoveryLoading=!0,this.recoveryInFlight.add(t.sessionId+\":\"+t.seq),this.spill.get(t.sessionId,t.seq).then(e=>{e&&!this.stopped?this.tr.enqueueRecovered(e.sessionId,e.seq,e.body,e.encoding):(this.recoveryInFlight.delete(t.sessionId+\":\"+t.seq),this.spill.delete(t.sessionId,t.seq))}).finally(()=>{this.recoveryLoading=!1}))}checkpointAndDrop(){if(this.mon?.ringBytes&&(this.q.droppedBytes+=this.mon.shed()),!this.session||!t[this.gov.level].allowDropP1)return void this.q.shed(this.q.bytes,2);if(this.awaitingDropSnapshot)return;const e=this.session;this.flushTimer&&(this.ct(this.flushTimer),this.flushTimer=null);const s=this.tr.dropQueued(e.sessionId);for(const t of s)this.spill.delete(t.sessionId,t.seq);const i=this.q.take(1),o=i.reduce((t,e)=>t+e.bytes,0)+s.reduce((t,e)=>t+e.body.byteLength,0);if(!i.length&&!s.length)return;const n=s.length?Math.min(...s.map(t=>t.seq)):e.seq+1,r=e.seq+1;let h=1/0,l=-1/0;for(const t of i)t.e.t<h&&(h=t.e.t),t.e.t>l&&(l=t.e.t);const a=i.length?Math.max(0,l-h):0,c={fromSeq:n,toSeq:r,ms:a,reason:\"memory\"};e.gaps.push(c),e.seq=r,this.q.push({type:\"control\",kind:\"gap\",t:this.now(),fromSeq:n,toSeq:r,ms:a,reason:\"memory\"}),this.lastPushed=null,this.abandoned={items:i,bytes:o,timer:this.st(()=>this.freeAbandoned(),2e3)},this.awaitingDropSnapshot=!0,this.d.post({type:\"checkpoint-request\",reason:\"drop\"}),this.cfg.debug&&console.warn(\"[backstory] checkpoint-and-drop: abandoned\",o,\"bytes, seq\",n,\"-\",r)}freeAbandoned(){this.awaitingDropSnapshot=!1,this.abandoned&&(this.ct(this.abandoned.timer),this.q.droppedBytes+=this.abandoned.bytes,this.abandoned=null)}metrics(){return{queuedBytes:this.totalBytes(),budgetBytes:this.cfg.memoryBudgetBytes,goodputBps:this.tr.goodputBps||(this.online?65536:1),online:this.online,spillFull:this.spill.full,ingest429:this.now()<this.ingest429Until}}sampleHealth(t){if(!this.session)return;const e=this.now();this.lastHealthSampleAt=e;const s=this.metrics(),{drainSec:i,memPct:o}=p.target(s);this.q.push({type:\"control\",kind:\"health\",t:e,level:t,health:{drainSec:Math.round(10*i)/10,memPct:Math.round(1e3*o)/1e3,queuedBytes:s.queuedBytes,goodputBps:Math.round(this.tr?.goodputBps??0),droppedBytes:this.q.droppedBytes,spillBytes:this.spill.bytes,selfJank:Math.round(1e3*this.gov.selfShare(e))/1e3,clockOffsetMs:Math.round(this.tr?.clockOffsetMs??0),online:s.online}})}recordWork(t){t>=50&&this.gov.recordLongTask(this.now(),t,!0),t>=500?++this.slowStrikes>=3&&this.tripWatchdog(\"slow_batches\"):this.slowStrikes=0}tripWatchdog(t){if(!this.watchdogTripped){this.watchdogTripped=!0;try{this.session&&(this.q.push({type:\"custom\",t:this.now(),name:\"backstory.watchdog\",props:{reason:t,level:this.gov.level,queuedBytes:this.totalBytes()}}),this.flushNow())}catch{}this.encoding.then(()=>{this.stop(),this.d.post({type:\"fatal\",reason:\"watchdog:\"+t})})}}tick(){if(!this.started||this.stopped)return;!this.online&&\"undefined\"!=typeof navigator&&navigator.onLine&&(this.online=!0),this.mon?.evict(this.now()),this.gov.tick(this.now(),this.metrics());const t=\"HEALTHY\"===this.gov.level?6e4:1e4;this.now()-this.lastHealthSampleAt>=t&&this.sampleHealth(this.gov.level),this.feedRecovery(),this.cfg.debug&&this.d.post({type:\"debug\",state:this.debugState()})}scheduleTick(){this.tickTimer=this.st(()=>{this.tick(),this.stopped||this.scheduleTick()},1e3)}debugState(){const t=this.metrics(),e=p.target(t),s=this.gov?.level??\"HEALTHY\",i=this.gov?.forced??null;let o=\"Capture is operating normally\";return i?o=`Pinned by project policy to ${i}`:!t.online&&t.spillFull?o=\"Offline and local recovery storage is full\":t.online?t.ingest429?o=\"Ingest is rate limiting uploads\":e.memPct>.95?o=\"Recording memory is nearly full\":e.drainSec>60?o=\"Upload queue is draining very slowly\":s!==e.level?o=`Recovering after pressure; target is ${e.level}`:\"HEALTHY\"!==s&&(o=\"Capture detail is reduced to protect the page\"):o=\"Offline; buffering locally\",{level:s,targetLevel:e.level,healthReason:o,drainSeconds:e.drainSec,memoryPressure:e.memPct,queuedBytes:this.totalBytes(),inFlightBytes:this.tr?.inFlightBytes??0,credits:this.tr?.credits??0,seq:this.session?.seq??0,acked:this.session?this.tr.acked.get(this.session.sessionId)??0:0,droppedBytes:this.q.droppedBytes,sessionId:this.session?.sessionId??\"\",visitId:this.session?.visitId??\"\",clockOffsetMs:this.tr?.clockOffsetMs??0,goodputBps:this.tr?.goodputBps??0,online:this.online,spillBytes:this.spill.bytes,spillBudgetBytes:this.spill.budgetBytes,spillChunks:this.spill.count,gaps:this.session?.gaps.length??0,forcedLevel:this.gov?.forced??null,selfLongTaskShare:this.gov?this.gov.selfShare(this.now()):0,watchdogStrikes:Math.max(this.throwStrikes,this.slowStrikes),transport:this.tr?.kind??\"fetch\",encoding:\"zstd\"===this.compression?\"json+zstd\":\"json+deflate\",mode:this.mon?.mode??this.pendingMode??\"normal\",preRollBytes:this.mon?.ringBytes??0,preRollEvents:this.mon?.ringLength??0,bursting:this.mon?.bursting??!1,lossyDropped:this.lossyDropped}}handover(){this.flushNow(),this.encoding.then(()=>{this.d.post({type:\"handover-state\",clockOffsetMs:this.tr.clockOffsetMs,credits:this.tr.credits,pending:this.tr.pendingCount,transport:this.tr.kind,mode:this.mon?.mode??\"normal\",burstUntil:this.mon?.burstUntil})})}drainToMain(){this.flushNow(),this.encoding.then(()=>{const t=this.tr.drain();this.d.post({type:\"drained\",chunks:t.map(t=>({seq:t.seq,body:t.body.buffer.slice(t.body.byteOffset,t.body.byteOffset+t.body.byteLength),encoding:t.encoding}))})})}stop(){this.stopped=!0,this.flushTimer&&this.ct(this.flushTimer),this.tickTimer&&this.ct(this.tickTimer),this.abandoned&&this.ct(this.abandoned.timer),this.tr?.stop()}get queues(){return this.q}get transport(){return this.tr}get governor(){return this.gov}get spillStore(){return this.spill}get abandonedBytes(){return this.abandoned?.bytes??0}get monitor(){return this.mon}ready(){return Promise.all([this.monLoading??Promise.resolve(),this.linkLoading??Promise.resolve()]).then(()=>{})}settle(){return this.encoding}setMeta(t){this.handle({type:\"meta\",meta:t})}}({post:t=>H.postMessage(t),fetch:(...t)=>fetch(...t)});H.onmessage=t=>O.handle(t.data)}();\n";

const LEVEL_POLICY = {
    HEALTHY: { pointerHz: 20, scrollHz: 10, canvasLevel: 1, network: "all", networkBodies: true, console: "all", attrCoalesceMs: 0, checkpointIntervalMultiplier: 1, allowDropP1: false },
    CONSTRAINED: { pointerHz: 8, scrollHz: 4, canvasLevel: 2, network: "meta", networkBodies: false, console: "warn", attrCoalesceMs: 0, checkpointIntervalMultiplier: 1, allowDropP1: false },
    DEGRADED: { pointerHz: 2, scrollHz: 2, canvasLevel: 4, network: "failures", networkBodies: false, console: "error", attrCoalesceMs: 250, checkpointIntervalMultiplier: 2, allowDropP1: false },
    SURVIVAL: { pointerHz: 0, scrollHz: 1, canvasLevel: 5, network: "failures", networkBodies: false, console: "error", attrCoalesceMs: 250, checkpointIntervalMultiplier: 0, allowDropP1: true },
    PAUSED: { pointerHz: 0, scrollHz: 0, canvasLevel: 6, network: "none", networkBodies: false, console: "none", attrCoalesceMs: 0, checkpointIntervalMultiplier: 0, allowDropP1: true },
};

/** PLAN §7.1 priority classes. */
function priorityOf(e) {
    switch (e.type) {
        case "control":
        case "snapshot":
        case "error":
        case "custom":
        case "assist":
            return 0;
        case "mutation":
        case "input":
        case "navigation":
        case "focus":
        case "blur":
        case "selection":
        case "viewport":
        case "visibility":
            return 1;
        case "pointer":
            return e.kind === "move" ? 3 : 1;
        case "console":
        case "network":
        case "vital":
        case "longtask":
        case "memory":
        case "media":
            return 2;
        case "scroll":
            return 3;
        case "canvas_cmds":
        case "canvas_frame":
            return 4;
        default:
            return 2;
    }
}
/** Exact byte accounting: UTF-8 length of the JSON form. */
function sizeOf(e) {
    const s = JSON.stringify(e);
    // Fast approximate UTF-8 length without TextEncoder allocation for ASCII-heavy payloads.
    let bytes = s.length;
    for (let i = 0; i < s.length; i++) {
        const c = s.charCodeAt(i);
        if (c > 0x7f)
            bytes += c > 0x7ff ? 2 : 1;
    }
    return bytes;
}
class PriorityQueues {
    q = [[], [], [], [], []];
    bytesBy = [0, 0, 0, 0, 0];
    droppedBytes = 0;
    push(e, bytes = sizeOf(e)) {
        const p = priorityOf(e);
        this.q[p].push({ e, bytes });
        this.bytesBy[p] += bytes;
    }
    get bytes() {
        return this.bytesBy[0] + this.bytesBy[1] + this.bytesBy[2] + this.bytesBy[3] + this.bytesBy[4];
    }
    bytesAt(p) {
        return this.bytesBy[p];
    }
    get length() {
        return this.q.reduce((n, a) => n + a.length, 0);
    }
    /** Drop the lowest-priority queues first until `target` bytes are freed. Returns bytes freed. */
    shed(target, lowestKeep = 2) {
        let freed = 0;
        for (let p = 4; p > lowestKeep && freed < target; p--) {
            const b = this.bytesBy[p];
            if (!b)
                continue;
            freed += b;
            this.q[p] = [];
            this.bytesBy[p] = 0;
        }
        this.droppedBytes += freed;
        return freed;
    }
    /** Remove and return every queued item at priority >= minPriority (checkpoint-and-drop). */
    take(minPriority) {
        const out = [];
        for (let p = minPriority; p < 5; p++) {
            const arr = this.q[p];
            for (let i = 0; i < arr.length; i++)
                out.push(arr[i]);
            this.q[p] = [];
            this.bytesBy[p] = 0;
        }
        return out;
    }
    /** Remove events the plan no longer accepts. Returns the bytes freed. */
    dropWhere(pred) {
        let freed = 0;
        for (let p = 0; p < 5; p++) {
            const arr = this.q[p];
            const keep = [];
            for (let i = 0; i < arr.length; i++) {
                const item = arr[i];
                if (pred(item.e))
                    freed += item.bytes;
                else
                    keep.push(item);
            }
            this.q[p] = keep;
            let bytes = 0;
            for (let i = 0; i < keep.length; i++)
                bytes += keep[i].bytes;
            this.bytesBy[p] = bytes;
        }
        this.droppedBytes += freed;
        return freed;
    }
    /** Remove and return all queued events sorted by time (stable). */
    drain() {
        const all = [];
        for (let p = 0; p < 5; p++) {
            const arr = this.q[p];
            for (let i = 0; i < arr.length; i++)
                all.push(arr[i]);
            this.q[p] = [];
            this.bytesBy[p] = 0;
        }
        all.sort((a, b) => a.e.t - b.e.t);
        return all;
    }
}

/** Hysteresis hold before an upgrade is allowed, per PLAN §7.4. */
const HOLD_MS = {
    HEALTHY: 0,
    CONSTRAINED: 20_000,
    DEGRADED: 30_000,
    SURVIVAL: 60_000,
    PAUSED: 60_000,
};
/** Self-protection (PLAN §7.9): our share of main-thread long tasks over this window. */
const SELF_WINDOW_MS = 30_000;
const SELF_SHARE_LIMIT = 0.05;
const rank = (l) => LEVELS$1.indexOf(l);
const step = (l, d) => LEVELS$1[Math.max(0, Math.min(LEVELS$1.length - 1, rank(l) + d))];
/**
 * Which condition selected this target level. Each branch mirrors the matching
 * branch of `target`, so the answer is the test that actually fired rather than
 * a second guess at it. A level no metric forced is a recovery step.
 */
function reasonFor(target, m, drainSec, memPct) {
    switch (target) {
        case "PAUSED":
            return "spill_full";
        case "SURVIVAL":
            return !m.online ? "offline" : memPct > 0.95 ? "memory" : "drain";
        case "DEGRADED":
            return m.ingest429 ? "rate_limited" : memPct > 0.85 ? "memory" : "drain";
        case "CONSTRAINED":
            return memPct > 0.6 ? "memory" : "drain";
        default:
            return "recovery";
    }
}
/**
 * Degradation state machine. Downgrades immediately; upgrades one step at a
 * time and only after the current level's hold window has elapsed with the
 * better condition holding. A forced level (remote config) pins the machine.
 * Long tasks attributed to the SDK downgrade one level when their share of all
 * long tasks exceeds SELF_SHARE_LIMIT over SELF_WINDOW_MS (rate-limited).
 */
class Governor {
    ev;
    level = "HEALTHY";
    forced = null;
    holdUntil = 0;
    betterSince = 0;
    lastCritical = -Infinity;
    lastSelfDowngrade = -Infinity;
    longTasks = [];
    constructor(ev = {}, initial = "HEALTHY") {
        this.ev = ev;
        this.level = initial;
    }
    static target(m) {
        const drainSec = m.queuedBytes / Math.max(m.goodputBps, 1);
        const memPct = m.budgetBytes > 0 ? m.queuedBytes / m.budgetBytes : 0;
        let level;
        if (!m.online && m.spillFull)
            level = "PAUSED";
        else if (drainSec > 60 || memPct > 0.95 || !m.online)
            level = "SURVIVAL";
        else if (drainSec > 15 || memPct > 0.85 || m.ingest429)
            level = "DEGRADED";
        else if (drainSec > 5 || memPct > 0.6)
            level = "CONSTRAINED";
        else
            level = "HEALTHY";
        return { level, drainSec, memPct };
    }
    /** Pin the level (remote config `forceLevel`); null returns control to metrics. */
    force(level, now, m) {
        this.forced = level;
        if (level)
            this.apply(level, now, m ?? { queuedBytes: 0, budgetBytes: 1, goodputBps: 1, online: true, spillFull: false, ingest429: false }, 0, 0, "forced");
    }
    /** Record a main-thread long task (or a slow worker batch, which counts as ours). */
    recordLongTask(now, ms, self) {
        this.longTasks.push({ t: now, ms, self });
        this.pruneSelf(now);
    }
    /** Our share of long-task time in the window (0..1). */
    selfShare(now) {
        this.pruneSelf(now);
        let all = 0;
        let self = 0;
        for (const x of this.longTasks) {
            all += x.ms;
            if (x.self)
                self += x.ms;
        }
        return all > 0 ? self / all : 0;
    }
    pruneSelf(now) {
        const cutoff = now - SELF_WINDOW_MS;
        while (this.longTasks.length && this.longTasks[0].t < cutoff)
            this.longTasks.shift();
    }
    tick(now, m) {
        const { level: target, drainSec, memPct } = Governor.target(m);
        if (this.forced) {
            if (this.level !== this.forced)
                this.apply(this.forced, now, m, drainSec, memPct, "forced");
        }
        else if (rank(target) > rank(this.level)) {
            this.apply(target, now, m, drainSec, memPct, reasonFor(target, m, drainSec, memPct));
        }
        else if (rank(target) < rank(this.level)) {
            if (!this.betterSince)
                this.betterSince = now;
            // Must hold better condition for the hold window and be past holdUntil.
            if (now >= this.holdUntil && now - this.betterSince >= HOLD_MS[this.level]) {
                this.apply(step(this.level, -1), now, m, drainSec, memPct, "recovery");
                this.betterSince = now; // next step needs its own hold
            }
        }
        else {
            this.betterSince = 0;
        }
        // Self-protection: if we are the cause of jank, back off one level regardless of bandwidth.
        if (!this.forced && this.level !== "PAUSED" && this.selfShare(now) > SELF_SHARE_LIMIT && this.longTasks.length >= 3 && now - this.lastSelfDowngrade > SELF_WINDOW_MS) {
            this.lastSelfDowngrade = now;
            this.longTasks = [];
            this.apply(step(this.level, 1), now, m, drainSec, memPct, "self_jank");
        }
        if (memPct > 0.95 && now - this.lastCritical > 5000) {
            this.lastCritical = now;
            this.ev.onCheckpointAndDrop?.();
        }
        return this.level;
    }
    apply(l, now, m, drainSec, memPct, reason) {
        if (l === this.level)
            return;
        this.level = l;
        this.holdUntil = now + HOLD_MS[l];
        this.betterSince = 0;
        this.ev.onLevel?.(l, m, drainSec, memPct, reason);
    }
}

function newSessionState(sessionId, visitId, segmentIndex, meta) {
    return { sessionId, visitId, segmentIndex, seq: 0, checkpointId: 0, meta, metaDirty: true, gaps: [] };
}
/**
 * Assembles a Chunk from drained events. Applies the clock offset to event
 * timestamps, assigns the next seq, and attaches meta when dirty.
 * A chunk never mixes checkpoints: the caller flushes before a snapshot.
 */
function assembleChunk(s, items, level, droppedBytes, clockOffsetMs) {
    if (!items.length)
        return null;
    const off = Math.round(clockOffsetMs);
    const events = new Array(items.length);
    let first = Infinity;
    let last = -Infinity;
    for (let i = 0; i < items.length; i++) {
        const e = items[i].e;
        const t = e.t + off;
        if (e.type === "snapshot")
            s.checkpointId = e.checkpointId;
        events[i] = off ? { ...e, t } : e;
        if (t < first)
            first = t;
        if (t > last)
            last = t;
    }
    s.seq += 1;
    const chunk = {
        v: PROTOCOL_VERSION,
        sessionId: s.sessionId,
        visitId: s.visitId,
        segmentIndex: s.segmentIndex,
        seq: s.seq,
        checkpointId: s.checkpointId,
        firstTs: first,
        lastTs: last,
        level,
        droppedBytes,
        events,
    };
    if (s.gaps.length) {
        chunk.gaps = s.gaps;
        s.gaps = [];
    }
    if (s.metaDirty && s.meta) {
        chunk.meta = s.meta;
        s.metaDirty = false;
    }
    return chunk;
}
/** Does this event force a chunk boundary before it? */
function startsNewChunk(e) {
    return e.type === "snapshot" || (e.type === "control" && e.kind === "checkpoint");
}

let enc = null;
function utf8(s) {
    if (typeof TextEncoder === "undefined") {
        // Exotic environments without TextEncoder: manual UTF-8 for correctness.
        const out = [];
        for (let i = 0; i < s.length; i++) {
            let c = s.codePointAt(i);
            if (c > 0xffff)
                i++;
            if (c < 0x80)
                out.push(c);
            else if (c < 0x800)
                out.push(0xc0 | (c >> 6), 0x80 | (c & 63));
            else if (c < 0x10000)
                out.push(0xe0 | (c >> 12), 0x80 | ((c >> 6) & 63), 0x80 | (c & 63));
            else
                out.push(0xf0 | (c >> 18), 0x80 | ((c >> 12) & 63), 0x80 | ((c >> 6) & 63), 0x80 | (c & 63));
        }
        return new Uint8Array(out);
    }
    return (enc ??= new TextEncoder()).encode(s);
}
let zstd = null;
let zstdDict = null;
let zstdFailures = 0;
const ZSTD_MAX_FAILURES = 3;
const ZSTD_LEVEL = 3;
/** Install (or remove) the zstd codec; the worker loader calls this after the wasm is ready. */
function setZstd(codec, dict = null) {
    zstd = codec;
    zstdDict = dict;
    zstdFailures = 0;
}
function zstdReady() {
    return !!zstd && zstdFailures < ZSTD_MAX_FAILURES;
}
// --- end transport & monitor ---
/**
 * Compress JSON. Prefers zstd when a codec is installed and `prefer === "zstd"`
 * (PROTOCOL §6: `json+zstd`), else CompressionStream('deflate') (zlib-wrapped),
 * else raw JSON. Any codec failure falls back to the next option; zstd is
 * disabled after repeated failures.
 */
async function encodeChunk(json, prefer = "deflate") {
    const raw = utf8(json);
    if (prefer === "zstd" && zstdReady()) {
        try {
            const body = zstdDict && zstd.compressUsingDict ? zstd.compressUsingDict(raw, zstdDict, ZSTD_LEVEL) : zstd.compress(raw, ZSTD_LEVEL);
            if (body && body.byteLength > 0)
                return { body, encoding: "json+zstd" };
            zstdFailures++;
        }
        catch {
            zstdFailures++;
        }
    }
    const CS = globalThis.CompressionStream;
    if (!CS)
        return { body: raw, encoding: "json" };
    try {
        const stream = new Blob([raw]).stream().pipeThrough(new CS("deflate"));
        const buf = await new Response(stream).arrayBuffer();
        return { body: new Uint8Array(buf), encoding: "json+deflate" };
    }
    catch {
        return { body: raw, encoding: "json" };
    }
}

/** Binary frame tag byte preceding the chunk body on WebSocket/WebTransport (PROTOCOL §8). */
const ENCODING_TAG = { json: 0x01, "json+deflate": 0x02, "json+zstd": 0x03, "pb+zstd": 0x04 };
function isFatalCode(code) {
    return code === "unauthorized" || code === "origin_not_allowed" || code === "subscription_ended" || code === "limit_reached" || code === "feature_not_included";
}
/** HTTP status the fatal code maps to. */
function fatalStatus(code) {
    if (code === "unauthorized")
        return 401;
    if (code === "origin_not_allowed")
        return 403;
    return 402;
}
/** Build a binary frame: 1 byte encoding tag + 4 byte big-endian seq + body. */
function frame(item) {
    const out = new Uint8Array(5 + item.body.byteLength);
    out[0] = ENCODING_TAG[item.encoding] ?? 0x01;
    const seq = item.seq >>> 0;
    out[1] = (seq >>> 24) & 0xff;
    out[2] = (seq >>> 16) & 0xff;
    out[3] = (seq >>> 8) & 0xff;
    out[4] = seq & 0xff;
    out.set(item.body, 5);
    return out;
}
/** Parse a server text frame; returns null when unparseable. */
function parseServerFrame(text) {
    try {
        const v = JSON.parse(text);
        if (!v || typeof v !== "object")
            return null;
        return v;
    }
    catch {
        return null;
    }
}
/** Derive the WebSocket ingest URL from the HTTP ingest URL (PROTOCOL §8.1). */
function deriveWsUrl(ingestUrl) {
    try {
        const u = new URL(ingestUrl);
        u.protocol = u.protocol === "https:" ? "wss:" : "ws:";
        u.pathname = u.pathname.replace(/\/+$/, "") + "/ws";
        return u.toString();
    }
    catch {
        return ingestUrl.replace(/^http/, "ws").replace(/\/+$/, "") + "/ws";
    }
}
/** Derive the WebTransport ingest URL from the HTTP ingest URL. */
function deriveWtUrl(ingestUrl) {
    try {
        const u = new URL(ingestUrl);
        u.protocol = "https:";
        u.pathname = u.pathname.replace(/\/+$/, "") + "/wt";
        return u.toString();
    }
    catch {
        return ingestUrl.replace(/\/+$/, "") + "/wt";
    }
}

/** Largest clock-offset correction accepted per sample (PLAN §5.6: no jumps). */
const MAX_OFFSET_STEP_MS = 500;
const SWITCH_BACK_MS = 5 * 60_000;
const RECOVERY_MIN_GAP_MS = 1000;
/** HTTP POST per chunk (the v1 transport; also the fallback and the path for recovered chunks).
 * Ordinary uploads are normal requests. Keepalive is only for the final send during unload. */
class FetchLink {
    o;
    kind = "fetch";
    degraded = false;
    constructor(o) {
        this.o = o;
    }
    async send(item) {
        const sentAt = this.o.now();
        let res;
        try {
            res = await this.o.fetch(this.o.url, {
                method: "POST",
                headers: {
                    "content-type": "application/octet-stream",
                    [HEADERS.projectKey]: this.o.projectKey,
                    [HEADERS.encoding]: item.encoding,
                    [HEADERS.sdkVersion]: this.o.sdkVersion,
                    [HEADERS.requestId]: this.o.requestId,
                },
                body: item.body,
            });
        }
        catch {
            return { ok: false, kind: "network", status: 0, rttMs: this.o.now() - sentAt, sentAt };
        }
        const rttMs = this.o.now() - sentAt;
        const st = res.headers.get(HEADERS.serverTime);
        const serverTime = st ? Number(st) : undefined;
        if (res.ok) {
            let body = null;
            try {
                body = (await res.json());
            }
            catch {
                /* tolerate empty body */
            }
            return { ok: true, ack: body?.ack, credits: body?.credits, serverTime: serverTime ?? body?.serverTime, config: body?.config, rttMs, sentAt };
        }
        if (res.status === 401 || res.status === 402 || res.status === 403) {
            // 402 carries the reason in the body: a lapsed subscription is final,
            // a cap or a plan feature can clear, and the recorder treats them differently.
            let fatal = res.status === 401 ? "unauthorized" : res.status === 402 ? "subscription_ended" : "origin_not_allowed";
            if (res.status === 402) {
                try {
                    const body = (await res.json());
                    if (isFatalCode(body?.code))
                        fatal = body.code;
                }
                catch {
                    /* keep the status-derived reason */
                }
            }
            return { ok: false, kind: "fatal", status: res.status, fatal, serverTime, rttMs, sentAt };
        }
        if (res.status === 400 || res.status === 413)
            return { ok: false, kind: res.status === 413 ? "413" : "400", status: res.status, serverTime, rttMs, sentAt };
        const ra = Number(res.headers.get("retry-after") || 0) * 1000;
        return { ok: false, kind: res.status === 429 ? "429" : "5xx", status: res.status, retryAfterMs: ra, serverTime, rttMs, sentAt };
    }
    close() { }
}
/**
 * Credit-based, ordered, retrying uploader (PLAN §7.7). Never has more than
 * `credits` bytes un-acknowledged. Delivery goes through a Link: WebSocket by
 * default (`transport: 'auto'`), WebTransport behind a flag, fetch as the
 * fallback after repeated connect failures (with a timed switch back).
 * Recovered chunks (previous sessions) always travel over fetch because the
 * socket handshake is bound to the live session.
 */
class Transport {
    o;
    queue = [];
    inflight = new Map();
    inFlightBytes = 0;
    credits;
    acked = new Map();
    goodputBps = 0;
    ackedBytesWindow = [];
    stopped = false;
    pumping = false;
    clockOffsetMs = 0;
    offsetSamples = 0;
    lastRecoveredAt = -Infinity;
    // --- transport & monitor ---
    fetchLink;
    preferred = null;
    active;
    switchBackTimer = null;
    clearT;
    /** Lossy chunks (datagrams) counted as delivered without an ack. */
    lossyDelivered = 0;
    // --- end transport & monitor ---
    /** The correlation id sent on every ingest call for this recording. */
    requestId;
    constructor(o) {
        this.o = o;
        this.requestId = guidOrNew(o.requestId);
        this.o.requestId = this.requestId;
        this.credits = o.initialCredits ?? 256 * 1024;
        if (typeof o.initialClockOffsetMs === "number" && Number.isFinite(o.initialClockOffsetMs)) {
            this.clockOffsetMs = o.initialClockOffsetMs;
            this.offsetSamples = 1;
        }
        this.clearT = o.clearTimeout ?? ((h) => clearTimeout(h));
        this.fetchLink = new FetchLink(o);
        this.active = this.fetchLink;
        this.preferred = this.buildPreferred();
        if (this.preferred)
            this.active = this.preferred;
    }
    buildPreferred() {
        // Streaming links (WebSocket, WebTransport) are lazily loaded modules installed by the pipeline with setPreferred().
        return null;
    }
    /** Unsolicited server frames (credits/config nudges without an ack). */
    applyServerFrame(f) {
        if (typeof f.credits === "number" && f.credits > 0)
            this.credits = f.credits;
        if (f.config)
            this.o.onConfig?.(f.config);
    }
    /** Preferred transport requested by config (installation happens via setPreferred). */
    get preference() {
        return this.o.transport ?? "fetch";
    }
    /** Install (or replace) the preferred link at runtime (lazily loaded WebTransport). */
    setPreferred(link) {
        this.preferred?.close();
        this.preferred = link;
        if (this.stopped)
            return;
        if (this.switchBackTimer)
            this.clearT(this.switchBackTimer);
        this.switchBackTimer = null;
        this.active = link;
        this.o.onTransportChange?.(link.kind);
        this.pump();
    }
    /** Called by a preferred link that gave up (public so lazily created links can reach it). */
    degradePreferred() {
        this.fallback();
    }
    /** WebTransport / WebSocket URLs for lazily created links. */
    get wtUrl() {
        return this.o.wtUrl ?? deriveWtUrl(this.o.url);
    }
    get wsUrl() {
        return this.o.wsUrl ?? deriveWsUrl(this.o.url);
    }
    /** Preferred link failed repeatedly: use fetch and try the preferred link again later. */
    fallback() {
        if (this.active === this.fetchLink)
            return;
        this.active = this.fetchLink;
        // Chunks that were waiting on the socket backoff go out over HTTP right away.
        const now = this.o.now();
        for (const q of this.queue)
            if (q.nextAt > now)
                q.nextAt = now;
        this.o.onTransportChange?.("fetch");
        if (this.switchBackTimer)
            this.clearT(this.switchBackTimer);
        this.switchBackTimer = this.o.setTimeout(() => this.switchBack(), this.o.switchBackMs ?? SWITCH_BACK_MS);
        this.pump();
    }
    switchBack() {
        this.switchBackTimer = null;
        if (this.stopped || !this.preferred)
            return;
        const r = this.preferred.reset;
        if (typeof r === "function")
            r.call(this.preferred);
        else if (this.preferred.degraded)
            return; // WebTransport does not recover automatically
        this.active = this.preferred;
        this.o.onTransportChange?.(this.preferred.kind);
        this.pump();
    }
    get kind() {
        return this.active.kind;
    }
    get activeLink() {
        return this.active;
    }
    get preferredLink() {
        return this.preferred;
    }
    get queuedBytes() {
        let b = 0;
        for (const q of this.queue)
            b += q.body.byteLength;
        return b;
    }
    get pendingCount() {
        return this.queue.length + this.inflight.size;
    }
    /** Live (non-recovered) chunks waiting or in flight. */
    get livePending() {
        let n = this.inflight.size;
        for (const q of this.queue)
            if (!q.recovered)
                n++;
        return n;
    }
    get stoppedState() {
        return this.stopped;
    }
    enqueue(sessionId, seq, body, encoding, lossy = false) {
        this.queue.push({ sessionId, seq, body, encoding, attempts: 0, nextAt: 0, lossy: lossy || undefined });
        this.pump();
    }
    /** Enqueue a chunk recovered from spill (lowest priority, rate limited). */
    enqueueRecovered(sessionId, seq, body, encoding) {
        this.queue.push({ sessionId, seq, body, encoding, attempts: 0, nextAt: 0, recovered: true });
        this.pump();
    }
    /** Remove not-yet-sent live chunks of a session (checkpoint-and-drop). In-flight chunks are left alone. */
    dropQueued(sessionId) {
        const dropped = [];
        this.queue = this.queue.filter((q) => {
            if (q.sessionId === sessionId && !q.recovered) {
                dropped.push(q);
                return false;
            }
            return true;
        });
        return dropped;
    }
    /** Take everything not yet acknowledged (for main-thread keepalive send on unload). */
    drain() {
        const all = [...this.inflight.values(), ...this.queue];
        this.queue = [];
        return all.sort((a, b) => a.seq - b.seq);
    }
    stop() {
        this.stopped = true;
        if (this.switchBackTimer)
            this.clearT(this.switchBackTimer);
        this.preferred?.close();
    }
    key(o) {
        return o.sessionId + ":" + o.seq + (o.lossy ? ":l" : "");
    }
    pump() {
        if (this.stopped || this.pumping)
            return;
        this.pumping = true;
        try {
            const now = this.o.now();
            // A persistent link can have more frames in flight than HTTP requests.
            const maxC = this.o.maxConcurrent ?? (this.active.kind === "fetch" ? 2 : 8);
            // Live chunks first in seq order; recovered chunks after, oldest session first.
            this.queue.sort((a, b) => Number(!!a.recovered) - Number(!!b.recovered) || a.sessionId.localeCompare(b.sessionId) || a.seq - b.seq || a.nextAt - b.nextAt);
            let scheduledRecovery = false;
            for (let i = 0; i < this.queue.length && this.inflight.size < maxC;) {
                const item = this.queue[i];
                if (item.nextAt > now) {
                    i++;
                    continue;
                }
                if (item.recovered) {
                    // Only when nothing live is pending, and at most one per second.
                    if (this.livePending > 0)
                        break;
                    const wait = RECOVERY_MIN_GAP_MS - (now - this.lastRecoveredAt);
                    if (wait > 0) {
                        if (!scheduledRecovery) {
                            scheduledRecovery = true;
                            this.o.setTimeout(() => this.pump(), wait);
                        }
                        break;
                    }
                    this.lastRecoveredAt = now;
                }
                if (this.inFlightBytes + item.body.byteLength > this.credits && this.inflight.size > 0)
                    break;
                this.queue.splice(i, 1);
                void this.send(item);
            }
            const next = this.queue.find((q) => q.nextAt > now);
            if (next)
                this.o.setTimeout(() => this.pump(), Math.max(10, next.nextAt - now));
        }
        finally {
            this.pumping = false;
        }
    }
    async send(item) {
        this.inflight.set(this.key(item), item);
        this.inFlightBytes += item.body.byteLength;
        item.attempts++;
        // Recovered chunks belong to other sessions: the socket handshake cannot carry them.
        const link = item.recovered ? this.fetchLink : this.active;
        const r = await link.send(item);
        if (this.stopped && !r.ok) {
            this.inflight.delete(this.key(item));
            this.inFlightBytes -= item.body.byteLength;
            return;
        }
        if (typeof r.serverTime === "number")
            this.sampleClock(r.serverTime, r.sentAt, r.rttMs);
        if (r.ok) {
            this.inflight.delete(this.key(item));
            this.inFlightBytes -= item.body.byteLength;
            this.recordGoodput(item.body.byteLength, r.rttMs);
            if (typeof r.credits === "number" && r.credits > 0)
                this.credits = r.credits;
            if (r.config)
                this.o.onConfig?.(r.config);
            if (item.lossy) {
                // Own seq space; never advances the reliable ack.
                this.lossyDelivered++;
                this.o.onAck(item.sessionId, this.acked.get(item.sessionId) ?? 0, item.body.byteLength);
            }
            else {
                const prev = this.acked.get(item.sessionId) ?? 0;
                let ack = Math.max(prev, item.seq);
                if (typeof r.ack === "number")
                    ack = Math.max(prev, r.ack);
                this.acked.set(item.sessionId, ack);
                this.o.onAck(item.sessionId, ack, item.body.byteLength);
            }
            this.pump();
            return;
        }
        if (r.kind === "fatal") {
            this.inflight.delete(this.key(item));
            this.inFlightBytes -= item.body.byteLength;
            this.stopped = true;
            this.o.onFatal(r.fatal ?? "unauthorized");
            return;
        }
        if (r.kind === "400" || r.kind === "413") {
            // Invalid or too large: not retried. The pipeline declares a gap (413 safety net).
            this.inflight.delete(this.key(item));
            this.inFlightBytes -= item.body.byteLength;
            this.o.onRejected?.(item.sessionId, item.seq, r.kind, !!item.lossy);
            if (!item.lossy) {
                // Treat as acknowledged for spill purposes so it does not haunt recovery forever.
                this.o.onAck(item.sessionId, Math.max(this.acked.get(item.sessionId) ?? 0, item.seq), 0);
            }
            this.pump();
            return;
        }
        if (item.lossy && r.kind === "network" && link.kind === "webtransport") {
            // Lossy chunks are never retried: the network dropped it, which is allowed.
            this.inflight.delete(this.key(item));
            this.inFlightBytes -= item.body.byteLength;
            this.pump();
            return;
        }
        // The link we used has been replaced (fallback) while this frame was pending: resend now on the new one.
        this.fail(item, r.kind, r.retryAfterMs ?? 0, link !== this.active && !item.recovered);
    }
    fail(item, kind, retryAfterMs, immediate = false) {
        this.inflight.delete(this.key(item));
        this.inFlightBytes -= item.body.byteLength;
        if (immediate) {
            item.nextAt = this.o.now();
            this.queue.push(item);
            this.o.onPressure(kind, 0);
            this.o.setTimeout(() => this.pump(), 0);
            return;
        }
        const base = Math.min(30_000, 1000 * 2 ** Math.min(item.attempts - 1, 5));
        const jitter = Math.random() * base * 0.3;
        // Socket reconnects are cheap: retry sooner than HTTP so the resend lands right after reconnect.
        const socketFast = kind === "network" && this.active.kind !== "fetch" ? Math.min(base, 250 * item.attempts) : null;
        const delay = Math.max(retryAfterMs, socketFast ?? base + jitter);
        item.nextAt = this.o.now() + delay;
        this.queue.push(item); // same seq, retried later
        this.o.onPressure(kind, delay);
        this.o.setTimeout(() => this.pump(), delay);
    }
    sampleClock(serverTime, sendAt, rtt) {
        if (!Number.isFinite(serverTime))
            return;
        const sample = serverTime - (sendAt + rtt / 2);
        this.offsetSamples++;
        if (this.offsetSamples === 1) {
            this.clockOffsetMs = sample;
        }
        else {
            // EWMA, but never move more than MAX_OFFSET_STEP_MS per update (no visible time jumps).
            const delta = 0.3 * (sample - this.clockOffsetMs);
            this.clockOffsetMs += Math.max(-MAX_OFFSET_STEP_MS, Math.min(MAX_OFFSET_STEP_MS, delta));
        }
        this.o.onServerTime(this.clockOffsetMs);
    }
    /**
     * Bytes per second measured over the time we were actually uploading, not
     * over wall clock. The governor divides the queue by this to estimate drain
     * time, so counting idle gaps between chunks would read a quiet page as a
     * slow link: one small chunk acked after a pause used to report a few hundred
     * B/s and pin a healthy session at SURVIVAL.
     */
    recordGoodput(bytes, rttMs) {
        const now = this.o.now();
        const busy = Number.isFinite(rttMs) && rttMs > 0 ? rttMs : 1;
        this.ackedBytesWindow.push({ t: now, b: bytes, ms: busy });
        const cutoff = now - 10_000;
        while (this.ackedBytesWindow.length && this.ackedBytesWindow[0].t < cutoff)
            this.ackedBytesWindow.shift();
        let total = 0;
        let uploading = 0;
        for (const x of this.ackedBytesWindow) {
            total += x.b;
            uploading += x.ms;
        }
        const inst = total / (Math.max(50, uploading) / 1000);
        this.goodputBps = this.goodputBps ? this.goodputBps + 0.3 * (inst - this.goodputBps) : inst;
    }
}

const GLOBAL_KEY = { ws: "__BACKSTORY_WS__", wt: "__BACKSTORY_WT__", zstd: "__BACKSTORY_ZSTD__", monitor: "__BACKSTORY_MONITOR__", inline: "__BACKSTORY_INLINE__" };
const loading = new Map();
function loadWorkerModule(name, url, injected) {
    const g = globalThis;
    const inj = injected?.[name];
    if (inj)
        return Promise.resolve(inj);
    const key = GLOBAL_KEY[name];
    if (g[key])
        return Promise.resolve(g[key]);
    if (!url)
        return Promise.resolve(null);
    const k = name + "|" + url;
    let p = loading.get(k);
    if (p)
        return p;
    p = new Promise((resolve) => {
        try {
            if (typeof g.importScripts === "function") {
                g.importScripts(url);
                resolve(g[key] ?? null);
                return;
            }
            if (g.document) {
                const s = g.document.createElement("script");
                s.async = true;
                s.src = url;
                s.onload = () => resolve(g[key] ?? null);
                s.onerror = () => resolve(null);
                g.document.head.appendChild(s);
                return;
            }
        }
        catch {
            /* fall through */
        }
        resolve(null);
    });
    loading.set(k, p);
    return p;
}

/** No-op store for environments without IndexedDB. */
class NullSpill {
    available = false;
    budgetBytes = 0;
    bytes = 0;
    count = 0;
    full = false;
    async open() { }
    async put() {
        return false;
    }
    async delete() { }
    async deleteThrough() { }
    stale() {
        return [];
    }
    async get() {
        return null;
    }
    async wipe() { }
}
function projectHash(key) {
    let h = 2166136261;
    for (let i = 0; i < key.length; i++) {
        h ^= key.charCodeAt(i);
        h = Math.imul(h, 16777619);
    }
    return (h >>> 0).toString(36);
}

const DB_NAME = "backstory-spill";
const STORE = "chunks";
const DEFAULT_BUDGET = 100 * 1024 * 1024;
const FALLBACK_BUDGET = 50 * 1024 * 1024;
/** Promise wrapper for IDBRequest. */
function req(r) {
    return new Promise((res, rej) => {
        r.onsuccess = () => res(r.result);
        r.onerror = () => rej(r.error);
    });
}
function done(tx) {
    return new Promise((res, rej) => {
        tx.oncomplete = () => res();
        tx.onerror = () => rej(tx.error);
        tx.onabort = () => rej(tx.error ?? new Error("aborted"));
    });
}
/**
 * IndexedDB-backed spill. Keeps an in-memory metadata index so budget and
 * eviction decisions are synchronous; bodies live only in IndexedDB.
 */
class IdbSpill {
    o;
    available = false;
    budgetBytes = FALLBACK_BUDGET;
    bytes = 0;
    count = 0;
    db = null;
    index = new Map(); // key sessionId:seq
    opening = null;
    constructor(o = {}) {
        this.o = o;
    }
    lastCurrent = "";
    /** At budget with nothing left to evict except the current session's snapshot chunks. */
    get full() {
        if (!this.available || this.bytes < this.budgetBytes)
            return false;
        for (const m of this.index.values())
            if (!(m.sessionId === this.lastCurrent && m.priorityHint === 0))
                return false;
        return true;
    }
    key(sid, seq) {
        return sid + ":" + seq;
    }
    open() {
        if (this.opening)
            return this.opening;
        this.opening = (async () => {
            const idb = this.o.indexedDB ?? globalThis.indexedDB;
            if (!idb)
                return;
            try {
                const r = idb.open(DB_NAME, 1);
                r.onupgradeneeded = () => {
                    const db = r.result;
                    if (!db.objectStoreNames.contains(STORE)) {
                        const s = db.createObjectStore(STORE, { keyPath: ["sessionId", "seq"] });
                        s.createIndex("bySession", "sessionId");
                        s.createIndex("byCreated", "createdAt");
                    }
                };
                const db = (await req(r));
                this.db = db;
                // Budget: min(configured or 100 MB, 20% of quota); never below 1 MB unless explicitly configured.
                const configured = this.o.budgetBytes;
                let budget = configured ?? DEFAULT_BUDGET;
                try {
                    const est = this.o.estimate ? await this.o.estimate() : await globalThis.navigator?.storage?.estimate?.();
                    if (est?.quota)
                        budget = Math.min(budget, Math.floor(est.quota * 0.2));
                }
                catch {
                    budget = Math.min(budget, FALLBACK_BUDGET);
                }
                this.budgetBytes = configured !== undefined ? budget : Math.max(1024 * 1024, budget);
                // Load metadata index (bodies stay on disk).
                const tx = db.transaction(STORE, "readonly");
                const cur = tx.objectStore(STORE).openCursor();
                await new Promise((res, rej) => {
                    cur.onsuccess = () => {
                        const c = cur.result;
                        if (!c)
                            return res();
                        const v = c.value;
                        const m = { sessionId: v.sessionId, seq: v.seq, bytes: v.bytes, createdAt: v.createdAt, priorityHint: v.priorityHint, project: v.project, encoding: v.encoding };
                        this.index.set(this.key(m.sessionId, m.seq), m);
                        this.bytes += m.bytes;
                        this.count++;
                        c.continue();
                    };
                    cur.onerror = () => rej(cur.error);
                });
                this.available = true;
            }
            catch {
                this.db = null;
                this.available = false;
            }
        })();
        return this.opening;
    }
    /** Eviction order (PLAN §7.8): oldest previous sessions, then current session's
     * low-priority chunks, then normal; snapshot chunks only when nothing else is left. */
    evictionCandidates(currentSessionId) {
        const all = [...this.index.values()];
        const prev = all.filter((m) => m.sessionId !== currentSessionId).sort((a, b) => a.createdAt - b.createdAt);
        const cur = all.filter((m) => m.sessionId === currentSessionId);
        const byHint = (h) => cur.filter((m) => m.priorityHint === h).sort((a, b) => a.createdAt - b.createdAt);
        return [...prev, ...byHint(2), ...byHint(1), ...byHint(0)];
    }
    async put(e, currentSessionId) {
        this.lastCurrent = currentSessionId;
        if (!this.db)
            return false;
        if (e.bytes > this.budgetBytes)
            return false;
        // Make room.
        if (this.bytes + e.bytes > this.budgetBytes) {
            const victims = this.evictionCandidates(currentSessionId);
            const toDelete = [];
            let freed = 0;
            for (const v of victims) {
                if (this.bytes - freed + e.bytes <= this.budgetBytes)
                    break;
                // Never evict the current session's snapshot chunks to make room for a non-snapshot chunk.
                if (v.sessionId === currentSessionId && v.priorityHint === 0 && e.priorityHint !== 0)
                    continue;
                toDelete.push(v);
                freed += v.bytes;
            }
            if (this.bytes - freed + e.bytes > this.budgetBytes)
                return false;
            const tx = this.db.transaction(STORE, "readwrite");
            const s = tx.objectStore(STORE);
            for (const v of toDelete)
                s.delete([v.sessionId, v.seq]);
            await done(tx);
            for (const v of toDelete)
                this.forget(v.sessionId, v.seq);
        }
        try {
            const tx = this.db.transaction(STORE, "readwrite");
            tx.objectStore(STORE).put(e);
            await done(tx);
        }
        catch {
            return false; // QuotaExceededError etc.
        }
        const k = this.key(e.sessionId, e.seq);
        if (!this.index.has(k)) {
            this.bytes += e.bytes;
            this.count++;
        }
        const { body: _b, ...meta } = e;
        this.index.set(k, meta);
        return true;
    }
    forget(sid, seq) {
        const k = this.key(sid, seq);
        const m = this.index.get(k);
        if (!m)
            return;
        this.index.delete(k);
        this.bytes -= m.bytes;
        this.count--;
    }
    async delete(sessionId, seq) {
        if (!this.db || !this.index.has(this.key(sessionId, seq)))
            return;
        try {
            const tx = this.db.transaction(STORE, "readwrite");
            tx.objectStore(STORE).delete([sessionId, seq]);
            await done(tx);
        }
        catch {
            /* best effort */
        }
        this.forget(sessionId, seq);
    }
    async deleteThrough(sessionId, ackSeq) {
        if (!this.db)
            return;
        const victims = [...this.index.values()].filter((m) => m.sessionId === sessionId && m.seq <= ackSeq);
        if (!victims.length)
            return;
        try {
            const tx = this.db.transaction(STORE, "readwrite");
            const s = tx.objectStore(STORE);
            for (const v of victims)
                s.delete([v.sessionId, v.seq]);
            await done(tx);
        }
        catch {
            /* best effort */
        }
        for (const v of victims)
            this.forget(v.sessionId, v.seq);
    }
    stale(currentSessionId, project) {
        return [...this.index.values()].filter((m) => m.sessionId !== currentSessionId && m.project === project).sort((a, b) => a.createdAt - b.createdAt || a.seq - b.seq);
    }
    async get(sessionId, seq) {
        if (!this.db)
            return null;
        try {
            const tx = this.db.transaction(STORE, "readonly");
            const v = (await req(tx.objectStore(STORE).get([sessionId, seq])));
            return v ?? null;
        }
        catch {
            return null;
        }
    }
    async wipe() {
        if (!this.db)
            return;
        try {
            const tx = this.db.transaction(STORE, "readwrite");
            tx.objectStore(STORE).clear();
            await done(tx);
        }
        catch {
            /* best effort */
        }
        this.index.clear();
        this.bytes = 0;
        this.count = 0;
    }
}

/** Watchdog thresholds (PLAN §7.9). */
const WATCHDOG_SLOW_BATCH_MS = 500;
const WATCHDOG_STRIKES = 3;
/**
 * Capture-health sampling cadence. A healthy recorder only needs to say so
 * occasionally; one under pressure is exactly when the dashboard needs detail,
 * and a sample is a few hundred bytes, so the faster rate costs far less than
 * the data it explains.
 */
const HEALTH_SAMPLE_HEALTHY_MS = 60_000;
const HEALTH_SAMPLE_PRESSURE_MS = 10_000;
/** Worker work above this counts as a long task attributed to us. */
const SELF_WORK_MS = 50;
/** How long the abandoned range is kept before it is freed even without a fresh snapshot. */
const DROP_FREE_FALLBACK_MS = 2000;
// --- transport & monitor ---
/** Pre-encode size guard (PROTOCOL §3 caps: 1 MB compressed, 8 MB raw). Estimates stay well under. */
const MAX_RAW_CHUNK_BYTES = 7 * 1024 * 1024;
const MAX_ESTIMATED_COMPRESSED_BYTES = 900 * 1024;
/** Expected compressed/raw ratio per event class used by the guard. */
const RATIO_MEDIA = 0.8; // canvas frames are already-compressed media in base64
const RATIO_TEXT = 0.35; // DOM/JSON compresses well
/** Lossy (datagram) chunk cap in bytes of encoded body (frame header adds 5). */
const LOSSY_MAX_BODY = 1195;
// --- end transport & monitor ---
/**
 * Environment-agnostic recorder pipeline: runs inside the Recorder Worker, or
 * on the main thread when Workers are unavailable. Owns queues, governor,
 * batching, compression, spill, and transport (PLAN §7.1).
 */
class Pipeline {
    d;
    cfg;
    q = new PriorityQueues();
    gov;
    tr;
    session = null;
    flushTimer = null;
    tickTimer = null;
    online = true;
    ingest429Until = 0;
    now;
    st;
    ct;
    started = false;
    encoding = Promise.resolve();
    stopped = false;
    lastPushed = null;
    // Spill
    spill;
    project = "";
    spillWrites = new Map();
    recovery = [];
    recoveryLoading = false;
    recoveryInFlight = new Set();
    /** Bytes drained from the queues but not yet handed to the transport (async encode). */
    encodingBytes = 0;
    // Checkpoint-and-drop (PLAN §7.6)
    abandoned = null;
    awaitingDropSnapshot = false;
    // Watchdog
    throwStrikes = 0;
    slowStrikes = 0;
    watchdogTripped = false;
    /** -Infinity so the first tick of a session always publishes a baseline sample. */
    lastHealthSampleAt = -Infinity;
    // --- transport & monitor ---
    /** Monitor controller: null until the lazily loaded module is installed. */
    mon = null;
    monLoading = null;
    linkLoading = null;
    pendingMode = null;
    compression = "deflate";
    /** Own seq space for lossy (datagram) chunks; never advances the reliable chain. */
    lossySeq = 0;
    lossyDropped = 0;
    /** What un-acked chunks contained, so a 413 can declare an honest gap and re-checkpoint. */
    sentContents = new Map();
    lastOversizeCheckpointAt = -Infinity;
    // --- end transport & monitor ---
    constructor(d) {
        this.d = d;
        this.now = d.now ?? (() => Date.now());
        this.st = d.setTimeout ?? ((fn, ms) => setTimeout(fn, ms));
        this.ct = d.clearTimeout ?? ((h) => clearTimeout(h));
        this.spill = d.spill ?? new NullSpill();
    }
    get level() {
        return this.gov?.level ?? "HEALTHY";
    }
    /** Message entry point, guarded by the watchdog: repeated exceptions stop recording. */
    handle(m) {
        try {
            this.dispatch(m);
            this.throwStrikes = 0;
        }
        catch (e) {
            this.throwStrikes++;
            if (this.cfg?.debug)
                console.warn("[backstory] worker error", e);
            if (this.throwStrikes >= WATCHDOG_STRIKES)
                this.tripWatchdog("exceptions");
        }
    }
    dispatch(m) {
        switch (m.type) {
            case "init":
                return this.init(m.cfg);
            case "session":
                this.flushNow();
                this.session = newSessionState(m.sessionId, m.visitId, m.segmentIndex, m.meta);
                this.feedRecovery();
                return;
            case "meta":
                if (this.session) {
                    this.session.meta = m.meta;
                    this.session.metaDirty = true;
                }
                return;
            case "event":
                return this.push(m.e);
            case "events":
                for (const e of m.es)
                    this.push(e);
                return;
            case "flush":
                return this.flushNow();
            case "drain":
                return this.drainToMain();
            case "online":
                this.online = m.online;
                if (m.online)
                    this.tr.pump();
                else if (this.mon?.mode === "monitor") {
                    // Disconnect is a monitor trigger: flush the pre-roll so the minute before is kept.
                    const trig = this.mon.offlineTrigger();
                    if (trig)
                        this.burst(trig);
                }
                return;
            // --- transport & monitor ---
            case "mode":
                return this.setMode(m.mode);
            case "monitor-triggers":
                this.mon?.setTriggers(m.triggers);
                return;
            // --- end transport & monitor ---
            case "debug":
                return this.d.post({ type: "debug", state: this.debugState() });
            case "longtask":
                this.gov.recordLongTask(this.now(), m.ms, m.self);
                return;
            case "force-level":
                this.gov.force(m.level, this.now(), this.metrics());
                return;
            case "handover":
                return this.handover();
            case "wipe":
                void this.spill.wipe();
                this.recovery = [];
                return;
            case "stop":
                return this.stop();
            case "drop-gated":
                return this.dropGated(m);
        }
    }
    init(cfg) {
        this.cfg = cfg;
        this.project = projectHash(cfg.projectKey);
        this.gov = new Governor({
            onLevel: (level, _m, drainSec, memPct, reason) => {
                this.q.push({ type: "control", kind: "level", t: this.now(), level, drainSec: Math.round(drainSec), memPct: Math.round(memPct * 100) / 100, reason });
                // A transition is the moment the numbers matter most, so pair it with
                // a sample instead of waiting out the cadence to explain it.
                this.sampleHealth(level);
                this.d.post({ type: "level", level, collectors: LEVEL_POLICY[level] });
            },
            onCheckpointAndDrop: () => this.checkpointAndDrop(),
        }, cfg.initialLevel ?? "HEALTHY");
        if (cfg.initialLevel && cfg.initialLevel !== "HEALTHY")
            this.d.post({ type: "level", level: cfg.initialLevel, collectors: LEVEL_POLICY[cfg.initialLevel] });
        this.tr = new Transport({
            url: cfg.ingestUrl,
            projectKey: cfg.projectKey,
            sdkVersion: cfg.sdkVersion,
            requestId: cfg.requestId,
            fetch: this.d.fetch,
            now: this.now,
            setTimeout: this.st,
            clearTimeout: this.ct,
            initialCredits: cfg.credits,
            initialClockOffsetMs: cfg.clockOffsetMs,
            onAck: (sid, ack) => this.onAck(sid, ack),
            onFatal: (reason) => {
                this.stop();
                this.d.post({ type: "fatal", reason });
            },
            onPressure: (kind, retryAfterMs) => {
                if (kind === "429")
                    this.ingest429Until = this.now() + Math.max(retryAfterMs, 10_000);
                if (kind === "network")
                    this.online = false;
            },
            onServerTime: () => { },
            onConfig: (remote) => this.d.post({ type: "config", remote }),
            // --- transport & monitor ---
            // Direct Pipeline users (tests, embedders) default to fetch; the Recorder passes the configured preference (auto → ws).
            transport: cfg.transport ?? "fetch",
            wsUrl: cfg.wsUrl,
            wtUrl: cfg.wtUrl,
            session: () => (this.session ? { sessionId: this.session.sessionId, visitId: this.session.visitId } : null),
            socketFactory: this.d.socketFactory,
            webTransportFactory: this.d.webTransportFactory,
            switchBackMs: this.d.switchBackMs,
            heartbeatMs: this.d.heartbeatMs,
            onRejected: (sid, seq, kind, lossy) => this.onRejected(sid, seq, kind, lossy),
            onTransportChange: (kind) => this.d.post({ type: "transport", kind }),
            // --- end transport & monitor ---
        });
        // --- transport & monitor ---
        if (cfg.initialMode === "monitor" || cfg.preloadMonitor) {
            void this.ensureMonitor().then(() => {
                if (cfg.initialMode && this.mon)
                    this.mon.restore({ mode: cfg.initialMode, burstUntil: cfg.burstUntil });
            });
        }
        this.linkLoading = this.installStreamingLink(cfg).catch(() => undefined);
        this.compression = cfg.compression ?? "deflate";
        if (this.compression === "zstd")
            void this.installZstd(cfg);
        // --- end transport & monitor ---
        if (!this.d.spill && cfg.spill !== false && typeof globalThis.indexedDB !== "undefined") {
            this.spill = new IdbSpill({ budgetBytes: cfg.spillBudgetBytes });
        }
        void this.spill.open().then(() => this.feedRecovery());
        this.started = true;
        this.scheduleTick();
        this.d.post({ type: "ready" });
    }
    // ---------------------------------------------------------------------------
    // Lazily loaded modules (PROTOCOL §8; PLAN §5.6). Failures leave the defaults in place.
    // ---------------------------------------------------------------------------
    liveSession() {
        return this.session ? { sessionId: this.session.sessionId, visitId: this.session.visitId } : null;
    }
    async installStreamingLink(cfg) {
        const pref = cfg.transport ?? "fetch";
        if (pref === "fetch")
            return;
        let link = null;
        if (pref === "webtransport") {
            const m = await loadWorkerModule("wt", cfg.wtJsUrl, this.d.modules);
            if (m && (this.d.webTransportFactory || m.available())) {
                link = m.create({ url: this.tr.wtUrl, projectKey: cfg.projectKey, sdkVersion: cfg.sdkVersion, requestId: this.tr.requestId, session: () => this.liveSession(), now: this.now, factory: this.d.webTransportFactory, onDegraded: () => this.tr.degradePreferred() });
            }
        }
        else {
            const m = await loadWorkerModule("ws", cfg.wsJsUrl, this.d.modules);
            if (m && (this.d.socketFactory || m.available())) {
                link = m.create({
                    url: this.tr.wsUrl,
                    projectKey: cfg.projectKey,
                    sdkVersion: cfg.sdkVersion,
                    requestId: this.tr.requestId,
                    session: () => this.liveSession(),
                    now: this.now,
                    setTimeout: this.st,
                    clearTimeout: this.ct,
                    factory: this.d.socketFactory,
                    heartbeatMs: this.d.heartbeatMs,
                    onDegraded: () => this.tr.degradePreferred(),
                    onServerFrame: (f) => this.tr.applyServerFrame(f),
                });
            }
        }
        if (this.stopped)
            return;
        if (link)
            this.tr.setPreferred(link);
        else if (cfg.debug)
            console.info("[backstory] streaming transport unavailable; using fetch");
    }
    async installZstd(cfg) {
        try {
            const g = await loadWorkerModule("zstd", cfg.zstdJsUrl, this.d.modules);
            if (!g)
                throw new Error("unavailable");
            await g.init(cfg.zstdWasmUrl);
            let dict = null;
            if (cfg.dictUrl) {
                try {
                    const r = await this.d.fetch(cfg.dictUrl);
                    if (r.ok)
                        dict = new Uint8Array(await r.arrayBuffer());
                }
                catch {
                    dict = null;
                }
            }
            const codec = { compress: (i, l) => g.compress(i, l), compressUsingDict: g.compressUsingDict ? (i, d, l) => g.compressUsingDict(i, d, l) : undefined };
            // Smoke test so a broken build never produces garbage chunks.
            if (!codec.compress(new Uint8Array([1, 2, 3, 4]), 1)?.byteLength)
                throw new Error("probe");
            setZstd(codec, dict);
            if (cfg.debug)
                console.info("[backstory] zstd ready");
        }
        catch {
            setZstd(null);
            this.compression = "deflate";
            if (cfg.debug)
                console.info("[backstory] zstd unavailable, using deflate");
        }
    }
    ensureMonitor() {
        if (this.mon)
            return Promise.resolve();
        if (this.monLoading)
            return this.monLoading;
        const cfg = this.cfg;
        const mc = cfg.monitor ?? { triggers: [...BUILTIN_TRIGGER_DEFAULTS], preRollMs: 60_000, preRollBytes: 2 * 1024 * 1024, burstMs: 60_000 };
        this.monLoading = loadWorkerModule("monitor", cfg.monitorJsUrl, this.d.modules).then((m) => {
            this.monLoading = null;
            if (!m || this.stopped) {
                if (cfg.debug && !m)
                    console.info("[backstory] monitor module unavailable; monitor mode disabled");
                return;
            }
            this.mon = m.create({ triggers: mc.triggers, preRollMs: mc.preRollMs, preRollBytes: mc.preRollBytes, burstMs: mc.burstMs, now: this.now });
            if (this.pendingMode) {
                const mode = this.pendingMode;
                this.pendingMode = null;
                this.setMode(mode);
            }
        });
        return this.monLoading;
    }
    setMode(mode) {
        if (!this.mon) {
            // Events keep flowing normally until the module is installed.
            this.pendingMode = mode;
            if (mode === "monitor")
                void this.ensureMonitor();
            return;
        }
        const flushed = this.mon.setMode(mode);
        for (const e of flushed)
            this.enqueueEvent(e);
        if (flushed.length)
            this.flushNow();
    }
    // ---------------------------------------------------------------------------
    // Intake
    // ---------------------------------------------------------------------------
    /** A feature the plan turned off must not leave in the next chunk or the pre-roll. */
    dropGated(m) {
        const drop = (e) => {
            if (m.canvas && (e.type === "canvas_frame" || e.type === "canvas_cmds"))
                return true;
            if (m.webrtc && (e.type === "webrtc" || e.type === "webrtc_stats"))
                return true;
            if (m.webrtc && e.type === "custom" && e.name === "backstory.webrtc_trigger")
                return true;
            if (m.live && e.type === "assist")
                return true;
            if (m.custom && e.type === "custom" && !e.name.startsWith("backstory."))
                return true;
            return false;
        };
        this.q.dropWhere(drop);
        this.mon?.dropWhere(drop);
    }
    push(e) {
        if (!this.started || this.stopped || !this.session)
            return;
        if (this.gov.level === "PAUSED" && e.type !== "control") {
            this.q.droppedBytes += sizeOf(e);
            return;
        }
        // --- transport & monitor ---
        if (this.mon && this.mon.mode === "monitor") {
            const r = this.mon.intake(e);
            if (r.disposition === "hold")
                return; // parked in the pre-roll ring (counted in the budget)
            if (r.burst) {
                this.enqueueEvent(r.burst);
                for (const held of r.flush ?? [])
                    this.enqueueEvent(held);
                this.d.post({ type: "burst", trigger: String(r.burst.props?.trigger ?? ""), preRollEvents: r.flush?.length ?? 0 });
            }
        }
        // --- end transport & monitor ---
        this.enqueueEvent(e);
    }
    /** Monitor trigger from outside the event stream (offline). */
    burst(trigger) {
        if (!this.session || !this.mon)
            return;
        const { events, marker } = this.mon.startBurst(trigger);
        this.enqueueEvent(marker);
        for (const e of events)
            this.enqueueEvent(e);
        this.d.post({ type: "burst", trigger, preRollEvents: events.length });
        this.flushNow();
    }
    enqueueEvent(e) {
        if (!this.session)
            return;
        if (e.type === "snapshot" && this.awaitingDropSnapshot)
            this.freeAbandoned();
        // A checkpoint control + its snapshot open a new chunk together (PROTOCOL §2.1).
        const onlyCheckpointPending = e.type === "snapshot" && this.q.length === 1 && this.lastPushed?.type === "control" && this.lastPushed.kind === "checkpoint";
        if (startsNewChunk(e) && !onlyCheckpointPending)
            this.flushNow();
        this.lastPushed = e;
        const bytes = sizeOf(e);
        // Hard budget: never accept beyond the cap; free the abandoned range and shed low priority first.
        if (this.totalBytes() + bytes > this.cfg.memoryBudgetBytes) {
            if (this.abandoned)
                this.freeAbandoned();
            if (this.totalBytes() + bytes > this.cfg.memoryBudgetBytes)
                this.q.shed(bytes, 1);
            if (this.totalBytes() + bytes > this.cfg.memoryBudgetBytes && priorityOf(e) !== 0) {
                this.q.droppedBytes += bytes;
                return;
            }
            // P0 (control, snapshot, error, custom) is always accepted: bounded and essential.
        }
        this.q.push(e, bytes);
        if (this.q.bytes >= this.cfg.batchBytes)
            this.flushNow();
        else if (!this.flushTimer)
            this.flushTimer = this.st(() => this.flushNow(), this.cfg.batchMs);
    }
    totalBytes() {
        return this.q.bytes + this.encodingBytes + (this.abandoned?.bytes ?? 0) + (this.tr ? this.tr.queuedBytes + this.tr.inFlightBytes : 0) + (this.mon?.ringBytes ?? 0);
    }
    // ---------------------------------------------------------------------------
    // Batching, encoding, spill, transport
    // ---------------------------------------------------------------------------
    /** Assemble, encode, and hand the chunk to transport. */
    flushNow() {
        if (this.flushTimer) {
            this.ct(this.flushTimer);
            this.flushTimer = null;
        }
        if (!this.session || !this.q.length)
            return;
        const t0 = this.now();
        const all = this.q.drain();
        const dropped = this.q.droppedBytes;
        this.q.droppedBytes = 0;
        // --- transport & monitor ---
        // Pre-encode size guard: never build a chunk that could exceed the gateway caps.
        const { groups, oversize } = splitBySize(all);
        for (const o of oversize)
            this.dropOversize(o);
        if (oversize.length && this.q.length) {
            // Gap markers and diagnostics produced by dropOversize ride along in this flush.
            const extra = this.q.drain();
            if (groups.length)
                groups[0] = [...groups[0], ...extra].sort((a, b) => a.e.t - b.e.t);
            else
                groups.push(extra);
        }
        let first = true;
        for (const g of groups) {
            // WebTransport: a group made only of P3/P4 events small enough for a datagram travels lossy.
            const lossy = this.tr.kind === "webtransport" && isLossyGroup(g);
            this.emitChunk(g, first ? dropped : 0, lossy);
            first = false;
        }
        this.recordWork(this.now() - t0);
        // --- end transport & monitor ---
    }
    /** Assemble one group into a chunk, encode (ordered), spill, and hand to the transport. */
    emitChunk(items, dropped, lossy) {
        if (!this.session || !items.length)
            return;
        const s = this.session;
        let chunk;
        if (lossy) {
            // Own seq space: assembleChunk must not consume a reliable seq.
            const save = s.seq;
            chunk = assembleChunk(s, items, this.gov.level, dropped, this.tr.clockOffsetMs);
            s.seq = save;
            if (!chunk)
                return;
            chunk.seq = ++this.lossySeq;
            chunk.lossy = true;
        }
        else {
            chunk = assembleChunk(s, items, this.gov.level, dropped, this.tr.clockOffsetMs);
            if (!chunk)
                return;
        }
        const sid = chunk.sessionId;
        const seq = chunk.seq;
        const json = JSON.stringify(chunk);
        const hint = priorityHint(items);
        const contents = { snapshot: items.some((i) => i.e.type === "snapshot"), p1: items.some((i) => priorityOf(i.e) === 1) };
        const pendingBytes = json.length;
        this.encodingBytes += pendingBytes;
        // Serialize encodes so seq order is preserved into the transport queue.
        this.encoding = this.encoding.then(async () => {
            let body;
            let encoding;
            try {
                ({ body, encoding } = await encodeChunk(json, this.compression));
            }
            finally {
                this.encodingBytes -= pendingBytes;
            }
            if (this.stopped)
                return;
            if (lossy) {
                if (body.byteLength > LOSSY_MAX_BODY) {
                    // Too big for a datagram after all: it still has its lossy seq, send it reliably.
                    this.tr.enqueue(sid, seq, body, encoding, true);
                    return;
                }
                this.tr.enqueue(sid, seq, body, encoding, true);
                return;
            }
            const key = sid + ":" + seq;
            this.sentContents.set(key, contents);
            const entry = { sessionId: sid, seq, body, encoding: encoding, bytes: body.byteLength, createdAt: this.now(), priorityHint: hint, project: this.project };
            // Write-behind: spill first (best effort, non-blocking), then send.
            const w = this.spill.put(entry, this.session?.sessionId ?? sid).catch(() => false);
            this.spillWrites.set(key, w);
            void w.finally(() => this.spillWrites.delete(key));
            this.tr.enqueue(sid, seq, body, encoding);
        });
    }
    /**
     * A single event larger than the caps cannot be sent. Mutations and snapshots
     * are causal, so the loss is declared as a `quota` gap and a fresh checkpoint
     * is requested (at most once a minute, so an oversize DOM cannot loop).
     */
    dropOversize(item) {
        const s = this.session;
        if (!s)
            return;
        this.q.droppedBytes += item.bytes;
        const p = priorityOf(item.e);
        if (item.e.type === "snapshot" || p === 1) {
            const seq = s.seq + 1;
            const gap = { fromSeq: seq, toSeq: seq, ms: 0, reason: "quota" };
            s.gaps.push(gap);
            s.seq = seq;
            this.q.push({ type: "control", kind: "gap", t: this.now(), fromSeq: seq, toSeq: seq, ms: 0, reason: "quota" });
            const now = this.now();
            if (now - this.lastOversizeCheckpointAt > 60_000) {
                this.lastOversizeCheckpointAt = now;
                this.q.push({ type: "custom", t: now, name: "backstory.oversize", props: { type: item.e.type, bytes: item.bytes } });
                this.d.post({ type: "checkpoint-request", reason: "drop" });
            }
        }
        if (this.cfg.debug)
            console.warn("[backstory] dropped oversize event", item.e.type, item.bytes);
    }
    /** 413/400 safety net: the gateway refused a chunk we already numbered. Declare the gap honestly. */
    onRejected(sid, seq, kind, lossy) {
        if (lossy) {
            this.lossyDropped++;
            return;
        }
        const key = sid + ":" + seq;
        const c = this.sentContents.get(key);
        this.sentContents.delete(key);
        if (!this.session || this.session.sessionId !== sid)
            return;
        const reason = kind === "413" ? "quota" : "memory";
        this.session.gaps.push({ fromSeq: seq, toSeq: seq, ms: 0, reason });
        this.q.push({ type: "control", kind: "gap", t: this.now(), fromSeq: seq, toSeq: seq, ms: 0, reason });
        if (c?.snapshot || c?.p1) {
            const now = this.now();
            if (now - this.lastOversizeCheckpointAt > 60_000) {
                this.lastOversizeCheckpointAt = now;
                this.d.post({ type: "checkpoint-request", reason: "drop" });
            }
        }
    }
    onAck(sid, ack) {
        for (const k of this.sentContents.keys()) {
            const [s, q] = k.split(":");
            if (s === sid && Number(q) <= ack)
                this.sentContents.delete(k);
        }
        // Delete spilled copies up to the ack once their writes have landed.
        const pending = [...this.spillWrites.entries()].filter(([k]) => k.startsWith(sid + ":")).map(([, p]) => p);
        void Promise.all(pending)
            .then(() => this.spill.deleteThrough(sid, ack))
            .then(() => {
            // Only now is the spilled copy gone, so a recovered chunk cannot be picked up twice.
            for (const k of this.recoveryInFlight) {
                const [s, q] = k.split(":");
                if (s === sid && Number(q) <= ack)
                    this.recoveryInFlight.delete(k);
            }
            this.feedRecovery();
        });
        this.feedRecovery();
    }
    /** Feed recovered chunks (previous sessions) to the transport, loading bodies lazily. */
    feedRecovery() {
        if (!this.started || this.stopped || !this.spill.available || !this.session)
            return;
        if (!this.recovery.length) {
            const stale = this.spill.stale(this.session.sessionId, this.project).filter((m) => !this.recoveryInFlight.has(m.sessionId + ":" + m.seq));
            if (!stale.length)
                return;
            this.recovery = stale;
        }
        if (this.recoveryLoading || this.tr.pendingCount > 2)
            return;
        const next = this.recovery.shift();
        if (!next)
            return;
        this.recoveryLoading = true;
        this.recoveryInFlight.add(next.sessionId + ":" + next.seq);
        void this.spill
            .get(next.sessionId, next.seq)
            .then((e) => {
            if (e && !this.stopped)
                this.tr.enqueueRecovered(e.sessionId, e.seq, e.body, e.encoding);
            else {
                this.recoveryInFlight.delete(next.sessionId + ":" + next.seq);
                void this.spill.delete(next.sessionId, next.seq);
            }
        })
            .finally(() => {
            this.recoveryLoading = false;
        });
    }
    // ---------------------------------------------------------------------------
    // Checkpoint-and-drop (PLAN §7.6)
    // ---------------------------------------------------------------------------
    /**
     * Memory is critical: abandon the current P1+ range (queued events and
     * not-yet-sent chunks of this session), record an honest gap marker, and ask
     * the main thread for a fresh full snapshot. The abandoned bytes are freed
     * when the snapshot arrives, after a 2 s fallback, or earlier if the budget
     * needs the room.
     */
    checkpointAndDrop() {
        if (this.mon?.ringBytes)
            this.q.droppedBytes += this.mon.shed(); // pre-roll is the first thing to go
        if (!this.session || !LEVEL_POLICY[this.gov.level].allowDropP1) {
            // Below SURVIVAL: shed only the thinnable classes.
            this.q.shed(this.q.bytes, 2);
            return;
        }
        if (this.awaitingDropSnapshot)
            return;
        const s = this.session;
        if (this.flushTimer) {
            this.ct(this.flushTimer);
            this.flushTimer = null;
        }
        // 1. Unsent chunks of this session are abandoned (in-flight ones will be acked or retried).
        const droppedChunks = this.tr.dropQueued(s.sessionId);
        for (const c of droppedChunks)
            void this.spill.delete(c.sessionId, c.seq);
        // 2. Queued P1..P4 events move to the abandoned buffer (P0 stays: control, errors, custom).
        const items = this.q.take(1);
        const bytes = items.reduce((n, x) => n + x.bytes, 0) + droppedChunks.reduce((n, c) => n + c.body.byteLength, 0);
        if (!items.length && !droppedChunks.length)
            return;
        // 3. Gap covers the dropped chunk seqs and the would-be chunk of the queued events.
        const fromSeq = droppedChunks.length ? Math.min(...droppedChunks.map((c) => c.seq)) : s.seq + 1;
        const toSeq = s.seq + 1;
        let first = Infinity;
        let last = -Infinity;
        for (const it of items) {
            if (it.e.t < first)
                first = it.e.t;
            if (it.e.t > last)
                last = it.e.t;
        }
        const ms = items.length ? Math.max(0, last - first) : 0;
        const gap = { fromSeq, toSeq, ms, reason: "memory" };
        s.gaps.push(gap);
        s.seq = toSeq; // the next chunk continues after the declared gap
        this.q.push({ type: "control", kind: "gap", t: this.now(), fromSeq, toSeq, ms, reason: "memory" });
        this.lastPushed = null;
        this.abandoned = { items, bytes, timer: this.st(() => this.freeAbandoned(), DROP_FREE_FALLBACK_MS) };
        this.awaitingDropSnapshot = true;
        this.d.post({ type: "checkpoint-request", reason: "drop" });
        if (this.cfg.debug)
            console.warn("[backstory] checkpoint-and-drop: abandoned", bytes, "bytes, seq", fromSeq, "-", toSeq);
    }
    freeAbandoned() {
        this.awaitingDropSnapshot = false;
        if (!this.abandoned)
            return;
        this.ct(this.abandoned.timer);
        this.q.droppedBytes += this.abandoned.bytes;
        this.abandoned = null;
    }
    // ---------------------------------------------------------------------------
    // Governor, self-protection, watchdog
    // ---------------------------------------------------------------------------
    metrics() {
        return {
            queuedBytes: this.totalBytes(),
            budgetBytes: this.cfg.memoryBudgetBytes,
            goodputBps: this.tr.goodputBps || (this.online ? 64 * 1024 : 1),
            online: this.online,
            spillFull: this.spill.full,
            ingest429: this.now() < this.ingest429Until,
        };
    }
    /**
     * Record what the recorder currently costs the page. Queued before the level
     * event's own bytes land, so the sample describes the state that caused the
     * transition rather than the state after reporting it.
     */
    sampleHealth(level) {
        if (!this.session)
            return;
        const now = this.now();
        this.lastHealthSampleAt = now;
        const m = this.metrics();
        const { drainSec, memPct } = Governor.target(m);
        this.q.push({
            type: "control",
            kind: "health",
            t: now,
            level,
            health: {
                drainSec: Math.round(drainSec * 10) / 10,
                memPct: Math.round(memPct * 1000) / 1000,
                queuedBytes: m.queuedBytes,
                goodputBps: Math.round(this.tr?.goodputBps ?? 0),
                droppedBytes: this.q.droppedBytes,
                spillBytes: this.spill.bytes,
                selfJank: Math.round(this.gov.selfShare(now) * 1000) / 1000,
                clockOffsetMs: Math.round(this.tr?.clockOffsetMs ?? 0),
                online: m.online,
            },
        });
    }
    recordWork(ms) {
        if (ms >= SELF_WORK_MS)
            this.gov.recordLongTask(this.now(), ms, true);
        if (ms >= WATCHDOG_SLOW_BATCH_MS) {
            if (++this.slowStrikes >= WATCHDOG_STRIKES)
                this.tripWatchdog("slow_batches");
        }
        else
            this.slowStrikes = 0;
    }
    /** Stop recording; send one diagnostic event first (no PII). */
    tripWatchdog(reason) {
        if (this.watchdogTripped)
            return;
        this.watchdogTripped = true;
        try {
            if (this.session) {
                this.q.push({ type: "custom", t: this.now(), name: "backstory.watchdog", props: { reason, level: this.gov.level, queuedBytes: this.totalBytes() } });
                this.flushNow();
            }
        }
        catch {
            /* best effort */
        }
        void this.encoding.then(() => {
            this.stop();
            this.d.post({ type: "fatal", reason: "watchdog:" + reason });
        });
    }
    tick() {
        if (!this.started || this.stopped)
            return;
        if (!this.online && typeof navigator !== "undefined" && navigator.onLine)
            this.online = true;
        this.mon?.evict(this.now());
        this.gov.tick(this.now(), this.metrics());
        const cadence = this.gov.level === "HEALTHY" ? HEALTH_SAMPLE_HEALTHY_MS : HEALTH_SAMPLE_PRESSURE_MS;
        if (this.now() - this.lastHealthSampleAt >= cadence)
            this.sampleHealth(this.gov.level);
        this.feedRecovery();
        if (this.cfg.debug)
            this.d.post({ type: "debug", state: this.debugState() });
    }
    scheduleTick() {
        this.tickTimer = this.st(() => {
            this.tick();
            if (!this.stopped)
                this.scheduleTick();
        }, 1000);
    }
    debugState() {
        const metrics = this.metrics();
        const target = Governor.target(metrics);
        const level = this.gov?.level ?? "HEALTHY";
        const forced = this.gov?.forced ?? null;
        let healthReason = "Capture is operating normally";
        if (forced)
            healthReason = `Pinned by project policy to ${forced}`;
        else if (!metrics.online && metrics.spillFull)
            healthReason = "Offline and local recovery storage is full";
        else if (!metrics.online)
            healthReason = "Offline; buffering locally";
        else if (metrics.ingest429)
            healthReason = "Ingest is rate limiting uploads";
        else if (target.memPct > 0.95)
            healthReason = "Recording memory is nearly full";
        else if (target.drainSec > 60)
            healthReason = "Upload queue is draining very slowly";
        else if (level !== target.level)
            healthReason = `Recovering after pressure; target is ${target.level}`;
        else if (level !== "HEALTHY")
            healthReason = "Capture detail is reduced to protect the page";
        return {
            level,
            targetLevel: target.level,
            healthReason,
            drainSeconds: target.drainSec,
            memoryPressure: target.memPct,
            queuedBytes: this.totalBytes(),
            inFlightBytes: this.tr?.inFlightBytes ?? 0,
            credits: this.tr?.credits ?? 0,
            seq: this.session?.seq ?? 0,
            acked: this.session ? this.tr.acked.get(this.session.sessionId) ?? 0 : 0,
            droppedBytes: this.q.droppedBytes,
            sessionId: this.session?.sessionId ?? "",
            visitId: this.session?.visitId ?? "",
            clockOffsetMs: this.tr?.clockOffsetMs ?? 0,
            goodputBps: this.tr?.goodputBps ?? 0,
            online: this.online,
            spillBytes: this.spill.bytes,
            spillBudgetBytes: this.spill.budgetBytes,
            spillChunks: this.spill.count,
            gaps: this.session?.gaps.length ?? 0,
            forcedLevel: this.gov?.forced ?? null,
            selfLongTaskShare: this.gov ? this.gov.selfShare(this.now()) : 0,
            watchdogStrikes: Math.max(this.throwStrikes, this.slowStrikes),
            // --- transport & monitor ---
            transport: this.tr?.kind ?? "fetch",
            encoding: this.compression === "zstd" ? "json+zstd" : "json+deflate",
            mode: this.mon?.mode ?? this.pendingMode ?? "normal",
            preRollBytes: this.mon?.ringBytes ?? 0,
            preRollEvents: this.mon?.ringLength ?? 0,
            bursting: this.mon?.bursting ?? false,
            lossyDropped: this.lossyDropped,
            // --- end transport & monitor ---
        };
    }
    // ---------------------------------------------------------------------------
    // Lifecycle
    // ---------------------------------------------------------------------------
    /** Segment rollover: flush and report transport state so the next worker starts warm. */
    handover() {
        this.flushNow();
        void this.encoding.then(() => {
            this.d.post({ type: "handover-state", clockOffsetMs: this.tr.clockOffsetMs, credits: this.tr.credits, pending: this.tr.pendingCount, transport: this.tr.kind, mode: this.mon?.mode ?? "normal", burstUntil: this.mon?.burstUntil });
        });
    }
    /** Hand un-acked chunks to the main thread for a keepalive send on unload. */
    drainToMain() {
        this.flushNow();
        void this.encoding.then(() => {
            const items = this.tr.drain();
            this.d.post({ type: "drained", chunks: items.map((i) => ({ seq: i.seq, body: i.body.buffer.slice(i.body.byteOffset, i.body.byteOffset + i.body.byteLength), encoding: i.encoding })) });
        });
    }
    stop() {
        this.stopped = true;
        if (this.flushTimer)
            this.ct(this.flushTimer);
        if (this.tickTimer)
            this.ct(this.tickTimer);
        if (this.abandoned)
            this.ct(this.abandoned.timer);
        this.tr?.stop();
    }
    // Test hooks
    get queues() {
        return this.q;
    }
    get transport() {
        return this.tr;
    }
    get governor() {
        return this.gov;
    }
    get spillStore() {
        return this.spill;
    }
    get abandonedBytes() {
        return this.abandoned?.bytes ?? 0;
    }
    get monitor() {
        return this.mon;
    }
    /** Await lazily loading modules (tests). */
    ready() {
        return Promise.all([this.monLoading ?? Promise.resolve(), this.linkLoading ?? Promise.resolve()]).then(() => undefined);
    }
    /** Await pending encode work (tests). */
    settle() {
        return this.encoding;
    }
    setMeta(meta) {
        this.handle({ type: "meta", meta });
    }
}
// --- transport & monitor ---
const BUILTIN_TRIGGER_DEFAULTS = ["error", "network_failure", "offline", "long_task"];
/** Estimated encoded size of one event. */
function estimatedEncoded(it) {
    const media = it.e.type === "canvas_frame";
    return Math.ceil(it.bytes * (media ? RATIO_MEDIA : RATIO_TEXT));
}
/**
 * Partition drained events (already time-sorted) into groups that stay under
 * the raw and estimated-compressed caps, preserving order. Events that alone
 * exceed a cap are returned as `oversize`. Snapshot/checkpoint boundaries are
 * preserved because the caller already flushed before them.
 */
function splitBySize(items, maxRaw = MAX_RAW_CHUNK_BYTES, maxEst = MAX_ESTIMATED_COMPRESSED_BYTES) {
    const groups = [];
    const oversize = [];
    let cur = [];
    let raw = 0;
    let est = 0;
    for (const it of items) {
        const e = estimatedEncoded(it);
        if (it.bytes > maxRaw || e > maxEst) {
            oversize.push(it);
            continue;
        }
        if (cur.length && (raw + it.bytes > maxRaw || est + e > maxEst)) {
            groups.push(cur);
            cur = [];
            raw = 0;
            est = 0;
        }
        cur.push(it);
        raw += it.bytes;
        est += e;
    }
    if (cur.length)
        groups.push(cur);
    return { groups, oversize };
}
/** A group is lossy-eligible when every event is P3/P4 and it is small enough for one datagram. */
function isLossyGroup(items) {
    let bytes = 0;
    for (const it of items) {
        if (priorityOf(it.e) < 3)
            return false;
        bytes += it.bytes;
    }
    // Raw JSON must compress into a datagram; use the conservative text ratio.
    return Math.ceil(bytes * RATIO_TEXT) + 120 <= LOSSY_MAX_BODY;
}
// --- end transport & monitor ---
/** 0 = contains a snapshot (keep longest in spill); 2 = mostly pointer/scroll/canvas; 1 otherwise. */
function priorityHint(items) {
    let low = 0;
    let total = 0;
    for (const it of items) {
        if (it.e.type === "snapshot")
            return 0;
        total += it.bytes;
        const p = priorityOf(it.e);
        if (p >= 3)
            low += it.bytes;
    }
    return total > 0 && low / total > 0.5 ? 2 : 1;
}

// --- transport & monitor ---
/** Consecutive connect failures before the link reports itself degraded (fallback to fetch). */
const WS_MAX_CONNECT_FAILURES = 3;
const WS_HEARTBEAT_MS = 20_000;
const CONNECT_TIMEOUT_MS = 8000;
const OPEN = 1;
/**
 * Persistent WebSocket link (PROTOCOL §8). One connection per worker, a JSON
 * handshake first, binary frames for chunks, JSON text frames back with acks
 * and credits. Un-acked frames are failed on close so the Transport re-queues
 * them with the same seq and they are resent after reconnect.
 */
class WsLink {
    o;
    kind = "ws";
    requestId;
    sock = null;
    opening = null;
    pending = [];
    connectFailures = 0;
    backoffMs = 500;
    heartbeat = null;
    closed = false;
    degraded = false;
    /** Statistics for the debug panel. */
    reconnects = 0;
    constructor(o) {
        this.o = o;
        this.requestId = guidOrNew(o.requestId);
    }
    factory() {
        if (this.o.factory)
            return this.o.factory;
        const W = globalThis.WebSocket;
        return W ? (url) => new W(url) : null;
    }
    static available() {
        return typeof globalThis.WebSocket === "function";
    }
    async send(item) {
        const sentAt = this.o.now();
        if (this.closed || this.degraded)
            return { ok: false, kind: "network", status: 0, rttMs: 0, sentAt };
        const ok = await this.ensureOpen();
        if (!ok)
            return { ok: false, kind: "network", status: 0, rttMs: this.o.now() - sentAt, sentAt };
        return new Promise((resolve) => {
            const p = { item, resolve, sentAt: this.o.now() };
            this.pending.push(p);
            try {
                this.sock.send(frame(item));
            }
            catch {
                this.pending = this.pending.filter((x) => x !== p);
                resolve({ ok: false, kind: "network", status: 0, rttMs: this.o.now() - sentAt, sentAt });
                this.dropSocket();
            }
        });
    }
    /** Open (or reuse) the socket and complete the handshake. */
    ensureOpen() {
        if (this.sock && this.sock.readyState === OPEN)
            return Promise.resolve(true);
        if (this.opening)
            return this.opening;
        const f = this.factory();
        if (!f) {
            this.markDegraded();
            return Promise.resolve(false);
        }
        this.opening = new Promise((resolve) => {
            let done = false;
            const finish = (ok) => {
                if (done)
                    return;
                done = true;
                this.opening = null;
                this.o.clearTimeout(timer);
                if (ok) {
                    this.connectFailures = 0;
                    this.backoffMs = 500;
                    this.startHeartbeat();
                }
                else {
                    this.connectFailures++;
                    this.backoffMs = Math.min(30_000, this.backoffMs * 2);
                    if (this.connectFailures >= WS_MAX_CONNECT_FAILURES)
                        this.markDegraded();
                }
                resolve(ok);
            };
            let sock;
            try {
                sock = f(this.o.url);
            }
            catch {
                finish(false);
                return;
            }
            const timer = this.o.setTimeout(() => {
                try {
                    sock.close();
                }
                catch {
                    /* ignore */
                }
                finish(false);
            }, this.o.connectTimeoutMs ?? CONNECT_TIMEOUT_MS);
            sock.binaryType = "arraybuffer";
            sock.onopen = () => {
                this.sock = sock;
                const s = this.o.session();
                sock.send(JSON.stringify({ projectKey: this.o.projectKey, sessionId: s?.sessionId ?? "", visitId: s?.visitId ?? "", sdk: this.o.sdkVersion, requestId: this.requestId }));
                finish(true);
            };
            sock.onmessage = (ev) => this.onMessage(ev.data);
            sock.onerror = () => {
                /* onclose follows */
            };
            sock.onclose = () => {
                const wasOpen = this.sock === sock;
                if (wasOpen) {
                    this.sock = null;
                    this.reconnects++;
                    this.stopHeartbeat();
                    this.failPending("network");
                }
                finish(false);
            };
        });
        return this.opening;
    }
    onMessage(data) {
        if (typeof data !== "string")
            return;
        const f = parseServerFrame(data);
        if (!f)
            return;
        if (f.type === "pong" || f.type === "hello")
            return;
        const now = this.o.now();
        if (f.error) {
            const code = f.code;
            if (isFatalCode(code)) {
                const fatal = code;
                const all = this.pending;
                this.pending = [];
                for (const p of all)
                    p.resolve({ ok: false, kind: "fatal", status: fatalStatus(code), fatal, rttMs: now - p.sentAt, sentAt: p.sentAt });
                this.closed = true;
                this.dropSocket();
                return;
            }
            // Per-seq errors: too_large / invalid refer to `seq` (or the oldest pending when absent).
            const target = typeof f.seq === "number" ? this.pending.find((p) => p.item.seq === f.seq) : this.pending[0];
            if (target) {
                this.pending = this.pending.filter((p) => p !== target);
                const kind = code === "too_large" ? "413" : code === "invalid" ? "400" : code === "rate_limited" || code === "quota_exceeded" ? "429" : "5xx";
                const status = kind === "413" ? 413 : kind === "400" ? 400 : kind === "429" ? 429 : 500;
                target.resolve({ ok: false, kind, status, retryAfterMs: (f.retryAfterSec ?? 0) * 1000, serverTime: f.serverTime, rttMs: now - target.sentAt, sentAt: target.sentAt });
            }
            if (this.pending.length === 0 && !target)
                this.o.onServerFrame?.(f);
            return;
        }
        if (typeof f.ack === "number") {
            // Cumulative ack: everything with seq <= ack for the primary session is delivered.
            const acked = this.pending.filter((p) => p.item.seq <= f.ack);
            this.pending = this.pending.filter((p) => p.item.seq > f.ack);
            for (const p of acked)
                p.resolve({ ok: true, ack: f.ack, credits: f.credits, serverTime: f.serverTime, config: f.config, rttMs: now - p.sentAt, sentAt: p.sentAt });
            if (!acked.length)
                this.o.onServerFrame?.(f);
            return;
        }
        this.o.onServerFrame?.(f);
    }
    failPending(kind) {
        const all = this.pending;
        this.pending = [];
        const now = this.o.now();
        for (const p of all)
            p.resolve({ ok: false, kind, status: 0, rttMs: now - p.sentAt, sentAt: p.sentAt });
    }
    startHeartbeat() {
        this.stopHeartbeat();
        const ms = this.o.heartbeatMs ?? WS_HEARTBEAT_MS;
        const beat = () => {
            if (this.closed || !this.sock || this.sock.readyState !== OPEN)
                return;
            try {
                this.sock.send(JSON.stringify({ type: "ping", t: this.o.now() }));
            }
            catch {
                this.dropSocket();
                return;
            }
            this.heartbeat = this.o.setTimeout(beat, ms);
        };
        this.heartbeat = this.o.setTimeout(beat, ms);
    }
    stopHeartbeat() {
        if (this.heartbeat)
            this.o.clearTimeout(this.heartbeat);
        this.heartbeat = null;
    }
    dropSocket() {
        const s = this.sock;
        this.sock = null;
        this.stopHeartbeat();
        if (s) {
            try {
                s.close();
            }
            catch {
                /* ignore */
            }
        }
        this.failPending("network");
    }
    markDegraded() {
        if (this.degraded)
            return;
        this.degraded = true;
        this.o.onDegraded?.();
    }
    /** Allow a later retry after being degraded (the Transport calls this on its switch-back timer). */
    reset() {
        this.degraded = false;
        this.connectFailures = 0;
        this.backoffMs = 500;
    }
    get pendingCount() {
        return this.pending.length;
    }
    get connected() {
        return !!this.sock && this.sock.readyState === OPEN;
    }
    get nextBackoffMs() {
        return this.backoffMs;
    }
    close() {
        this.closed = true;
        this.dropSocket();
    }
}

// Lazily loaded WebSocket link (dist/backstory-ws.js): registers `self.__BACKSTORY_WS__`
// for the Recorder Worker (importScripts) and `window.__BACKSTORY_MOD__.ws` for the
// main-thread fallback. The core bundle only carries the fetch transport.
const ws = {
    available() {
        return WsLink.available();
    },
    create(o) {
        return new WsLink(o);
    },
};
const g = globalThis;
g.__BACKSTORY_WS__ = ws;
g.__BACKSTORY_MOD__ ??= {};
g.__BACKSTORY_MOD__.ws = ws;

// Static inline-pipeline resolution (ESM build and tests): the main-thread
// fallback Pipeline is part of the bundle. The socket transport ships with it,
// so the default connection is a WebSocket instead of a separate script.
function createInlinePipeline(deps) {
    return new Pipeline({ ...deps, modules: { ws, ...deps.modules } });
}

/** Main-thread pipeline host; the Pipeline may arrive asynchronously (lazy module), so messages queue until then. */
function inlineHost(listeners, initial = []) {
    let p = null;
    let stopped = false;
    const queued = initial.slice(0, 2000);
    const r = createInlinePipeline({ post: (m) => listeners.forEach((l) => l(m)), fetch: (...a) => fetch(...a) });
    const attach = (pl) => {
        if (!pl || stopped)
            return;
        p = pl;
        for (const m of queued.splice(0))
            p.handle(m);
    };
    if (r instanceof Promise)
        void r.then(attach, () => undefined);
    else
        attach(r);
    return {
        kind: "inline",
        post: (m) => {
            if (p)
                p.handle(m);
            else if (queued.length < 2000)
                queued.push(m);
        },
        onMessage: (fn) => listeners.push(fn),
        terminate: () => {
            stopped = true;
            p?.stop();
        },
    };
}
/**
 * Starts the Recorder Worker from `workerUrl`, else from the inlined source as
 * a Blob URL (ESM build), else from `workerScriptUrl` (dist/worker.js next to
 * the script-tag bundle) through a Blob bootstrap that `importScripts` it
 * (works cross-origin, unlike `new Worker(crossOriginUrl)`), else runs the
 * Pipeline on the main thread (same code path).
 */
/** Same pipeline as the worker, running on the page so its uploads are ordinary requests. */
function createInlineHost() {
    return inlineHost([]);
}
function createHost(workerUrl, workerScriptUrl) {
    const W = globalThis.Worker;
    if (W) {
        let url = workerUrl;
        let blobUrl = null;
        if (!url) {
            const src = workerSource$1;
            {
                try {
                    blobUrl = URL.createObjectURL(new Blob([src], { type: "text/javascript" }));
                    url = blobUrl;
                }
                catch {
                    url = undefined;
                }
            }
        }
        if (url) {
            try {
                const w = new W(url);
                const listeners = [];
                const queued = [];
                let inline = null;
                let failed = false;
                w.onmessage = (e) => {
                    if (e.data?.type === "ready")
                        queued.length = 0; // booted: no replay needed
                    listeners.forEach((l) => l(e.data));
                };
                // Boot failure (script 404, CSP): replay everything posted so far into a main-thread pipeline.
                w.onerror = () => {
                    if (failed)
                        return;
                    failed = true;
                    try {
                        w.terminate();
                    }
                    catch {
                        /* ignore */
                    }
                    inline = inlineHost(listeners, queued.splice(0));
                };
                return {
                    get kind() {
                        return inline ? "inline" : "worker";
                    },
                    post: (m) => {
                        if (inline)
                            return inline.post(m);
                        if (queued.length < 2000)
                            queued.push(m);
                        w.postMessage(m);
                    },
                    onMessage: (fn) => listeners.push(fn),
                    terminate: () => {
                        inline?.terminate();
                        w.terminate();
                        if (blobUrl)
                            URL.revokeObjectURL(blobUrl);
                    },
                };
            }
            catch {
                /* fall through to inline */
            }
        }
    }
    return inlineHost([]);
}

const CACHE_TTL_MS = 5 * 60_000;
const cacheKey = (projectKey) => "__backstory_cfg:" + projectKey;
const LIMIT_REASONS = ["not_included", "cap_reached", "payment_overdue", "trial_ceiling", "subscription_ended", "disabled"];
/**
 * Validate a `limits` block from the config endpoint or an ack nudge. Returns
 * null when the server sent nothing usable, so callers keep their last known
 * state instead of flipping features on by accident.
 */
function parseLimits(v) {
    if (!v || typeof v !== "object")
        return null;
    const p = v;
    const reason = (x) => (LIMIT_REASONS.includes(x) ? x : undefined);
    const feature = (x, def) => {
        if (!x || typeof x !== "object")
            return def;
        const f = x;
        const allowed = typeof f.allowed === "boolean" ? f.allowed : def.allowed;
        const r = allowed ? undefined : reason(f.reason);
        return r ? { allowed, reason: r } : { allowed };
    };
    const sessions = p.sessions === "off" || p.sessions === "triggers-only" ? p.sessions : "ok";
    const out = {
        sessions,
        canvas: feature(p.canvas, DEFAULT_SDK_LIMITS.canvas),
        live: feature(p.live, DEFAULT_SDK_LIMITS.live),
        remoteControl: feature(p.remoteControl, DEFAULT_SDK_LIMITS.remoteControl),
        supportCalling: feature(p.supportCalling, DEFAULT_SDK_LIMITS.supportCalling),
        customEvents: feature(p.customEvents, DEFAULT_SDK_LIMITS.customEvents),
        webrtc: feature(p.webrtc, DEFAULT_SDK_LIMITS.webrtc),
        recheckSec: typeof p.recheckSec === "number" && Number.isFinite(p.recheckSec) && p.recheckSec > 0 ? Math.max(60, Math.min(24 * 3600, p.recheckSec)) : DEFAULT_SDK_LIMITS.recheckSec,
    };
    const sr = sessions === "ok" ? undefined : reason(p.sessionsReason);
    if (sr)
        out.sessionsReason = sr;
    return out;
}
/** `https://ingest.example.com/v1/ingest` → `https://ingest.example.com` (PLAN: ingest and api share a host behind the ingress). */
function deriveApiUrl(ingestUrl) {
    try {
        const u = new URL(ingestUrl);
        const path = u.pathname.replace(/\/v1\/ingest\/?$/, "");
        return u.origin + path.replace(/\/$/, "");
    }
    catch {
        return ingestUrl.replace(/\/v1\/ingest\/?$/, "");
    }
}
/** Deep-merge a partial server config over the defaults; unknown keys are kept. */
function mergeRemoteConfig(partial) {
    const d = DEFAULT_SDK_REMOTE_CONFIG;
    const p = (partial && typeof partial === "object" ? partial : {});
    const pick = (v, def, ok) => (ok(v) ? v : def);
    const isNum = (x) => typeof x === "number" && Number.isFinite(x);
    const isBool = (x) => typeof x === "boolean";
    const isObj = (x) => !!x && typeof x === "object";
    const s = (isObj(p.sampling) ? p.sampling : {});
    const pr = (isObj(p.privacy) ? p.privacy : {});
    const c = (isObj(p.collectors) ? p.collectors : {});
    const out = {
        recording: pick(p.recording, d.recording, (x) => x === "on" || x === "off" || x === "triggers-only"),
        sampling: {
            mode: pick(s.mode, d.sampling.mode, (x) => x === "always" || x === "conditional" || x === "triggers-only"),
            baselineSampleRate: Math.max(0, Math.min(1, pick(s.baselineSampleRate, d.sampling.baselineSampleRate, isNum))),
            triggers: Array.isArray(s.triggers) ? s.triggers.filter((x) => typeof x === "string") : d.sampling.triggers,
            bufferSeconds: pick(s.bufferSeconds, d.sampling.bufferSeconds, isNum),
            routes: Array.isArray(s.routes) ? s.routes : undefined,
            alwaysRecordUsers: Array.isArray(s.alwaysRecordUsers) ? s.alwaysRecordUsers : undefined,
        },
        privacy: {
            mode: normalizeMode(pick(pr.mode, d.privacy.mode, (x) => x === "strict" || x === "balanced" || x === "permissive")),
            allowUnmask: pick(pr.allowUnmask, d.privacy.allowUnmask, isBool),
        },
        canvas: pick(p.canvas, d.canvas, isBool),
        memoryBudgetMB: Math.max(4, Math.min(256, pick(p.memoryBudgetMB, d.memoryBudgetMB, isNum))),
        segmentHours: Math.max(1, Math.min(24, pick(p.segmentHours, d.segmentHours, isNum))),
        checkpointMinutes: Math.max(1, Math.min(120, pick(p.checkpointMinutes, d.checkpointMinutes, isNum))),
        monitorModeAfterMinutes: Math.max(0, pick(p.monitorModeAfterMinutes, d.monitorModeAfterMinutes, isNum)),
        forceLevel: pick(p.forceLevel, undefined, (x) => x === "HEALTHY" || x === "CONSTRAINED" || x === "DEGRADED" || x === "SURVIVAL" || x === "PAUSED"),
        flags: isObj(p.flags) ? Object.fromEntries(Object.entries(p.flags).filter(([, v]) => typeof v === "boolean")) : { ...d.flags },
        collectors: {
            console: pick(c.console, d.collectors.console, isBool),
            network: pick(c.network, d.collectors.network, isBool),
            vitals: pick(c.vitals, d.collectors.vitals, isBool),
            longTasks: pick(c.longTasks, d.collectors.longTasks, isBool),
            errors: pick(c.errors, d.collectors.errors, isBool),
        },
        limits: parseLimits(p.limits) ?? { ...DEFAULT_SDK_LIMITS },
        generatedAt: pick(p.generatedAt, 0, isNum),
    };
    if (out.forceLevel === undefined)
        delete out.forceLevel;
    if (out.sampling.routes === undefined)
        delete out.sampling.routes;
    if (out.sampling.alwaysRecordUsers === undefined)
        delete out.sampling.alwaysRecordUsers;
    return out;
}
async function fetchRemoteConfig(apiUrl, projectKey, sdkVersion, o = {}) {
    const now = o.now ?? (() => Date.now());
    const storage = o.storage === undefined ? safeSessionStorage() : o.storage;
    const key = cacheKey(projectKey);
    try {
        const raw = o.fresh ? null : storage?.getItem(key);
        if (raw) {
            const c = JSON.parse(raw);
            // `age < TTL` alone trusts the stored clock: an entry written with a
            // future timestamp (clock skew, a restored profile) never expires, and a
            // collector switched off in that copy stays off for the life of the tab.
            const age = now() - (c?.at ?? 0);
            if (c && typeof c.at === "number" && age >= 0 && age < CACHE_TTL_MS) {
                return { config: mergeRemoteConfig(c.cfg), source: "cache", raw: asRaw(c.cfg) };
            }
        }
    }
    catch {
        /* ignore cache errors */
    }
    const f = o.fetch ?? (typeof fetch === "function" ? (...a) => fetch(...a) : null);
    if (!f)
        return { config: mergeRemoteConfig(null), source: "default", raw: {} };
    const timeoutMs = o.timeoutMs ?? 300;
    const ctl = typeof AbortController !== "undefined" ? new AbortController() : null;
    const timer = setTimeout(() => ctl?.abort(), timeoutMs);
    try {
        const url = `${apiUrl.replace(/\/$/, "")}/v1/config?key=${encodeURIComponent(projectKey)}&sdk=${encodeURIComponent(sdkVersion)}`;
        const res = await f(url, { method: "GET", signal: ctl?.signal, credentials: "omit", ...(o.fresh ? { cache: "no-store" } : {}) });
        if (!res.ok)
            return { config: mergeRemoteConfig(null), source: "default", raw: {} };
        const body = await res.json();
        const cfg = mergeRemoteConfig(body);
        try {
            storage?.setItem(key, JSON.stringify({ at: now(), cfg: body }));
        }
        catch {
            /* quota */
        }
        return { config: cfg, source: "network", raw: asRaw(body) };
    }
    catch {
        return { config: mergeRemoteConfig(null), source: "default", raw: {} };
    }
    finally {
        clearTimeout(timer);
    }
}
function asRaw(body) {
    return body && typeof body === "object" ? body : {};
}
function safeSessionStorage() {
    try {
        return typeof sessionStorage !== "undefined" ? sessionStorage : null;
    }
    catch {
        return null;
    }
}

const FEATURE_NAMES = {
    recording: "Session recording",
    canvas: "Canvas recording",
    live: "Live Assist",
    remoteControl: "Remote control",
    supportCalling: "Support calling",
    customEvents: "Custom events",
    webrtc: "Call diagnostics",
};
/** Even shorter than limitLabel, so a debug-panel row stays on one line. */
function panelReason(reason) {
    switch (reason) {
        case "not_included":
        case "feature_not_included":
            return "not on this plan";
        case "disabled":
            return "turned off";
        default:
            return limitLabel(reason);
    }
}
/** Short label for the debug panel ("cap reached"). */
function limitLabel(reason) {
    switch (reason) {
        case "not_included":
            return "not included on this plan";
        case "cap_reached":
        case "limit_reached":
            return "cap reached";
        case "payment_overdue":
            return "payment overdue";
        case "trial_ceiling":
            return "trial limit reached";
        case "subscription_ended":
            return "subscription ended";
        case "feature_not_included":
            return "not included on this plan";
        case "disabled":
            return "turned off";
        default:
            return reason ? String(reason).replace(/_/g, " ") : "limited";
    }
}
/**
 * The full warning. `state` is the sessions state for recording
 * ("triggers-only" | "off"); other features are simply off.
 */
function limitMessage(feature, reason, state) {
    const name = FEATURE_NAMES[feature];
    if (feature === "recording" && state === "triggers-only") {
        switch (reason) {
            case "payment_overdue":
                return "Session recording is limited to sessions with errors or triggers while payment is overdue.";
            case "trial_ceiling":
                return "Session recording is limited to sessions with errors or triggers for the rest of the trial.";
            default:
                return "Session recording has reached this month's cap; only sessions with errors or triggers are recorded until it resets.";
        }
    }
    switch (reason) {
        case "not_included":
        case "feature_not_included":
            return `${name} is not included on this plan; it is off.`;
        case "cap_reached":
        case "limit_reached":
            return `${name} has reached this month's cap; it is off until the cap is raised or the month resets.`;
        case "payment_overdue":
            return `${name} is off while payment is overdue.`;
        case "trial_ceiling":
            return `${name} is off for the rest of the trial.`;
        case "subscription_ended":
            return `${name} is off because the subscription has ended.`;
        case "disabled":
            return `${name} is turned off for this organization.`;
        default:
            return `${name} is off for this organization.`;
    }
}
// --- end limits ---

/** Customer events kept per session. Recording of everything else continues. */
const CUSTOM_EVENTS_PER_MINUTE = 300;
const CUSTOM_EVENTS_BURST = 1000;
/**
 * A token bucket. It starts full (the burst) and refills at the per-minute
 * rate. One take is one custom event the page may record.
 */
class CustomEventBudget {
    now;
    perMinute;
    burst;
    tokens;
    /** Unset until the first event, so construction does not need the clock. */
    updated = Number.NaN;
    constructor(now, perMinute = CUSTOM_EVENTS_PER_MINUTE, burst = CUSTOM_EVENTS_BURST) {
        this.now = now;
        this.perMinute = perMinute;
        this.burst = burst;
        this.tokens = burst;
    }
    /** True when this event may be recorded. */
    take() {
        const t = this.now();
        if (!Number.isNaN(this.updated)) {
            const elapsed = Math.max(0, t - this.updated);
            this.tokens = Math.min(this.burst, this.tokens + (elapsed / 60_000) * this.perMinute);
        }
        this.updated = t;
        if (this.tokens < 1)
            return false;
        this.tokens -= 1;
        return true;
    }
}

const CSS$1 = `:host{all:initial}
.p{position:fixed;right:8px;bottom:8px;z-index:2147483647;font:11px/1.35 ui-monospace,Menlo,monospace;color:#e6e8ef;background:rgba(12,14,22,.92);border:1px solid #2a2f45;border-radius:8px;padding:8px 10px;min-width:220px;max-width:320px;box-shadow:0 6px 24px rgba(0,0,0,.4);pointer-events:auto}
.h{display:flex;justify-content:space-between;align-items:center;margin-bottom:6px;font-weight:600}
.h b{color:#8ab4ff}.tools{display:flex;align-items:center;gap:4px}.h span{padding:1px 6px;border-radius:999px;background:#1f2a44}
.tools button{appearance:none;border:1px solid #343b55;border-radius:4px;background:#151a29;color:#b7c0d4;font:12px/1 ui-monospace,Menlo,monospace;width:22px;height:20px;padding:0;cursor:pointer}.tools button:hover,.tools button:focus-visible{border-color:#8ab4ff;color:#fff;outline:none}
.l-HEALTHY{background:#123d2a!important;color:#7ee2b0}.l-CONSTRAINED{background:#3d3512!important;color:#f0d36e}.l-DEGRADED{background:#4a2a12!important;color:#f5a76e}.l-SURVIVAL{background:#4a1616!important;color:#ff8f8f}.l-PAUSED{background:#333!important;color:#bbb}
table{border-collapse:collapse;width:100%}td{padding:1px 0;vertical-align:top}td:first-child{color:#9aa3b5;padding-right:8px;white-space:nowrap}
.c{margin-top:6px;border-top:1px solid #2a2f45;padding-top:4px;color:#9aa3b5}.c div{color:#e6e8ef}
.health{margin:5px 0 7px;padding:5px 6px;border-radius:5px;background:#151a29;color:#cbd3e3}.health.warn{border-left:3px solid #f5a76e}.health.ok{border-left:3px solid #7ee2b0}
.collapsed .details{display:none}.collapsed{min-width:180px}
.k{margin-top:6px;color:#6f7890;font-size:10px}`;
const kb = (n) => (n / 1024).toFixed(1) + " KB";
/**
 * Debug overlay (TASKS T0.3.13): mounted in a closed shadow root so host CSS
 * cannot touch it. Toggle with Ctrl+Shift+R. Vanilla DOM, no dependencies.
 */
class DebugPanel {
    o;
    host = null;
    root = null;
    body = null;
    timer = null;
    visible = true;
    collapsed = false;
    onKey = (e) => {
        if (e.ctrlKey && e.shiftKey && (e.key === "R" || e.key === "r")) {
            e.preventDefault();
            this.toggle();
        }
    };
    constructor(o) {
        this.o = o;
    }
    mount() {
        const doc = this.o.doc ?? document;
        const win = this.o.win ?? window;
        if (this.host || !doc.body)
            return;
        this.host = doc.createElement("div");
        this.host.setAttribute("data-backstory-block", ""); // never record ourselves
        this.host.setAttribute("aria-label", "Backstory recording diagnostics");
        this.root = this.host.attachShadow({ mode: "closed" });
        const style = doc.createElement("style");
        style.textContent = CSS$1;
        this.body = doc.createElement("div");
        this.body.className = "p";
        this.root.append(style, this.body);
        doc.body.appendChild(this.host);
        win.addEventListener("keydown", this.onKey, true);
        this.render();
        this.timer = win.setInterval(() => this.render(), 1000);
    }
    toggle() {
        this.visible = !this.visible;
        if (this.body)
            this.body.style.display = this.visible ? "" : "none";
    }
    render() {
        if (!this.body || !this.visible)
            return;
        const s = this.o.getState();
        const canvases = this.o.getCanvases?.() ?? [];
        const calls = this.o.getWebRtc?.() ?? [];
        const extra = this.o.extra?.() ?? {};
        const activeCanvases = canvases.filter((c) => c.recordable).length;
        const rows = s
            ? [
                ["session", s.sessionId.slice(0, 12) + "…"],
                ["visit", s.visitId.slice(0, 12) + "…"],
                ["seq / acked", `${s.seq} / ${s.acked}`],
                ["queued", kb(s.queuedBytes)],
                ["in flight", kb(s.inFlightBytes)],
                ["credits", kb(s.credits)],
                ["goodput", kb(s.goodputBps) + "/s"],
                ["dropped", kb(s.droppedBytes)],
                ["gaps", String(s.gaps)],
                ["spill", `${kb(s.spillBytes)} / ${kb(s.spillBudgetBytes)} (${s.spillChunks})`],
                ["queue drain", s.drainSeconds !== undefined ? (s.drainSeconds < 0.1 ? "<0.1 s" : `${s.drainSeconds.toFixed(1)} s`) : "—"],
                ["queue pressure", s.memoryPressure !== undefined ? `${(s.memoryPressure * 100).toFixed(1)}%` : "—"],
                ["clock offset", Math.round(s.clockOffsetMs) + " ms"],
                ["self jank", (s.selfLongTaskShare * 100).toFixed(1) + "%"],
                ["online", s.online ? "yes" : "no"],
                ...(s.forcedLevel ? [["forced", s.forcedLevel]] : []),
            ]
            : [["state", "waiting for worker…"]];
        for (const [k, v] of Object.entries(extra))
            rows.push([k, String(v)]);
        const esc = (x) => x.replace(/[<>&]/g, (c) => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;" })[c]);
        const reason = s?.healthReason ?? (s ? "Capture state is available" : "Waiting for the recording worker");
        const healthy = s?.level === "HEALTHY";
        const canvasReason = (reason) => {
            if (reason === "offscreen")
                return "waiting: off-screen";
            if (reason === "not-allowlisted")
                return "not selected for capture";
            if (reason === "hidden")
                return "waiting: hidden";
            if (reason === "zero-size")
                return "waiting: zero size";
            if (reason === "detached")
                return "not attached to the page";
            return reason;
        };
        this.body.innerHTML =
            `<div class="p-inner${this.collapsed ? " collapsed" : ""}"><div class="h"><b>Backstory</b><div class="tools"><span class="l-${s?.level ?? "PAUSED"}">${esc(s?.level ?? "—")}</span><button type="button" data-collapse aria-label="${this.collapsed ? "Expand diagnostics" : "Collapse diagnostics"}" title="${this.collapsed ? "Expand" : "Collapse"}">${this.collapsed ? "+" : "−"}</button><button type="button" data-close aria-label="Hide diagnostics" title="Hide">×</button></div></div>` +
                `<div class="details"><div class="health ${healthy ? "ok" : "warn"}">${esc(reason)}${s?.targetLevel && s.targetLevel !== s.level ? ` · current ${esc(s.level)}, target ${esc(s.targetLevel)}` : ""}</div>` +
                `<table>${rows.map(([k, v]) => `<tr><td>${esc(k)}</td><td>${esc(v)}</td></tr>`).join("")}</table>` +
                (canvases.length
                    ? `<div class="c">canvas (${activeCanvases} active / ${canvases.length} found)${canvases.map((c) => `<div>#${c.id} ${esc(c.tag.replace("[unknown]", ""))} · ${c.recordable ? esc(c.strategy) : esc(canvasReason(c.reason))}</div>`).join("")}</div>`
                    : "") +
                (calls.length
                    ? `<div class="c">calls (${calls.length})${calls
                        .map((c) => `<div>${esc(c.pc)} ${esc(c.state)} · ${c.tracks} track${c.tracks === 1 ? "" : "s"}${c.relayed ? " · relay" : ""} · ${c.rttMs !== undefined ? Math.round(c.rttMs) + "ms" : "no RTT"} · ${c.lossPct !== undefined ? c.lossPct.toFixed(1) + "% loss" : "no loss sample"}${c.fps !== undefined ? ` · ${Math.round(c.fps)} fps` : ""}${c.quality !== undefined ? " · q" + c.quality : ""}${c.sampled ? "" : " · quality not sampled"}</div>`)
                        .join("")}</div>`
                    : "") +
                `<div class="k">Ctrl+Shift+R to show or hide</div></div></div>`;
        this.body.querySelector("[data-collapse]")?.addEventListener("click", () => {
            this.collapsed = !this.collapsed;
            this.render();
        });
        this.body.querySelector("[data-close]")?.addEventListener("click", () => this.toggle());
    }
    unmount() {
        const win = this.o.win ?? window;
        if (this.timer)
            win.clearInterval(this.timer);
        win.removeEventListener("keydown", this.onKey, true);
        this.host?.remove();
        this.host = this.root = this.body = null;
    }
}

// Static module loading: used by the ESM build and tests. The bundler inlines or
// code-splits these imports as it sees fit.
function loadState() {
    return Promise.resolve().then(function () { return index$1; });
}
function loadOtel() {
    return Promise.resolve().then(function () { return index; });
}
function loadCanvas() {
    return Promise.resolve().then(function () { return canvasModule; });
}
function loadLive() {
    return Promise.resolve().then(function () { return liveModule; });
}
function loadWebRtc() {
    return Promise.resolve().then(function () { return webrtcModule; });
}
let staticBase;
function setModulesUrl(url) {
    staticBase = url ? url.replace(/\/$/, "") + "/" : undefined;
}
/** Base URL for assets that cannot be bundled (zstd.wasm); undefined when not configured. */
function modulesBaseUrl() {
    return staticBase;
}

const MONITOR_DEFAULTS = { preRollMs: 60_000, preRollBytes: 2 * 1024 * 1024, burstMs: 60_000 };

/** Monitor mode checkpoints every 30 minutes (PLAN §5.6). */
const MONITOR_CHECKPOINT_MINUTES = 30;
// --- end transport & monitor ---
const SDK_VERSION = "0.1.0";
/** Route-change churn (adds+removes vs. known nodes) above this triggers a checkpoint (PLAN §5.4). */
const ROUTE_CHURN_THRESHOLD = 0.6;
const ROUTE_CHURN_WINDOW_MS = 2000;
/** How long an old worker may keep uploading after a segment rollover before we drain it. */
const HANDOVER_MAX_MS = 10_000;
const BUSY_WINDOW_MS = 30_000;
class Recorder {
    cfg;
    doc;
    win;
    host = null;
    map = new NodeMap();
    ctx;
    mutations = null;
    interactions = null;
    errors = null;
    crosstab = null;
    visitId = "";
    /** One UUID for every ingest call from this page. */
    requestId = guid();
    tabId = 0;
    sessionId = "";
    segmentIndex = 0;
    checkpointId = 0;
    mode = "normal";
    endUserHash;
    endUserName;
    level = "HEALTHY";
    policy = LEVEL_POLICY.HEALTHY;
    lastDebug = null;
    lastTouch = 0;
    sessionOpen = false;
    timers = [];
    inactivityTimer = null;
    stopped = false;
    consented;
    off = [];
    snapshotting = false;
    remote = null;
    remoteSource = "default";
    // --- limits ---
    /** What the organization may record right now (plan, caps, account state). */
    limits = { ...DEFAULT_SDK_LIMITS };
    /** False until the first remote config answer. Gated collectors stay off so a Free plan never samples. */
    limitsSeen = false;
    customEvents = new CustomEventBudget(() => this.now());
    /** Set while recording is held back by a limit; the timer is the next re-check. */
    paused = null;
    /** Warnings already written this page load (feature + reason). */
    warned = new Set();
    // --- end limits ---
    initialLevel = "HEALTHY";
    panel = null;
    starting = false;
    // Effective knobs (config, low-end adjustments, remote config).
    memoryBudgetMB;
    pointerHz;
    checkpointMinutes;
    segmentHours;
    canvasEnabled = false;
    cpuState;
    // --- webrtc ---
    webrtcEnabled = false;
    /** WebRTC collector reports per-connection quality here for the debug panel. */
    getWebRtc = () => [];
    /** Network collector reports aggregate application socket activity here. */
    getWebSockets = () => ({ active: 0, connections: 0, sent: 0, received: 0 });
    // --- end webrtc ---
    lowEnd = false;
    // Route churn detection.
    churn = null;
    // Self-protection: our busy intervals on the main thread for long-task attribution.
    busy = [];
    longTaskObserver = null;
    // Warm-start state carried across worker recycles.
    handoverState = {};
    // --- transport & monitor ---
    monitorAfterMinutes = 0;
    transportKind = "-";
    // --- end transport & monitor ---
    /** Phase 4 hook: canvas collector reports its per-canvas state here for the debug panel. */
    getCanvases = () => [];
    constructor(cfg, doc = document, win = window) {
        this.cfg = cfg;
        this.doc = doc;
        this.win = win;
        this.consented = !cfg.consent.required;
        this.endUserHash = cfg.endUserId;
        this.endUserName = cfg.endUserName;
        this.memoryBudgetMB = cfg.recording.memoryBudgetMB;
        this.pointerHz = cfg.recording.pointerHz;
        this.checkpointMinutes = cfg.recording.checkpointMinutes;
        this.segmentHours = cfg.recording.segmentHours;
        this.monitorAfterMinutes = cfg.recording.monitorModeAfterMinutes;
        // These fields also feed developer diagnostics. Start with the effective
        // local config; remote config may enable either feature later.
        this.canvasEnabled = cfg.canvas.enabled;
        this.webrtcEnabled = cfg.webrtc.enabled;
        this.applyDeviceClass();
    }
    now = () => Date.now();
    /** Low-end device budgets (PLAN §7.9, TASKS T1.14) and connection hints. */
    applyDeviceClass() {
        const nav = this.win.navigator;
        const mem = nav.deviceMemory;
        const cores = nav.hardwareConcurrency;
        if ((typeof mem === "number" && mem <= 2) || (typeof cores === "number" && cores <= 2)) {
            this.lowEnd = true;
            this.memoryBudgetMB = Math.min(this.memoryBudgetMB, 8);
            this.pointerHz = Math.min(this.pointerHz, 8);
            this.checkpointMinutes = Math.max(this.checkpointMinutes, 10);
        }
        if (nav.connection?.saveData)
            this.initialLevel = "CONSTRAINED";
    }
    emit = (e) => {
        if (!this.host || !this.sessionOpen || this.stopped)
            return;
        // Plan-gated features never enter the upload queue. Stopping the collector
        // is not enough: a sample already in hand would still be sent.
        if (this.blockedByPlan(e))
            return;
        if (e.type === "navigation" && e.kind !== "load")
            this.beginChurnWindow();
        else if (e.type === "mutation" && this.churn)
            this.trackChurn(e.adds?.length ?? 0, e.removes?.length ?? 0);
        this.host.post({ type: "event", e });
    };
    // ---------------------------------------------------------------------------
    // Startup
    // ---------------------------------------------------------------------------
    start() {
        // A hard stop lasts for this page load. A new page reads config again.
        if (this.stopped || this.starting || this.host || this.paused)
            return;
        if (!this.consented)
            return; // wait for consent.grant()
        this.starting = true;
        void this.boot();
    }
    async boot() {
        // 1. Remote config (≤ 300 ms, cached 5 min, defaults on failure).
        const { config, source, raw } = await fetchRemoteConfig(this.cfg.apiUrl ?? deriveApiUrl(this.cfg.ingestUrl), this.cfg.projectKey, SDK_VERSION, { timeoutMs: 300 });
        // Consent can be withdrawn while boot awaits (Global Privacy Control is
        // evaluated right after start() is called). Without this check the worker
        // still spawned and the DOM was recorded with no collectors attached: a
        // session that looked healthy but never had a network or console panel.
        if (this.stopped || !this.consented) {
            this.starting = false;
            return;
        }
        this.remote = config;
        this.remoteSource = source;
        // --- limits ---
        // Limits first: a stopped feature is warned about once and never sends
        // anything, whether the answer came from the network or the cache.
        const limits = parseLimits(raw.limits) ?? { ...DEFAULT_SDK_LIMITS };
        this.applyLimits(limits, false);
        if (limits.sessions === "off") {
            this.starting = false;
            this.hold(limits.sessionsReason ?? "cap_reached");
            return;
        }
        // --- end limits ---
        if (config.recording === "off") {
            this.starting = false;
            if (this.cfg.debug)
                console.info("[backstory] recording disabled by remote config");
            return;
        }
        this.applyRemote(config, raw);
        // 2. Cross-tab visit id and tab index.
        this.crosstab = new CrossTab({ projectKey: this.cfg.projectKey, inactivityMs: this.cfg.recording.inactivityMinutes * 60_000 });
        const ct = await this.crosstab.start();
        if (this.stopped || !this.consented) {
            this.crosstab.stop();
            this.crosstab = null;
            this.starting = false;
            return;
        }
        this.visitId = ct.visitId;
        this.tabId = ct.tabId;
        // 3. Worker and first session.
        this.host = this.spawnHost();
        this.initHost(this.host);
        this.ctx = { map: this.map, rules: this.cfg.privacy, doc: this.doc };
        this.beginSession(0);
        this.attachLifecycle();
        this.observeLongTasks();
        if (this.cfg.debug)
            this.mountPanel();
        this.starting = false;
    }
    pipelineConfig() {
        return {
            ingestUrl: this.cfg.ingestUrl,
            projectKey: this.cfg.projectKey,
            sdkVersion: SDK_VERSION,
            requestId: this.requestId,
            batchMs: this.cfg.recording.batchMs,
            batchBytes: this.cfg.recording.batchBytes,
            memoryBudgetBytes: this.memoryBudgetMB * 1024 * 1024,
            debug: this.cfg.debug,
            initialLevel: this.initialLevel,
            clockOffsetMs: this.handoverState.clockOffsetMs,
            credits: this.handoverState.credits,
            spill: this.cfg.spill,
            // --- transport & monitor ---
            transport: this.cfg.transport.transport,
            wsUrl: this.cfg.transport.wsUrl,
            wtUrl: this.cfg.transport.wtUrl,
            compression: this.cfg.transport.compression,
            zstdJsUrl: this.assetUrl("backstory-zstd.js"),
            zstdWasmUrl: this.assetUrl("zstd.wasm"),
            wtJsUrl: this.assetUrl("backstory-wt.js"),
            wsJsUrl: this.assetUrl("backstory-ws.js"),
            monitorJsUrl: this.assetUrl("backstory-monitor.js"),
            preloadMonitor: this.monitorAfterMinutes > 0 || this.mode === "monitor",
            dictUrl: this.cfg.transport.dictUrl,
            monitor: { triggers: ["error", "network_failure", "offline", "long_task", ...this.cfg.recording.monitorTriggers, ...this.remoteMonitorTriggers()], ...MONITOR_DEFAULTS },
            initialMode: this.mode,
            burstUntil: this.handoverState.burstUntil,
            // --- end transport & monitor ---
        };
    }
    // --- transport & monitor ---
    /** Absolute URL of a dist asset (worker Blob URLs cannot resolve relative paths). */
    assetUrl(name) {
        const base = this.cfg.modulesUrl ? this.cfg.modulesUrl.replace(/\/$/, "") + "/" : modulesBaseUrl();
        if (!base)
            return undefined;
        try {
            return new URL(name, /^https?:/.test(base) ? base : new URL(base, this.win.location.href).toString()).toString();
        }
        catch {
            return undefined;
        }
    }
    /** `custom:<name>` triggers from the remote sampling policy also start monitor bursts. */
    remoteMonitorTriggers() {
        const t = this.remote?.sampling?.triggers ?? [];
        return t.filter((x) => typeof x === "string" && x.startsWith("custom:"));
    }
    // --- end transport & monitor ---
    spawnHost() {
        const host = this.cfg.worker ? createHost(this.cfg.workerUrl, this.assetUrl("worker.js")) : createInlineHost();
        host.onMessage((m) => this.onMessage(m, host));
        return host;
    }
    /** Must run after `this.host` is assigned: the inline host delivers replies synchronously. */
    initHost(host) {
        host.post({ type: "init", cfg: this.pipelineConfig() });
        if (this.remote?.forceLevel)
            host.post({ type: "force-level", level: this.remote.forceLevel });
    }
    /** Server-provided keys override local config; defaults filled in by the merge do not. */
    applyRemote(c, raw) {
        if (raw.memoryBudgetMB !== undefined)
            this.memoryBudgetMB = Math.min(this.memoryBudgetMB, c.memoryBudgetMB);
        if (raw.segmentHours !== undefined)
            this.segmentHours = c.segmentHours;
        if (raw.checkpointMinutes !== undefined)
            this.checkpointMinutes = this.lowEnd ? Math.max(c.checkpointMinutes, 10) : c.checkpointMinutes;
        if (raw.canvas !== undefined)
            this.canvasEnabled = c.canvas;
        if (raw.privacy && typeof raw.privacy === "object" && raw.privacy.mode !== undefined) {
            this.cfg.privacy.mode = c.privacy.mode;
            if (c.privacy.mode === "strict")
                this.cfg.network.captureWebSocketMessages = false;
        }
        // --- webrtc: remote config may enable call observability per project or cohort.
        const rc = raw;
        if (rc.collectors && typeof rc.collectors.webrtc === "boolean")
            this.webrtcEnabled = rc.collectors.webrtc;
        if (c.flags && typeof c.flags.webrtc === "boolean")
            this.webrtcEnabled = c.flags.webrtc;
        if (c.forceLevel)
            this.initialLevel = c.forceLevel;
        if (c.monitorModeAfterMinutes > 0)
            this.monitorAfterMinutes = c.monitorModeAfterMinutes;
        if (this.monitorAfterMinutes > 0)
            this.armMonitorMode(this.monitorAfterMinutes);
    }
    /** Nudges arriving in IngestResponse.config (subset applied live). */
    applyNudge(remote) {
        // --- limits ---
        const limits = parseLimits(remote.limits);
        if (limits) {
            this.applyLimits(limits, true);
            if (limits.sessions === "off")
                return;
        }
        // --- end limits ---
        if (remote.recording === "off")
            return this.stop();
        if ("forceLevel" in remote)
            this.host?.post({ type: "force-level", level: remote.forceLevel ?? null });
        if (typeof remote.canvas === "boolean")
            this.canvasEnabled = remote.canvas;
        const rcol = remote.collectors;
        if (rcol && typeof rcol.webrtc === "boolean")
            this.webrtcEnabled = rcol.webrtc;
        if (this.remote && remote.flags && typeof remote.flags === "object")
            this.remote.flags = { ...this.remote.flags, ...remote.flags };
        this.notifyNudge();
    }
    nudgeSubs = new Set();
    /** Subscribe to server config nudges; live boot uses it to watch the flags, canvas and sampling watch the limits. */
    onConfigNudge(fn) {
        this.nudgeSubs.add(fn);
        return () => this.nudgeSubs.delete(fn);
    }
    notifyNudge() {
        for (const fn of this.nudgeSubs)
            fn();
    }
    // ---------------------------------------------------------------------------
    // Organization limits (plan features, caps, account state)
    // ---------------------------------------------------------------------------
    // --- limits ---
    /** Current limits (defaults until the first config answer). */
    getLimits() {
        return this.limits;
    }
    /** True after remote config has been applied. Plan-gated collectors wait for this. */
    limitsReady() {
        return this.limitsSeen;
    }
    /**
     * Events the plan does not include. Internal diagnostics (`backstory.*`)
     * stay, except call triggers, which exist only for call quality.
     */
    blockedByPlan(e) {
        switch (e.type) {
            case "canvas_frame":
            case "canvas_cmds":
                return !this.limits.canvas.allowed;
            case "webrtc":
            case "webrtc_stats":
                return !this.limits.webrtc.allowed;
            case "assist":
                return !this.limits.live.allowed;
            case "custom": {
                if (e.name === "backstory.webrtc_trigger")
                    return !this.limits.webrtc.allowed;
                if (e.name.startsWith("backstory."))
                    return false;
                return !!this.limits.customEvents && !this.limits.customEvents.allowed;
            }
            default:
                return false;
        }
    }
    /** True after a hard stop. Recording stays off until the page loads again. */
    isHeld() {
        return this.paused !== null;
    }
    /** Write the one-line warning for a stopped feature, once per page load and reason. */
    noteLimit(feature, reason, state) {
        const key = feature + ":" + (state ?? "") + ":" + (reason ?? "");
        if (this.warned.has(key))
            return;
        this.warned.add(key);
        console.warn("[backstory] " + limitMessage(feature, reason, state));
    }
    /**
     * Adopt a new set of limits. `live` means it arrived while recording (an
     * ack nudge), so a session cap takes effect immediately.
     */
    applyLimits(next, live) {
        this.limits = next;
        this.limitsSeen = true;
        if (this.host && (!next.canvas.allowed || !next.webrtc.allowed || !next.live.allowed || (next.customEvents && !next.customEvents.allowed))) {
            this.host.post({
                type: "drop-gated",
                canvas: !next.canvas.allowed,
                webrtc: !next.webrtc.allowed,
                live: !next.live.allowed,
                custom: !!next.customEvents && !next.customEvents.allowed,
            });
        }
        // Canvas is warned about when this page asked for it (snippet or project
        // switch); Live Assist warns when someone actually tries to use it
        // (live-boot). Recording always says why it is held back.
        if (!next.canvas.allowed && (this.cfg.canvas.enabled || this.canvasEnabled))
            this.noteLimit("canvas", next.canvas.reason);
        if (next.customEvents && !next.customEvents.allowed)
            this.noteLimit("customEvents", next.customEvents.reason);
        if (next.webrtc && !next.webrtc.allowed && (this.cfg.webrtc.enabled || this.webrtcEnabled))
            this.noteLimit("webrtc", next.webrtc.reason);
        if (next.sessions !== "ok")
            this.noteLimit("recording", next.sessionsReason, next.sessions);
        if (live && next.sessions === "off")
            this.hold(next.sessionsReason ?? "cap_reached");
        this.notifyNudge();
    }
    /**
     * Stop recording for the rest of this page load. Collectors detach and the
     * worker is dropped with whatever it had buffered, so nothing more is sent.
     * The next page load reads config once and starts again if the limit has lifted.
     */
    hold(reason) {
        if (this.stopped)
            return;
        if (this.paused) {
            this.paused.reason = reason;
            return;
        }
        this.sessionOpen = false;
        const h = this.host;
        this.teardown();
        h?.terminate();
        this.paused = { reason };
        if (this.cfg.debug)
            console.info("[backstory] recording stopped:", reason);
    }
    /** Detach everything attached since boot. Callers own the worker handle. */
    teardown() {
        for (const f of this.off)
            f();
        this.off = [];
        this.stopCollectors();
        this.stopListeningForReturn();
        if (this.inactivityTimer)
            this.win.clearTimeout(this.inactivityTimer);
        if (this.monitorTimer)
            this.win.clearTimeout(this.monitorTimer);
        this.inactivityTimer = this.monitorTimer = null;
        this.longTaskObserver?.disconnect();
        this.longTaskObserver = null;
        this.crosstab?.stop();
        this.crosstab = null;
        this.panel?.unmount();
        this.panel = null;
        this.host = null;
    }
    // --- end limits ---
    monitorTimer = null;
    /** Idle timer: no interaction for `afterMinutes` → monitor mode (PLAN §5.6). Re-armed on every exit. */
    armMonitorMode(afterMinutes) {
        if (this.monitorTimer)
            this.win.clearTimeout(this.monitorTimer);
        if (afterMinutes <= 0)
            return;
        this.monitorTimer = this.win.setTimeout(() => {
            this.monitorTimer = null;
            if (this.mode === "normal" && this.sessionOpen && !this.stopped)
                this.setMode("monitor");
        }, afterMinutes * 60_000);
    }
    // ---------------------------------------------------------------------------
    // Session meta and lifecycle
    // ---------------------------------------------------------------------------
    meta() {
        const nav = this.win.navigator;
        const c = nav.connection;
        const m = {
            url: this.win.location.href,
            referrer: this.doc.referrer || undefined,
            userAgent: nav.userAgent,
            language: nav.language,
            timezone: (() => {
                try {
                    return Intl.DateTimeFormat().resolvedOptions().timeZone;
                }
                catch {
                    return undefined;
                }
            })(),
            viewport: { w: this.win.innerWidth, h: this.win.innerHeight, dpr: this.win.devicePixelRatio || 1 },
            privacyMode: this.cfg.privacy.mode,
            recordingMode: this.mode,
            sdkVersion: SDK_VERSION,
            release: this.cfg.release,
            environment: this.cfg.environment,
            endUserId: this.endUserHash,
            endUserName: this.endUserName,
            deviceMemoryGB: nav.deviceMemory,
            connection: c ? { effectiveType: c.effectiveType, downlinkMbps: c.downlink, saveData: !!c.saveData } : undefined,
            tabId: this.tabId,
        };
        return m;
    }
    /**
     * Surface state (PLAN §5.2): whether this browsing context is on screen and
     * whether it holds window focus. The recorder owns it rather than the
     * interaction collector so it survives collector restarts, and re-emits it on
     * every segment so playback can read a segment's state without replaying the
     * ones before it.
     */
    surfaceVisible = true;
    surfaceFocused = true;
    readSurfaceState() {
        const visible = this.doc.visibilityState !== "hidden";
        let focused = visible;
        try {
            if (typeof this.doc.hasFocus === "function")
                focused = this.doc.hasFocus();
        }
        catch {
            /* jsdom and sandboxed frames may throw */
        }
        return { state: visible ? "visible" : "hidden", focused: visible && focused };
    }
    /** `force` re-emits an unchanged state, which is what a new segment needs. */
    emitSurfaceState(force = false) {
        if (!this.sessionOpen || this.stopped)
            return;
        const s = this.readSurfaceState();
        const visible = s.state === "visible";
        if (!force && visible === this.surfaceVisible && s.focused === this.surfaceFocused)
            return;
        this.surfaceVisible = visible;
        this.surfaceFocused = s.focused;
        this.emit({ type: "visibility", t: this.now(), state: s.state, focused: s.focused });
    }
    /** Start a new segment (session id) and take a full snapshot. */
    beginSession(segmentIndex) {
        this.stopListeningForReturn();
        this.sessionId = newId();
        this.segmentIndex = segmentIndex;
        this.sessionOpen = true;
        this.host.post({ type: "session", sessionId: this.sessionId, visitId: this.visitId, segmentIndex, meta: this.meta() });
        this.stopCollectors();
        this.map.reset();
        this.checkpointId = 0;
        void this.takeSnapshot("manual").then(() => {
            // After the snapshot, so a segment's first chunk still opens with it.
            this.emitSurfaceState(true);
            this.startCollectors();
        });
        this.armInactivity();
    }
    async takeSnapshot(reason) {
        if (this.snapshotting)
            return;
        this.snapshotting = true;
        try {
            const id = ++this.checkpointId;
            const node = await snapshotDocument(this.ctx, { onWork: (ms) => this.recordBusy(ms) });
            if (!this.sessionOpen)
                return;
            const t = this.now();
            this.emit({ type: "control", kind: "checkpoint", t, checkpointId: id, reason });
            this.emit({
                type: "snapshot",
                t,
                checkpointId: id,
                node,
                url: this.win.location.href,
                title: this.doc.title,
                viewport: { w: this.win.innerWidth, h: this.win.innerHeight, dpr: this.win.devicePixelRatio || 1 },
                scroll: { x: this.win.scrollX || 0, y: this.win.scrollY || 0 },
            });
        }
        finally {
            this.snapshotting = false;
        }
    }
    startCollectors() {
        if (!this.sessionOpen || this.stopped)
            return;
        this.mutations = new MutationCollector({ ctx: this.ctx, emit: this.emit, now: this.now, onWork: (ms) => this.recordBusy(ms) });
        this.mutations.coalesceMs = this.policy.attrCoalesceMs;
        this.mutations.observe(this.doc);
        this.interactions = new InteractionCollector({
            map: this.map,
            rules: this.cfg.privacy,
            emit: this.emit,
            now: this.now,
            doc: this.doc,
            win: this.win,
            pointerHz: this.mode === "monitor" ? 0 : Math.min(this.policy.pointerHz, this.pointerHz),
            scrollHz: this.mode === "monitor" ? 0 : this.policy.scrollHz,
            onActivity: () => this.activity(),
        });
        this.interactions.start();
        this.errors = new ErrorCollector(this.win, this.emit, this.now);
        this.errors.start();
        this.armTimers();
    }
    /** Periodic checkpoints (scaled by level policy) and segment rollover. */
    armTimers() {
        for (const t of this.timers)
            this.win.clearTimeout(t);
        this.timers = [];
        const mult = this.policy.checkpointIntervalMultiplier;
        const minutes = this.mode === "monitor" ? MONITOR_CHECKPOINT_MINUTES : this.checkpointMinutes;
        if (mult > 0)
            this.timers.push(this.win.setInterval(() => void this.checkpoint("interval"), minutes * 60_000 * mult));
        const elapsed = this.now() - this.segmentStartedAt;
        this.timers.push(this.win.setTimeout(() => this.rollover(), Math.max(1000, this.segmentHours * 3_600_000 - elapsed)));
    }
    segmentStartedAt = 0;
    stopCollectors() {
        this.mutations?.stop();
        this.interactions?.stop();
        this.errors?.stop();
        this.mutations = this.interactions = this.errors = null;
        for (const t of this.timers)
            this.win.clearTimeout(t);
        this.timers = [];
        this.churn = null;
        this.segmentStartedAt = this.now();
    }
    activity() {
        const t = this.now();
        if (t - this.lastTouch > 5000) {
            this.lastTouch = t;
            this.crosstab?.touch();
        }
        if (!this.sessionOpen && !this.stopped && this.host) {
            // Inactivity ended the previous session: a new segment starts with the activity.
            this.beginSession(this.segmentIndex + 1);
            return;
        }
        // --- transport & monitor ---
        // Any interaction returns to normal mode immediately (PLAN §5.6).
        if (this.mode === "monitor") {
            this.setMode("normal");
            return;
        }
        if (this.monitorAfterMinutes > 0)
            this.armMonitorMode(this.monitorAfterMinutes);
        // --- end transport & monitor ---
        this.armInactivity();
    }
    armInactivity() {
        if (this.inactivityTimer)
            this.win.clearTimeout(this.inactivityTimer);
        if (this.mode === "monitor")
            return;
        this.inactivityTimer = this.win.setTimeout(() => this.endSession("inactivity"), this.cfg.recording.inactivityMinutes * 60_000);
    }
    endSession(reason) {
        if (!this.sessionOpen)
            return;
        this.emit({ type: "control", kind: "end", t: this.now(), reason });
        this.sessionOpen = false;
        this.stopCollectors();
        this.host?.post({ type: "flush" });
        // The collectors were the only thing that noticed a click. After a quiet
        // stretch, keep listening so the next action opens a new session.
        if (reason === "inactivity")
            this.listenForReturn();
    }
    wakeOff = [];
    listenForReturn() {
        this.stopListeningForReturn();
        const wake = () => this.activity();
        const listen = (target, type) => {
            target.addEventListener(type, wake, true);
            this.wakeOff.push(() => target.removeEventListener(type, wake, true));
        };
        listen(this.doc, "pointerdown");
        listen(this.doc, "keydown");
        listen(this.win, "touchstart");
    }
    stopListeningForReturn() {
        for (const off of this.wakeOff)
            off();
        this.wakeOff = [];
    }
    /**
     * Segment rollover (PLAN §5.6, TASKS T0.3.12): the old worker keeps
     * uploading what it has while a fresh worker starts the next segment
     * immediately, so the boundary is invisible. Un-acked chunks of the old
     * worker are also in IndexedDB spill, so a slow drain never loses data.
     */
    rollover() {
        if (!this.sessionOpen || !this.host)
            return;
        const next = newId();
        this.emit({ type: "control", kind: "segment_rollover", t: this.now(), nextSegmentIndex: this.segmentIndex + 1, nextSessionId: next });
        this.stopCollectors();
        const old = this.host;
        old.post({ type: "flush" });
        old.post({ type: "handover" });
        this.retireHost(old);
        // New worker, warm-started with the last known clock offset and credits.
        this.host = this.spawnHost();
        this.initHost(this.host);
        this.sessionId = next;
        this.segmentIndex += 1;
        this.sessionOpen = true;
        this.host.post({ type: "session", sessionId: this.sessionId, visitId: this.visitId, segmentIndex: this.segmentIndex, meta: this.meta() });
        this.map.reset();
        this.checkpointId = 0;
        void this.takeSnapshot("segment").then(() => {
            this.emitSurfaceState(true);
            this.startCollectors();
        });
        this.armInactivity();
    }
    /** Let a retired worker finish uploading, then terminate it (drain to keepalive as a last resort). */
    retireHost(old) {
        const started = this.now();
        let done = false;
        const finish = () => {
            if (done)
                return;
            done = true;
            old.terminate();
        };
        old.onMessage((m) => {
            if (m.type === "handover-state") {
                this.handoverState = { clockOffsetMs: m.clockOffsetMs, credits: m.credits, burstUntil: m.burstUntil };
                if (m.pending === 0)
                    finish();
            }
            if (m.type === "drained") {
                this.sendKeepalive(m.chunks);
                finish();
            }
        });
        const poll = () => {
            if (done)
                return;
            if (this.now() - started > HANDOVER_MAX_MS) {
                old.post({ type: "drain" });
                this.win.setTimeout(finish, 1000);
                return;
            }
            old.post({ type: "handover" });
            this.win.setTimeout(poll, 1000);
        };
        this.win.setTimeout(poll, 1000);
    }
    async checkpoint(reason = "manual") {
        if (!this.sessionOpen)
            return;
        // Keep observing; the new snapshot reuses the existing stable node ids.
        this.mutations?.flush();
        await this.takeSnapshot(reason);
    }
    // ---------------------------------------------------------------------------
    // Route churn → checkpoint (PLAN §5.4)
    // ---------------------------------------------------------------------------
    beginChurnWindow() {
        const churn = { until: this.now() + ROUTE_CHURN_WINDOW_MS, changes: 0, baseline: Math.max(1, this.map.size) };
        this.churn = churn;
        // Every SPA route needs its own full checkpoint for replay and heatmap
        // backgrounds. Heavy DOM churn can trigger this earlier; quiet route
        // changes still checkpoint once the new view has had time to settle.
        this.timers.push(this.win.setTimeout(() => {
            if (this.churn !== churn)
                return;
            this.churn = null;
            void this.checkpoint("route");
        }, ROUTE_CHURN_WINDOW_MS));
    }
    trackChurn(adds, removes) {
        const c = this.churn;
        if (!c)
            return;
        if (this.now() > c.until) {
            this.churn = null;
            return;
        }
        c.changes += adds + removes;
        if (c.changes / c.baseline > ROUTE_CHURN_THRESHOLD) {
            this.churn = null;
            void this.checkpoint("route");
        }
    }
    // ---------------------------------------------------------------------------
    // Self-protection: attribute main-thread long tasks to our own busy intervals
    // ---------------------------------------------------------------------------
    recordBusy(ms) {
        if (ms <= 0)
            return;
        const e = this.now();
        this.busy.push({ s: e - ms, e });
        const cutoff = e - BUSY_WINDOW_MS;
        while (this.busy.length && this.busy[0].e < cutoff)
            this.busy.shift();
    }
    observeLongTasks() {
        const PO = this.win.PerformanceObserver;
        if (!PO || !PO.supportedEntryTypes?.includes("longtask"))
            return;
        try {
            const origin = (this.win.performance?.timeOrigin ?? 0) || Date.now() - this.win.performance.now();
            this.longTaskObserver = new PO((list) => {
                for (const entry of list.getEntries()) {
                    const s = origin + entry.startTime;
                    const e = s + entry.duration;
                    let overlap = 0;
                    for (const b of this.busy)
                        overlap += Math.max(0, Math.min(e, b.e) - Math.max(s, b.s));
                    const self = overlap / Math.max(1, entry.duration) > 0.5;
                    this.host?.post({ type: "longtask", ms: entry.duration, self });
                }
            });
            this.longTaskObserver.observe({ type: "longtask", buffered: false });
        }
        catch {
            this.longTaskObserver = null;
        }
    }
    // ---------------------------------------------------------------------------
    // Worker messages
    // ---------------------------------------------------------------------------
    onMessage(m, from) {
        if (from !== this.host)
            return; // retired workers are handled by retireHost
        switch (m.type) {
            case "level":
                this.level = m.level;
                this.policy = m.collectors;
                this.interactions?.setRates(Math.min(m.collectors.pointerHz, this.pointerHz), m.collectors.scrollHz);
                if (this.mutations)
                    this.mutations.coalesceMs = m.collectors.attrCoalesceMs;
                if (this.sessionOpen && this.interactions)
                    this.armTimers();
                if (this.cfg.debug)
                    console.info("[backstory] level", m.level);
                return;
            case "checkpoint-request":
                void this.checkpoint(m.reason);
                return;
            case "debug":
                this.lastDebug = m.state;
                return;
            case "fatal":
                // --- limits ---
                // The server refused the upload. Stop for this page load so nothing
                // else is sent; a reload reads the limit again.
                if (m.reason === "limit_reached" || m.reason === "feature_not_included" || m.reason === "subscription_ended") {
                    this.noteLimit("recording", m.reason === "limit_reached" ? "cap_reached" : "not_included", "off");
                    this.hold(m.reason);
                    return;
                }
                // --- end limits ---
                console.warn("[backstory] recording stopped:", m.reason);
                this.stop();
                return;
            case "drained":
                this.sendKeepalive(m.chunks);
                return;
            case "config":
                this.applyNudge(m.remote);
                return;
            case "handover-state":
                this.handoverState = { clockOffsetMs: m.clockOffsetMs, credits: m.credits, burstUntil: m.burstUntil };
                return;
            // --- transport & monitor ---
            case "transport":
                this.transportKind = m.kind;
                if (this.cfg.debug)
                    console.info("[backstory] transport", m.kind);
                return;
            case "burst":
                if (this.cfg.debug)
                    console.info("[backstory] monitor burst", m.trigger, "pre-roll events", m.preRollEvents);
                return;
            // --- end transport & monitor ---
            case "ready":
                return;
        }
    }
    /** Final-chunk delivery on unload from the main thread (workers may be killed). */
    sendKeepalive(chunks) {
        for (const c of chunks) {
            if (c.body.byteLength > 60 * 1024)
                continue; // keepalive cap
            try {
                void fetch(this.cfg.ingestUrl, {
                    method: "POST",
                    keepalive: true,
                    headers: {
                        "content-type": "application/octet-stream",
                        [HEADERS.projectKey]: this.cfg.projectKey,
                        [HEADERS.encoding]: c.encoding,
                        [HEADERS.sdkVersion]: SDK_VERSION,
                        [HEADERS.requestId]: this.requestId,
                    },
                    body: c.body,
                });
            }
            catch {
                /* best effort */
            }
        }
    }
    attachLifecycle() {
        const onHide = () => {
            this.emitSurfaceState();
            if (this.doc.visibilityState === "hidden") {
                this.mutations?.flush();
                this.interactions?.flushMoves();
                this.host?.post({ type: "drain" });
            }
        };
        const onSurfaceFocus = () => this.emitSurfaceState();
        const onPageHide = () => {
            this.emit({ type: "control", kind: "end", t: this.now(), reason: "unload" });
            this.host?.post({ type: "drain" });
            this.crosstab?.stop();
        };
        const onOnline = () => this.host?.post({ type: "online", online: true });
        const onOffline = () => this.host?.post({ type: "online", online: false });
        this.doc.addEventListener("visibilitychange", onHide);
        this.win.addEventListener("pagehide", onPageHide);
        this.win.addEventListener("online", onOnline);
        this.win.addEventListener("offline", onOffline);
        this.win.addEventListener("focus", onSurfaceFocus);
        this.win.addEventListener("blur", onSurfaceFocus);
        this.off.push(() => this.doc.removeEventListener("visibilitychange", onHide), () => this.win.removeEventListener("pagehide", onPageHide), () => this.win.removeEventListener("online", onOnline), () => this.win.removeEventListener("offline", onOffline), () => this.win.removeEventListener("focus", onSurfaceFocus), () => this.win.removeEventListener("blur", onSurfaceFocus));
    }
    mountPanel() {
        try {
            this.panel = new DebugPanel({
                getState: () => this.getDebugState(),
                getCanvases: () => this.getCanvases(),
                getWebRtc: () => this.getWebRtc(),
                extra: () => {
                    const level = this.lastDebug?.level ?? this.level;
                    const sockets = this.getWebSockets();
                    const socketPayloads = this.cfg.network.captureWebSocketMessages
                        ? `${this.cfg.network.webSocketMessageAllowlistUrls.length} allowlisted URL${this.cfg.network.webSocketMessageAllowlistUrls.length === 1 ? "" : "s"}`
                        : "off";
                    return {
                        "recording mode": this.mode,
                        "tab / role": `${this.tabId} / ${this.crosstab?.leader ? "visit coordinator" : "recording tab"}`,
                        "config source": this.remoteSource,
                        "worker host": this.host?.kind ?? "-",
                        "ingest transport": this.transportKind,
                        "app WebSockets": this.cfg.collectors.network
                            ? `metadata on · payloads ${socketPayloads} · ${sockets.active} active / ${sockets.connections} seen · ↑${sockets.sent} ↓${sockets.received}`
                            : "network collector off",
                        WebRTC: this.isWebRtcEnabled()
                            ? `on · ${this.getWebRtc().length} active${level === "SURVIVAL" || level === "PAUSED" ? " · quality sampling paused" : ""}`
                            : this.limits.webrtc.allowed
                                ? "off"
                                : `off · ${panelReason(this.limits.webrtc.reason)}`,
                        "cpu pressure": this.cpuState ?? "not reported",
                        "canvas capture": this.isCanvasEnabled() ? "on" : this.limits.canvas.allowed ? "off" : `off · ${panelReason(this.limits.canvas.reason)}`,
                        ...this.limitRows(),
                        "low-end policy": this.lowEnd ? "active" : "not needed",
                    };
                },
                doc: this.doc,
                win: this.win,
            });
            this.panel.mount();
        }
        catch {
            this.panel = null;
        }
    }
    /** Latest CPU pressure from the page, for the debug panel. */
    setCpuState(state) {
        this.cpuState = state;
    }
    // --- limits ---
    /**
     * One row per feature that is not running. An empty set is a single
     * "limits: none" so the panel does not imply a restriction that is not there.
     */
    limitRows() {
        const l = this.limits;
        const rows = {};
        if (this.paused || l.sessions === "off")
            rows.sessions = `stopped · ${panelReason(this.paused?.reason ?? l.sessionsReason)}`;
        else if (l.sessions === "triggers-only")
            rows.sessions = `errors and triggers · ${panelReason(l.sessionsReason)}`;
        if (!l.live.allowed)
            rows["live assist"] = `off · ${panelReason(l.live.reason)}`;
        if (!l.remoteControl.allowed)
            rows["remote control"] = `off · ${panelReason(l.remoteControl.reason)}`;
        if (!l.supportCalling.allowed)
            rows["support calls"] = `off · ${panelReason(l.supportCalling.reason)}`;
        if (l.customEvents && !l.customEvents.allowed)
            rows["custom events"] = `off · ${panelReason(l.customEvents.reason)}`;
        if (l.webrtc && !l.webrtc.allowed)
            rows["call quality"] = `off · ${panelReason(l.webrtc.reason)}`;
        if (Object.keys(rows).length === 0 && l.canvas.allowed && l.customEvents?.allowed !== false && l.webrtc.allowed)
            rows.limits = "none";
        return rows;
    }
    // --- end limits ---
    // ---------------------------------------------------------------------------
    // Public surface used by index.ts
    // ---------------------------------------------------------------------------
    identify(id, name) {
        const next = id.trim();
        // A blank id is a sign-out. reset() is the call apps should make.
        if (!next) {
            if (this.endUserHash)
                this.reset();
            return;
        }
        // Two known people never share a recording. End this session first, while
        // it still belongs to the previous person, then open the next one.
        if (this.endUserHash && this.endUserHash !== next) {
            if (this.sessionOpen)
                this.endSession("manual");
            this.endUserHash = next;
            this.endUserName = name;
            this.openForPerson();
            this.namePerson(next, name);
            return;
        }
        // A sign-in during a quiet stretch opens the session it belongs to.
        if (!this.sessionOpen && !this.stopped && this.host && this.consented) {
            this.endUserHash = next;
            this.endUserName = name;
            this.openForPerson();
        }
        else {
            this.endUserHash = next; // stored as sent; the field name predates readable identity
            this.endUserName = name;
        }
        this.namePerson(next, name);
    }
    /** End the current session and forget the person. The next action starts a new anonymous session. */
    reset() {
        if (this.sessionOpen)
            this.endSession("manual");
        this.endUserHash = undefined;
        this.endUserName = undefined;
        if (!this.stopped && this.host && this.consented)
            this.listenForReturn();
    }
    openForPerson() {
        if (this.stopped || !this.host || !this.consented || this.sessionOpen)
            return;
        this.beginSession(this.segmentIndex + 1);
    }
    namePerson(id, name) {
        this.emit({ type: "control", kind: "identify", t: this.now(), endUserHash: id, name });
        this.host?.post({ type: "meta", meta: this.meta() });
    }
    track(name, props, options) {
        const internal = name.startsWith("backstory.");
        if (!internal) {
            if (this.limits.customEvents && !this.limits.customEvents.allowed)
                return;
            if (!this.customEvents.take())
                return;
        }
        this.activity();
        this.emit({ type: "custom", t: this.now(), name, props, severity: options?.severity ?? "info" });
    }
    /**
     * Framework fallback when a router changes views without a patched history
     * event. Ids in the path are grouped the same way the history patch groups
     * them, so "/orders/88" from a router hook and from pushState are one route.
     */
    route(path) {
        this.activity();
        this.emit({
            type: "navigation",
            t: this.now(),
            url: this.win.location.href,
            route: routePattern(path || this.win.location.pathname),
            title: this.doc.title,
            kind: "replace",
        });
    }
    setMode(mode) {
        if (mode === this.mode)
            return;
        this.mode = mode;
        this.emit({ type: "control", kind: "mode", t: this.now(), mode });
        // --- transport & monitor ---
        this.host?.post({ type: "mode", mode });
        // Pointer/scroll collectors off while idle; back to the level policy on exit. Checkpoint cadence follows the mode.
        if (mode === "monitor")
            this.interactions?.setRates(0, 0);
        else
            this.interactions?.setRates(Math.min(this.policy.pointerHz, this.pointerHz), this.policy.scrollHz);
        if (this.sessionOpen && this.interactions)
            this.armTimers();
        if (mode === "normal" && this.monitorAfterMinutes > 0)
            this.armMonitorMode(this.monitorAfterMinutes);
        if (mode === "monitor" && this.monitorTimer) {
            this.win.clearTimeout(this.monitorTimer);
            this.monitorTimer = null;
        }
        // --- end transport & monitor ---
        this.host?.post({ type: "meta", meta: this.meta() });
        this.armInactivity();
    }
    grantConsent() {
        if (this.consented)
            return;
        this.consented = true;
        if (!this.host)
            this.start();
        this.emit({ type: "control", kind: "consent", t: this.now(), granted: true });
    }
    revokeConsent() {
        this.consented = false;
        if (this.sessionOpen)
            this.emit({ type: "control", kind: "consent", t: this.now(), granted: false });
        this.endSession("consent_revoked");
        this.host?.post({ type: "wipe" });
    }
    getSessionId() {
        return this.sessionId;
    }
    getVisitId() {
        return this.visitId;
    }
    getTabId() {
        return this.tabId;
    }
    getLevel() {
        return this.level;
    }
    getCheckpointId() {
        return this.checkpointId;
    }
    getMode() {
        return this.mode;
    }
    /** Effective remote config (null until boot completes). */
    getRemoteConfig() {
        return this.remote;
    }
    isCanvasEnabled() {
        return this.limitsSeen && (this.cfg.canvas.enabled || this.canvasEnabled) && this.limits.canvas.allowed;
    }
    // --- webrtc ---
    isWebRtcEnabled() {
        return this.limitsSeen && (this.cfg.webrtc.enabled || this.webrtcEnabled) && this.limits.webrtc.allowed;
    }
    // --- end webrtc ---
    getDebugState() {
        this.host?.post({ type: "debug" });
        return this.lastDebug;
    }
    stop() {
        if (this.stopped)
            return;
        if (this.sessionOpen)
            this.endSession("manual");
        this.stopped = true;
        this.paused = null;
        const h = this.host;
        this.teardown();
        // Give the pipeline a moment to flush before terminating.
        this.win.setTimeout(() => h?.terminate(), 2000);
    }
}

const LEVELS = ["log", "info", "warn", "error", "debug", "trace"];
/** Which console levels are recorded at each governor level (PLAN §7.4). */
function allowedConsoleLevels(level) {
    switch (level) {
        case "HEALTHY":
            return new Set(LEVELS);
        case "CONSTRAINED":
            return new Set(["warn", "error"]);
        case "DEGRADED":
        case "SURVIVAL":
            return new Set(["error"]);
        default:
            return new Set();
    }
}
const MAX_DEPTH = 3;
const MAX_KEYS = 50;
const MAX_ARR = 50;
function describeNode(n) {
    if (n.nodeType === 3)
        return `#text`;
    if (n.nodeType !== 1)
        return `<${n.nodeName.toLowerCase()}>`;
    const el = n;
    let s = `<${el.tagName.toLowerCase()}`;
    if (el.id)
        s += `#${el.id}`;
    const cls = typeof el.className === "string" ? el.className.trim() : "";
    if (cls)
        s += "." + cls.split(/\s+/).slice(0, 3).join(".");
    return s + ">";
}
function cap$1(s, max = LIMITS.maxConsoleArgBytes) {
    return s.length > max ? s.slice(0, max) + "…" : s;
}
/**
 * Serialize a console argument to a compact, cycle-safe, masked string.
 * Depth 3, 1 KB cap per argument, Errors → message + stack, DOM nodes → selector-ish.
 */
function serializeArg(v, depth = 0, seen = new WeakSet()) {
    const t = typeof v;
    if (v === null)
        return "null";
    if (v === undefined)
        return "undefined";
    if (t === "string")
        return cap$1(maskPII(v)[0]);
    if (t === "number" || t === "boolean" || t === "bigint")
        return String(v);
    if (t === "symbol")
        return v.toString();
    if (t === "function")
        return `[Function ${v.name || "anonymous"}]`;
    if (v instanceof Error)
        return cap$1(`${v.name}: ${maskPII(v.message)[0]}${v.stack ? "\n" + maskPII(String(v.stack))[0] : ""}`, 2048);
    if (typeof Node !== "undefined" && v instanceof Node)
        return describeNode(v);
    if (v instanceof Date)
        return isNaN(v.getTime()) ? "Invalid Date" : v.toISOString();
    if (v instanceof RegExp)
        return v.toString();
    if (t === "object") {
        const o = v;
        if (seen.has(o))
            return "[Circular]";
        if (depth >= MAX_DEPTH)
            return Array.isArray(o) ? `[Array(${o.length})]` : `[${o.constructor?.name || "Object"}]`;
        seen.add(o);
        try {
            if (Array.isArray(o)) {
                const items = o.slice(0, MAX_ARR).map((x) => serializeArg(x, depth + 1, seen));
                if (o.length > MAX_ARR)
                    items.push(`…+${o.length - MAX_ARR}`);
                return cap$1(`[${items.join(", ")}]`);
            }
            if (typeof o.entries === "function" && (o instanceof Map || o instanceof Set)) {
                return cap$1(`${o.constructor.name}(${o.size})`);
            }
            const keys = Object.keys(o);
            const parts = [];
            for (const k of keys.slice(0, MAX_KEYS)) {
                let val;
                try {
                    val = o[k];
                }
                catch {
                    val = "[Getter]";
                }
                parts.push(`${k}: ${serializeArg(val, depth + 1, seen)}`);
            }
            if (keys.length > MAX_KEYS)
                parts.push(`…+${keys.length - MAX_KEYS}`);
            const name = o.constructor && o.constructor !== Object ? o.constructor.name + " " : "";
            return cap$1(`${name}{${parts.join(", ")}}`);
        }
        finally {
            seen.delete(o);
        }
    }
    return cap$1(String(v));
}
/** Patches console.* and emits ConsoleEvents; restores originals on stop(). */
class ConsoleCollector {
    o;
    originals = {};
    active = false;
    /** Re-entrancy guard: our own console.warn calls must not be recorded. */
    inHook = false;
    constructor(o) {
        this.o = o;
    }
    start() {
        if (this.active)
            return;
        const c = this.o.target ?? console;
        for (const level of LEVELS) {
            const orig = c[level];
            if (typeof orig !== "function")
                continue;
            this.originals[level] = orig;
            const self = this;
            c[level] = function (...args) {
                try {
                    orig.apply(this === undefined ? c : this, args);
                }
                finally {
                    if (!self.inHook) {
                        self.inHook = true;
                        try {
                            self.record(level, args);
                        }
                        catch {
                            /* never break the host */
                        }
                        self.inHook = false;
                    }
                }
            };
        }
        this.active = true;
    }
    record(level, args) {
        if (!allowedConsoleLevels(this.o.getLevel()).has(level))
            return;
        // Do not record our own diagnostics.
        if (typeof args[0] === "string" && args[0].startsWith("[backstory]"))
            return;
        const ser = args.slice(0, 20).map((a) => serializeArg(a));
        const e = { type: "console", t: this.o.now(), level, args: ser };
        if (level === "error" || level === "warn" || level === "trace") {
            const stack = new Error().stack;
            if (stack) {
                // Drop our own frames (first 3 lines) and cap.
                const lines = stack.split("\n").slice(3, 13).join("\n");
                if (lines)
                    e.stack = cap$1(lines, 2048);
            }
        }
        this.o.emit(e);
    }
    stop() {
        if (!this.active)
            return;
        const c = this.o.target ?? console;
        for (const level of LEVELS) {
            const orig = this.originals[level];
            if (orig)
                c[level] = orig;
        }
        this.originals = {};
        this.active = false;
    }
}

const KEY = "__BACKSTORY_BUS__";
function state() {
    const g = globalThis;
    let s = g[KEY];
    if (!s) {
        s = { emit: null, taps: new Set(), facts: null, pending: [], beforeRequest: null };
        g[KEY] = s;
    }
    return s;
}
const PENDING_MAX = 200;
const bus = {
    /** Emit an event into the recorder (buffered until the recorder attaches). */
    emit(e) {
        const s = state();
        if (s.emit)
            s.emit(e);
        else if (s.pending.length < PENDING_MAX)
            s.pending.push(e);
    },
    /** Observe every event that flows into the recorder (network, pointer, navigation, ...). */
    tap(fn) {
        const s = state();
        s.taps.add(fn);
        return () => void s.taps.delete(fn);
    },
    /** Called by the tap wrapper around the recorder's emit. */
    notify(e) {
        for (const t of state().taps) {
            try {
                t(e);
            }
            catch {
                /* taps never break the recorder */
            }
        }
    },
    attach(emit, facts) {
        const s = state();
        s.emit = emit;
        s.facts = facts;
        const pending = s.pending;
        s.pending = [];
        for (const e of pending)
            emit(e);
    },
    detach() {
        const s = state();
        s.emit = null;
        s.facts = null;
        s.pending = [];
    },
    facts() {
        return state().facts;
    },
    setBeforeRequest(h) {
        state().beforeRequest = h;
    },
    beforeRequest() {
        return state().beforeRequest;
    },
    /** Test helper. */
    _reset() {
        const s = state();
        s.emit = null;
        s.facts = null;
        s.pending = [];
        s.taps.clear();
        s.beforeRequest = null;
    },
};

/** Headers that are never captured regardless of allowlist. */
const FORBIDDEN_HEADERS = new Set(["authorization", "cookie", "set-cookie", "proxy-authorization", "x-api-key", "x-auth-token"]);
function setBeforeRequest(h) {
    bus.setBeforeRequest(h);
}
let reqCounter = 0;
function nextReqId() {
    reqCounter = (reqCounter + 1) % 1e9;
    return `r${Date.now().toString(36)}${reqCounter.toString(36)}`;
}
/** Extract a GraphQL operation name from a JSON POST body (string only). */
function graphqlOperation(body) {
    if (typeof body !== "string" || body.length > 256 * 1024)
        return undefined;
    const s = body.trimStart();
    if (!s.startsWith("{") && !s.startsWith("["))
        return undefined;
    try {
        const j = JSON.parse(s);
        if (Array.isArray(j))
            return j.map((o) => o.operationName).filter(Boolean).join(",") || undefined;
        if (j.operationName)
            return String(j.operationName);
        if (typeof j.query === "string") {
            const m = /^\s*(?:query|mutation|subscription)\s+([A-Za-z_][A-Za-z0-9_]*)/.exec(j.query);
            return m ? m[1] : undefined;
        }
    }
    catch {
        /* not JSON */
    }
    return undefined;
}
/** Filter headers by allowlist and forbidden set; lower-cases names. */
function pickHeaders(headers, allow) {
    if (!headers || !allow.length)
        return undefined;
    const set = new Set(allow.map((h) => h.toLowerCase()));
    const out = {};
    let n = 0;
    for (const [k, v] of headers) {
        const lk = k.toLowerCase();
        if (FORBIDDEN_HEADERS.has(lk) || !set.has(lk))
            continue;
        out[lk] = maskPII(String(v).slice(0, 512))[0];
        n++;
    }
    return n ? out : undefined;
}
function bodyAllowed(url, cfg) {
    if (!cfg.captureBodies)
        return false;
    if (!cfg.bodyAllowlistUrls.length)
        return true;
    return cfg.bodyAllowlistUrls.some((p) => url.startsWith(p));
}
function webSocketMessageAllowed(url, cfg) {
    return cfg.captureWebSocketMessages && cfg.webSocketMessageAllowlistUrls.some((p) => url.startsWith(p));
}
/** Keeps a body that fits. A longer one is cut and the original length is returned. */
function captureText(body, max) {
    const bytes = body.length;
    const cut = bytes > max;
    return { text: maskPII(cut ? body.slice(0, max) : body)[0], cut, bytes };
}
function applyReqBody(event, body, max) {
    const captured = captureText(body, max);
    event.reqBody = captured.text;
    event.reqBodyBytes = captured.bytes;
    if (captured.cut)
        event.reqBodyCut = true;
}
function headersOf(h) {
    if (!h)
        return undefined;
    if (typeof Headers !== "undefined" && h instanceof Headers)
        return h.entries();
    if (Array.isArray(h))
        return h;
    return Object.entries(h);
}
/**
 * Patches fetch, XMLHttpRequest, WebSocket, EventSource, and sendBeacon, and
 * observes PerformanceResourceTiming for static resources. Bodies/headers are
 * off by default (PLAN §8.1); forbidden headers are never captured.
 */
class NetworkCollector {
    o;
    off = [];
    ingestOrigin;
    apiOrigin;
    po = null;
    seenResources = new WeakSet();
    wsDebug = { active: 0, connections: 0, sent: 0, received: 0 };
    /** The wrappers we installed, so we can tell if something replaced them. */
    installed = {};
    guard = null;
    repatches = 0;
    debugWebSockets() {
        return { ...this.wsDebug };
    }
    /**
     * Whether the wrappers we installed are still the ones the page calls. A
     * `false` here is the difference between "this app made no requests" and
     * "something took our instrumentation away", which is otherwise invisible.
     */
    instrumentation() {
        const w = this.o.win;
        return {
            fetch: !this.installed.fetch || w.fetch === this.installed.fetch,
            xhr: !this.installed.xhrOpen || w.XMLHttpRequest?.prototype?.open === this.installed.xhrOpen,
            ws: !this.installed.ws || w.WebSocket === this.installed.ws,
        };
    }
    constructor(o) {
        this.o = o;
        this.ingestOrigin = safeOrigin$1(o.ingestUrl);
        this.apiOrigin = o.apiUrl ? safeOrigin$1(o.apiUrl) : "";
    }
    level() {
        return this.o.getLevel ? this.o.getLevel() : "all";
    }
    skip(url) {
        const origin = safeOrigin$1(url);
        if (this.ingestOrigin && origin === this.ingestOrigin)
            return true;
        // The live socket and remote config live on the api origin. Recording them
        // would put our own traffic in the customer's Network panel and, worse, let
        // every agent message during Live Assist produce an event that is itself
        // streamed to the agent. Only our `/v1/…` paths are skipped, so an app that
        // happens to share the origin keeps its own requests.
        return !!this.apiOrigin && origin === this.apiOrigin && safePath(url).startsWith("/v1/");
    }
    emitNet(e) {
        const lvl = this.level();
        if (lvl === "none")
            return;
        if (lvl === "failures") {
            if (e.kind === "ws") {
                // WebSocket lifecycle is tiny and essential for diagnosing a missing
                // connection. Under pressure, retain open/error/close plus
                // logarithmically sampled frame totals (1, 2, 4, 8...) without payloads.
                // This bounds high-volume sockets while preserving direction and scale.
                const frames = (e.wsSent ?? 0) + (e.wsReceived ?? 0);
                const sampledTotal = frames <= 2 || (frames > 0 && Number.isInteger(Math.log2(frames)));
                if (e.wsPhase === "message" && !sampledTotal)
                    return;
            }
            else if (!(e.failed || (e.status && e.status >= 400) || (e.durationMs && e.durationMs > 2000))) {
                return;
            }
        }
        if (lvl !== "all") {
            delete e.reqHeaders;
            delete e.resHeaders;
            delete e.reqBody;
            delete e.resBody;
            delete e.wsText;
        }
        this.o.emit(e);
    }
    start() {
        this.patchFetch();
        this.patchXhr();
        this.patchWebSocket();
        this.patchEventSource();
        this.patchBeacon();
        this.observeResources();
        this.guardPatches();
    }
    /**
     * Privacy extensions, mocking libraries, and analytics SDKs all replace
     * `fetch`/`XMLHttpRequest` wholesale, and whoever assigns last wins. When
     * that happens after us our wrapper is orphaned and the Network panel stays
     * empty with nothing to explain why, so re-wrap whatever is there now. The
     * repatch cap stops a patch war with a script that re-asserts on a timer.
     */
    guardPatches() {
        const w = this.o.win;
        const id = this.o.win.setInterval(() => {
            if (this.repatches >= 5)
                return;
            const displaced = [];
            if (this.installed.fetch && w.fetch !== this.installed.fetch) {
                displaced.push("fetch");
                this.patchFetch();
            }
            if (this.installed.xhrOpen && w.XMLHttpRequest?.prototype?.open !== this.installed.xhrOpen) {
                displaced.push("XMLHttpRequest");
                this.patchXhr();
            }
            if (this.installed.ws && w.WebSocket !== this.installed.ws) {
                displaced.push("WebSocket");
                this.patchWebSocket();
            }
            if (!displaced.length)
                return;
            this.repatches++;
            if (this.o.debug)
                console.info(`[backstory] re-instrumented ${displaced.join(", ")} after another script replaced it`);
        }, 3000);
        this.guard = id;
        this.off.push(() => {
            if (this.guard !== null)
                this.o.win.clearInterval(this.guard);
            this.guard = null;
        });
    }
    patchFetch() {
        const w = this.o.win;
        const orig = w.fetch;
        if (typeof orig !== "function")
            return;
        const self = this;
        const patched = function (input, init) {
            const start = self.o.now();
            let url = "";
            let method = "GET";
            let reqHeaders;
            let reqBody;
            try {
                if (typeof input === "string")
                    url = input;
                else if (input instanceof URL)
                    url = input.href;
                else {
                    url = input.url;
                    method = input.method || method;
                    reqHeaders = input.headers?.entries();
                }
                if (init) {
                    method = init.method || method;
                    if (init.headers)
                        reqHeaders = headersOf(init.headers);
                    reqBody = init.body;
                }
            }
            catch {
                /* ignore */
            }
            const abs = absolute(url, self.o.win);
            if (self.skip(abs))
                return orig.call(this ?? w, input, init);
            const reqId = nextReqId();
            const headerList = reqHeaders ? Array.from(reqHeaders) : [];
            const hook = bus.beforeRequest();
            if (hook) {
                try {
                    const extra = hook({ reqId, url: abs, method: method.toUpperCase(), headers: headerList });
                    if (extra && extra.length) {
                        headerList.push(...extra);
                        init = { ...(init ?? {}) };
                        const h = new Headers(init.headers ?? (typeof input !== "string" && !(input instanceof URL) ? input.headers : undefined));
                        for (const [k, v] of extra)
                            h.set(k, v);
                        init.headers = h;
                    }
                }
                catch {
                    /* hook errors never break requests */
                }
            }
            const traceparent = headerList.find(([k]) => k.toLowerCase() === "traceparent")?.[1];
            const traceId = traceparent ? traceparent.split("-")[1] : undefined;
            const operation = method.toUpperCase() === "POST" ? graphqlOperation(reqBody) : undefined;
            const base = { type: "network", t: start, kind: "fetch", reqId, method: method.toUpperCase(), url: stripUrl(abs), traceId, operation };
            if (self.o.cfg.headerAllowlist.length)
                base.reqHeaders = pickHeaders(headerList, self.o.cfg.headerAllowlist);
            if (typeof reqBody === "string" && bodyAllowed(abs, self.o.cfg))
                applyReqBody(base, reqBody, self.o.cfg.maxBodyBytes);
            let p;
            try {
                p = orig.call(this ?? w, input, init);
            }
            catch (err) {
                self.emitNet({ ...base, failed: true, durationMs: 0 });
                throw err;
            }
            return p.then((res) => {
                const e = { ...base, status: res.status, durationMs: Math.max(0, self.o.now() - start) };
                try {
                    const cl = res.headers.get("content-length");
                    if (cl)
                        e.size = parseInt(cl, 10) || undefined;
                    if (self.o.cfg.headerAllowlist.length)
                        e.resHeaders = pickHeaders(res.headers.entries(), self.o.cfg.headerAllowlist);
                    if (bodyAllowed(abs, self.o.cfg) && typeof res.clone === "function") {
                        if (e.size !== undefined && e.size > self.o.cfg.maxBodyBytes) {
                            e.resBodySkipped = true;
                        }
                        else {
                            res
                                .clone()
                                .text()
                                .then((txt) => {
                                if (txt.length > self.o.cfg.maxBodyBytes) {
                                    e.resBodySkipped = true;
                                    e.size = txt.length;
                                }
                                else {
                                    e.resBody = maskPII(txt)[0];
                                    if (e.size === undefined)
                                        e.size = txt.length;
                                }
                                self.emitNet(e);
                            })
                                .catch(() => self.emitNet(e));
                            return res;
                        }
                    }
                }
                catch {
                    /* ignore */
                }
                self.emitNet(e);
                return res;
            }, (err) => {
                self.emitNet({ ...base, failed: true, durationMs: Math.max(0, self.o.now() - start) });
                throw err;
            });
        };
        w.fetch = patched;
        this.installed.fetch = patched;
        this.off.push(() => {
            if (w.fetch === patched)
                w.fetch = orig;
        });
    }
    patchXhr() {
        const w = this.o.win;
        const X = w.XMLHttpRequest;
        if (!X)
            return;
        const proto = X.prototype;
        const origOpen = proto.open;
        const origSend = proto.send;
        const origSet = proto.setRequestHeader;
        if (!origOpen || !origSend)
            return;
        const self = this;
        const metas = new WeakMap();
        proto.open = function (method, url, ...rest) {
            metas.set(this, { method: String(method || "GET").toUpperCase(), url: absolute(String(url), self.o.win), headers: [], start: 0 });
            return origOpen.call(this, method, url, ...rest);
        };
        proto.setRequestHeader = function (k, v) {
            metas.get(this)?.headers.push([k, v]);
            return origSet.call(this, k, v);
        };
        proto.send = function (body) {
            const m = metas.get(this);
            if (m && !self.skip(m.url)) {
                m.start = self.o.now();
                const reqId = nextReqId();
                const hook = bus.beforeRequest();
                if (hook) {
                    try {
                        const extra = hook({ reqId, url: m.url, method: m.method, headers: m.headers });
                        if (extra) {
                            for (const [k, v] of extra) {
                                m.headers.push([k, v]);
                                origSet.call(this, k, v);
                            }
                        }
                    }
                    catch {
                        /* ignore */
                    }
                }
                const tp = m.headers.find(([k]) => k.toLowerCase() === "traceparent")?.[1];
                const base = {
                    type: "network",
                    t: m.start,
                    kind: "xhr",
                    reqId,
                    method: m.method,
                    url: stripUrl(m.url),
                    traceId: tp ? tp.split("-")[1] : undefined,
                    operation: m.method === "POST" ? graphqlOperation(body) : undefined,
                };
                if (self.o.cfg.headerAllowlist.length)
                    base.reqHeaders = pickHeaders(m.headers, self.o.cfg.headerAllowlist);
                if (typeof body === "string" && bodyAllowed(m.url, self.o.cfg))
                    applyReqBody(base, body, self.o.cfg.maxBodyBytes);
                const done = () => {
                    const e = { ...base, status: this.status || undefined, durationMs: Math.max(0, self.o.now() - m.start), failed: this.status === 0 };
                    try {
                        const cl = this.getResponseHeader("content-length");
                        if (cl)
                            e.size = parseInt(cl, 10) || undefined;
                        if (self.o.cfg.headerAllowlist.length) {
                            const all = (this.getAllResponseHeaders() || "")
                                .trim()
                                .split(/[\r\n]+/)
                                .filter(Boolean)
                                .map((line) => {
                                const i = line.indexOf(":");
                                return [line.slice(0, i).trim(), line.slice(i + 1).trim()];
                            });
                            e.resHeaders = pickHeaders(all, self.o.cfg.headerAllowlist);
                        }
                        if (bodyAllowed(m.url, self.o.cfg) && (this.responseType === "" || this.responseType === "text")) {
                            const txt = this.responseText;
                            if (txt && txt.length > self.o.cfg.maxBodyBytes) {
                                e.resBodySkipped = true;
                                e.size = txt.length;
                            }
                            else if (txt) {
                                e.resBody = maskPII(txt)[0];
                                if (e.size === undefined)
                                    e.size = txt.length;
                            }
                        }
                    }
                    catch {
                        /* ignore */
                    }
                    self.emitNet(e);
                };
                this.addEventListener("loadend", done, { once: true });
            }
            return origSend.call(this, body);
        };
        this.installed.xhrOpen = proto.open;
        this.off.push(() => {
            proto.open = origOpen;
            proto.send = origSend;
            proto.setRequestHeader = origSet;
        });
    }
    patchWebSocket() {
        const w = this.o.win;
        const Orig = w.WebSocket;
        if (typeof Orig !== "function")
            return;
        const self = this;
        const Patched = function (url, protocols) {
            const ws = protocols === undefined ? new Orig(url) : new Orig(url, protocols);
            const abs = absolute(String(url), self.o.win);
            if (self.skip(abs))
                return ws;
            const reqId = nextReqId();
            const start = self.o.now();
            let sent = 0;
            let received = 0;
            let closed = false;
            self.wsDebug.connections++;
            self.wsDebug.active++;
            const stripped = stripUrl(abs);
            const captureText = webSocketMessageAllowed(abs, self.o.cfg);
            const frame = (direction, data) => {
                const text = typeof data === "string" ? data : undefined;
                self.emitNet({
                    type: "network",
                    t: self.o.now(),
                    kind: "ws",
                    reqId,
                    method: direction === "send" ? "SEND" : "RECEIVE",
                    url: stripped,
                    wsPhase: "message",
                    wsDirection: direction,
                    wsDataType: text === undefined ? "binary" : "text",
                    wsBytes: dataBytes(data),
                    wsText: captureText && text !== undefined ? maskWebSocketText(text, self.o.cfg.maxWebSocketMessageBytes) : undefined,
                    wsSent: sent,
                    wsReceived: received,
                });
            };
            const origSend = ws.send;
            ws.send = function (data) {
                const result = origSend.call(this, data);
                sent++;
                self.wsDebug.sent++;
                frame("send", data);
                return result;
            };
            ws.addEventListener("open", () => self.emitNet({ type: "network", t: start, kind: "ws", reqId, method: "OPEN", url: stripped, durationMs: self.o.now() - start, status: 101, wsPhase: "open" }));
            ws.addEventListener("message", (ev) => {
                received++;
                self.wsDebug.received++;
                frame("receive", ev.data);
            });
            ws.addEventListener("close", (ev) => {
                if (!closed) {
                    closed = true;
                    self.wsDebug.active = Math.max(0, self.wsDebug.active - 1);
                }
                self.emitNet({
                    type: "network",
                    t: self.o.now(),
                    kind: "ws",
                    reqId,
                    method: "CLOSE",
                    url: stripped,
                    status: ev.code,
                    durationMs: self.o.now() - start,
                    failed: !ev.wasClean,
                    wsPhase: "close",
                    wsSent: sent,
                    wsReceived: received,
                });
            });
            ws.addEventListener("error", () => self.emitNet({
                type: "network",
                t: self.o.now(),
                kind: "ws",
                reqId,
                method: "ERROR",
                url: stripped,
                failed: true,
                durationMs: self.o.now() - start,
                wsPhase: "error",
                wsSent: sent,
                wsReceived: received,
            }));
            return ws;
        };
        Patched.prototype = Orig.prototype;
        for (const k of ["CONNECTING", "OPEN", "CLOSING", "CLOSED"])
            Patched[k] = Orig[k];
        w.WebSocket = Patched;
        this.installed.ws = Patched;
        this.off.push(() => {
            if (w.WebSocket === Patched)
                w.WebSocket = Orig;
        });
    }
    patchEventSource() {
        const w = this.o.win;
        const Orig = w.EventSource;
        if (typeof Orig !== "function")
            return;
        const self = this;
        const Patched = function (url, init) {
            const es = new Orig(url, init);
            const abs = absolute(String(url), self.o.win);
            if (self.skip(abs))
                return es;
            const start = self.o.now();
            const reqId = nextReqId();
            es.addEventListener("open", () => self.emitNet({ type: "network", t: start, kind: "sse", reqId, method: "GET", url: stripUrl(abs), status: 200, durationMs: self.o.now() - start }));
            es.addEventListener("error", () => self.emitNet({ type: "network", t: self.o.now(), kind: "sse", reqId, method: "GET", url: stripUrl(abs), failed: true }));
            return es;
        };
        Patched.prototype = Orig.prototype;
        w.EventSource = Patched;
        this.off.push(() => {
            if (w.EventSource === Patched)
                w.EventSource = Orig;
        });
    }
    patchBeacon() {
        const nav = this.o.win.navigator;
        const orig = nav?.sendBeacon;
        if (typeof orig !== "function")
            return;
        const self = this;
        const patched = function (url, data) {
            const ok = orig.call(this ?? nav, url, data);
            const abs = absolute(String(url), self.o.win);
            if (!self.skip(abs))
                self.emitNet({ type: "network", t: self.o.now(), kind: "beacon", reqId: nextReqId(), method: "POST", url: stripUrl(abs), status: ok ? 200 : undefined, failed: !ok, durationMs: 0 });
            return ok;
        };
        nav.sendBeacon = patched;
        this.off.push(() => {
            if (nav.sendBeacon === patched)
                nav.sendBeacon = orig;
        });
    }
    observeResources() {
        const PO = this.o.win.PerformanceObserver;
        if (!PO)
            return;
        try {
            this.po = new PO((list) => {
                for (const entry of list.getEntries())
                    this.onResource(entry);
            });
            this.po.observe({ type: "resource", buffered: true });
            this.off.push(() => this.po?.disconnect());
        }
        catch {
            this.po = null;
        }
    }
    /** Static resources (img/css/script/font/media) become `resource` events with a timing breakdown. */
    onResource(r) {
        const it = r.initiatorType;
        if (it === "fetch" || it === "xmlhttprequest" || it === "beacon" || it === "other")
            return; // covered by patches
        if (this.seenResources.has(r))
            return;
        this.seenResources.add(r);
        if (this.skip(r.name))
            return;
        const base = (this.o.win.performance?.timeOrigin ?? 0) || 0;
        const t = base ? Math.round(base + r.startTime) : this.o.now();
        const e = {
            type: "network",
            t,
            kind: "resource",
            reqId: nextReqId(),
            method: "GET",
            url: stripUrl(r.name),
            durationMs: Math.round(r.duration),
            size: r.transferSize || r.encodedBodySize || undefined,
            initiatorType: it,
            status: r.responseStatus || undefined,
            timing: {
                dns: nz(r.domainLookupEnd - r.domainLookupStart),
                connect: nz(r.connectEnd - r.connectStart),
                tls: r.secureConnectionStart ? nz(r.connectEnd - r.secureConnectionStart) : undefined,
                ttfb: nz(r.responseStart - r.requestStart),
                download: nz(r.responseEnd - r.responseStart),
            },
        };
        if (r.transferSize === 0 && r.encodedBodySize === 0 && r.duration > 0)
            e.failed = false; // cached or opaque; not a failure
        this.emitNet(e);
    }
    stop() {
        for (const f of this.off)
            f();
        this.off = [];
    }
}
function nz(v) {
    return Number.isFinite(v) && v > 0 ? Math.round(v * 10) / 10 : undefined;
}
function safeOrigin$1(u) {
    try {
        const url = new URL(u, typeof location !== "undefined" ? location.href : "http://localhost/");
        // ws/wss compare equal to the http/https origin they are served from, so a
        // socket to our own ingest or api is recognised as ours.
        if (url.protocol === "ws:" || url.protocol === "wss:")
            return `${url.protocol === "wss:" ? "https:" : "http:"}//${url.host}`;
        return url.origin;
    }
    catch {
        return "";
    }
}
function safePath(u) {
    try {
        return new URL(u, typeof location !== "undefined" ? location.href : "http://localhost/").pathname;
    }
    catch {
        return "";
    }
}
function absolute(u, win) {
    try {
        return new URL(u, win.location?.href || "http://localhost/").href;
    }
    catch {
        return u;
    }
}
function dataBytes(data) {
    if (typeof data === "string") {
        try {
            return new TextEncoder().encode(data).byteLength;
        }
        catch {
            return data.length;
        }
    }
    if (typeof Blob !== "undefined" && data instanceof Blob)
        return data.size;
    if (data instanceof ArrayBuffer)
        return data.byteLength;
    if (ArrayBuffer.isView(data))
        return data.byteLength;
    return undefined;
}
function maskWebSocketText(text, maxBytes) {
    const masked = maskPII(text)[0];
    try {
        const encoded = new TextEncoder().encode(masked);
        if (encoded.byteLength <= maxBytes)
            return masked;
        return new TextDecoder().decode(encoded.subarray(0, maxBytes)) + "…";
    }
    catch {
        return masked.length > maxBytes ? masked.slice(0, maxBytes) + "…" : masked;
    }
}

/** web.dev thresholds: [good, poor]. */
const THRESHOLDS = {
    LCP: [2500, 4000],
    CLS: [0.1, 0.25],
    INP: [200, 500],
    FCP: [1800, 3000],
    TTFB: [800, 1800],
};
function rate(name, value) {
    const [good, poor] = THRESHOLDS[name];
    return value <= good ? "good" : value <= poor ? "needs-improvement" : "poor";
}
/**
 * CLS session-window aggregator (max 5 s window, 1 s gap), pure for testing.
 * Returns the current CLS value and the sources of the worst window.
 */
class ClsAggregator {
    windowValue = 0;
    windowStart = 0;
    windowLast = 0;
    windowSources = [];
    max = 0;
    maxSources = [];
    add(e) {
        if (e.hadRecentInput)
            return;
        if (this.windowValue && e.startTime - this.windowLast < 1000 && e.startTime - this.windowStart < 5000) {
            this.windowValue += e.value;
            this.windowLast = e.startTime;
            if (e.sources?.length)
                this.windowSources = [...(this.windowSources || []), ...e.sources].slice(-5);
        }
        else {
            this.windowValue = e.value;
            this.windowStart = this.windowLast = e.startTime;
            this.windowSources = e.sources ? [...e.sources] : [];
        }
        if (this.windowValue > this.max) {
            this.max = this.windowValue;
            this.maxSources = this.windowSources;
        }
    }
}
/**
 * INP estimator: worst interaction for < 50 interactions, else the 98th
 * percentile approximated by dropping one candidate per 50 interactions.
 */
class InpAggregator {
    byId = new Map();
    longest = [];
    count = 0;
    add(e) {
        if (!e.interactionId)
            return;
        const prev = this.byId.get(e.interactionId);
        if (!prev) {
            this.count++;
            this.byId.set(e.interactionId, e);
        }
        else if (e.duration > prev.duration)
            this.byId.set(e.interactionId, e);
        else
            return;
        const cur = this.byId.get(e.interactionId);
        this.longest = this.longest.filter((x) => x.interactionId !== cur.interactionId);
        this.longest.push(cur);
        this.longest.sort((a, b) => b.duration - a.duration);
        if (this.longest.length > 10)
            this.longest.length = 10;
        if (this.byId.size > 500) {
            // bound memory
            const keep = new Set(this.longest.map((x) => x.interactionId));
            for (const k of this.byId.keys())
                if (!keep.has(k))
                    this.byId.delete(k);
        }
    }
    value() {
        if (!this.longest.length)
            return undefined;
        const idx = Math.min(this.longest.length - 1, Math.floor(this.count / 50));
        return this.longest[idx];
    }
}
const toBox = (r) => ({ x: Math.round(r.x), y: Math.round(r.y), w: Math.round(r.width), h: Math.round(r.height) });
/**
 * Web Vitals (LCP, CLS, INP, FCP, TTFB) and long tasks / long animation frames
 * via PerformanceObserver. Final values are emitted when the page is hidden or
 * unloading (LCP/CLS/INP) and immediately for FCP/TTFB. Feature-detected.
 */
class VitalsCollector {
    o;
    observers = [];
    off = [];
    lcp = null;
    lcpFinal = false;
    cls = new ClsAggregator();
    inp = new InpAggregator();
    reported = new Set();
    selfMarkers;
    constructor(o) {
        this.o = o;
        this.selfMarkers = o.selfMarkers ?? ["backstory"];
    }
    start() {
        const w = this.o.win;
        const PO = w.PerformanceObserver;
        if (!PO)
            return;
        const supported = PO.supportedEntryTypes || [];
        const observe = (type, cb, extra = {}) => {
            if (supported.length && !supported.includes(type))
                return;
            try {
                const po = new PO((list) => cb(list.getEntries()));
                po.observe({ type, buffered: true, ...extra });
                this.observers.push(po);
            }
            catch {
                /* unsupported */
            }
        };
        observe("paint", (entries) => {
            for (const e of entries)
                if (e.name === "first-contentful-paint")
                    this.report("FCP", e.startTime);
        });
        observe("navigation", (entries) => {
            const nav = entries[0];
            if (nav && nav.responseStart > 0)
                this.report("TTFB", nav.responseStart);
        });
        if (!supported.includes("navigation")) {
            const nav = this.o.win.performance?.getEntriesByType?.("navigation")?.[0];
            if (nav && nav.responseStart > 0)
                this.report("TTFB", nav.responseStart);
        }
        observe("largest-contentful-paint", (entries) => {
            if (this.lcpFinal)
                return;
            const last = entries[entries.length - 1];
            if (last)
                this.lcp = { value: last.startTime, node: last.element };
        });
        observe("layout-shift", (entries) => {
            for (const e of entries)
                this.cls.add(e);
        });
        observe("event", (entries) => {
            for (const e of entries)
                this.inp.add(e);
        }, { durationThreshold: 40 });
        if (this.o.longTasks !== false) {
            observe("long-animation-frame", (entries) => {
                for (const e of entries) {
                    if (e.duration < 50)
                        continue;
                    const scripts = e.scripts || [];
                    const self = scripts.some((s) => this.isSelf(String(s.sourceURL || s.invoker || "")));
                    this.o.emit({ type: "longtask", t: this.absTime(e.startTime), durationMs: Math.round(e.duration), self: self || undefined });
                }
            });
            if (!supported.includes("long-animation-frame")) {
                observe("longtask", (entries) => {
                    for (const e of entries) {
                        const attr = e.attribution || [];
                        const self = attr.some((a) => this.isSelf(String(a.containerSrc || a.containerName || "")));
                        this.o.emit({ type: "longtask", t: this.absTime(e.startTime), durationMs: Math.round(e.duration), self: self || undefined });
                    }
                });
            }
        }
        // LCP stops at first interaction; everything finalizes when hidden/unloading.
        const finalizeLcp = () => {
            if (!this.lcpFinal) {
                this.lcpFinal = true;
                this.flushLcp();
            }
        };
        for (const t of ["keydown", "pointerdown"]) {
            this.o.doc.addEventListener(t, finalizeLcp, { once: true, capture: true });
            this.off.push(() => this.o.doc.removeEventListener(t, finalizeLcp, true));
        }
        const onHide = () => {
            if (this.o.doc.visibilityState === "hidden")
                this.finalize();
        };
        this.o.doc.addEventListener("visibilitychange", onHide);
        this.o.win.addEventListener("pagehide", () => this.finalize());
        this.off.push(() => this.o.doc.removeEventListener("visibilitychange", onHide));
    }
    isSelf(s) {
        return this.selfMarkers.some((m) => s.includes(m));
    }
    absTime(startTime) {
        const origin = this.o.win.performance?.timeOrigin;
        return origin ? Math.round(origin + startTime) : this.o.now();
    }
    idOf(n) {
        return n ? this.o.map.id(n) : undefined;
    }
    report(name, value, extra = {}) {
        if (this.reported.has(name))
            return;
        this.reported.add(name);
        const v = name === "CLS" ? Math.round(value * 10000) / 10000 : Math.round(value);
        this.o.emit({ type: "vital", t: this.o.now(), name, value: v, rating: rate(name, v), ...extra });
    }
    flushLcp() {
        if (this.lcp)
            this.report("LCP", this.lcp.value, { id: this.idOf(this.lcp.node) });
    }
    /** Emit final LCP, CLS, INP once. Safe to call repeatedly. */
    finalize() {
        this.lcpFinal = true;
        this.flushLcp();
        if (this.cls.max > 0 || this.observers.length) {
            const sources = (this.cls.maxSources || [])
                .map((s) => ({ id: this.idOf(s.node) ?? 0, prev: toBox(s.previousRect), curr: toBox(s.currentRect) }))
                .slice(0, 5);
            this.report("CLS", this.cls.max, sources.length ? { sources } : {});
        }
        const inp = this.inp.value();
        if (inp)
            this.report("INP", inp.duration, { id: this.idOf(inp.target) });
    }
    stop() {
        this.finalize();
        for (const po of this.observers)
            po.disconnect();
        this.observers = [];
        for (const f of this.off)
            f();
        this.off = [];
    }
}

const mb = (bytes) => typeof bytes === "number" && Number.isFinite(bytes) && bytes >= 0
    ? Math.round((bytes / (1024 * 1024)) * 10) / 10
    : undefined;
/**
 * Samples the page's JS heap where Chromium exposes it and Backstory's own
 * bounded recorder queue everywhere. Heap values are intentionally kept
 * separate from recorder pressure: one implicates the application, the other
 * explains capture degradation or gaps.
 */
class MemoryCollector {
    o;
    timer = null;
    pressure = null;
    cpuState;
    memoryState;
    onVisibility = () => {
        if (this.o.win.document.visibilityState === "visible")
            this.sample();
    };
    constructor(o) {
        this.o = o;
    }
    start() {
        this.sample();
        this.timer = this.o.win.setInterval(() => {
            if (this.o.win.document.visibilityState !== "hidden")
                this.sample();
        }, this.o.sampleMs ?? 10_000);
        this.o.win.document.addEventListener("visibilitychange", this.onVisibility);
        this.startPressureObserver();
    }
    /** Latest CPU pressure, or undefined when this browser does not report it. */
    cpuPressure() {
        return this.cpuState;
    }
    sample(pressureState) {
        if (pressureState)
            this.memoryState = pressureState;
        const memory = this.o.win.performance?.memory;
        // getDebugState posts a fresh request and returns the latest completed
        // snapshot, so the next sample sees current queue pressure without making
        // collection async or coupling it to worker message timing.
        const recorder = this.o.recorderState();
        const event = {
            type: "memory",
            t: this.o.now(),
            jsHeapUsedMB: mb(memory?.usedJSHeapSize),
            jsHeapTotalMB: mb(memory?.totalJSHeapSize),
            jsHeapLimitMB: mb(memory?.jsHeapSizeLimit),
            recorderUsedMB: mb(recorder?.queuedBytes),
            recorderBudgetMB: this.o.recorderBudgetMB,
            recorderPressure: recorder?.memoryPressure,
            pressureState: this.memoryState,
            cpuState: this.cpuState,
        };
        if (event.jsHeapUsedMB !== undefined ||
            event.recorderUsedMB !== undefined ||
            event.recorderPressure !== undefined ||
            event.pressureState !== undefined ||
            event.cpuState !== undefined) {
            this.o.emit(event);
        }
        if (this.cpuState)
            this.o.onCpu?.(this.cpuState);
    }
    startPressureObserver() {
        const Ctor = this.o.win.PressureObserver;
        if (!Ctor)
            return;
        // Only ask for a source the browser lists. Chromium exposes "cpu" and
        // rejects "memory"; asking for an unlisted source rejects a promise the
        // page would see as an uncaught error.
        const known = Ctor.knownSources;
        const sources = ["cpu", "memory"].filter((s) => !known || known.includes(s));
        if (sources.length === 0)
            return;
        try {
            const observer = new Ctor((records) => {
                for (const rec of records) {
                    if (!rec.state)
                        continue;
                    if (rec.source === "cpu")
                        this.cpuState = rec.state;
                    else if (rec.source === "memory")
                        this.memoryState = rec.state;
                    else if (!rec.source && sources.length === 1 && sources[0] === "cpu")
                        this.cpuState = rec.state;
                }
                this.sample();
            });
            this.pressure = observer;
            const interval = this.o.sampleMs ?? 10_000;
            for (const source of sources) {
                const result = observer.observe(source, { sampleInterval: interval });
                if (result && typeof result.catch === "function")
                    result.catch(() => undefined);
            }
        }
        catch {
            this.pressure = null;
        }
    }
    stop() {
        if (this.timer !== null)
            this.o.win.clearInterval(this.timer);
        this.timer = null;
        this.pressure?.disconnect();
        this.pressure = null;
        this.o.win.document.removeEventListener("visibilitychange", this.onVisibility);
    }
}

/** Glob-ish route match: `*` matches any run of characters; a plain prefix also matches. */
function routeMatches(pattern, path) {
    if (pattern === path)
        return true;
    if (pattern.includes("*")) {
        const re = new RegExp("^" + pattern.split("*").map((s) => s.replace(/[.+?^${}()|[\]\\]/g, "\\$&")).join(".*") + "$");
        return re.test(path);
    }
    return path.startsWith(pattern.endsWith("/") ? pattern : pattern + "/") || path === pattern;
}
/** Route rule lookup; first match wins. */
function routeAction(policy, path) {
    for (const r of policy.routes || [])
        if (routeMatches(r.match, path))
            return r.action;
    return undefined;
}
/**
 * Initial decision at session start, before any events. `random` is injected
 * for determinism in tests and is called at most once.
 */
function initialDecision(policy, ctx) {
    const ra = routeAction(policy, ctx.path);
    if (ra === "skip")
        return { decision: "skip", reason: "route:skip" };
    if (ra === "record")
        return { decision: "record", reason: "route:record" };
    if (ctx.endUserId && policy.alwaysRecordUsers?.includes(ctx.endUserId))
        return { decision: "record", reason: "always_record_user" };
    const mode = ra === "triggers-only" ? "triggers-only" : policy.mode;
    if (mode === "always")
        return { decision: "record", reason: "always" };
    // conditional / triggers-only: baseline roll happens once, up front, so the
    // outcome is known but only applied when the buffer fills without a trigger.
    const rate = mode === "triggers-only" ? 0 : policy.baselineSampleRate;
    if (rate >= 1)
        return { decision: "record", reason: "baseline" };
    const roll = rate > 0 && ctx.random() < rate;
    return { decision: "undecided", reason: roll ? "baseline:pass" : "baseline:fail" };
}
/** Detects rage clicks (≥3 within 1 s inside 30 px) and dead clicks (no reaction within 1 s). */
class ClickDetectors {
    onTrigger;
    setTimer;
    clearTimer;
    clicks = [];
    pending = [];
    lastRage = -Infinity;
    constructor(onTrigger, setTimer = (fn, ms) => setTimeout(fn, ms), clearTimer = (h) => clearTimeout(h)) {
        this.onTrigger = onTrigger;
        this.setTimer = setTimer;
        this.clearTimer = clearTimer;
    }
    /** Feed every event; returns nothing, fires onTrigger. */
    observe(e) {
        if (e.type === "pointer" && e.kind === "click") {
            this.clicks.push({ t: e.t, x: e.x, y: e.y });
            this.clicks = this.clicks.filter((c) => e.t - c.t <= 1000);
            const near = this.clicks.filter((c) => Math.hypot(c.x - e.x, c.y - e.y) <= 30);
            if (near.length >= 3 && e.t - this.lastRage > 1000) {
                this.lastRage = e.t;
                this.onTrigger("rage_click", { count: near.length, x: e.x, y: e.y, nodeId: e.id });
            }
            const timer = this.setTimer(() => {
                this.pending = this.pending.filter((p) => p.timer !== timer);
                this.onTrigger("dead_click", { x: e.x, y: e.y, nodeId: e.id });
            }, 1000);
            this.pending.push({ t: e.t, timer });
            return;
        }
        // Any of these within 1 s of a click means the click was alive.
        if (e.type === "mutation" || e.type === "navigation" || e.type === "network" || e.type === "scroll") {
            for (const p of this.pending)
                this.clearTimer(p.timer);
            this.pending = [];
        }
    }
    stop() {
        for (const p of this.pending)
            this.clearTimer(p.timer);
        this.pending = [];
    }
}
/** Maps an event to a trigger name when it satisfies one of the policy's triggers. */
function eventTrigger(policy, e) {
    const has = (t) => policy.triggers.includes(t);
    switch (e.type) {
        case "error":
            return has("error") ? "error" : undefined;
        case "network":
            return has("network_failure") && (e.failed || (e.status !== undefined && e.status >= 500)) ? "network_failure" : undefined;
        case "longtask":
            return has("long_task") && e.durationMs > 1000 ? "long_task" : undefined;
        case "custom": {
            const t = `custom:${e.name}`;
            return has(t) ? t : undefined;
        }
        default:
            return undefined;
    }
}

/**
 * Conditional recording (PLAN §19.2, TASKS T1.2): buffers events on the main
 * thread in a bounded ring until a decision is made. On "record" the ring is
 * flushed in order; on "skip" everything is dropped. In conditional mode a
 * failed baseline keeps the ring as pre-roll so a later trigger still records.
 */
class HoldBuffer {
    o;
    decision;
    reason;
    ring = [];
    bytes = 0;
    hasSnapshot = false;
    evictedSnapshot = false;
    baselinePass;
    detectors;
    windowTimer = null;
    maxBytes;
    routeSkip;
    constructor(o) {
        this.o = o;
        const r = initialDecision(o.policy, { path: o.path, endUserId: o.endUserId, random: o.random ?? Math.random });
        this.decision = r.decision;
        this.reason = r.reason;
        this.routeSkip = r.reason === "route:skip";
        this.baselinePass = r.reason === "baseline:pass" || r.reason === "baseline";
        this.maxBytes = o.maxBytes ?? 4 * 1024 * 1024;
        const setT = o.setTimer ?? ((fn, ms) => setTimeout(fn, ms));
        const clearT = o.clearTimer ?? ((h) => clearTimeout(h));
        this.detectors = new ClickDetectors((trigger, detail) => this.onTrigger(trigger, detail), setT, clearT);
        if (this.decision === "undecided") {
            this.windowTimer = setT(() => this.onWindowElapsed(), o.policy.bufferSeconds * 1000);
        }
    }
    /** The emit wrapper installed on the recorder. */
    emit = (e) => {
        if (this.decision === "record") {
            this.o.forward(e);
            return;
        }
        if (this.decision === "skip") {
            // route:skip never records; a plain skip (baseline fail) still watches triggers.
            if (this.routeSkip)
                return;
            this.push(e);
            this.detectors.observe(e);
            const trig = eventTrigger(this.o.policy, e);
            if (trig)
                this.onTrigger(trig, {});
            return;
        }
        // undecided
        this.push(e);
        this.detectors.observe(e);
        const trig = eventTrigger(this.o.policy, e);
        if (trig)
            this.onTrigger(trig, {});
    };
    push(e) {
        const size = JSON.stringify(e).length;
        if (e.type === "snapshot") {
            // A new snapshot supersedes everything before it.
            this.ring = [];
            this.bytes = 0;
            this.hasSnapshot = true;
            this.evictedSnapshot = false;
        }
        this.ring.push(e);
        this.bytes += size;
        while (this.bytes > this.maxBytes && this.ring.length > 1) {
            const dropped = this.ring.shift();
            this.bytes -= JSON.stringify(dropped).length;
            if (dropped.type === "snapshot" || (dropped.type === "control" && dropped.kind === "checkpoint")) {
                this.hasSnapshot = false;
                this.evictedSnapshot = true;
            }
        }
    }
    onWindowElapsed() {
        this.windowTimer = null;
        if (this.decision !== "undecided")
            return;
        if (this.baselinePass)
            this.decide("record", "baseline");
        else
            this.decide("skip", "baseline:fail");
    }
    onTrigger(trigger, detail) {
        if (this.decision === "record")
            return;
        if (!this.o.policy.triggers.includes(trigger))
            return; // detectors fire for every click; the policy decides
        this.decide("record", `trigger:${trigger}`, detail);
    }
    /** Force a decision (public API `forceRecord`). */
    force(reason) {
        if (this.decision === "record")
            return;
        this.decide("record", `forced:${reason}`);
    }
    decide(d, reason, detail = {}) {
        if (this.windowTimer) {
            (this.o.clearTimer ?? ((h) => clearTimeout(h)))(this.windowTimer);
            this.windowTimer = null;
        }
        this.reason = reason;
        if (d === "skip") {
            this.decision = "skip";
            // Keep the ring as pre-roll (bounded) for a later trigger.
            return;
        }
        this.decision = "record";
        this.detectors.stop();
        const decisionEvent = { type: "custom", t: this.o.now(), name: "backstory.sampling", props: { decision: "record", reason, ...detail } };
        if (this.hasSnapshot || !this.evictedSnapshot) {
            for (const e of this.ring)
                this.o.forward(e);
            this.o.forward(decisionEvent);
        }
        else {
            // Pre-roll lost its snapshot: drop it and start from a fresh checkpoint.
            this.o.forward(decisionEvent);
            this.o.requestCheckpoint();
        }
        this.ring = [];
        this.bytes = 0;
    }
    bufferedBytes() {
        return this.bytes;
    }
    stop() {
        this.detectors.stop();
        if (this.windowTimer)
            (this.o.clearTimer ?? ((h) => clearTimeout(h)))(this.windowTimer);
        this.windowTimer = null;
    }
}

/** Global Privacy Control signal (PLAN §8.4): treated as opt-out unless ignored. */
function gpcOptOut(nav, ignore) {
    if (ignore || !nav)
        return false;
    return nav.globalPrivacyControl === true;
}
/**
 * Attaches a consent-management-platform adapter. Returns a detach function.
 * Each adapter calls hooks.grant() when the required purposes are consented and
 * hooks.revoke() when they are withdrawn. Adapters never throw.
 */
function attachConsentAdapter(cfg, hooks, win = window) {
    try {
        switch (cfg.adapter) {
            case "tcf":
                return tcf(cfg, hooks, win);
            case "onetrust":
                return oneTrust(hooks, win);
            case "cookiebot":
                return cookiebot(hooks, win);
            case "osano":
                return osano(hooks, win);
            case "callback":
                cfg.callback?.(hooks.grant, hooks.revoke);
                return () => { };
            default:
                return () => { };
        }
    }
    catch {
        return () => { };
    }
}
// --- TCF 2.x --------------------------------------------------------------
function tcf(cfg, hooks, win) {
    const api = win.__tcfapi;
    if (typeof api !== "function")
        return () => { };
    let listenerId;
    let granted = null;
    api("addEventListener", 2, (data, ok) => {
        if (!ok || !data)
            return;
        if (data.listenerId !== undefined)
            listenerId = data.listenerId;
        const st = data.eventStatus;
        if (st !== "tcloaded" && st !== "useractioncomplete")
            return;
        if (data.gdprApplies === false) {
            if (granted !== true)
                hooks.grant();
            granted = true;
            return;
        }
        const consents = data.purpose?.consents || {};
        const all = cfg.purposeIds.every((id) => consents[String(id)] === true);
        if (all && granted !== true)
            hooks.grant();
        if (!all && granted !== false)
            hooks.revoke();
        granted = all;
    });
    return () => {
        if (listenerId !== undefined)
            api("removeEventListener", 2, () => { }, listenerId);
    };
}
// --- OneTrust ---------------------------------------------------------------
function oneTrustGroupsAllow(groups, required = ["C0002"]) {
    if (!groups)
        return false;
    const set = new Set(groups.split(",").map((s) => s.trim()).filter(Boolean));
    return required.every((g) => set.has(g));
}
function oneTrust(hooks, win) {
    const w = win;
    let last = null;
    const evaluate = () => {
        const ok = oneTrustGroupsAllow(w.OnetrustActiveGroups);
        if (ok && last !== true)
            hooks.grant();
        if (!ok && last === true)
            hooks.revoke();
        last = ok;
    };
    evaluate();
    const prev = w.OptanonWrapper;
    w.OptanonWrapper = function (...a) {
        try {
            if (typeof prev === "function")
                prev.apply(this, a);
        }
        finally {
            evaluate();
        }
    };
    const onChange = () => evaluate();
    win.addEventListener("OneTrustGroupsUpdated", onChange);
    return () => {
        win.removeEventListener("OneTrustGroupsUpdated", onChange);
        if (w.OptanonWrapper && prev !== undefined)
            w.OptanonWrapper = prev;
    };
}
// --- Cookiebot --------------------------------------------------------------
function cookiebot(hooks, win) {
    const w = win;
    let last = null;
    const evaluate = () => {
        const ok = !!w.Cookiebot?.consent?.statistics;
        if (ok && last !== true)
            hooks.grant();
        if (!ok && last === true)
            hooks.revoke();
        last = ok;
    };
    if (w.Cookiebot?.consented)
        evaluate();
    const onAccept = () => evaluate();
    const onDecline = () => {
        if (last !== false)
            hooks.revoke();
        last = false;
    };
    win.addEventListener("CookiebotOnAccept", onAccept);
    win.addEventListener("CookiebotOnDecline", onDecline);
    return () => {
        win.removeEventListener("CookiebotOnAccept", onAccept);
        win.removeEventListener("CookiebotOnDecline", onDecline);
    };
}
// --- Osano ------------------------------------------------------------------
function osano(hooks, win) {
    const cm = win.Osano?.cm;
    if (!cm || typeof cm.addEventListener !== "function")
        return () => { };
    let last = null;
    const evaluate = (consent) => {
        const c = consent || cm.getConsent?.() || {};
        const ok = c.ANALYTICS === "ACCEPT";
        if (ok && last !== true)
            hooks.grant();
        if (!ok && last === true)
            hooks.revoke();
        last = ok;
    };
    const handler = (c) => evaluate(c);
    cm.addEventListener("osano-cm-consent-saved", handler);
    cm.addEventListener("osano-cm-initialized", handler);
    if (cm.getConsent)
        evaluate();
    return () => {
        cm.removeEventListener?.("osano-cm-consent-saved", handler);
        cm.removeEventListener?.("osano-cm-initialized", handler);
    };
}

function cap(v) {
    if (typeof v === "boolean" || typeof v === "number")
        return v;
    return maskPII(String(v).slice(0, 128))[0];
}
class ApplicationCollector {
    o;
    timer = null;
    flags = {}; // provider -> flags
    experiments = {};
    off = [];
    constructor(o) {
        this.o = o;
    }
    start() {
        this.snapshot();
        this.timer = setInterval(() => this.snapshot(), this.o.cfg.intervalMinutes * 60_000);
    }
    stop() {
        if (this.timer)
            clearInterval(this.timer);
        for (const f of this.off)
            f();
        this.off = [];
    }
    /** Public API: `Backstory.setFlags({...})`. */
    setFlags(flags, provider = "custom") {
        const out = {};
        for (const k of Object.keys(flags).slice(0, 500))
            out[k] = cap(flags[k]);
        this.flags[provider] = out;
        this.emitFlags(provider, out);
    }
    setExperiments(exps) {
        const out = {};
        for (const k of Object.keys(exps).slice(0, 200))
            out[k] = cap(exps[k]);
        this.experiments = out;
        this.o.emit({ type: "application", t: this.o.now(), kind: "experiments", flags: out });
    }
    // --- flag provider adapters -------------------------------------------------
    attachLaunchDarkly(client) {
        const read = () => this.setFlags(client.allFlags() ?? {}, "launchdarkly");
        read();
        client.on?.("change", read);
        return () => { };
    }
    attachStatsig(client) {
        const read = () => {
            const snap = client.getSnapshot?.() ?? {};
            this.setFlags(snap, "statsig");
        };
        read();
        return () => { };
    }
    attachSplit(client, names) {
        const read = () => this.setFlags(client.getTreatments(names) ?? {}, "split");
        read();
        const ev = client.Event?.SDK_UPDATE ?? "state::update";
        client.on?.(ev, read);
        return () => { };
    }
    attachUnleash(client) {
        const read = () => {
            const out = {};
            for (const t of client.getAllToggles() ?? [])
                out[t.name] = t.variant?.name ? `${t.enabled}:${t.variant.name}` : t.enabled;
            this.setFlags(out, "unleash");
        };
        read();
        client.on?.("update", read);
        return () => { };
    }
    emitFlags(provider, flags) {
        this.o.emit({ type: "application", t: this.o.now(), kind: "flags", provider, flags });
    }
    /** Emit storage/cookies/service worker/env facts. */
    snapshot() {
        const win = this.o.win ?? (typeof window !== "undefined" ? window : undefined);
        if (!win)
            return;
        const t = this.o.now();
        const allow = new Set(this.o.cfg.storageAllowlist);
        for (const [name, store] of [
            ["localStorage", safe(() => win.localStorage)],
            ["sessionStorage", safe(() => win.sessionStorage)],
        ]) {
            if (!store)
                continue;
            const keys = [];
            const values = {};
            try {
                for (let i = 0; i < Math.min(store.length, 200); i++) {
                    const k = store.key(i);
                    if (!k)
                        continue;
                    if (k.startsWith("__backstory_"))
                        continue; // our own state
                    keys.push(k);
                    if (allow.has(k.toLowerCase()))
                        values[k] = maskPII(String(store.getItem(k) ?? "").slice(0, 256))[0];
                }
            }
            catch {
                /* storage blocked */
            }
            const e = { type: "application", t, kind: "storage", provider: name, keys };
            if (Object.keys(values).length)
                e.values = values;
            this.o.emit(e);
        }
        // Cookies: names only.
        const cookieNames = safe(() => (win.document?.cookie || "").split(";").map((c) => c.split("=")[0].trim()).filter(Boolean)) ?? [];
        this.o.emit({ type: "application", t, kind: "cookies", keys: cookieNames.slice(0, 100) });
        // Service worker.
        const sw = win.navigator.serviceWorker;
        if (sw) {
            const st = sw.controller?.state;
            this.o.emit({ type: "application", t, kind: "service_worker", swState: st ?? "none" });
        }
        // Environment facts.
        const nav = win.navigator;
        const env = {};
        if (nav.deviceMemory)
            env.deviceMemoryGB = String(nav.deviceMemory);
        if (nav.hardwareConcurrency)
            env.cores = String(nav.hardwareConcurrency);
        if (nav.connection?.effectiveType)
            env.effectiveType = nav.connection.effectiveType;
        if (nav.connection?.downlink)
            env.downlinkMbps = String(nav.connection.downlink);
        if (nav.connection?.saveData)
            env.saveData = "true";
        if (nav.language)
            env.language = nav.language;
        try {
            env.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        }
        catch {
            /* ignore */
        }
        this.o.emit({ type: "application", t, kind: "env", values: env });
        // Re-emit known flags so every checkpoint window has them.
        for (const p of Object.keys(this.flags))
            this.emitFlags(p, this.flags[p]);
        if (Object.keys(this.experiments).length)
            this.o.emit({ type: "application", t, kind: "experiments", flags: this.experiments });
    }
}
function safe(f) {
    try {
        return f();
    }
    catch {
        return undefined;
    }
}

let configured = null;
let modPromise = null;
function mod() {
    if (modPromise)
        return modPromise;
    const p = loadState().then((m) => {
        if (configured)
            m.configureState(configured);
        return m;
    });
    p.catch(() => {
        if (modPromise === p)
            modPromise = null;
    });
    modPromise = p;
    return p;
}
/** Called by the core when the recorder starts. */
function configureStateApi(cfg) {
    configured = cfg;
    if (modPromise)
        void modPromise.then((m) => m.configureState(cfg));
}
function deferred(f) {
    let detach = null;
    let cancelled = false;
    void mod()
        .then((m) => {
        if (cancelled)
            return;
        detach = f(m);
    })
        .catch((e) => console.warn("[backstory] state module unavailable", e));
    return () => {
        cancelled = true;
        detach?.();
    };
}
const stateApi = {
    /** Generic store capture; resolves once the module is loaded. */
    captureStore(o) {
        return mod().then((m) => m.captureStore(o));
    },
    /** Redux middleware, usable synchronously. */
    backstoryMiddleware(opts = {}) {
        return (api) => {
            let cap = null;
            const queued = [];
            void mod()
                .then((m) => {
                const c = m.captureStore({ store: "redux", getState: api.getState, ...opts });
                cap = c;
                for (const a of queued)
                    c.capture(a);
                queued.length = 0;
            })
                .catch(() => { });
            return (next) => (action) => {
                const result = next(action);
                const type = action?.type;
                const name = typeof type === "string" ? type : typeof action === "function" ? "thunk" : "dispatch";
                if (cap)
                    cap.capture(name);
                else if (queued.length < 100)
                    queued.push(name);
                return result;
            };
        };
    },
    attachRedux: (store, opts) => deferred((m) => m.attachRedux(store, opts)),
    attachZustand: (useStore, opts) => deferred((m) => m.attachZustand(useStore, opts)),
    attachNgrx: (store$, actions$, opts) => deferred((m) => m.attachNgrx(store$, actions$, opts)),
    attachMobx: (o) => deferred((m) => m.attachMobx(o)),
    attachQueryClient: (client, opts) => deferred((m) => m.attachQueryClient(client, opts)),
    attachApollo: (client, opts) => deferred((m) => m.attachApollo(client, opts)),
    attachReactDevtoolsHook: (o) => deferred((m) => m.attachReactDevtoolsHook(o)),
    /** Pinia plugin: `pinia.use(await Backstory.state.piniaPlugin())`, or use the `@backstory/browser/state` import for a sync version. */
    piniaPlugin(opts) {
        const pending = [];
        let plugin = null;
        void mod()
            .then((m) => {
            const pl = m.piniaPlugin(opts);
            plugin = pl;
            for (const c of pending)
                pl(c);
            pending.length = 0;
        })
            .catch(() => { });
        return (ctx) => {
            if (plugin)
                plugin(ctx);
            else
                pending.push(ctx);
        };
    },
};

function internals(r) {
    return r;
}
const VISIT_KEY = "__backstory_visit";
/**
 * Attaches console/network/vitals collectors, the consent adapter, and the
 * conditional-recording hold buffer to a started Recorder.
 */
function attachExtras(recorder, cfg, api, win = window, doc = document) {
    const r = internals(recorder);
    const now = () => Date.now();
    const getLevel = () => r.level ?? "HEALTHY";
    const netLevel = () => LEVEL_POLICY[getLevel()]?.network ?? "all";
    // --- sampling hold: wraps recorder.emit before collectors capture it -------
    let hold = null;
    const originalEmit = r.emit;
    // --- state & otel: every event that reaches the recorder is also offered to bus taps.
    const withTaps = (inner) => (e) => {
        bus.notify(e);
        inner(e);
    };
    // --- end state & otel ---
    const installHold = (policy) => {
        hold?.stop();
        if (policy.mode === "always" && !policy.routes?.length && !policy.alwaysRecordUsers?.length) {
            hold = null;
            r.emit = withTaps(originalEmit);
            return;
        }
        hold = new HoldBuffer({
            policy,
            path: win.location?.pathname || "/",
            endUserId: cfg.endUserId,
            forward: originalEmit,
            requestCheckpoint: () => void r.checkpoint("drop"),
            now,
        });
        r.emit = withTaps(hold.emit);
    };
    installHold(cfg.recording.sampling);
    // --- limits ---
    // A session cap, the trial ceiling, or overdue payment puts the
    // organization on triggers-only: baseline sessions are buffered and thrown
    // away, only a trigger uploads. When the limit lifts the local policy returns.
    let appliedPolicy = cfg.recording.sampling;
    let limitedSessions = false;
    const syncLimits = () => {
        const limited = recorder.getLimits().sessions === "triggers-only";
        if (limited === limitedSessions)
            return;
        limitedSessions = limited;
        installHold(limited ? { ...appliedPolicy, mode: "triggers-only", baselineSampleRate: 0 } : appliedPolicy);
    };
    const offLimits = recorder.onConfigNudge(syncLimits);
    syncLimits();
    // --- end limits ---
    // --- state & otel ---
    setModulesUrl(cfg.modulesUrl);
    configureStateApi(cfg.state);
    bus.attach((e) => r.emit(e), {
        sessionId: () => recorder.getSessionId(),
        visitId: () => recorder.getVisitId(),
        checkpointId: () => r.getCheckpointId?.() ?? 0,
        level: getLevel,
        projectKey: () => cfg.projectKey,
        apiUrl: () => cfg.apiUrl,
    });
    let application = null;
    let otelStop = null;
    const holder = { extras: null };
    // --- end state & otel ---
    // Collectors below capture `emit` lazily so a later policy swap still applies.
    const emit = (e) => r.emit(e);
    // --- collectors -------------------------------------------------------------
    const started = [];
    const startCollectors = () => {
        if (started.length)
            return;
        if (cfg.collectors.console) {
            const c = new ConsoleCollector({ emit, now, getLevel });
            c.start();
            started.push(c);
        }
        if (cfg.collectors.network && cfg.network.captureMeta) {
            const n = new NetworkCollector({ emit, now, cfg: cfg.network, ingestUrl: cfg.ingestUrl, apiUrl: cfg.apiUrl ?? deriveApiUrl(cfg.ingestUrl), win, getLevel: netLevel, debug: cfg.debug });
            n.start();
            r.getWebSockets = () => n.debugWebSockets();
            started.push(n);
            if (cfg.debug) {
                // Support diagnostic: record whether our instrumentation is still the
                // one the page calls, so an empty Network panel can be explained from
                // the session itself instead of from a console someone has to paste.
                const t = win.setTimeout(() => {
                    const i = n.instrumentation();
                    emit({ type: "custom", t: now(), name: "backstory.diagnostics", props: { collector: "network", fetch: i.fetch, xhr: i.xhr, ws: i.ws, hasConnectionApi: !!win.navigator.connection } });
                }, 5000);
                started.push({ stop: () => win.clearTimeout(t) });
            }
        }
        else if (cfg.debug) {
            // A silently absent collector is indistinguishable from an app that made
            // no requests, which sends people hunting through the player instead of
            // their configuration. Say which switch turned it off.
            const why = !cfg.collectors.network ? "collectors.network is false (check the project's remote config)" : "network.captureMeta is false";
            console.info(`[backstory] network collector off: ${why}`);
        }
        if (cfg.collectors.vitals || cfg.collectors.longTasks) {
            const v = new VitalsCollector({ emit, now, map: r.map, win, doc, longTasks: cfg.collectors.longTasks });
            v.start();
            started.push(v);
        }
        if (cfg.collectors.vitals) {
            const memory = new MemoryCollector({
                emit,
                now,
                win,
                recorderState: () => r.getDebugState(),
                recorderBudgetMB: cfg.recording.memoryBudgetMB,
                onCpu: (state) => recorder.setCpuState(state),
            });
            memory.start();
            started.push(memory);
        }
        // --- state & otel ---
        if (cfg.application.enabled) {
            application = new ApplicationCollector({ emit, now, cfg: cfg.application, win });
            application.start();
            started.push(application);
            if (holder.extras)
                holder.extras.application = application;
        }
        if (cfg.otel.enabled) {
            loadOtel()
                .then((m) => {
                const tracer = m.startOtel(cfg, "0.1.0" , win);
                if (tracer)
                    otelStop = () => m.stopOtel();
            })
                .catch((e) => console.warn("[backstory] otel module unavailable", e));
        }
        // --- end state & otel ---
    };
    const stopCollectors = () => {
        for (const s of started)
            s.stop();
        started.length = 0;
        r.getWebSockets = () => ({ active: 0, connections: 0, sent: 0, received: 0 });
        // --- state & otel ---
        application = null;
        if (holder.extras)
            holder.extras.application = null;
        otelStop?.();
        otelStop = null;
        // --- end state & otel ---
    };
    // --- consent ----------------------------------------------------------------
    let detachConsent = () => { };
    let optedOut = false;
    const gpc = gpcOptOut(win.navigator, cfg.consent.ignoreGPC);
    const grant = () => {
        if (optedOut)
            return;
        api.grant();
        startCollectors();
    };
    const revoke = () => {
        stopCollectors();
        api.revoke();
    };
    if (gpc) {
        // GPC = opt-out. Nothing is attached; the recorder never starts. Say so in
        // debug mode: a browser sending Global Privacy Control otherwise looks like
        // an SDK that silently stopped recording.
        if (cfg.debug)
            console.info("[backstory] recording off: this browser sends Global Privacy Control (set consent.ignoreGPC: true to record anyway)");
        revoke();
    }
    else if (cfg.consent.required) {
        detachConsent = attachConsentAdapter(cfg.consent, { grant, revoke }, win);
    }
    else {
        startCollectors();
    }
    const extras = {
        application: null,
        forceRecord(reason) {
            hold?.force(reason);
        },
        optOut() {
            optedOut = true;
            revoke(); // emits control:consent {granted:false} and ends the session
            try {
                win.sessionStorage?.removeItem(VISIT_KEY);
                win.localStorage?.setItem("__backstory_optout", "1");
            }
            catch {
                /* storage unavailable */
            }
            // Spill/remote workstreams wipe their own state on this event.
            try {
                win.dispatchEvent(new CustomEvent("backstory:optout"));
            }
            catch {
                /* ignore */
            }
            api.stop();
        },
        applySamplingPolicy(policy) {
            // --- limits ---
            appliedPolicy = policy;
            installHold(limitedSessions ? { ...policy, mode: "triggers-only", baselineSampleRate: 0 } : policy);
            // --- end limits ---
        },
        detach() {
            detachConsent();
            stopCollectors();
            // --- limits ---
            offLimits();
            // --- end limits ---
            hold?.stop();
            r.emit = originalEmit;
            bus.detach();
        },
    };
    holder.extras = extras;
    extras.application = application;
    return extras;
}
/** True when the user previously opted out on this origin (checked before init). */
function previouslyOptedOut(win = window) {
    try {
        return win.localStorage?.getItem("__backstory_optout") === "1";
    }
    catch {
        return false;
    }
}

function bootCanvas(recorder, cfg) {
    let collector = null;
    let started = false;
    let stopped = false;
    // A cap or an organization switch that stops canvas stays stopped for this
    // page load. The next page reads config again.
    let blocked = false;
    let tries = 0;
    let timer = null;
    // `isCanvasEnabled` folds in the local switch, the project's remote switch,
    // and the plan limit, so this is the single question the boot asks.
    const tryStart = () => {
        if (started)
            return true;
        if (stopped || !recorder.isCanvasEnabled())
            return false;
        started = true;
        loadCanvas()
            .then((m) => {
            if (!started)
                return;
            collector = m.attachCanvas(recorder, cfg);
        })
            .catch((e) => console.warn("[backstory] canvas module failed to load", e));
        return true;
    };
    const halt = () => {
        started = false;
        if (timer)
            clearTimeout(timer);
        timer = null;
        collector?.stop();
        collector = null;
    };
    const poll = () => {
        timer = null;
        if (stopped || tryStart() || ++tries > 15)
            return;
        timer = setTimeout(poll, 2000);
    };
    if (!tryStart())
        timer = setTimeout(poll, 2000);
    // Limits arrive with the config answer and on every ack. Once canvas is
    // stopped, it stays stopped: no more frames are encoded or sent.
    const unsubscribe = recorder.onConfigNudge(() => {
        if (stopped || blocked)
            return;
        if (started && !recorder.isCanvasEnabled()) {
            blocked = true;
            halt();
            return;
        }
        if (!started && recorder.isCanvasEnabled())
            tryStart();
    });
    return {
        markFrame: (c) => collector?.markFrame(c),
        list: () => collector?.debugList() ?? [],
        stop: () => {
            stopped = true;
            unsubscribe();
            halt();
        },
    };
}
// --- end canvas ---

function bootWebRtc(recorder, cfg) {
    let collector = null;
    let started = false;
    let tries = 0;
    let timer = null;
    const pending = [];
    let queuedGroup;
    let pictures = false;
    let blocked = false;
    const tryStart = () => {
        if (started)
            return true;
        if (blocked || !recorder.isWebRtcEnabled())
            return false;
        started = true;
        loadWebRtc()
            .then((m) => {
            if (!started)
                return;
            collector = m.attachWebRtc(recorder, cfg);
            if (queuedGroup !== undefined)
                collector.setGroup(queuedGroup);
            if (pictures)
                collector.allowPictures();
            for (const pc of pending.splice(0))
                collector.attach(pc);
        })
            .catch((e) => console.warn("[backstory] webrtc module failed to load", e));
        return true;
    };
    const halt = () => {
        started = false;
        if (timer)
            clearTimeout(timer);
        timer = null;
        collector?.stop();
        collector = null;
    };
    if (!tryStart()) {
        // Remote config may turn calls on after init (a flag flip, or a project
        // that only enables observability for a cohort).
        const poll = () => {
            if (tryStart() || ++tries > 15)
                return;
            timer = setTimeout(poll, 2000);
        };
        timer = setTimeout(poll, 2000);
    }
    // A plan that does not include call diagnostics stops collection for this
    // page load. The next page reads config again.
    const unsubscribe = recorder.onConfigNudge(() => {
        if (blocked)
            return;
        if (started && !recorder.isWebRtcEnabled()) {
            blocked = true;
            halt();
            pending.length = 0;
            return;
        }
        if (!started && recorder.isWebRtcEnabled())
            tryStart();
    });
    return {
        list: () => collector?.debugList() ?? [],
        attach: (pc) => {
            if (blocked || !recorder.isWebRtcEnabled())
                return;
            if (collector)
                collector.attach(pc);
            else {
                if (pending.length < 8)
                    pending.push(pc);
                tryStart();
            }
        },
        setGroup: (group) => {
            queuedGroup = group;
            collector?.setGroup(group);
            tryStart();
        },
        allowPictures: () => {
            pictures = true;
            collector?.allowPictures();
        },
        stop: () => {
            unsubscribe();
            halt();
            pending.length = 0;
        },
    };
}
// --- end webrtc ---

const IDLE_STATE = { connected: false, consent: { cobrowse: false, voice: false, annotate: false, control: false, record: false }, agent: null, code: null, controlRemainingMs: 0, voice: null };
function bootLive(recorder, cfg) {
    let live = null;
    let loading = null;
    let stopped = false;
    let blocked = false;
    let agentWasWaiting = false;
    // Live is on unless the snippet opted out, the project turned it off
    // centrally with the remote flag `live: false`, or the organization's plan
    // does not include it.
    const allowed = () => recorder.limitsReady() && recorder.getLimits().live.allowed;
    const enabled = () => cfg.live.enabled && !stopped && allowed() && recorder.getRemoteConfig()?.flags?.live !== false;
    const ensure = () => {
        if (blocked)
            return Promise.resolve(null);
        if (live)
            return Promise.resolve(live);
        if (!enabled()) {
            // Asked for explicitly (showCode/requestHelp) while the plan says no:
            // say so once, and hand back nothing to show.
            if (cfg.live.enabled && !stopped && recorder.limitsReady() && !recorder.getLimits().live.allowed)
                recorder.noteLimit("live", recorder.getLimits().live.reason);
            return Promise.resolve(null);
        }
        if (!loading) {
            loading = loadLive()
                .then((m) => {
                if (stopped)
                    return null;
                live = m.attachLive(recorder, cfg, { version: "0.1.0" });
                return live;
            })
                .catch((e) => {
                console.warn("[backstory] live module failed to load", e);
                loading = null;
                return null;
            });
        }
        return loading;
    };
    // Agent-waiting nudge: the gateway sets flags.liveAgentWaiting on the ingest
    // ack while an agent console is attached, so an idle page costs nothing and
    // the module loads the moment someone is actually on the other end.
    const check = () => {
        if (blocked)
            return;
        if (!enabled()) {
            // The plan changed or minutes ran out mid-assist: end it and drop the
            // module. It stays down until the page loads again.
            if (live && !allowed()) {
                blocked = true;
                live.destroy();
                live = null;
                loading = null;
                agentWasWaiting = false;
            }
            return;
        }
        const waiting = recorder.getRemoteConfig()?.flags?.liveAgentWaiting === true;
        if (!waiting) {
            agentWasWaiting = false;
            return;
        }
        if (agentWasWaiting)
            return;
        agentWasWaiting = true;
        void ensure().then((l) => l?.agentWaiting());
    };
    let unsubscribe = null;
    if (cfg.live.enabled) {
        unsubscribe = recorder.onConfigNudge(check);
        check();
    }
    return {
        showCode: () => ensure().then((l) => l?.showCode() ?? ""),
        requestHelp: (message) => ensure().then((l) => l?.requestHelp(message) ?? ""),
        end: () => live?.end(),
        getState: () => (enabled() || live ? (live?.getState() ?? IDLE_STATE) : null),
        stop: () => {
            stopped = true;
            unsubscribe?.();
            live?.destroy();
            live = null;
        },
    };
}
// --- end live ---

let extras = null;
let canvasHandle = null;
let webrtcHandle = null;
let liveHandle = null;
// --- end live ---
let recorder = null;
function warnOnce(msgs) {
    if (msgs.length && typeof console !== "undefined")
        console.warn("[backstory] config: " + msgs.join("; "));
}
const Backstory = {
    version: "0.1.0",
    /** Start recording. Never throws; invalid config logs one warning and records with defaults. */
    init(config) {
        if (recorder)
            return;
        if (typeof window === "undefined" || typeof document === "undefined")
            return;
        const { config: cfg, warnings } = resolveConfig(config);
        warnOnce(warnings);
        if (!cfg)
            return;
        // --- collectors & consent ---
        if (previouslyOptedOut())
            return; // user opted out on this origin; never start
        // --- end collectors & consent ---
        try {
            recorder = new Recorder(cfg);
            recorder.start();
            // --- collectors & consent ---
            extras = attachExtras(recorder, cfg, { grant: () => recorder?.grantConsent(), revoke: () => recorder?.revokeConsent(), stop: () => Backstory.stop() });
            // --- end collectors & consent ---
            // --- canvas ---
            canvasHandle = bootCanvas(recorder, cfg);
            // --- end canvas ---
            // --- webrtc ---
            webrtcHandle = bootWebRtc(recorder, cfg);
            // --- end webrtc ---
            // --- live ---
            liveHandle = bootLive(recorder, cfg);
            if (cfg.live.enabled && cfg.live.showCode)
                void liveHandle.showCode();
            // --- end live ---
        }
        catch (e) {
            console.warn("[backstory] failed to start", e);
            recorder = null;
        }
    },
    /**
     * Say who is using the app. `id` is your own identifier for the person and is
     * stored exactly as given; `name` is what the dashboard shows instead of the
     * id. A first sign-in stays on the session already open. Naming a different
     * person ends that session and starts a new one.
     */
    identify(id, name) {
        recorder?.identify(id, name);
    },
    /** Call on sign-out. Ends the session, forgets the person, and the next action starts an anonymous session. */
    reset() {
        recorder?.reset();
    },
    /**
     * Record an application-defined diagnostic event. Unlike console output,
     * these events are searchable, alertable, and shown in their own player tab.
     */
    track(name, props, options) {
        recorder?.track(name, props, options);
    },
    /** Framework integrations use this only when normal history instrumentation missed a route. */
    route(path) {
        recorder?.route(path);
    },
    checkpoint() {
        void recorder?.checkpoint("manual");
    },
    setMode(mode) {
        recorder?.setMode(mode);
    },
    consent: {
        grant() {
            recorder?.grantConsent();
        },
        revoke() {
            recorder?.revokeConsent();
        },
    },
    stop() {
        // --- live ---
        liveHandle?.stop();
        liveHandle = null;
        // --- end live ---
        // --- canvas ---
        canvasHandle?.stop();
        canvasHandle = null;
        // --- end canvas ---
        // --- webrtc ---
        webrtcHandle?.stop();
        webrtcHandle = null;
        // --- end webrtc ---
        // --- collectors & consent ---
        extras?.detach();
        extras = null;
        // --- end collectors & consent ---
        recorder?.stop();
        recorder = null;
    },
    // --- collectors & consent ---
    /** Opt the user out: stops recording, wipes local state, and never starts again on this origin. */
    optOut() {
        if (extras)
            extras.optOut();
        else {
            try {
                localStorage.setItem("__backstory_optout", "1");
                sessionStorage.removeItem("__backstory_visit");
            }
            catch {
                /* ignore */
            }
            Backstory.stop();
        }
    },
    /** Promote a conditionally recorded session to "record" (PLAN §19.2). */
    forceRecord(reason = "manual") {
        extras?.forceRecord(reason);
    },
    /** Apply a sampling policy at runtime (used by remote config). */
    applySamplingPolicy(policy) {
        extras?.applySamplingPolicy(policy);
    },
    // --- end collectors & consent ---
    // --- state & otel ---
    /** State bridges (Redux, Zustand, Pinia, NgRx, MobX, TanStack Query, Apollo, React tree). Sync versions: `@backstory/browser/state`. */
    state: stateApi,
    /** Feature flags / experiments visible in the Application panel. */
    setFlags(flags, provider = "custom") {
        extras?.application?.setFlags(flags, provider);
    },
    setExperiments(exps) {
        extras?.application?.setExperiments(exps);
    },
    flags: {
        launchDarkly(client) {
            extras?.application?.attachLaunchDarkly(client);
        },
        statsig(client) {
            extras?.application?.attachStatsig(client);
        },
        split(client, names) {
            extras?.application?.attachSplit(client, names);
        },
        unleash(client) {
            extras?.application?.attachUnleash(client);
        },
    },
    otel: {
        /** Flush pending spans now (e.g. before a hard navigation). */
        flush() {
            return loadOtel()
                .then((m) => m.getTracer()?.flush(true))
                .catch(() => undefined);
        },
    },
    // --- end state & otel ---
    // --- canvas ---
    canvas: {
        /** Developer hint: the frame on this canvas is complete (PLAN §6.3). */
        markFrame(canvas) {
            canvasHandle?.markFrame(canvas);
        },
        /** Every canvas on the page with its recordable state and reason (also shown in the debug panel). */
        list() {
            return canvasHandle?.list() ?? [];
        },
    },
    // --- end canvas ---
    // --- webrtc ---
    /**
     * WebRTC call observability (PLAN §5.7). Quality is metrics and state.
     * Pictures of the remote video are stored only after the project turns them
     * on and the page calls allowPictures() once the visitor agrees. Audio is never stored.
     */
    webrtc: {
        /** Every tracked peer connection with its live quality (also shown in the debug panel). */
        list() {
            return webrtcHandle?.list() ?? [];
        },
        /**
         * Instrument a peer connection the app created before the SDK loaded, or one
         * built with a constructor reference captured ahead of the patch.
         */
        attach(pc) {
            webrtcHandle?.attach(pc);
        },
        /**
         * Link this session into a multi-party call. Every later connection in this
         * session is grouped with the other sessions that use the same callGroupId.
         * Use stable application ids, never a person's name.
         */
        setGroup(group) {
            webrtcHandle?.setGroup(group);
        },
        /**
         * The visitor agreed to a few stored pictures of the remote video they are
         * watching. Nothing is stored until the project has also turned this on,
         * and visual-recording minutes remain. Audio is never included.
         */
        allowPictures() {
            webrtcHandle?.allowPictures();
        },
    },
    // --- end webrtc ---
    // --- live ---
    /** Live Assist: masked viewing, one support grant for chat/drawing/control, and a separate voice grant. */
    live: {
        /** Show a 6-digit help code the user reads to a support agent. Resolves to the code. */
        showCode() {
            return liveHandle?.showCode() ?? Promise.resolve("");
        },
        /** Ask for help (optionally with a message); also shows the code. */
        requestHelp(message) {
            return liveHandle?.requestHelp(message) ?? Promise.resolve("");
        },
        /** End the assist session and revoke every capability. */
        end() {
            liveHandle?.end();
        },
        /** Current consent state, agent, code and voice status; null when Live Assist is disabled. */
        getState() {
            return liveHandle?.getState() ?? null;
        },
    },
    // --- end live ---
    getSessionId() {
        return recorder?.getSessionId();
    },
    getVisitId() {
        return recorder?.getVisitId();
    },
    /**
     * Index of this browsing context within the visit. Same-origin tabs and
     * windows share a visit and each claim their own surface, starting at 0.
     */
    getSurfaceId() {
        return recorder?.getTabId();
    },
    getDebugState() {
        return recorder?.getDebugState() ?? null;
    },
    // --- transport & monitor ---
    /** Current recording mode ("normal" | "monitor"). */
    getMode() {
        return recorder?.getMode();
    },
    // --- end transport & monitor ---
};

const DEFAULT_NORMALIZE = { maxDepth: 8, maxString: 256, maxArray: 200, maxKeys: 200, allowPaths: [], denyPaths: [] };
const REDACTED = "[redacted]";
function escapePointer(seg) {
    return seg.replace(/~/g, "~0").replace(/\//g, "~1");
}
function pathAllowed(path, allow) {
    if (!allow.length)
        return true;
    // Keep a node when it is on the way to an allowed subtree or inside one.
    return allow.some((a) => path === a || path.startsWith(a + "/") || a.startsWith(path + "/") || path === "");
}
function pathDenied(path, deny) {
    return deny.some((d) => path === d || path.startsWith(d + "/"));
}
/**
 * Produce a plain, bounded, masked JSON value from arbitrary app state.
 * Functions, symbols, DOM nodes, and cycles are replaced with markers; strings
 * pass the PII detectors; depth/array/key caps apply.
 */
function normalize(value, opts = DEFAULT_NORMALIZE, path = "", seen = new WeakSet(), depth = 0) {
    if (pathDenied(path, opts.denyPaths))
        return REDACTED;
    if (value === null || value === undefined)
        return value ?? null;
    const t = typeof value;
    if (t === "string") {
        const s = value;
        const capped = s.length > opts.maxString ? s.slice(0, opts.maxString) + "…" : s;
        return maskPII(capped)[0];
    }
    if (t === "number")
        return Number.isFinite(value) ? value : String(value);
    if (t === "boolean")
        return value;
    if (t === "bigint")
        return String(value);
    if (t === "function" || t === "symbol")
        return `[${t}]`;
    if (depth >= opts.maxDepth)
        return "[depth]";
    const obj = value;
    if (seen.has(obj))
        return "[cycle]";
    if (typeof Node !== "undefined" && obj instanceof Node)
        return `[node ${obj.nodeName?.toLowerCase() ?? ""}]`;
    if (obj instanceof Date)
        return isNaN(obj.getTime()) ? null : obj.toISOString();
    if (obj instanceof Map)
        return normalize(Object.fromEntries(Array.from(obj.entries()).map(([k, v]) => [String(k), v])), opts, path, seen, depth);
    if (obj instanceof Set)
        return normalize(Array.from(obj.values()), opts, path, seen, depth);
    if (typeof ArrayBuffer !== "undefined" && (obj instanceof ArrayBuffer || ArrayBuffer.isView(obj)))
        return `[binary ${obj.byteLength}B]`;
    seen.add(obj);
    try {
        if (Array.isArray(obj)) {
            const out = [];
            const n = Math.min(obj.length, opts.maxArray);
            for (let i = 0; i < n; i++)
                out.push(normalize(obj[i], opts, `${path}/${i}`, seen, depth + 1));
            if (obj.length > n)
                out.push(`[+${obj.length - n} more]`);
            return out;
        }
        const out = {};
        let k = 0;
        for (const key of Object.keys(obj)) {
            const p = `${path}/${escapePointer(key)}`;
            if (!pathAllowed(p, opts.allowPaths))
                continue;
            if (k++ >= opts.maxKeys) {
                out["[more]"] = true;
                break;
            }
            out[key] = normalize(obj[key], opts, p, seen, depth + 1);
        }
        return out;
    }
    finally {
        seen.delete(obj);
    }
}
function isObj(v) {
    return v !== null && typeof v === "object" && !Array.isArray(v);
}
/**
 * JSON Patch diff between two normalized values. Arrays are diffed by index;
 * when more than half the elements changed (or the length changed by more
 * than half), the whole array is replaced.
 */
function diff(a, b, path = "", out = []) {
    if (a === b)
        return out;
    if (isObj(a) && isObj(b)) {
        for (const k of Object.keys(a)) {
            const p = `${path}/${escapePointer(k)}`;
            if (!(k in b))
                out.push({ op: "remove", path: p });
            else
                diff(a[k], b[k], p, out);
        }
        for (const k of Object.keys(b))
            if (!(k in a))
                out.push({ op: "add", path: `${path}/${escapePointer(k)}`, value: b[k] });
        return out;
    }
    if (Array.isArray(a) && Array.isArray(b)) {
        const n = Math.min(a.length, b.length);
        const max = Math.max(a.length, b.length);
        let changed = Math.abs(a.length - b.length);
        for (let i = 0; i < n; i++)
            if (JSON.stringify(a[i]) !== JSON.stringify(b[i]))
                changed++;
        if (max === 0)
            return out;
        // Small arrays are always diffed by index; larger ones are replaced when
        // more than half of the elements changed.
        if (max >= 4 && changed > max / 2) {
            out.push({ op: "replace", path: path || "", value: b });
            return out;
        }
        for (let i = 0; i < n; i++)
            diff(a[i], b[i], `${path}/${i}`, out);
        for (let i = n; i < b.length; i++)
            out.push({ op: "add", path: `${path}/${i}`, value: b[i] });
        for (let i = a.length - 1; i >= n; i--)
            out.push({ op: "remove", path: `${path}/${i}` });
        return out;
    }
    if (JSON.stringify(a) !== JSON.stringify(b))
        out.push({ op: "replace", path: path || "", value: b });
    return out;
}
/** Apply a patch (used in tests and by the Player's State panel). */
function applyPatch(base, ops) {
    let root = base === undefined ? undefined : JSON.parse(JSON.stringify(base));
    for (const op of ops) {
        if (op.path === "") {
            if (op.op === "remove")
                root = undefined;
            else
                root = op.value;
            continue;
        }
        const segs = op.path.split("/").slice(1).map((s) => s.replace(/~1/g, "/").replace(/~0/g, "~"));
        let cur = root;
        for (let i = 0; i < segs.length - 1; i++) {
            const s = segs[i];
            if (cur[s] === undefined)
                cur[s] = /^\d+$/.test(segs[i + 1]) ? [] : {};
            cur = cur[s];
        }
        const last = segs[segs.length - 1];
        if (op.op === "remove") {
            if (Array.isArray(cur))
                cur.splice(Number(last), 1);
            else
                delete cur[last];
        }
        else if (Array.isArray(cur) && op.op === "add")
            cur.splice(Number(last), 0, op.value);
        else
            cur[last] = op.value;
    }
    return root;
}
function byteLength(v) {
    try {
        return JSON.stringify(v)?.length ?? 0;
    }
    catch {
        return 0;
    }
}

let shared = {
    allowPaths: [],
    denyPaths: [],
    snapshotEverySec: 60,
    maxSnapshotBytes: 32 * 1024,
    maxDiffBytes: 8 * 1024,
};
/** Set by the core when the recorder starts with `state` config. */
function configureState(cfg) {
    shared = { ...shared, ...cfg };
}
/**
 * Generic store capture: full snapshot first (and periodically), JSON Patch
 * diffs on change, all masked and size-capped. Independent of any framework.
 */
function captureStore(o) {
    const now = o.now ?? (() => Date.now());
    const emit = o.emit ?? ((e) => bus.emit(e));
    const norm = { ...DEFAULT_NORMALIZE, allowPaths: o.allowPaths ?? shared.allowPaths, denyPaths: o.denyPaths ?? shared.denyPaths };
    const maxSnap = o.maxSnapshotBytes ?? shared.maxSnapshotBytes;
    const maxDiff = o.maxDiffBytes ?? shared.maxDiffBytes;
    const every = (o.snapshotEverySec ?? shared.snapshotEverySec) * 1000;
    const debounceMs = o.debounceMs ?? 50;
    let prev;
    let lastSnapshot = 0;
    let stopped = false;
    let pendingAction;
    let timer = null;
    let unsub = null;
    const bounded = (value) => {
        const size = byteLength(value);
        if (size <= maxSnap)
            return { value, truncated: 0 };
        // Second pass with tighter caps, then give up with a marker.
        const tight = normalize(value, { ...norm, maxDepth: 4, maxString: 64, maxArray: 20, maxKeys: 50 });
        const tsize = byteLength(tight);
        if (tsize <= maxSnap)
            return { value: tight, truncated: size - tsize };
        return { value: { "[truncated]": size }, truncated: size };
    };
    const snapshot = () => {
        if (stopped)
            return;
        let cur;
        try {
            cur = normalize(o.getState(), norm);
        }
        catch {
            return;
        }
        const b = bounded(cur);
        prev = b.value;
        lastSnapshot = now();
        const e = { type: "state", t: lastSnapshot, store: o.store, storeName: o.storeName, snapshot: b.value };
        if (b.truncated)
            e.truncated = b.truncated;
        emit(e);
    };
    const flush = () => {
        timer = null;
        if (stopped)
            return;
        const action = pendingAction;
        pendingAction = undefined;
        if (now() - lastSnapshot >= every || prev === undefined) {
            snapshot();
            if (action)
                emit({ type: "state", t: now(), store: o.store, storeName: o.storeName, action });
            return;
        }
        let cur;
        try {
            cur = normalize(o.getState(), norm);
        }
        catch {
            return;
        }
        const ops = diff(prev, cur);
        if (!ops.length && !action)
            return;
        const e = { type: "state", t: now(), store: o.store, storeName: o.storeName, action };
        if (ops.length) {
            const size = byteLength(ops);
            if (size > maxDiff) {
                // Too large to send as a diff: fall back to a bounded replace.
                const b = bounded(cur);
                e.diff = [{ op: "replace", path: "", value: b.value }];
                e.truncated = Math.max(size - maxDiff, b.truncated);
                prev = b.value;
            }
            else {
                e.diff = ops;
                prev = cur;
            }
        }
        emit(e);
    };
    const capture = (action) => {
        if (stopped)
            return;
        if (action)
            pendingAction = pendingAction ? `${pendingAction},${action}`.slice(0, 200) : action;
        if (timer)
            return;
        timer = setTimeout(flush, debounceMs);
    };
    snapshot();
    if (o.subscribe) {
        try {
            unsub = o.subscribe((action) => capture(action));
        }
        catch {
            unsub = null;
        }
    }
    return {
        capture,
        snapshot,
        stop() {
            stopped = true;
            if (timer)
                clearTimeout(timer);
            unsub?.();
        },
    };
}

/**
 * Framework adapters over captureStore(). All are dependency-free: the host
 * passes its own store objects and we only use their public surface.
 */
const reduxCaptures = new WeakMap();
/** Redux middleware: captures the action type and the state diff after each dispatch. */
function backstoryMiddleware(opts = {}) {
    return (api) => {
        let cap = reduxCaptures.get(api);
        if (!cap) {
            cap = captureStore({ store: "redux", getState: api.getState, ...opts });
            reduxCaptures.set(api, cap);
        }
        const c = cap;
        return (next) => (action) => {
            const result = next(action);
            const type = action?.type;
            c.capture(typeof type === "string" ? type : typeof action === "function" ? "thunk" : "dispatch");
            return result;
        };
    };
}
/** Subscribe-based Redux capture when adding middleware is not possible (actions appear as "update"). */
function attachRedux(store, opts = {}) {
    const cap = captureStore({ store: "redux", getState: store.getState, subscribe: (on) => store.subscribe(() => on("update")), ...opts });
    return () => cap.stop();
}
function attachZustand(useStore, opts = {}) {
    const cap = captureStore({ store: "zustand", getState: useStore.getState, subscribe: (on) => useStore.subscribe(() => on("setState")), ...opts });
    return () => cap.stop();
}
/** `pinia.use(piniaPlugin())` */
function piniaPlugin(opts = {}) {
    return ({ store }) => {
        const cap = captureStore({ store: "pinia", storeName: store.$id, getState: () => store.$state, ...opts });
        store.$subscribe((mutation) => {
            const ev = mutation.events;
            const key = Array.isArray(ev) ? ev.map((e) => e.key).filter(Boolean).join(",") : ev?.key;
            cap.capture(key ? `${mutation.type}:${key}` : mutation.type);
        }, { detached: true });
    };
}
function attachNgrx(store$, actions$, opts = {}) {
    let latest;
    let lastAction;
    const cap = captureStore({
        store: "ngrx",
        getState: () => latest,
        subscribe: (on) => {
            const s = store$.subscribe((v) => {
                latest = v;
                on(lastAction);
                lastAction = undefined;
            });
            return typeof s === "function" ? s : () => s.unsubscribe();
        },
        ...opts,
    });
    let a = null;
    if (actions$) {
        a = actions$.subscribe((act) => {
            lastAction = act?.type;
        });
    }
    return () => {
        cap.stop();
        if (a)
            typeof a === "function" ? a() : a.unsubscribe();
    };
}
// ---------------------------------------------------------------------------
// MobX: the host passes its `reaction` (or `autorun`) so we do not import mobx.
// ---------------------------------------------------------------------------
function attachMobx(o) {
    const { name, getSnapshot, reaction, ...rest } = o;
    const cap = captureStore({ store: "mobx", storeName: name, getState: getSnapshot, subscribe: (on) => reaction(() => JSON.stringify(getSnapshot()), () => on("reaction")), ...rest });
    return () => cap.stop();
}
/** Query keys, status, fetch status, and dataUpdatedAt; data only when `includeData` (masked, capped). */
function attachQueryClient(client, opts = {}) {
    const { includeData, ...rest } = opts;
    const view = () => {
        const out = {};
        for (const q of client.getQueryCache().getAll()) {
            out[q.queryHash] = {
                key: q.queryKey,
                status: q.state.status,
                fetchStatus: q.state.fetchStatus,
                dataUpdatedAt: q.state.dataUpdatedAt,
                error: q.state.error ? String(q.state.error.message ?? q.state.error) : undefined,
                ...(includeData ? { data: q.state.data } : {}),
            };
        }
        return out;
    };
    const cap = captureStore({
        store: "tanstack-query",
        getState: view,
        subscribe: (on) => client.getQueryCache().subscribe((ev) => on(ev.query ? `${ev.type}:${ev.query.queryHash.slice(0, 60)}` : ev.type)),
        ...rest,
    });
    return () => cap.stop();
}
function attachApollo(client, opts = {}) {
    const { includeEntities, pollMs = 5000, ...rest } = opts;
    const view = () => {
        const data = client.cache.extract() || {};
        if (includeEntities)
            return data;
        const keys = {};
        for (const k of Object.keys(data))
            keys[k] = Object.keys(data[k] ?? {}).length;
        return keys;
    };
    let timer = null;
    let restore = null;
    const cap = captureStore({
        store: "apollo",
        getState: view,
        subscribe: (on) => {
            const cache = client.cache;
            if (typeof cache.broadcastWatches === "function") {
                const orig = cache.broadcastWatches;
                cache.broadcastWatches = function (...a) {
                    const r = orig.apply(this, a);
                    on("broadcast");
                    return r;
                };
                restore = () => {
                    cache.broadcastWatches = orig;
                };
            }
            else {
                timer = setInterval(() => on("poll"), pollMs);
            }
            return () => {
                restore?.();
                if (timer)
                    clearInterval(timer);
            };
        },
        ...rest,
    });
    return () => cap.stop();
}

const REACT_TREE_LIMITS = { maxDepth: 25, maxNodes: 500 };
function nameOf(f) {
    const t = f.type;
    if (typeof t === "string")
        return t; // host element
    if (typeof t === "function")
        return t.displayName || t.name || "Anonymous";
    if (t && typeof t === "object") {
        if (t.displayName)
            return t.displayName;
        if (t.render)
            return t.render.displayName || t.render.name || "ForwardRef";
        if (t.type)
            return nameOf({ ...f, type: t.type }); // memo
        if (t._payload)
            return "Lazy";
    }
    switch (f.tag) {
        case 3:
            return "Root";
        case 7:
            return "Fragment";
        case 10:
            return "Provider";
        case 11:
            return "ForwardRef";
        case 13:
            return "Suspense";
        default:
            return null;
    }
}
/** Serialize a fiber tree into a compact TreeNode with caps. */
function serializeFiberTree(root, limits = REACT_TREE_LIMITS) {
    let count = 0;
    let truncated = false;
    const walk = (f, depth) => {
        if (count >= limits.maxNodes) {
            truncated = true;
            return null;
        }
        const name = nameOf(f);
        const node = { n: name ?? "?" };
        count++;
        if (f.key)
            node.k = String(f.key);
        if (f.memoizedProps && typeof f.memoizedProps === "object") {
            const keys = Object.keys(f.memoizedProps).filter((k) => k !== "children").slice(0, 20);
            if (keys.length)
                node.p = keys;
        }
        if (depth >= limits.maxDepth) {
            if (f.child)
                truncated = true;
            return node;
        }
        const children = [];
        for (let c = f.child; c; c = c.sibling) {
            const cn = walk(c, depth + 1);
            if (cn)
                children.push(cn);
            else
                break;
        }
        if (children.length)
            node.c = children;
        return node;
    };
    const tree = walk(root, 0) ?? { n: "Root" };
    return { tree, nodes: count, truncated };
}
/** Attach to `__REACT_DEVTOOLS_GLOBAL_HOOK__` if present; no-op otherwise. */
function attachReactDevtoolsHook(o = {}) {
    const win = o.win ?? (typeof window !== "undefined" ? window : undefined);
    const hook = o.hook ?? win?.__REACT_DEVTOOLS_GLOBAL_HOOK__;
    if (!hook || !hook.renderers || typeof hook.getFiberRoots !== "function") {
        if (typeof console !== "undefined")
            console.info("[backstory] React tree capture needs the React DevTools hook (install the extension or react-devtools-core in dev)");
        return () => { };
    }
    const now = o.now ?? (() => Date.now());
    const emit = o.emit ?? ((e) => bus.emit(e));
    let last = "";
    const snap = () => {
        try {
            const trees = [];
            let nodes = 0;
            let truncated = false;
            for (const id of hook.renderers.keys()) {
                for (const root of hook.getFiberRoots(id)) {
                    const r = serializeFiberTree(root.current);
                    trees.push(r.tree);
                    nodes += r.nodes;
                    truncated = truncated || r.truncated;
                }
            }
            const payload = { roots: trees, nodes, truncated };
            const s = JSON.stringify(payload);
            if (s === last)
                return;
            last = s;
            const e = { type: "state", t: now(), store: "react", snapshot: payload };
            if (truncated)
                e.truncated = 1;
            emit(e);
        }
        catch {
            /* never break the app */
        }
    };
    snap();
    const timer = setInterval(snap, o.intervalMs ?? 10_000);
    const untap = bus.tap((e) => {
        if (e.type === "navigation")
            setTimeout(snap, 250);
    });
    return () => {
        clearInterval(timer);
        untap();
    };
}

/**
 * State bridges module: `@backstory/browser/state` and `Backstory.state.*`.
 * Loaded lazily by the script-tag bundle; imported statically by the ESM build.
 */

var index$1 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    applyPatch: applyPatch,
    attachApollo: attachApollo,
    attachMobx: attachMobx,
    attachNgrx: attachNgrx,
    attachQueryClient: attachQueryClient,
    attachReactDevtoolsHook: attachReactDevtoolsHook,
    attachRedux: attachRedux,
    attachZustand: attachZustand,
    backstoryMiddleware: backstoryMiddleware,
    captureStore: captureStore,
    configureState: configureState,
    diff: diff,
    normalize: normalize,
    piniaPlugin: piniaPlugin,
    serializeFiberTree: serializeFiberTree
});

const HEX = "0123456789abcdef";
function randomHex(bytes) {
    const arr = new Uint8Array(bytes);
    if (typeof crypto !== "undefined" && crypto.getRandomValues)
        crypto.getRandomValues(arr);
    else
        for (let i = 0; i < bytes; i++)
            arr[i] = (Math.random() * 256) | 0;
    let s = "";
    for (const b of arr)
        s += HEX[b >> 4] + HEX[b & 15];
    return s;
}
const newTraceId = () => randomHex(16);
const newSpanId = () => randomHex(8);
/** Glob match on origins: "*.example.com" and "https://api.example.com". */
function originMatches(origin, pattern) {
    if (pattern === "*")
        return true;
    const host = origin.replace(/^https?:\/\//, "");
    const p = pattern.replace(/^https?:\/\//, "");
    if (!p.includes("*"))
        return host === p || origin === pattern;
    const re = new RegExp("^" + p.split("*").map((s) => s.replace(/[.+?^${}()|[\]\\]/g, "\\$&")).join(".*") + "$");
    return re.test(host) || re.test(origin);
}
function ns(ms) {
    // Avoid float precision issues: integer ms * 1e6 as a string.
    return `${Math.round(ms)}000000`;
}
function attrList(a) {
    return Object.entries(a).map(([key, v]) => ({
        key,
        value: typeof v === "string" ? { stringValue: v } : typeof v === "boolean" ? { boolValue: v } : Number.isInteger(v) ? { intValue: String(v) } : { doubleValue: v },
    }));
}
/** OTLP/JSON ExportTraceServiceRequest. */
function toOtlp(spans, resource, version) {
    return {
        resourceSpans: [
            {
                resource: { attributes: attrList(resource) },
                scopeSpans: [
                    {
                        scope: { name: "@backstory/browser", version },
                        spans: spans.map((s) => ({
                            traceId: s.traceId,
                            spanId: s.spanId,
                            parentSpanId: s.parentSpanId,
                            name: s.name,
                            kind: s.kind,
                            startTimeUnixNano: ns(s.start),
                            endTimeUnixNano: ns(s.end ?? s.start),
                            attributes: attrList(s.attrs),
                            status: { code: s.status === "error" ? 2 : s.status === "ok" ? 1 : 0 },
                        })),
                    },
                ],
            },
        ],
    };
}
const INTERACTION_WINDOW_MS = 2000;
class Tracer {
    o;
    queue = [];
    open = new Map(); // reqId -> span
    interaction = null;
    pageSpan = null;
    timer = null;
    untap = null;
    previousSession;
    now;
    exported = 0;
    dropped = 0;
    constructor(o) {
        this.o = o;
        this.now = o.now ?? (() => Date.now());
    }
    start() {
        const w = this.o.win ?? (typeof window !== "undefined" ? window : undefined);
        if (this.o.externalApi) {
            // Do not create spans: enrich the active span on our events.
            this.untap = bus.tap((e) => this.enrichExternal(e));
            return;
        }
        // Page load root span.
        const t0 = w?.performance?.timeOrigin ?? this.now();
        this.pageSpan = this.span("page.load", 1, undefined, t0);
        this.pageSpan.attrs["http.url"] = strip(w?.location?.href ?? "");
        const onLoad = () => {
            if (!this.pageSpan)
                return;
            this.endSpan(this.pageSpan);
            this.pageSpan = null;
        };
        if (w && w.document && w.document.readyState === "complete")
            setTimeout(onLoad, 0);
        else
            w?.addEventListener("load", onLoad, { once: true });
        setBeforeRequest((ctx) => this.onRequest(ctx));
        this.untap = bus.tap((e) => this.onEvent(e));
    }
    stop() {
        setBeforeRequest(null);
        this.untap?.();
        this.untap = null;
        if (this.timer)
            clearTimeout(this.timer);
        void this.flush(true);
    }
    common() {
        const a = {};
        const s = this.o.session();
        if (s)
            a["session.id"] = s;
        if (this.previousSession)
            a["session.previous_id"] = this.previousSession;
        const v = this.o.visit();
        if (v)
            a["backstory.visit_id"] = v;
        a["backstory.checkpoint_id"] = this.o.checkpoint();
        return a;
    }
    span(name, kind, parent, start = this.now()) {
        return {
            traceId: parent ? parent.traceId : newTraceId(),
            spanId: newSpanId(),
            parentSpanId: parent?.spanId,
            name,
            kind,
            start,
            attrs: this.common(),
        };
    }
    endSpan(s, end = this.now()) {
        s.end = end;
        if (!this.o.cfg.exportSpans)
            return;
        const lvl = this.o.level();
        if (lvl === "SURVIVAL" || lvl === "PAUSED") {
            this.dropped++;
            return;
        }
        this.queue.push(s);
        if (this.queue.length >= this.o.cfg.batchSize)
            void this.flush();
        else if (!this.timer)
            this.timer = setTimeout(() => void this.flush(), this.o.cfg.batchMs);
    }
    shouldPropagate(url) {
        let origin = "";
        try {
            origin = new URL(url, this.o.ownOrigin || "http://localhost/").origin;
        }
        catch {
            return false;
        }
        if (this.o.cfg.propagateSameOrigin && origin === this.o.ownOrigin)
            return true;
        return this.o.cfg.propagateTraceContext.some((p) => originMatches(origin, p));
    }
    /** Network pre-request hook: create a client span; inject headers when allowed. */
    onRequest(ctx) {
        const parent = this.interaction && this.now() - this.interaction.start <= INTERACTION_WINDOW_MS ? this.interaction : this.pageSpan ?? undefined;
        const s = this.span(`HTTP ${ctx.method}`, 3, parent);
        s.attrs["http.request.method"] = ctx.method;
        s.attrs["url.full"] = strip(ctx.url);
        this.open.set(ctx.reqId, s);
        if (this.open.size > 500)
            this.open.delete(this.open.keys().next().value);
        if (!this.shouldPropagate(ctx.url))
            return;
        if (ctx.headers.some(([k]) => k.toLowerCase() === "traceparent"))
            return; // app already propagates
        const extra = [["traceparent", `00-${s.traceId}-${s.spanId}-01`]];
        const sid = this.o.session();
        if (sid)
            extra.push(["baggage", `session.id=${encodeURIComponent(sid)}`]);
        return extra;
    }
    onEvent(e) {
        switch (e.type) {
            case "network": {
                const s = this.open.get(e.reqId);
                if (!s)
                    return;
                if (e.kind === "ws" && e.method === "OPEN")
                    return; // wait for close
                this.open.delete(e.reqId);
                if (e.status)
                    s.attrs["http.response.status_code"] = e.status;
                if (e.failed || (e.status && e.status >= 500))
                    s.status = "error";
                else if (e.status)
                    s.status = "ok";
                this.endSpan(s, s.start + (e.durationMs ?? 0));
                return;
            }
            case "pointer": {
                if (e.kind !== "click")
                    return;
                if (this.interaction)
                    this.endSpan(this.interaction, Math.min(this.now(), this.interaction.start + INTERACTION_WINDOW_MS));
                const s = this.span("user.interaction", 1);
                s.attrs["ui.interaction"] = "click";
                if (e.id)
                    s.attrs["ui.target.node_id"] = e.id;
                this.interaction = s;
                setTimeout(() => {
                    if (this.interaction === s) {
                        this.endSpan(s);
                        this.interaction = null;
                    }
                }, INTERACTION_WINDOW_MS);
                return;
            }
            case "vital": {
                if (e.name === "INP" && this.interaction)
                    this.interaction.attrs["ui.inp_ms"] = e.value;
                return;
            }
            case "navigation": {
                if (e.kind === "load")
                    return;
                const s = this.span("navigation", 1);
                s.attrs["http.url"] = strip(e.url);
                if (e.route)
                    s.attrs["http.route"] = e.route;
                this.endSpan(s);
                return;
            }
            case "control": {
                if (e.kind === "segment_rollover")
                    this.previousSession = this.o.session();
                return;
            }
            default:
                return;
        }
    }
    enrichExternal(e) {
        if (e.type !== "navigation" && !(e.type === "control" && e.kind === "checkpoint"))
            return;
        try {
            const span = this.o.externalApi?.trace?.getActiveSpan?.();
            if (!span?.setAttribute)
                return;
            for (const [k, v] of Object.entries(this.common()))
                span.setAttribute(k, v);
        }
        catch {
            /* ignore */
        }
    }
    async flush(final = false) {
        if (this.timer) {
            clearTimeout(this.timer);
            this.timer = null;
        }
        if (!this.queue.length)
            return;
        const spans = this.queue.splice(0, this.o.cfg.batchSize * 2);
        const body = JSON.stringify(toOtlp(spans, { "service.name": this.o.cfg.serviceName, "telemetry.sdk.name": "backstory", "telemetry.sdk.language": "webjs", "telemetry.sdk.version": this.o.sdkVersion }, this.o.sdkVersion));
        const f = this.o.fetchFn ?? (typeof fetch !== "undefined" ? fetch.bind(globalThis) : undefined);
        if (!f)
            return;
        try {
            await f(this.o.endpoint, {
                method: "POST",
                headers: { "Content-Type": "application/json", "X-Backstory-Project-Key": this.o.projectKey },
                body,
                keepalive: final || body.length < 60_000,
            });
            this.exported += spans.length;
        }
        catch {
            this.dropped += spans.length; // best effort; never retried (telemetry, not replay data)
        }
    }
    /** Test helper. */
    _pending() {
        return this.queue.slice();
    }
}
function strip(u) {
    try {
        const x = new URL(u);
        return x.origin + x.pathname;
    }
    catch {
        return u.split("?")[0];
    }
}
/** Detect an existing OpenTelemetry JS API on the page. */
function detectExternalOtel(win = typeof window !== "undefined" ? window : undefined) {
    if (!win)
        return null;
    const g = win;
    const sym = Symbol.for("opentelemetry.js.api.1");
    const api = (g[sym] ?? g.__OTEL_API__ ?? g.otel);
    return api && typeof api === "object" && "trace" in api ? api : null;
}

let active = null;
function defaultEndpoint(cfg) {
    const base = cfg.apiUrl ?? cfg.ingestUrl.replace(/\/v1\/ingest\/?$/, "");
    return base.replace(/\/$/, "") + "/v1/otlp/traces";
}
/** Start the tracer against the running recorder (facts come from the bus). */
function startOtel(cfg, sdkVersion, win = window) {
    if (active)
        return active;
    const facts = bus.facts();
    if (!facts)
        return null;
    const tracer = new Tracer({
        cfg: cfg.otel,
        projectKey: cfg.projectKey,
        sdkVersion,
        endpoint: cfg.otel.endpoint ?? defaultEndpoint(cfg),
        ownOrigin: safeOrigin(win.location?.href ?? ""),
        session: facts.sessionId,
        visit: facts.visitId,
        checkpoint: facts.checkpointId,
        level: facts.level,
        win,
        externalApi: detectExternalOtel(win),
    });
    tracer.start();
    active = tracer;
    return tracer;
}
function stopOtel() {
    active?.stop();
    active = null;
}
function getTracer() {
    return active;
}
function safeOrigin(u) {
    try {
        return new URL(u).origin;
    }
    catch {
        return "";
    }
}

var index = /*#__PURE__*/Object.freeze({
    __proto__: null,
    INTERACTION_WINDOW_MS: INTERACTION_WINDOW_MS,
    Tracer: Tracer,
    defaultEndpoint: defaultEndpoint,
    detectExternalOtel: detectExternalOtel,
    getTracer: getTracer,
    originMatches: originMatches,
    startOtel: startOtel,
    stopOtel: stopOtel,
    toOtlp: toOtlp
});

var workerSource = "/* Backstory SDK v0.1.0 — backstory.io */\n!function(){\"use strict\";function e(e){const t=Math.floor(e.length/4),n=new Uint8Array(t);for(let r=0;r<t;r++){const t=e[4*r],a=e[4*r+1],o=e[4*r+2];n[r]=77*t+151*a+28*o>>8>>3}return function(e,t=2166136261){let n=t>>>0;for(let t=0;t<e.length;t++)n^=255&e[t],n=Math.imul(n,16777619)>>>0;return n.toString(36).padStart(7,\"0\")}(n)}const t=\"ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/\";function n(e){const n=e instanceof Uint8Array?e:new Uint8Array(e);let r=\"\",a=0;for(;a+2<n.length;a+=3){const e=n[a]<<16|n[a+1]<<8|n[a+2];r+=t[e>>18&63]+t[e>>12&63]+t[e>>6&63]+t[63&e]}if(a<n.length){const e=n.length-a,o=n[a]<<16|(e>1?n[a+1]:0)<<8;r+=t[o>>18&63]+t[o>>12&63]+(e>1?t[o>>6&63]:\"=\")+\"=\"}return r}const r=[{name:\"av1\",codec:\"av01.0.04M.08\"},{name:\"vp9\",codec:\"vp09.00.10.08\"},{name:\"h264\",codec:\"avc1.42001f\"}];const a=function(t,a=globalThis){const o=new Map,c=\"function\"==typeof a.OffscreenCanvas?new a.OffscreenCanvas(32,32):null,i=c?.getContext(\"2d\",{willReadFrequently:!0});let s=null;function d(e){try{e.encoder?.close()}catch{}e.encoder=null,e.codec=null,e.encKey=\"\"}async function f(e,o,c,i){const f=await async function(){return\"function\"!=typeof a.VideoEncoder?null:(s||(s=(async()=>{for(const e of r)try{const t=await a.VideoEncoder.isConfigSupported({codec:e.codec,width:320,height:240,bitrate:2e5,framerate:8});if(t?.supported)return e}catch{}return null})()),s)}();if(!f)return!1;const l=`${f.codec}:${o.w}x${o.h}:${c}:${i}`;if(o.encoder&&o.encKey===l&&\"configured\"===o.encoder.state)return!0;d(o);const u=new a.VideoEncoder({output:(r,a)=>{const c=new Uint8Array(r.byteLength);r.copyTo(c);const i=a?.decoderConfig?.description;i&&(o.desc=n(i instanceof ArrayBuffer?i:new Uint8Array(i.buffer,i.byteOffset,i.byteLength))),t({type:\"frame\",id:e,codec:f.name,keyframe:\"key\"===r.type,w:o.w,h:o.h,data:n(c),desc:\"key\"===r.type?o.desc:void 0,ts:Math.round(r.timestamp/1e3),bytes:c.length})},error:n=>t({type:\"error\",id:e,message:String(n?.message||n)})});return u.configure({codec:f.codec,width:o.w,height:o.h,bitrate:Math.max(5e4,1e3*c),framerate:Math.max(1,i),latencyMode:\"realtime\"}),o.encoder=u,o.codec=f.name,o.encKey=l,o.pendingKey=!0,!0}async function l(r){const s=r.frame??r.bitmap,d=Math.max(1,Math.round(r.w*r.scale)),l=Math.max(1,Math.round(r.h*r.scale)),u=function(e,t,n){if(\"function\"!=typeof a.OffscreenCanvas)return null;let r=o.get(e);if(!r){const c=new a.OffscreenCanvas(t,n);r={canvas:c,ctx:c.getContext(\"2d\"),w:t,h:n,lastHash:\"\",encoder:null,codec:null,encKey:\"\",pendingKey:!0},o.set(e,r)}return r.w===t&&r.h===n||(r.canvas.width=t,r.canvas.height=n,r.w=t,r.h=n,r.pendingKey=!0),r}(r.id,d,l);try{if(!s||!u)return void t({type:\"dropped\",id:r.id,reason:\"error\"});if(i&&c){i.drawImage(s,0,0,32,32);const n=e(i.getImageData(0,0,32,32).data);if(!r.keyframe&&!u.pendingKey&&n===u.lastHash)return void t({type:\"dropped\",id:r.id,reason:\"unchanged\"});u.lastHash=n}u.ctx.drawImage(s,0,0,d,l);const o=r.keyframe||u.pendingKey;if(!r.webp&&await f(r.id,u,r.kbps,r.fps)&&u.encoder&&\"function\"==typeof a.VideoFrame){const e=new a.VideoFrame(u.canvas,{timestamp:Math.round(1e3*r.ts)});return u.encoder.encode(e,{keyFrame:o}),e.close(),void(u.pendingKey=!1)}const y=await u.canvas.convertToBlob({type:\"image/webp\",quality:r.quality}),h=await function(e,t=globalThis){return\"function\"==typeof e.arrayBuffer?e.arrayBuffer().then(e=>new Uint8Array(e)):new Promise((n,r)=>{const a=t.FileReader??globalThis.FileReader;if(!a)return r(new Error(\"no arrayBuffer/FileReader\"));const o=new a;o.onload=()=>n(new Uint8Array(o.result)),o.onerror=()=>r(o.error),o.readAsArrayBuffer(e)})}(y,a);u.pendingKey=!1,t({type:\"frame\",id:r.id,codec:\"webp\",keyframe:!0,w:d,h:l,data:n(h),ts:r.ts,bytes:h.length})}catch(e){t({type:\"error\",id:r.id,message:String(e?.message||e)})}finally{try{r.frame?.close(),r.bitmap?.close()}catch{}}}return{handle(e){if(\"encode\"===e.type)l(e);else if(\"close\"===e.type){const t=o.get(e.id);t&&d(t),o.delete(e.id)}else if(\"flush\"===e.type){const t=o.get(e.id);t?.encoder?.flush().catch(()=>{})}else if(\"reset\"===e.type){for(const e of o.values())d(e);o.clear()}},_state:o}}(e=>postMessage(e));onmessage=e=>a.handle(e.data)}();\n";

class VisibilityTracker {
    o;
    states = new WeakMap();
    list = new Set();
    io = null;
    pageVisible = true;
    timer = null;
    doc;
    win;
    onVis = () => {
        this.pageVisible = this.doc.visibilityState !== "hidden";
        this.evaluateAll();
    };
    constructor(o) {
        this.o = o;
        this.doc = o.doc ?? document;
        this.win = o.win ?? window;
        this.pageVisible = this.doc.visibilityState !== "hidden";
        const IO = this.win.IntersectionObserver;
        if (IO) {
            this.io = new IO((entries) => {
                for (const e of entries) {
                    const st = this.states.get(e.target);
                    if (st) {
                        st.nearViewport = e.isIntersecting;
                        this.evaluate(st);
                    }
                }
            }, { rootMargin: "25%" });
        }
        this.doc.addEventListener("visibilitychange", this.onVis);
    }
    /** Start tracking a canvas (idempotent). */
    track(el) {
        let st = this.states.get(el);
        if (st)
            return st;
        const id = this.o.idOf(el) ?? 0;
        st = {
            id,
            el,
            allowed: false,
            phi: false,
            attached: false,
            rendered: false,
            sized: false,
            nearViewport: this.io ? false : true,
            recordable: false,
            reason: "disabled",
            ctxType: "unknown",
            strategy: "skip",
            level: 6,
        };
        this.states.set(el, st);
        this.list.add(el);
        this.io?.observe(el);
        this.evaluate(st, true);
        return st;
    }
    untrack(el) {
        const st = this.states.get(el);
        if (!st)
            return;
        this.io?.unobserve(el);
        this.list.delete(el);
        const prev = st.recordable;
        st.recordable = false;
        st.reason = "detached";
        if (prev)
            this.o.onChange(st, prev);
        this.states.delete(el);
    }
    get(el) {
        return this.states.get(el);
    }
    all() {
        const out = [];
        for (const el of this.list) {
            const st = this.states.get(el);
            if (st)
                out.push(st);
        }
        return out;
    }
    /** Called after DOM mutations that may affect canvases (debounced 2 s). */
    scheduleReevaluate() {
        if (this.timer)
            return;
        this.timer = this.win.setTimeout(() => {
            this.timer = null;
            this.evaluateAll();
        }, this.o.debounceMs ?? 2000);
    }
    evaluateAll() {
        for (const el of this.list) {
            const st = this.states.get(el);
            if (st)
                this.evaluate(st);
        }
    }
    /** Re-run every test for one canvas; notifies on recordable transitions. */
    evaluate(st, initial = false) {
        const el = st.el;
        const prev = st.recordable;
        if (!st.id)
            st.id = this.o.idOf(el) ?? 0;
        const attr = el.getAttribute("data-backstory-canvas");
        st.phi = attr === "record-phi";
        st.allowed = attr !== null ? attr !== "skip" && attr !== "false" : this.o.autoDetect && !this.o.strictMode;
        st.attached = el.isConnected;
        st.rendered = st.attached ? isRendered(el) : false;
        const rect = st.attached ? safeRect(el) : null;
        st.sized = !!rect && rect.width > 1 && rect.height > 1 && el.width > 0 && el.height > 0;
        if (!this.io && st.attached && rect) {
            // No IntersectionObserver: approximate with the viewport rectangle + 25% margin.
            const vw = this.win.innerWidth || 1024;
            const vh = this.win.innerHeight || 768;
            st.nearViewport = rect.bottom >= -vh * 0.25 && rect.top <= vh * 1.25 && rect.right >= -vw * 0.25 && rect.left <= vw * 1.25;
        }
        let reason = "recording";
        if (!st.attached)
            reason = "detached";
        else if (!st.allowed)
            reason = attr === null && this.o.strictMode && this.o.autoDetect ? "strict-mode" : "not-allowlisted";
        else if (!st.rendered)
            reason = "hidden";
        else if (!st.sized)
            reason = "zero-size";
        else if (!this.pageVisible)
            reason = "page-hidden";
        else if (!st.nearViewport)
            reason = "offscreen";
        st.reason = reason;
        st.recordable = reason === "recording";
        if (st.recordable !== prev || initial)
            this.o.onChange(st, prev);
    }
    destroy() {
        this.doc.removeEventListener("visibilitychange", this.onVis);
        if (this.timer)
            this.win.clearTimeout(this.timer);
        this.io?.disconnect();
        this.list.clear();
    }
}
function safeRect(el) {
    try {
        return el.getBoundingClientRect();
    }
    catch {
        return null;
    }
}
/** checkVisibility() where available, else a computed-style walk up the tree. */
function isRendered(el) {
    const cv = el.checkVisibility;
    if (typeof cv === "function") {
        try {
            return cv.call(el, { checkOpacity: true, checkVisibilityCSS: true, contentVisibilityAuto: true, opacityProperty: true, visibilityProperty: true });
        }
        catch {
            /* fall through */
        }
    }
    const win = el.ownerDocument.defaultView;
    if (!win)
        return false;
    let node = el;
    let depth = 0;
    while (node && depth++ < 64) {
        if (node.hidden)
            return false;
        const cs = win.getComputedStyle(node);
        if (cs.display === "none" || cs.visibility === "hidden" || cs.visibility === "collapse")
            return false;
        if (cs.opacity !== "" && Number(cs.opacity) === 0)
            return false;
        if (cs.contentVisibility === "hidden")
            return false;
        const parent = node.parentElement ?? (node.getRootNode().host ?? null);
        node = parent;
    }
    return true;
}

/** PLAN §6.5 quality ladder. */
const LADDER = [
    { level: 0, fps: 15, scale: 1.0, kbps: 800, interactionOnly: false, idleMs: 0 },
    { level: 1, fps: 8, scale: 0.75, kbps: 400, interactionOnly: false, idleMs: 0 },
    { level: 2, fps: 4, scale: 0.5, kbps: 200, interactionOnly: false, idleMs: 0 },
    { level: 3, fps: 2, scale: 0.5, kbps: 100, interactionOnly: false, idleMs: 0 },
    { level: 4, fps: 1, scale: 0.33, kbps: 50, interactionOnly: false, idleMs: 0 },
    { level: 5, fps: 0, scale: 0.33, kbps: 0, interactionOnly: true, idleMs: 10_000 },
    { level: 6, fps: 0, scale: 0, kbps: 0, interactionOnly: false, idleMs: 0 },
];
/** Governor level → canvas ladder level (worker LevelPolicy is the source of truth). */
function canvasLevelFor(gov, bestLevel = 1) {
    const l = LEVEL_POLICY[gov]?.canvasLevel ?? 6;
    return Math.max(l, bestLevel);
}
function ladderRow(level) {
    return LADDER[Math.min(6, Math.max(0, Math.round(level)))];
}
function detectCapabilities(g = globalThis) {
    return {
        videoFrame: typeof g.VideoFrame === "function",
        videoEncoder: typeof g.VideoEncoder === "function",
        offscreenCanvas: typeof g.OffscreenCanvas === "function",
        createImageBitmap: typeof g.createImageBitmap === "function",
        worker: typeof g.Worker === "function",
    };
}
/** PLAN §6.4 heuristic. */
function selectStrategy(i) {
    if (i.ctxType === "2d" && i.callsPerSec < i.maxCallsPerSec && i.sourceBytesPerMin < i.maxSourceBytesPerMin)
        return "command";
    if (i.caps.videoFrame && i.caps.videoEncoder)
        return "video";
    if (i.caps.offscreenCanvas && i.caps.createImageBitmap)
        return "webp";
    if (i.areaPx > 4_000_000)
        return "skip";
    return "blob";
}
/** Per-canvas byte budget over a rolling minute. */
class ByteBudget {
    perMinute;
    now;
    window = [];
    constructor(perMinute, now = () => Date.now()) {
        this.perMinute = perMinute;
        this.now = now;
    }
    add(bytes) {
        this.window.push({ t: this.now(), b: bytes });
        this.prune();
    }
    used() {
        this.prune();
        let s = 0;
        for (const w of this.window)
            s += w.b;
        return s;
    }
    remaining() {
        return Math.max(0, this.perMinute - this.used());
    }
    exhausted() {
        return this.remaining() <= 0;
    }
    setPerMinute(b) {
        this.perMinute = b;
    }
    prune() {
        const cutoff = this.now() - 60_000;
        while (this.window.length && this.window[0].t < cutoff)
            this.window.shift();
    }
}
/** Calls-per-second meter with a 1 s window. */
class RateMeter {
    now;
    count = 0;
    windowStart;
    last = 0;
    constructor(now = () => Date.now()) {
        this.now = now;
        this.windowStart = now();
    }
    hit(n = 1) {
        const t = this.now();
        if (t - this.windowStart >= 1000) {
            this.last = this.count;
            this.count = 0;
            this.windowStart = t;
        }
        this.count += n;
    }
    /** Rate in the last completed second (or the current partial second, whichever is higher). */
    perSec() {
        const t = this.now();
        const elapsed = t - this.windowStart;
        // Extrapolate the partial second only once it is long enough to be meaningful.
        const partial = elapsed >= 1000 ? this.count : elapsed >= 200 ? (this.count * 1000) / elapsed : 0;
        return Math.max(this.last, partial);
    }
}

/** Marker commands understood by the Player (in addition to real context calls). */
const MARK = {
    state: "__state", // p: { recording, reason, strategy, w, h }
    frame: "__frame", // p: { n }  one per animation frame
    source: "__src", // p: { ref, w, h, data?, url?, masked? }  image source registration
    path: "__path", // p: { ref, ops: [{f,a}], svg? }  Path2D registration
};

const METHODS_2D = [
    "save", "restore", "scale", "rotate", "translate", "transform", "setTransform", "resetTransform", "reset",
    "createLinearGradient", "createRadialGradient", "createConicGradient", "createPattern",
    "clearRect", "fillRect", "strokeRect", "beginPath", "closePath", "fill", "stroke", "clip",
    "fillText", "strokeText", "drawImage", "putImageData", "setLineDash",
    "moveTo", "lineTo", "quadraticCurveTo", "bezierCurveTo", "arc", "arcTo", "ellipse", "rect", "roundRect",
];
const PROPS_2D = [
    "fillStyle", "strokeStyle", "lineWidth", "lineCap", "lineJoin", "miterLimit", "lineDashOffset", "font", "textAlign",
    "textBaseline", "direction", "globalAlpha", "globalCompositeOperation", "shadowBlur", "shadowColor", "shadowOffsetX",
    "shadowOffsetY", "imageSmoothingEnabled", "imageSmoothingQuality", "filter", "letterSpacing", "wordSpacing", "fontKerning",
];
const PATH_METHODS = ["moveTo", "lineTo", "quadraticCurveTo", "bezierCurveTo", "arc", "arcTo", "ellipse", "rect", "roundRect", "closePath", "addPath"];
const CREATE_METHODS = new Set(["createLinearGradient", "createRadialGradient", "createConicGradient", "createPattern"]);
const IMAGE_ARG0 = new Set(["drawImage", "putImageData", "createPattern"]);
const PATCHED = "__BACKSTORY_CANVAS_PATCHED__";
/** Global registries shared by the prototype patches. */
const gradIds = new WeakMap();
const pathLogs = new WeakMap();
let gradSeq = 0;
let pathSeq = 0;
function installInstrumentation(lookup, g = globalThis) {
    if (g[PATCHED]) {
        g[PATCHED].lookup = lookup;
        return { uninstall: () => void (g[PATCHED].lookup = () => null) };
    }
    const state = { lookup };
    g[PATCHED] = state;
    const restores = [];
    const Ctx = g.CanvasRenderingContext2D;
    if (Ctx) {
        for (const name of METHODS_2D) {
            const orig = Ctx.prototype[name];
            if (typeof orig !== "function")
                continue;
            Ctx.prototype[name] = function (...args) {
                const result = orig.apply(this, args);
                const track = state.lookup(this.canvas);
                if (track)
                    track.record(name, args, result);
                return result;
            };
            restores.push(() => void (Ctx.prototype[name] = orig));
        }
        for (const prop of PROPS_2D) {
            const desc = findDescriptor(Ctx.prototype, prop);
            if (!desc || !desc.set || !desc.get)
                continue;
            const { get, set } = desc;
            Object.defineProperty(Ctx.prototype, prop, {
                configurable: true,
                enumerable: desc.enumerable,
                get,
                set(v) {
                    set.call(this, v);
                    const track = state.lookup(this.canvas);
                    if (track)
                        track.recordSet(prop, v);
                },
            });
            restores.push(() => Object.defineProperty(Ctx.prototype, prop, desc));
        }
    }
    const Grad = g.CanvasGradient;
    if (Grad && typeof Grad.prototype.addColorStop === "function") {
        const orig = Grad.prototype.addColorStop;
        Grad.prototype.addColorStop = function (offset, color) {
            const r = orig.call(this, offset, color);
            const owner = gradIds.get(this);
            if (owner)
                owner.track.record("g.addColorStop", [this, offset, color], undefined);
            return r;
        };
        restores.push(() => void (Grad.prototype.addColorStop = orig));
    }
    const Path = g.Path2D;
    if (Path) {
        // Subclass so `new Path2D(svg)` arguments are captured; instanceof stays true.
        class BackstoryPath2D extends Path {
            constructor(p) {
                super(p);
                const log = { ops: [] };
                if (typeof p === "string")
                    log.svg = p;
                else if (p && pathLogs.has(p))
                    log.ops = [...pathLogs.get(p).ops];
                pathLogs.set(this, log);
            }
        }
        for (const name of PATH_METHODS) {
            const orig = Path.prototype[name];
            if (typeof orig !== "function")
                continue;
            Path.prototype[name] = function (...args) {
                const r = orig.apply(this, args);
                let log = pathLogs.get(this);
                if (!log) {
                    log = { ops: [] };
                    pathLogs.set(this, log);
                }
                if (log.ops.length < 5000)
                    log.ops.push({ f: name, a: args.map((a) => (a instanceof Path && pathLogs.has(a) ? { $ops: pathLogs.get(a).ops, $svg: pathLogs.get(a).svg } : plain(a))) });
                return r;
            };
            restores.push(() => void (Path.prototype[name] = orig));
        }
        g.Path2D = BackstoryPath2D;
        restores.push(() => void (g.Path2D = Path));
    }
    return {
        uninstall() {
            for (const r of restores.reverse())
                r();
            delete g[PATCHED];
        },
    };
}
function findDescriptor(proto, prop) {
    let p = proto;
    while (p) {
        const d = Object.getOwnPropertyDescriptor(p, prop);
        if (d)
            return d;
        p = Object.getPrototypeOf(p);
    }
    return undefined;
}
function plain(a) {
    if (a == null)
        return a;
    const t = typeof a;
    if (t === "number")
        return Number.isFinite(a) ? Math.round(a * 1000) / 1000 : 0;
    if (t === "string" || t === "boolean")
        return a;
    if (Array.isArray(a))
        return a.map(plain);
    const m = a;
    if (typeof m.a === "number" && typeof m.f === "number")
        return [m.a, m.b, m.c, m.d, m.e, m.f]; // DOMMatrix
    return null;
}
/** Probes getContext so the collector learns each canvas's context type (and sees canvases early). */
function installGetContextProbe(onContext, g = globalThis) {
    const proto = g.HTMLCanvasElement?.prototype;
    if (!proto || proto.__rwProbe)
        return () => { };
    const orig = proto.getContext;
    proto.getContext = function (type, ...rest) {
        const ctx = orig.call(this, type, ...rest);
        if (ctx) {
            try {
                onContext(this, String(type));
            }
            catch {
                /* never break the app */
            }
        }
        return ctx;
    };
    proto.__rwProbe = true;
    return () => {
        proto.getContext = orig;
        delete proto.__rwProbe;
    };
}
/** Per-canvas command batch, emitted once per animation frame. */
class CommandTrack {
    o;
    meter;
    buf = [];
    pending = [];
    scheduled = false;
    frame = 0;
    overflowed = false;
    knownPaths = new Set();
    stopped = false;
    constructor(o) {
        this.o = o;
        this.meter = new RateMeter(o.now);
    }
    get id() {
        return this.o.id;
    }
    record(f, args, result) {
        if (this.stopped)
            return;
        this.meter.hit();
        if (this.buf.length >= (this.o.maxCmdsPerFrame ?? 20_000)) {
            this.overflowed = true;
            return;
        }
        const cmd = { f };
        if (args.length)
            cmd.a = args.map((a, i) => this.serializeArg(f, i, a));
        if (CREATE_METHODS.has(f) && result && typeof result === "object") {
            const id = `g${++gradSeq}`;
            gradIds.set(result, { id, track: this });
            cmd.p = { ref: id };
        }
        this.buf.push(cmd);
        this.flushLater();
    }
    recordSet(prop, v) {
        if (this.stopped)
            return;
        this.meter.hit();
        this.buf.push({ f: "set", a: [prop, this.serializeArg("set", 1, v)] });
        this.flushLater();
    }
    /** Emit a marker immediately (used for __state transitions). */
    marker(cmd) {
        this.buf.unshift(cmd);
        this.flushLater();
    }
    stop() {
        this.stopped = true;
        this.buf = [];
    }
    serializeArg(f, index, a) {
        if (a == null)
            return a;
        if (typeof a !== "object" && typeof a !== "function")
            return plain(a);
        const g = gradIds.get(a);
        if (g)
            return { $g: g.id };
        if (pathLogs.has(a) || (typeof globalThis.Path2D === "function" && a instanceof globalThis.Path2D))
            return this.pathRef(a);
        if (index === 0 && IMAGE_ARG0.has(f))
            return this.imageRef(a);
        return plain(a);
    }
    pathRef(p) {
        let log = pathLogs.get(p);
        if (!log) {
            log = { ops: [] };
            pathLogs.set(p, log);
        }
        let id = log.id;
        if (!id) {
            id = `p${++pathSeq}`;
            log.id = id;
        }
        // Register once per track; the ops may still grow later, so re-register when the length changes.
        const key = `${id}:${log.ops.length}`;
        if (!this.knownPaths.has(key)) {
            this.knownPaths.add(key);
            this.buf.push({ f: MARK.path, p: { ref: id, ops: log.ops, svg: log.svg } });
        }
        return { $p: id };
    }
    imageRef(src) {
        const q = this.o.quality();
        const placeholder = { $img: "" };
        const slot = this.buf.length; // the __src marker must precede the command using it
        const p = this.o.sources
            .resolve(src, q.q, q.scale)
            .then((ref) => {
            placeholder.$img = ref.ref;
            if (ref.isNew)
                this.buf.splice(Math.min(slot, this.buf.length), 0, { f: MARK.source, p: { ref: ref.ref, w: ref.w, h: ref.h, data: ref.data, url: ref.url, masked: ref.masked } });
        })
            .catch(() => {
            placeholder.$img = "x";
        });
        this.pending.push(p);
        return placeholder;
    }
    flushLater() {
        if (this.scheduled)
            return;
        this.scheduled = true;
        this.o.schedule(() => void this.flush());
    }
    async flush() {
        this.scheduled = false;
        if (this.pending.length) {
            const pend = this.pending;
            this.pending = [];
            await Promise.all(pend);
        }
        if (this.stopped)
            return;
        const cmds = this.buf;
        this.buf = [];
        if (this.overflowed) {
            this.overflowed = false;
            this.o.onOverflow?.();
            return;
        }
        if (!cmds.length)
            return;
        this.o.emit([{ f: MARK.frame, p: { n: this.frame++ } }, ...cmds], this.frame - 1);
    }
}

/** FNV-1a over bytes, returned as an 8-char base36 string. */
function fnv1a(bytes, seed = 0x811c9dc5) {
    let h = seed >>> 0;
    for (let i = 0; i < bytes.length; i++) {
        h ^= bytes[i] & 0xff;
        h = Math.imul(h, 0x01000193) >>> 0;
    }
    return h.toString(36).padStart(7, "0");
}
/**
 * Change-detection hash of a 32×32 grayscale downsample. `rgba` is the pixel
 * buffer of the downsampled frame (32*32*4 bytes). Small luminance differences
 * are quantized away so anti-aliasing jitter does not defeat deduplication.
 */
function frameHash(rgba) {
    const n = Math.floor(rgba.length / 4);
    const gray = new Uint8Array(n);
    for (let i = 0; i < n; i++) {
        const r = rgba[i * 4];
        const g = rgba[i * 4 + 1];
        const b = rgba[i * 4 + 2];
        gray[i] = ((r * 77 + g * 151 + b * 28) >> 8) >> 3; // 5-bit luminance
    }
    return fnv1a(gray);
}
const B64 = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/";
/** Dependency-free base64 for ArrayBuffer/Uint8Array (works in workers without btoa on large inputs). */
function toBase64(input) {
    const bytes = input instanceof Uint8Array ? input : new Uint8Array(input);
    let out = "";
    let i = 0;
    for (; i + 2 < bytes.length; i += 3) {
        const n = (bytes[i] << 16) | (bytes[i + 1] << 8) | bytes[i + 2];
        out += B64[(n >> 18) & 63] + B64[(n >> 12) & 63] + B64[(n >> 6) & 63] + B64[n & 63];
    }
    if (i < bytes.length) {
        const rem = bytes.length - i;
        const n = (bytes[i] << 16) | ((rem > 1 ? bytes[i + 1] : 0) << 8);
        out += B64[(n >> 18) & 63] + B64[(n >> 12) & 63] + (rem > 1 ? B64[(n >> 6) & 63] : "=") + "=";
    }
    return out;
}

/** Default encoder using OffscreenCanvas; throws → caller falls back to URL reference. */
function offscreenEncoder(g = globalThis) {
    if (typeof g.OffscreenCanvas !== "function")
        return null;
    const oc = new g.OffscreenCanvas(16, 16);
    const fp = oc.getContext("2d", { willReadFrequently: true });
    return {
        fingerprint(src, _w, _h) {
            if (!fp)
                return null;
            try {
                fp.clearRect(0, 0, 16, 16);
                if (isImageData(src)) {
                    const tmp = new g.OffscreenCanvas(src.width, src.height);
                    tmp.getContext("2d").putImageData(src, 0, 0);
                    fp.drawImage(tmp, 0, 0, 16, 16);
                }
                else
                    fp.drawImage(src, 0, 0, 16, 16);
                return fp.getImageData(0, 0, 16, 16).data;
            }
            catch {
                return null; // tainted
            }
        },
        async encode(src, w, h, quality, scale) {
            try {
                const tw = Math.max(1, Math.round(w * scale));
                const th = Math.max(1, Math.round(h * scale));
                const c = new g.OffscreenCanvas(tw, th);
                const ctx = c.getContext("2d");
                if (isImageData(src)) {
                    const tmp = new g.OffscreenCanvas(src.width, src.height);
                    tmp.getContext("2d").putImageData(src, 0, 0);
                    ctx.drawImage(tmp, 0, 0, tw, th);
                }
                else
                    ctx.drawImage(src, 0, 0, tw, th);
                const blob = await c.convertToBlob({ type: "image/webp", quality });
                return new Uint8Array(await blob.arrayBuffer());
            }
            catch {
                return null;
            }
        },
    };
}
/** Structural check: ImageData may not exist as a global (workers, jsdom). */
function isImageData(src) {
    const s = src;
    return !!s && typeof s === "object" && s.data instanceof Uint8ClampedArray && typeof s.width === "number" && typeof s.height === "number";
}
function sourceSize(src) {
    const s = src;
    if (isImageData(src))
        return { w: src.width, h: src.height };
    if (typeof s.naturalWidth === "number")
        return { w: s.naturalWidth || s.width || 0, h: s.naturalHeight || s.height || 0 };
    if (typeof s.videoWidth === "number")
        return { w: s.videoWidth || 0, h: s.videoHeight || 0 };
    if (typeof s.displayWidth === "number")
        return { w: s.displayWidth || 0, h: s.displayHeight || 0 };
    return { w: s.width || 0, h: s.height || 0 };
}
function isMaskedSource(src) {
    const el = src;
    if (!el || typeof el.closest !== "function")
        return false;
    try {
        return !!el.closest("[data-backstory-mask],[data-backstory-block]");
    }
    catch {
        return false;
    }
}
class SourceCache {
    enc;
    maxEntries;
    now;
    lru = new Map();
    /** Encodes in flight, so two draws of the same new source in one frame encode once. */
    inflight = new Map();
    bytesWindow = [];
    seq = 0;
    constructor(enc, maxEntries = 64, now = () => Date.now()) {
        this.enc = enc;
        this.maxEntries = maxEntries;
        this.now = now;
    }
    /** Bytes of unique sources emitted in the last minute (strategy heuristic input). */
    bytesPerMinute() {
        const cutoff = this.now() - 60_000;
        while (this.bytesWindow.length && this.bytesWindow[0].t < cutoff)
            this.bytesWindow.shift();
        let s = 0;
        for (const w of this.bytesWindow)
            s += w.b;
        return s;
    }
    /**
     * Resolve a source to a ref. Returns a promise because encoding is async;
     * the command batch awaits all pending refs before emitting the frame.
     */
    async resolve(src, quality, scale) {
        const { w, h } = sourceSize(src);
        if (isMaskedSource(src)) {
            const key = `m:${w}x${h}`;
            return this.remember(key, { ref: key, w, h, masked: true, bytes: 0, isNew: !this.lru.has(key) });
        }
        const fp = this.enc?.fingerprint(src, w, h) ?? null;
        if (!fp) {
            // Tainted or unreadable: reference by URL when we have one.
            const url = src.currentSrc || src.src;
            const key = url ? `u:${fnv1a(Array.from(String(url)).map((c) => c.charCodeAt(0)))}` : `x:${w}x${h}:${this.seq++}`;
            return this.remember(key, { ref: key, w, h, url: url ? String(url) : undefined, bytes: 0, isNew: !this.lru.has(key) });
        }
        const key = `s:${w}x${h}:${fnv1a(fp)}`;
        const hit = this.lru.get(key);
        if (hit) {
            this.lru.delete(key);
            this.lru.set(key, hit);
            return { ...hit, data: undefined, isNew: false };
        }
        const pending = this.inflight.get(key);
        if (pending)
            return pending.then((r) => ({ ...r, data: undefined, isNew: false }));
        const job = (async () => {
            const bytes = await this.enc.encode(src, w, h, quality, scale);
            if (!bytes) {
                const url = src.currentSrc || src.src;
                return this.remember(key, { ref: key, w, h, url: url ? String(url) : undefined, bytes: 0, isNew: true });
            }
            this.bytesWindow.push({ t: this.now(), b: bytes.length });
            return this.remember(key, { ref: key, w, h, data: b64$2(bytes), bytes: bytes.length, isNew: true });
        })();
        this.inflight.set(key, job);
        try {
            return await job;
        }
        finally {
            this.inflight.delete(key);
        }
    }
    remember(key, ref) {
        if (this.lru.has(key)) {
            this.lru.delete(key);
            this.lru.set(key, this.lru.get(key));
            return { ...ref, data: undefined, isNew: false };
        }
        this.lru.set(key, { ...ref, data: undefined });
        while (this.lru.size > this.maxEntries)
            this.lru.delete(this.lru.keys().next().value);
        return ref;
    }
    clear() {
        this.lru.clear();
    }
}
function b64$2(bytes) {
    let s = "";
    const chunk = 0x8000;
    for (let i = 0; i < bytes.length; i += chunk)
        s += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + chunk)));
    return typeof btoa === "function" ? btoa(s) : "";
}

const CODECS = [
    { name: "av1", codec: "av01.0.04M.08" },
    { name: "vp9", codec: "vp09.00.10.08" },
    { name: "h264", codec: "avc1.42001f" },
];
function createEncoderCore(post, g = globalThis) {
    const per = new Map();
    const hashCanvas = typeof g.OffscreenCanvas === "function" ? new g.OffscreenCanvas(32, 32) : null;
    const hashCtx = hashCanvas?.getContext("2d", { willReadFrequently: true });
    let supported = null;
    async function pickCodec() {
        if (typeof g.VideoEncoder !== "function")
            return null;
        if (!supported) {
            supported = (async () => {
                for (const c of CODECS) {
                    try {
                        const r = await g.VideoEncoder.isConfigSupported({ codec: c.codec, width: 320, height: 240, bitrate: 200_000, framerate: 8 });
                        if (r?.supported)
                            return c;
                    }
                    catch {
                        /* try next */
                    }
                }
                return null;
            })();
        }
        return supported;
    }
    function stateFor(id, w, h) {
        if (typeof g.OffscreenCanvas !== "function")
            return null;
        let s = per.get(id);
        if (!s) {
            const canvas = new g.OffscreenCanvas(w, h);
            s = { canvas, ctx: canvas.getContext("2d"), w, h, lastHash: "", encoder: null, codec: null, encKey: "", pendingKey: true };
            per.set(id, s);
        }
        if (s.w !== w || s.h !== h) {
            s.canvas.width = w;
            s.canvas.height = h;
            s.w = w;
            s.h = h;
            s.pendingKey = true;
        }
        return s;
    }
    function closeEncoder(s) {
        try {
            s.encoder?.close();
        }
        catch {
            /* ignore */
        }
        s.encoder = null;
        s.codec = null;
        s.encKey = "";
    }
    async function ensureEncoder(id, s, kbps, fps) {
        const c = await pickCodec();
        if (!c)
            return false;
        const key = `${c.codec}:${s.w}x${s.h}:${kbps}:${fps}`;
        if (s.encoder && s.encKey === key && s.encoder.state === "configured")
            return true;
        closeEncoder(s);
        const encoder = new g.VideoEncoder({
            output: (chunk, meta) => {
                const buf = new Uint8Array(chunk.byteLength);
                chunk.copyTo(buf);
                const d = meta?.decoderConfig?.description;
                if (d)
                    s.desc = toBase64(d instanceof ArrayBuffer ? d : new Uint8Array(d.buffer, d.byteOffset, d.byteLength));
                post({ type: "frame", id, codec: c.name, keyframe: chunk.type === "key", w: s.w, h: s.h, data: toBase64(buf), desc: chunk.type === "key" ? s.desc : undefined, ts: Math.round(chunk.timestamp / 1000), bytes: buf.length });
            },
            error: (e) => post({ type: "error", id, message: String(e?.message || e) }),
        });
        encoder.configure({ codec: c.codec, width: s.w, height: s.h, bitrate: Math.max(50_000, kbps * 1000), framerate: Math.max(1, fps), latencyMode: "realtime" });
        s.encoder = encoder;
        s.codec = c.name;
        s.encKey = key;
        s.pendingKey = true;
        return true;
    }
    async function encode(req) {
        const src = (req.frame ?? req.bitmap);
        const tw = Math.max(1, Math.round(req.w * req.scale));
        const th = Math.max(1, Math.round(req.h * req.scale));
        const s = stateFor(req.id, tw, th);
        try {
            if (!src || !s) {
                post({ type: "dropped", id: req.id, reason: "error" });
                return;
            }
            // Change detection on a 32×32 grayscale downsample.
            if (hashCtx && hashCanvas) {
                hashCtx.drawImage(src, 0, 0, 32, 32);
                const h = frameHash(hashCtx.getImageData(0, 0, 32, 32).data);
                if (!req.keyframe && !s.pendingKey && h === s.lastHash) {
                    post({ type: "dropped", id: req.id, reason: "unchanged" });
                    return;
                }
                s.lastHash = h;
            }
            s.ctx.drawImage(src, 0, 0, tw, th);
            const wantKey = req.keyframe || s.pendingKey;
            if (!req.webp && (await ensureEncoder(req.id, s, req.kbps, req.fps)) && s.encoder && typeof g.VideoFrame === "function") {
                const vf = new g.VideoFrame(s.canvas, { timestamp: Math.round(req.ts * 1000) });
                s.encoder.encode(vf, { keyFrame: wantKey });
                vf.close();
                s.pendingKey = false;
                return;
            }
            const blob = await s.canvas.convertToBlob({ type: "image/webp", quality: req.quality });
            const buf = await blobBytes(blob, g);
            s.pendingKey = false;
            post({ type: "frame", id: req.id, codec: "webp", keyframe: true, w: tw, h: th, data: toBase64(buf), ts: req.ts, bytes: buf.length });
        }
        catch (e) {
            post({ type: "error", id: req.id, message: String(e?.message || e) });
        }
        finally {
            try {
                req.frame?.close();
                req.bitmap?.close();
            }
            catch {
                /* ignore */
            }
        }
    }
    return {
        handle(m) {
            if (m.type === "encode")
                void encode(m);
            else if (m.type === "close") {
                const s = per.get(m.id);
                if (s)
                    closeEncoder(s);
                per.delete(m.id);
            }
            else if (m.type === "flush") {
                const s = per.get(m.id);
                void s?.encoder?.flush().catch(() => undefined);
            }
            else if (m.type === "reset") {
                for (const s of per.values())
                    closeEncoder(s);
                per.clear();
            }
        },
        /** Test hook. */
        _state: per,
    };
}
/** Blob → bytes, with a FileReader fallback for runtimes whose Blob lacks arrayBuffer(). */
function blobBytes(blob, g = globalThis) {
    if (typeof blob.arrayBuffer === "function")
        return blob.arrayBuffer().then((b) => new Uint8Array(b));
    return new Promise((resolve, reject) => {
        const FR = (g.FileReader ?? globalThis.FileReader);
        if (!FR)
            return reject(new Error("no arrayBuffer/FileReader"));
        const r = new FR();
        r.onload = () => resolve(new Uint8Array(r.result));
        r.onerror = () => reject(r.error);
        r.readAsArrayBuffer(blob);
    });
}

function createEncoderPort(workerSource, g = globalThis) {
    const listeners = [];
    const W = g.Worker;
    if (W && workerSource && typeof g.OffscreenCanvas === "function") {
        try {
            const url = URL.createObjectURL(new Blob([workerSource], { type: "text/javascript" }));
            const w = new W(url);
            w.onmessage = (e) => listeners.forEach((l) => l(e.data));
            return {
                kind: "worker",
                send: (m, transfer) => w.postMessage(m, transfer ?? []),
                onMessage: (fn) => listeners.push(fn),
                terminate: () => {
                    w.terminate();
                    URL.revokeObjectURL(url);
                },
            };
        }
        catch {
            /* fall back to inline */
        }
    }
    const core = createEncoderCore((m) => listeners.forEach((l) => l(m)), g);
    return { kind: "inline", send: (m) => core.handle(m), onMessage: (fn) => listeners.push(fn), terminate: () => core.handle({ type: "reset" }) };
}
/**
 * Strategy B/C (PLAN §6.2–6.3): captures the canvas bitmap per animation frame
 * at the ladder frame rate and hands it to the encoder. Keyframes are forced on
 * start, checkpoints, interaction bursts (L5) and every 2 s otherwise.
 */
class VideoCapture {
    o;
    lastCapture = -Infinity;
    lastKey = -Infinity;
    keyRequested = true;
    inflight = 0;
    stopped = false;
    detach;
    frames = 0;
    dropped = 0;
    constructor(o) {
        this.o = o;
        const onMsg = (m) => {
            if (m.id !== this.o.id)
                return;
            if (m.type === "frame") {
                this.inflight = Math.max(0, this.inflight - 1);
                this.frames++;
                this.o.onBytes(m.bytes);
                const ev = { type: "canvas_frame", t: m.ts || this.o.now(), id: this.o.id, codec: m.codec, keyframe: m.keyframe, w: m.w, h: m.h, data: m.data, ts: m.ts };
                if (m.desc)
                    ev.desc = m.desc;
                if (this.o.phi)
                    ev.phi = true;
                this.o.emit(ev);
            }
            else if (m.type === "dropped") {
                this.inflight = Math.max(0, this.inflight - 1);
                this.dropped++;
            }
            else if (m.type === "error") {
                this.inflight = Math.max(0, this.inflight - 1);
            }
        };
        o.port.onMessage(onMsg);
        this.detach = () => {
            /* port listeners are process-wide; the id filter makes stale listeners inert */
        };
    }
    requestKeyframe() {
        this.keyRequested = true;
    }
    /** Called from the collector's rAF loop. Returns true when a frame was captured. */
    tick(now, interacted) {
        if (this.stopped)
            return false;
        const row = this.o.ladder();
        if (row.level >= 6)
            return false;
        const fps = Math.min(row.fps, this.o.maxFps);
        let due;
        if (row.interactionOnly) {
            due = this.keyRequested || interacted || now - this.lastCapture >= row.idleMs;
            if (due)
                this.keyRequested = true; // interaction-only frames are always keyframes
        }
        else {
            if (this.o.blobFallback)
                due = now - this.lastCapture >= 1000;
            else
                due = fps > 0 && now - this.lastCapture >= 1000 / fps;
        }
        if (!due || this.inflight >= 2)
            return false;
        if (!this.keyRequested && now - this.lastKey >= 2000)
            this.keyRequested = true;
        const key = this.keyRequested;
        this.keyRequested = false;
        this.lastCapture = now;
        if (key)
            this.lastKey = now;
        return this.capture(now, key, row, fps);
    }
    capture(now, keyframe, row, fps) {
        const el = this.o.el;
        const w = el.width;
        const h = el.height;
        if (!w || !h)
            return false;
        const base = { type: "encode", id: this.o.id, ts: now, keyframe, w, h, scale: scaleFor(row, w), kbps: row.kbps, fps: Math.max(1, fps), quality: qualityFor(row) };
        const g = globalThis;
        try {
            if (this.o.blobFallback || (!this.o.caps.videoFrame && !this.o.caps.createImageBitmap)) {
                if (typeof el.toBlob !== "function")
                    return false;
                this.inflight++;
                el.toBlob((blob) => {
                    this.inflight = Math.max(0, this.inflight - 1);
                    if (!blob)
                        return;
                    void blob.arrayBuffer().then((buf) => {
                        this.frames++;
                        this.o.onBytes(buf.byteLength);
                        const ev = { type: "canvas_frame", t: now, id: this.o.id, codec: "webp", keyframe: true, w, h, data: b64$1(new Uint8Array(buf)), ts: now };
                        if (this.o.phi)
                            ev.phi = true;
                        this.o.emit(ev);
                    });
                }, "image/webp", base.quality);
                return true;
            }
            if (this.o.caps.videoFrame && this.o.caps.videoEncoder && this.o.port.kind === "worker" || (this.o.caps.videoFrame && this.o.caps.videoEncoder && this.o.port.kind === "inline")) {
                const frame = new g.VideoFrame(el, { timestamp: Math.round(now * 1000) });
                this.inflight++;
                this.o.port.send({ ...base, frame }, [frame]);
                return true;
            }
            if (this.o.caps.createImageBitmap) {
                this.inflight++;
                void g.createImageBitmap(el).then((bitmap) => this.o.port.send({ ...base, bitmap, webp: !this.o.caps.videoEncoder }, [bitmap]), () => void (this.inflight = Math.max(0, this.inflight - 1)));
                return true;
            }
        }
        catch {
            this.inflight = Math.max(0, this.inflight - 1);
        }
        return false;
    }
    stop() {
        this.stopped = true;
        this.detach();
        this.o.port.send({ type: "close", id: this.o.id });
    }
}
/** Resolution scale per ladder row, additionally capped at 1920 px wide. */
function scaleFor(row, width) {
    const s = row.scale || 0.33;
    return width * s > 1920 ? 1920 / width : s;
}
function qualityFor(row) {
    return row.level <= 1 ? 0.8 : row.level <= 3 ? 0.65 : 0.5;
}
function b64$1(bytes) {
    let s = "";
    for (let i = 0; i < bytes.length; i += 0x8000)
        s += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + 0x8000)));
    return typeof btoa === "function" ? btoa(s) : "";
}

/**
 * Orchestrates canvas capture (PLAN §6): discovery, visibility gating, strategy
 * selection, the quality ladder driven by the governor level, keyframes on
 * checkpoints and interactions, per-canvas byte budgets, and the debug listing.
 */
class CanvasCollector {
    r;
    cfg;
    o;
    win;
    doc;
    now;
    caps;
    visibility;
    captures = new Map();
    ctxTypes = new WeakMap();
    port = null;
    sources;
    mo = null;
    raf = null;
    strategyTimer = null;
    untapCheckpoint = null;
    lastLevel;
    uninstall = [];
    stopped = false;
    constructor(r, cfg, o = {}) {
        this.r = r;
        this.cfg = cfg;
        this.o = o;
        this.win = o.win ?? window;
        this.doc = o.doc ?? document;
        this.now = o.now ?? (() => Date.now());
        this.caps = o.caps ?? detectCapabilities(this.win);
        this.lastLevel = r.level ?? "HEALTHY";
        this.sources = new SourceCache(offscreenEncoder(this.win));
        this.visibility = new VisibilityTracker({
            allowlist: cfg.canvas.allowlist,
            autoDetect: cfg.canvas.autoDetect,
            strictMode: cfg.privacy.mode === "strict",
            idOf: (el) => r.map.id(el) ?? r.map.assign(el),
            onChange: (st, prev) => this.onRecordableChange(st, prev),
            doc: this.doc,
            win: this.win,
        });
    }
    start() {
        const g = this.win;
        // Learn context types (and see canvases the moment they get a context).
        this.uninstall.push(installGetContextProbe((canvas, type) => {
            const t = type.startsWith("webgl2") ? "webgl2" : type.startsWith("webgl") ? "webgl" : type === "webgpu" ? "webgpu" : type === "bitmaprenderer" ? "bitmaprenderer" : type === "2d" ? "2d" : "unknown";
            this.ctxTypes.set(canvas, t);
            const st = this.visibility.get(canvas);
            if (st)
                st.ctxType = t;
            if (canvas.isConnected)
                this.consider(canvas);
        }, g));
        // Command interception, gated per canvas. A 2D call is also how we learn the
        // context type of canvases whose context was created before this module loaded.
        const handle = installInstrumentation((canvas) => {
            const c = this.captures.get(canvas);
            if (!c) {
                if (!this.ctxTypes.has(canvas))
                    this.ctxTypes.set(canvas, "2d");
                return null;
            }
            if (c.st.ctxType === "unknown") {
                c.st.ctxType = "2d";
                this.ctxTypes.set(canvas, "2d");
                this.applyStrategy(c, this.chooseStrategy(c));
                this.emitState(c.st, c.st.recordable);
                return c.strategy === "command" && c.st.recordable && c.track ? c.track : null;
            }
            return c.strategy === "command" && c.st.recordable && c.track ? c.track : null;
        }, g);
        this.uninstall.push(() => handle.uninstall());
        for (const c of this.scan(this.doc))
            this.consider(c);
        const MO = g.MutationObserver;
        if (MO) {
            this.mo = new MO((records) => {
                let touched = false;
                for (const rec of records) {
                    for (const n of Array.from(rec.addedNodes)) {
                        if (n.nodeType !== 1)
                            continue;
                        const el = n;
                        if (el.tagName === "CANVAS")
                            this.consider(el);
                        else if (typeof el.querySelectorAll === "function")
                            for (const c of Array.from(el.querySelectorAll("canvas")))
                                this.consider(c);
                        touched = true;
                    }
                    for (const n of Array.from(rec.removedNodes)) {
                        if (n.nodeType !== 1)
                            continue;
                        const el = n;
                        if (el.tagName === "CANVAS")
                            this.visibility.untrack(el);
                        else if (typeof el.querySelectorAll === "function")
                            for (const c of Array.from(el.querySelectorAll("canvas")))
                                this.visibility.untrack(c);
                        touched = true;
                    }
                    if (rec.type === "attributes")
                        touched = true;
                }
                if (touched)
                    this.visibility.scheduleReevaluate();
            });
            this.mo.observe(this.doc, { childList: true, subtree: true, attributes: true, attributeFilter: ["data-backstory-canvas", "style", "class", "hidden", "width", "height"] });
        }
        // Keyframes on checkpoints; ladder level from the governor.
        this.untapCheckpoint = bus.tap((e) => {
            // Taps run before the tapped event is queued, and the snapshot follows
            // the checkpoint, so re-announce on the next microtask to land after it.
            if (e.type === "control" && e.kind === "checkpoint")
                void Promise.resolve().then(() => this.restate());
            if (e.type === "control" && e.kind === "level")
                this.onLevel(e.level);
        });
        const interact = () => {
            for (const c of this.captures.values())
                c.interacted = true;
        };
        for (const ev of ["pointerdown", "keydown", "touchstart"]) {
            this.doc.addEventListener(ev, interact, { capture: true, passive: true });
            this.uninstall.push(() => this.doc.removeEventListener(ev, interact, { capture: true }));
        }
        this.strategyTimer = this.win.setInterval(() => this.reevaluateStrategies(), 5000);
        this.r.getCanvases = () => this.debugList();
        this.loop();
    }
    /** Developer hint (PLAN §6.3): the app tells us a frame is complete. */
    markFrame(canvas) {
        const c = this.captures.get(canvas);
        if (c?.video)
            c.video.tick(this.now(), true);
    }
    stop() {
        this.stopped = true;
        if (this.raf != null)
            this.win.cancelAnimationFrame?.(this.raf);
        if (this.strategyTimer)
            this.win.clearInterval(this.strategyTimer);
        this.untapCheckpoint?.();
        this.mo?.disconnect();
        for (const c of this.captures.values())
            this.teardownCapture(c);
        this.captures.clear();
        this.visibility.destroy();
        for (const u of this.uninstall.reverse())
            u();
        this.port?.terminate();
        this.r.getCanvases = () => [];
    }
    states() {
        return this.visibility.all();
    }
    debugList() {
        return this.visibility.all().map((st) => ({ id: st.id, tag: `canvas${st.el.id ? "#" + st.el.id : ""}[${st.ctxType}]`, strategy: `${st.strategy} L${st.level}`, recordable: st.recordable, reason: st.reason }));
    }
    // -------------------------------------------------------------------------
    /**
     * The node id to stamp on an event. The node map is renumbered whenever the
     * document is re-serialized (segment start, or the consistency re-run in
     * snapshot.ts when the DOM moved while slicing), so the id a canvas was
     * discovered with can point at a different node in the replayed DOM. Resolve
     * against the live map at emit time and keep the tracked state in step.
     */
    nodeId(st) {
        const id = this.r.map.id(st.el) ?? this.r.map.assign(st.el);
        st.id = id;
        return id;
    }
    scan(root) {
        const out = [];
        const walk = (node) => {
            for (const c of Array.from(node.querySelectorAll("canvas")))
                out.push(c);
            for (const el of Array.from(node.querySelectorAll("*")))
                if (el.shadowRoot)
                    walk(el.shadowRoot);
        };
        walk(root);
        return out;
    }
    /**
     * A checkpoint rebuilds the replayed DOM from a new snapshot, which drops
     * every canvas track and renumbers node ids. Re-announce each canvas (and ask
     * video captures for a keyframe) so playback knows what is being recorded
     * instead of falling back to the "not recorded" hatch.
     */
    restate() {
        if (this.stopped)
            return;
        for (const st of this.visibility.all()) {
            this.emitState(st, st.recordable);
            this.captures.get(st.el)?.video?.requestKeyframe();
        }
    }
    consider(canvas) {
        if (this.stopped)
            return;
        const st = this.visibility.track(canvas);
        st.ctxType = this.ctxTypes.get(canvas) ?? st.ctxType;
    }
    onLevel(level) {
        if (level === this.lastLevel)
            return;
        this.lastLevel = level;
        for (const c of this.captures.values()) {
            c.st.level = this.effectiveLevel(c);
            c.video?.requestKeyframe();
        }
        this.reevaluateStrategies();
    }
    effectiveLevel(c) {
        return Math.min(6, canvasLevelFor(this.lastLevel, this.cfg.canvas.bestLevel) + c.penalty);
    }
    onRecordableChange(st, prev) {
        let c = this.captures.get(st.el);
        if (st.recordable && !c) {
            c = this.setupCapture(st);
            this.captures.set(st.el, c);
            this.emitState(st, true);
        }
        else if (!st.recordable && c) {
            this.emitState(st, false);
            this.teardownCapture(c);
            this.captures.delete(st.el);
            st.strategy = "skip";
        }
        else if (st.recordable && c && !prev) {
            this.emitState(st, true);
            c.video?.requestKeyframe();
        }
    }
    setupCapture(st) {
        const c = { st, strategy: "skip", track: null, video: null, budget: new ByteBudget(this.cfg.canvas.bytesPerMinute, this.now), penalty: 0, interacted: false };
        st.level = this.effectiveLevel(c);
        this.applyStrategy(c, this.chooseStrategy(c));
        return c;
    }
    chooseStrategy(c) {
        const st = c.st;
        if (st.level >= 6)
            return "skip";
        // Unknown context type (created before the module loaded): capture frames,
        // which works for any context; the first intercepted 2D call flips it to
        // command recording. Without a frame path, assume 2D.
        const ctxType = st.ctxType === "unknown" ? (this.caps.videoFrame || this.caps.createImageBitmap ? "webgl" : "2d") : st.ctxType;
        return selectStrategy({
            ctxType,
            callsPerSec: c.track?.meter.perSec() ?? 0,
            sourceBytesPerMin: this.sources.bytesPerMinute(),
            areaPx: st.el.width * st.el.height,
            caps: this.caps,
            maxCallsPerSec: this.cfg.canvas.maxCallsPerSec,
            maxSourceBytesPerMin: this.cfg.canvas.maxSourceBytesPerMin,
        });
    }
    applyStrategy(c, strategy) {
        if (c.strategy === strategy && (c.track || c.video || strategy === "skip"))
            return;
        this.teardownCapture(c, false);
        c.strategy = strategy;
        c.st.strategy = strategy;
        const st = c.st;
        const emit = (e) => this.r.emit(e);
        if (strategy === "command") {
            c.track = new CommandTrack({
                id: st.id,
                phi: st.phi,
                sources: this.sources,
                quality: () => {
                    const row = ladderRow(st.level);
                    return { q: qualityFor(row), scale: scaleFor(row, st.el.width) };
                },
                schedule: (cb) => (this.win.requestAnimationFrame ? void this.win.requestAnimationFrame(cb) : void this.win.setTimeout(cb, 16)),
                now: this.now,
                onOverflow: () => this.applyStrategy(c, this.caps.videoFrame ? "video" : "webp"),
                emit: (cmds) => {
                    const bytes = approxBytes(cmds);
                    c.budget.add(bytes);
                    const ev = { type: "canvas_cmds", t: this.now(), id: this.nodeId(st), cmds };
                    if (st.phi)
                        ev.phi = true;
                    emit(ev);
                    if (c.budget.exhausted())
                        this.penalize(c);
                },
            });
        }
        else if (strategy === "video" || strategy === "webp" || strategy === "blob") {
            this.port ??= createEncoderPort(this.o.workerSource, this.win);
            c.video = new VideoCapture({
                id: st.id,
                el: st.el,
                phi: st.phi,
                caps: strategy === "webp" ? { ...this.caps, videoEncoder: false } : this.caps,
                port: this.port,
                ladder: () => ladderRow(st.level),
                maxFps: this.cfg.canvas.maxFps,
                now: this.now,
                blobFallback: strategy === "blob",
                // The capture keeps its own id to route encoder-worker traffic; the
                // event carries the current node id so playback can find the canvas.
                emit: (e) => emit({ ...e, id: this.nodeId(st) }),
                onBytes: (n) => {
                    c.budget.add(n);
                    if (c.budget.exhausted())
                        this.penalize(c);
                },
            });
        }
    }
    /** Budget exhausted: step one level down until the rolling minute recovers. */
    penalize(c) {
        if (c.st.level >= 5)
            return;
        c.penalty++;
        c.st.level = this.effectiveLevel(c);
        c.budget.setPerMinute(this.cfg.canvas.bytesPerMinute * 1.25); // hysteresis
    }
    reevaluateStrategies() {
        for (const c of this.captures.values()) {
            if (c.penalty && !c.budget.exhausted() && c.budget.used() < this.cfg.canvas.bytesPerMinute * 0.5) {
                c.penalty--;
                c.budget.setPerMinute(this.cfg.canvas.bytesPerMinute);
            }
            c.st.level = this.effectiveLevel(c);
            const s = this.chooseStrategy(c);
            if (s !== c.strategy) {
                this.applyStrategy(c, s);
                this.emitState(c.st, c.st.recordable);
            }
        }
    }
    teardownCapture(c, closePort = true) {
        c.track?.stop();
        c.video?.stop();
        c.track = null;
        c.video = null;
    }
    emitState(st, recording) {
        const ev = {
            type: "canvas_cmds",
            t: this.now(),
            id: this.nodeId(st),
            cmds: [{ f: MARK.state, p: { recording, reason: st.reason, strategy: st.strategy, level: st.level, w: st.el.width, h: st.el.height, ctx: st.ctxType } }],
        };
        if (st.phi)
            ev.phi = true;
        this.r.emit(ev);
    }
    loop() {
        if (this.stopped)
            return;
        const raf = this.win.requestAnimationFrame;
        const step = () => {
            if (this.stopped)
                return;
            const now = this.now();
            for (const c of this.captures.values()) {
                if (c.video && c.st.recordable) {
                    c.video.tick(now, c.interacted);
                    c.interacted = false;
                }
            }
            this.raf = raf ? raf.call(this.win, step) : this.win.setTimeout(step, 100);
        };
        this.raf = raf ? raf.call(this.win, step) : this.win.setTimeout(step, 100);
    }
}
function approxBytes(cmds) {
    let n = 0;
    for (const c of cmds) {
        n += 8 + c.f.length;
        if (c.a)
            for (const a of c.a)
                n += typeof a === "string" ? a.length : 8;
        const data = c.p?.data;
        if (typeof data === "string")
            n += data.length;
    }
    return n;
}
/** Entry used by the core: attaches the collector to a started Recorder. */
function attachCanvas$1(recorder, cfg, opts = {}) {
    const collector = new CanvasCollector(recorder, cfg, opts);
    collector.start();
    return collector;
}

// Lazily loaded canvas module (dist/backstory-canvas.js). Registers on
// window.__BACKSTORY_MOD__.canvas for the script-tag bundle; imported statically by
// the ESM build. The canvas encoding worker source is inlined at build time.
function attachCanvas(recorder, cfg, opts = {}) {
    return attachCanvas$1(recorder, cfg, { workerSource: workerSource, ...opts });
}

var canvasModule = /*#__PURE__*/Object.freeze({
    __proto__: null,
    CanvasCollector: CanvasCollector,
    CommandTrack: CommandTrack,
    LADDER: LADDER,
    SourceCache: SourceCache,
    VideoCapture: VideoCapture,
    VisibilityTracker: VisibilityTracker,
    attachCanvas: attachCanvas,
    canvasLevelFor: canvasLevelFor,
    createEncoderCore: createEncoderCore,
    frameHash: frameHash,
    installInstrumentation: installInstrumentation,
    isRendered: isRendered,
    selectStrategy: selectStrategy
});

const DEFAULT_TTL_MS = 10_000;
const LASER_TTL_MS = 1_500;
class AnnotationLayer {
    map;
    doc;
    win;
    now;
    host = null;
    canvas = null;
    ctx = null;
    shapes = new Map();
    raf = null;
    off = [];
    constructor(map, doc = document, win = window, now = () => Date.now()) {
        this.map = map;
        this.doc = doc;
        this.win = win;
        this.now = now;
    }
    get size() {
        return this.shapes.size;
    }
    /** Test hook: the shapes currently alive. */
    list() {
        return [...this.shapes.values()].map((s) => s.shape);
    }
    mount() {
        if (this.host)
            return;
        const host = this.doc.createElement("div");
        host.setAttribute("data-backstory", "annotations");
        host.style.cssText = "position:fixed;inset:0;z-index:2147483646;pointer-events:none;";
        const root = host.attachShadow({ mode: "closed" });
        const style = this.doc.createElement("style");
        style.textContent = ":host{pointer-events:none} canvas{position:fixed;inset:0;width:100vw;height:100vh;pointer-events:none;display:block}";
        root.appendChild(style);
        const canvas = this.doc.createElement("canvas");
        canvas.setAttribute("aria-hidden", "true");
        root.appendChild(canvas);
        (this.doc.body ?? this.doc.documentElement).appendChild(host);
        this.host = host;
        this.canvas = canvas;
        this.ctx = canvas.getContext ? canvas.getContext("2d") : null;
        this.resize();
        const onResize = () => this.resize();
        const onScroll = () => this.schedule();
        this.win.addEventListener("resize", onResize);
        this.win.addEventListener("scroll", onScroll, { passive: true, capture: true });
        this.off.push(() => this.win.removeEventListener("resize", onResize), () => this.win.removeEventListener("scroll", onScroll, { capture: true }));
    }
    resize() {
        if (!this.canvas)
            return;
        const dpr = Math.min(this.win.devicePixelRatio || 1, 3);
        this.canvas.width = Math.max(1, Math.round(this.win.innerWidth * dpr));
        this.canvas.height = Math.max(1, Math.round(this.win.innerHeight * dpr));
        this.ctx?.setTransform(dpr, 0, 0, dpr, 0, 0);
        this.schedule();
    }
    add(shape) {
        this.mount();
        const ttl = shape.kind === "laser" ? LASER_TTL_MS : Math.max(500, Math.min(60_000, shape.ttlMs || DEFAULT_TTL_MS));
        this.shapes.set(shape.id, { shape, expiresAt: this.now() + ttl });
        this.schedule();
    }
    clear() {
        this.shapes.clear();
        this.schedule();
    }
    destroy() {
        this.shapes.clear();
        if (this.raf !== null)
            this.win.cancelAnimationFrame?.(this.raf);
        this.raf = null;
        for (const f of this.off)
            f();
        this.off = [];
        this.host?.remove();
        this.host = null;
        this.canvas = null;
        this.ctx = null;
    }
    schedule() {
        if (this.raf !== null || !this.host)
            return;
        const raf = this.win.requestAnimationFrame ?? ((cb) => this.win.setTimeout(() => cb(this.now()), 16));
        this.raf = raf(() => {
            this.raf = null;
            this.draw();
            if (this.shapes.size > 0)
                this.schedule();
        });
    }
    /** Expire and render everything. Public for tests (jsdom has no canvas). */
    draw() {
        const t = this.now();
        for (const [id, s] of this.shapes)
            if (s.expiresAt <= t)
                this.shapes.delete(id);
        const ctx = this.ctx;
        if (!ctx)
            return;
        const W = this.win.innerWidth;
        const H = this.win.innerHeight;
        ctx.clearRect(0, 0, W, H);
        for (const { shape, expiresAt } of this.shapes.values()) {
            const remaining = expiresAt - t;
            const alpha = remaining < 800 ? Math.max(0.1, remaining / 800) : 1;
            ctx.save();
            ctx.globalAlpha = alpha;
            const color = "color" in shape ? shape.color : "#a855f7";
            ctx.strokeStyle = color;
            ctx.fillStyle = color;
            ctx.lineCap = "round";
            ctx.lineJoin = "round";
            switch (shape.kind) {
                case "pen": {
                    ctx.lineWidth = Math.max(1, shape.width || 3);
                    ctx.beginPath();
                    shape.points.forEach((p, i) => {
                        const x = p.x * W;
                        const y = p.y * H;
                        if (i === 0)
                            ctx.moveTo(x, y);
                        else {
                            // Smooth with a quadratic curve through the midpoint of consecutive samples.
                            const prev = shape.points[i - 1];
                            const mx = ((prev.x + p.x) / 2) * W;
                            const my = ((prev.y + p.y) / 2) * H;
                            ctx.quadraticCurveTo(prev.x * W, prev.y * H, mx, my);
                        }
                    });
                    ctx.stroke();
                    break;
                }
                case "arrow": {
                    const x1 = shape.from.x * W, y1 = shape.from.y * H, x2 = shape.to.x * W, y2 = shape.to.y * H;
                    ctx.lineWidth = 3;
                    ctx.beginPath();
                    ctx.moveTo(x1, y1);
                    ctx.lineTo(x2, y2);
                    ctx.stroke();
                    const ang = Math.atan2(y2 - y1, x2 - x1);
                    ctx.beginPath();
                    ctx.moveTo(x2, y2);
                    ctx.lineTo(x2 - 14 * Math.cos(ang - 0.4), y2 - 14 * Math.sin(ang - 0.4));
                    ctx.lineTo(x2 - 14 * Math.cos(ang + 0.4), y2 - 14 * Math.sin(ang + 0.4));
                    ctx.closePath();
                    ctx.fill();
                    break;
                }
                case "rect": {
                    ctx.lineWidth = 3;
                    ctx.strokeRect(shape.x * W, shape.y * H, shape.w * W, shape.h * H);
                    break;
                }
                case "highlight": {
                    const el = this.map.node(shape.nodeId);
                    const target = el && el.nodeType === 1 ? el : el?.parentElement;
                    if (!target || !target.isConnected)
                        break;
                    const r = target.getBoundingClientRect();
                    const pulse = 0.6 + 0.4 * Math.abs(Math.sin(t / 300));
                    ctx.globalAlpha = alpha * pulse;
                    ctx.lineWidth = 3;
                    ctx.strokeRect(r.left - 4, r.top - 4, r.width + 8, r.height + 8);
                    ctx.globalAlpha = alpha * 0.12;
                    ctx.fillRect(r.left - 4, r.top - 4, r.width + 8, r.height + 8);
                    break;
                }
                case "laser": {
                    const life = remaining / LASER_TTL_MS;
                    ctx.globalAlpha = Math.max(0.15, life);
                    ctx.beginPath();
                    ctx.arc(shape.x * W, shape.y * H, 8, 0, Math.PI * 2);
                    ctx.fill();
                    break;
                }
                case "text": {
                    ctx.font = "14px system-ui, sans-serif";
                    const x = shape.x * W, y = shape.y * H;
                    const pad = 6;
                    const w = ctx.measureText(shape.text).width + pad * 2;
                    ctx.fillStyle = "rgba(17,24,39,0.9)";
                    ctx.fillRect(x, y - 20, w, 26);
                    ctx.fillStyle = color;
                    ctx.fillText(shape.text, x + pad, y - 1);
                    break;
                }
            }
            ctx.restore();
        }
    }
}

const BLOCKED_AUTOCOMPLETE = new Set(CONTROL_POLICY.blockedAutocomplete);
const BLOCKED_TYPES = new Set(CONTROL_POLICY.blockedInputTypes);
/** Policy check shared by every command that touches an element (PLAN §14.4). */
function policyFor(el) {
    for (const sel of CONTROL_POLICY.blockedSelectors) {
        try {
            if (el.closest(sel))
                return "blocked_by_policy";
        }
        catch {
            /* invalid selector: ignore */
        }
    }
    const tag = el.tagName.toLowerCase();
    if (tag === "input") {
        const input = el;
        const type = (input.type || "text").toLowerCase();
        if (BLOCKED_TYPES.has(type))
            return "blocked_by_policy";
        if (type === "file")
            return "not_synthesizable";
        const ac = (input.getAttribute("autocomplete") || "").toLowerCase();
        if (ac && [...BLOCKED_AUTOCOMPLETE].some((b) => ac.split(/\s+/).includes(b)))
            return "blocked_by_policy";
    }
    if (tag === "textarea" || tag === "select") {
        const ac = (el.getAttribute("autocomplete") || "").toLowerCase();
        if (ac && BLOCKED_AUTOCOMPLETE.has(ac))
            return "blocked_by_policy";
    }
    if (tag === "a" && el.hasAttribute("download"))
        return "not_synthesizable";
    return null;
}
/** Sets a form control's value the way React 16+ expects (native setter + tracker reset). */
function setNativeValue(el, value) {
    const proto = Object.getPrototypeOf(el);
    const desc = Object.getOwnPropertyDescriptor(proto, "value") ?? Object.getOwnPropertyDescriptor(el, "value");
    const prev = el.value;
    if (desc?.set)
        desc.set.call(el, value);
    else
        el.value = value;
    // React's controlled inputs keep a tracker of the last value they set; if it
    // matches the new value the change is swallowed. Rewind it to the previous value.
    const tracker = el._valueTracker;
    if (tracker)
        tracker.setValue(prev);
}
function isFormField(el) {
    if (!el)
        return false;
    const t = el.tagName.toLowerCase();
    return t === "input" || t === "textarea" || t === "select" || el.isContentEditable === true;
}
function center(el) {
    const r = el.getBoundingClientRect();
    return { x: Math.round(r.left + r.width / 2), y: Math.round(r.top + r.height / 2) };
}
/**
 * Executes agent commands on the live page under the control policy. Every
 * executed action is recorded with `actor` so the replay shows a second cursor.
 */
class ControlExecutor {
    deps;
    win;
    doc;
    constructor(deps) {
        this.deps = deps;
        this.win = deps.win ?? window;
        this.doc = deps.doc ?? document;
    }
    execute(cmd, actor) {
        try {
            switch (cmd.kind) {
                case "click":
                    return this.click(cmd.nodeId, actor, cmd.x, cmd.y);
                case "input":
                    return this.input(cmd.nodeId, cmd.value, actor);
                case "key":
                    return this.key(cmd.key, actor, cmd.nodeId);
                case "scroll":
                    return this.scroll(cmd.nodeId, cmd.top, cmd.left, cmd.by === true);
                case "navigate":
                    return this.navigate(cmd.url);
                case "focus":
                    return this.focus(cmd.nodeId);
                default:
                    return { ok: false, error: "not_synthesizable" };
            }
        }
        catch (e) {
            return { ok: false, error: "not_synthesizable", detail: e?.message };
        }
    }
    resolve(nodeId) {
        const n = this.deps.map.node(nodeId);
        if (!n || !n.isConnected)
            return { error: "target_missing" };
        const el = n.nodeType === 1 ? n : n.parentElement;
        if (!el)
            return { error: "target_missing" };
        const pol = policyFor(el);
        if (pol)
            return { error: pol };
        return { el };
    }
    mouse(el, type, x, y, extra = {}) {
        const init = { bubbles: true, cancelable: true, composed: true, clientX: x, clientY: y, button: 0, buttons: type.endsWith("down") ? 1 : 0, ...extra };
        const ev = type.startsWith("pointer") && typeof PointerEvent !== "undefined" ? new PointerEvent(type, { ...init, pointerId: 1, pointerType: "mouse", isPrimary: true }) : new MouseEvent(type, init);
        return el.dispatchEvent(ev);
    }
    click(nodeId, actor, nx, ny) {
        const r = this.resolve(nodeId);
        if ("error" in r)
            return { ok: false, error: r.error };
        const el = r.el;
        if (el.closest("[disabled]") || el.disabled)
            return { ok: false, error: "not_synthesizable" };
        const pt = typeof nx === "number" && typeof ny === "number" && nx <= 1 && ny <= 1 ? { x: Math.round(nx * this.win.innerWidth), y: Math.round(ny * this.win.innerHeight) } : center(el);
        el.focus?.({ preventScroll: true });
        this.mouse(el, "pointerdown", pt.x, pt.y);
        this.mouse(el, "mousedown", pt.x, pt.y, { buttons: 1 });
        this.mouse(el, "pointerup", pt.x, pt.y);
        this.mouse(el, "mouseup", pt.x, pt.y);
        this.mouse(el, "click", pt.x, pt.y);
        const id = this.deps.map.id(el);
        const t = this.deps.now();
        this.deps.emit({ type: "pointer", t, kind: "click", x: pt.x, y: pt.y, id, actor });
        return { ok: true };
    }
    input(nodeId, value, actor) {
        const r = this.resolve(nodeId);
        if ("error" in r)
            return { ok: false, error: r.error };
        const el = r.el;
        if (!isFormField(el))
            return { ok: false, error: "not_synthesizable" };
        if (el.readOnly || el.disabled)
            return { ok: false, error: "not_synthesizable" };
        el.focus?.();
        if (el.isContentEditable && el.tagName.toLowerCase() !== "input") {
            el.textContent = value;
        }
        else {
            setNativeValue(el, value);
        }
        el.dispatchEvent(typeof InputEvent !== "undefined" ? new InputEvent("input", { bubbles: true, data: value, inputType: "insertText" }) : new Event("input", { bubbles: true }));
        el.dispatchEvent(new Event("change", { bubbles: true }));
        const id = this.deps.map.id(el);
        // The value is recorded masked: the agent typed it, it may still be personal data.
        this.deps.emit({ type: "input", t: this.deps.now(), id: id ?? nodeId, value: "•".repeat(Math.min(value.length, 32)), masked: true, actor });
        return { ok: true };
    }
    key(key, actor, nodeId) {
        let target = this.doc.activeElement;
        if (typeof nodeId === "number") {
            const r = this.resolve(nodeId);
            if ("error" in r)
                return { ok: false, error: r.error };
            target = r.el;
        }
        if (!target || target === this.doc.body)
            target = this.doc.body;
        if (!target)
            return { ok: false, error: "target_missing" };
        const pol = policyFor(target);
        if (pol)
            return { ok: false, error: pol };
        const init = { key, bubbles: true, cancelable: true, composed: true };
        const down = target.dispatchEvent(new KeyboardEvent("keydown", init));
        target.dispatchEvent(new KeyboardEvent("keypress", init));
        target.dispatchEvent(new KeyboardEvent("keyup", init));
        if (down && key === "Enter" && isFormField(target)) {
            const form = target.form ?? target.closest("form");
            if (form) {
                if (typeof form.requestSubmit === "function")
                    form.requestSubmit();
                else
                    form.dispatchEvent(new Event("submit", { bubbles: true, cancelable: true }));
            }
        }
        this.deps.emit({ type: "custom", t: this.deps.now(), name: "backstory.assist.key", props: { key: key.length === 1 ? "•" : key, actor } });
        return { ok: true };
    }
    scroll(nodeId, top, left, by = false) {
        // Wheel deltas arrive one after another, so relative scrolls jump instantly;
        // smooth animation would make each one fight the next.
        const behavior = by ? "auto" : "smooth";
        if (nodeId === 0) {
            const scroller = this.doc.scrollingElement ?? this.doc.documentElement;
            const base = by ? { top: scroller?.scrollTop ?? this.win.scrollY, left: scroller?.scrollLeft ?? this.win.scrollX } : { top: 0, left: this.win.scrollX };
            const nextTop = by ? base.top + top : top;
            const nextLeft = by ? base.left + (left ?? 0) : left ?? this.win.scrollX;
            if (typeof this.win.scrollTo === "function")
                this.win.scrollTo({ top: nextTop, left: nextLeft, behavior });
            else if (scroller) {
                scroller.scrollTop = nextTop;
                scroller.scrollLeft = nextLeft;
            }
            return { ok: true };
        }
        const r = this.resolve(nodeId);
        if ("error" in r)
            return { ok: false, error: r.error };
        const el = r.el;
        const nextTop = by ? el.scrollTop + top : top;
        const nextLeft = by ? el.scrollLeft + (left ?? 0) : left ?? el.scrollLeft;
        if (typeof el.scrollTo === "function")
            el.scrollTo({ top: nextTop, left: nextLeft, behavior });
        else {
            el.scrollTop = nextTop;
            el.scrollLeft = nextLeft;
        }
        return { ok: true };
    }
    navigate(url) {
        let target;
        try {
            target = new URL(url, this.win.location.href);
        }
        catch {
            return { ok: false, error: "blocked_by_policy" };
        }
        if (target.origin !== this.win.location.origin || !/^https?:$/.test(target.protocol))
            return { ok: false, error: "blocked_by_policy" };
        this.win.location.assign(target.href);
        return { ok: true };
    }
    focus(nodeId) {
        const r = this.resolve(nodeId);
        if ("error" in r)
            return { ok: false, error: r.error };
        r.el.focus?.();
        return { ok: true };
    }
}

/**
 * Consent ladder (PLAN §14.2a). Pure state machine: no DOM, no sockets. The
 * controller wires it to the modal UI and the live socket.
 *
 * Rules enforced here:
 *  - one request → one answer; default is "no" (60 s timeout, navigation)
 *  - one support grant (`cobrowse` on the wire) enables chat, annotations, and
 *    remote control; voice remains an independent request
 *  - grants remain active until either side stops them
 *  - tenant policy can only narrow (disabled capabilities auto-decline)
 */
const CAPABILITIES = ["cobrowse", "voice", "annotate", "control", "record"];
const REQUEST_TIMEOUT_MS = 60_000;
function substituteWording(text, agent) {
    return text.replace(/\{agent\}/g, agent.name || "A support agent").replace(/\{org\}/g, agent.org || "support");
}
class ConsentLadder {
    send;
    consent = { cobrowse: false, voice: false, annotate: false, control: false, record: false };
    agent = null;
    pending = new Map();
    listeners = new Set();
    disabled;
    now;
    requestTimeoutMs;
    constructor(send, opts = {}) {
        this.send = send;
        this.disabled = new Set(opts.disabled ?? []);
        this.now = opts.now ?? (() => Date.now());
        this.requestTimeoutMs = opts.requestTimeoutMs ?? REQUEST_TIMEOUT_MS;
    }
    on(fn) {
        this.listeners.add(fn);
        return () => void this.listeners.delete(fn);
    }
    fire(e) {
        for (const l of this.listeners) {
            try {
                l(e);
            }
            catch {
                /* listeners never break the ladder */
            }
        }
    }
    setDisabled(caps) {
        this.disabled = new Set(caps);
        for (const c of this.disabled)
            if (this.consent[c])
                this.revoke(c, "user");
    }
    isDisabled(cap) {
        return this.disabled.has(cap);
    }
    get active() {
        return CAPABILITIES.filter((c) => this.consent[c]);
    }
    get anyActive() {
        return this.active.length > 0;
    }
    get pendingRequests() {
        return [...this.pending.values()];
    }
    /** An agent asked for a capability. Returns false when auto-declined. */
    request(requestId, capability, agent, wording) {
        if (!CAPABILITIES.includes(capability))
            return false;
        this.agent = agent;
        if (this.disabled.has(capability)) {
            this.send({ type: "response", requestId, capability, granted: false, reason: "declined" });
            return false;
        }
        if (this.consent[capability]) {
            // Already granted: answer immediately, no new prompt.
            this.send({ type: "response", requestId, capability, granted: true });
            return true;
        }
        // One outstanding prompt per capability; a repeat replaces the old one.
        for (const p of this.pending.values())
            if (p.capability === capability)
                this.closePending(p.requestId, false, "declined", false);
        const text = substituteWording(wording && wording.trim() ? wording : CONSENT_WORDING[capability], agent);
        const req = { requestId, capability, agent, wording: text, expiresAt: this.now() + this.requestTimeoutMs, timer: null };
        req.timer = setTimeout(() => this.respond(requestId, false, "timeout"), this.requestTimeoutMs);
        this.pending.set(requestId, req);
        this.fire({ type: "prompt", request: req });
        return true;
    }
    /** The user answered a prompt (or the system did: timeout / navigation). */
    respond(requestId, granted, reason) {
        const p = this.pending.get(requestId);
        if (!p)
            return;
        this.closePending(requestId, granted, reason, true);
        if (granted)
            this.grant(p.capability, p.agent);
    }
    closePending(requestId, granted, reason, notify) {
        const p = this.pending.get(requestId);
        if (!p)
            return;
        if (p.timer)
            clearTimeout(p.timer);
        this.pending.delete(requestId);
        this.fire({ type: "prompt_closed", requestId });
        if (notify)
            this.send({ type: "response", requestId, capability: p.capability, granted, reason: granted ? undefined : (reason ?? "declined") });
    }
    grant(cap, agent) {
        this.consent[cap] = true;
        if (cap === "cobrowse") {
            if (!this.disabled.has("annotate"))
                this.consent.annotate = true;
            if (!this.disabled.has("control"))
                this.consent.control = true;
        }
        delete this.consent.controlExpiresAt;
        this.fire({ type: "granted", capability: cap, agent });
        this.fire({ type: "changed", consent: { ...this.consent } });
    }
    /** Revoke a capability. Cobrowse revocation cascades to annotate and control. */
    revoke(cap, by) {
        if (cap === "cobrowse") {
            if (!this.consent.cobrowse && !this.consent.annotate && !this.consent.control)
                return;
            this.consent.cobrowse = false;
            this.consent.annotate = false;
            this.consent.control = false;
            delete this.consent.controlExpiresAt;
            this.send({ type: "revoked", capability: "cobrowse", by });
            this.fire({ type: "revoked", capability: "cobrowse", by });
            this.fire({ type: "changed", consent: { ...this.consent } });
            return;
        }
        const cascade = [cap];
        let changed = false;
        for (const c of cascade) {
            if (!this.consent[c])
                continue;
            this.consent[c] = false;
            if (c === "control") {
                delete this.consent.controlExpiresAt;
            }
            changed = true;
            this.send({ type: "revoked", capability: c, by });
            this.fire({ type: "revoked", capability: c, by });
        }
        if (changed)
            this.fire({ type: "changed", consent: { ...this.consent } });
    }
    /** Page navigation declines stale prompts; an accepted support session continues. */
    onNavigation() {
        for (const p of [...this.pending.values()])
            this.respond(p.requestId, false, "navigated");
    }
    /** Revoke everything (session end / stop). */
    reset(by = "user") {
        for (const p of [...this.pending.values()])
            this.closePending(p.requestId, false, "declined", true);
        for (const c of ["control", "annotate", "record", "voice", "cobrowse"])
            if (this.consent[c])
                this.revoke(c, by);
        this.agent = null;
    }
}

/** Base URL of the api: the configured apiUrl, else the ingest URL with `/v1/ingest` removed. */
function apiBaseUrl(apiUrl, ingestUrl) {
    if (apiUrl)
        return apiUrl.replace(/\/$/, "");
    return ingestUrl.replace(/\/v1\/ingest\/?$/, "").replace(/\/$/, "");
}
function deriveLiveUrl(apiBase, projectKey, sessionId, visitId, sdk) {
    let u;
    try {
        u = new URL(apiBase, typeof location !== "undefined" ? location.href : "http://localhost");
    }
    catch {
        u = new URL("http://localhost:8081");
    }
    u.protocol = u.protocol === "https:" ? "wss:" : "ws:";
    u.pathname = (u.pathname.replace(/\/$/, "") || "") + "/v1/live";
    u.search = "";
    u.searchParams.set("key", projectKey);
    u.searchParams.set("session", sessionId);
    u.searchParams.set("visit", visitId);
    u.searchParams.set("sdk", sdk);
    return u.toString();
}
class LiveSocket {
    opts;
    ws = null;
    attempts = 0;
    closedByUs = false;
    reconnectTimer = null;
    heartbeat = null;
    queue = [];
    connected = false;
    constructor(opts) {
        this.opts = opts;
    }
    get url() {
        return this.opts.url;
    }
    connect() {
        if (this.ws || this.closedByUs)
            return;
        const Impl = this.opts.WebSocketImpl ?? (typeof WebSocket !== "undefined" ? WebSocket : undefined);
        if (!Impl) {
            this.opts.log?.("[backstory] live: WebSocket unavailable");
            return;
        }
        let ws;
        try {
            ws = new Impl(this.opts.url);
        }
        catch (e) {
            this.opts.log?.("[backstory] live: connect failed", e);
            this.scheduleReconnect();
            return;
        }
        this.ws = ws;
        ws.onopen = () => {
            if (this.ws !== ws)
                return;
            this.connected = true;
            this.attempts = 0;
            const st = this.opts.stateProvider();
            if (st)
                this.raw(st);
            for (const m of this.queue.splice(0))
                this.raw(m);
            this.startHeartbeat();
            this.opts.onOpen?.();
        };
        ws.onmessage = (ev) => {
            if (typeof ev.data !== "string")
                return;
            let msg;
            try {
                msg = JSON.parse(ev.data);
            }
            catch {
                return;
            }
            if (!msg || typeof msg.type !== "string")
                return;
            switch (msg.type) {
                case "hello":
                    this.opts.onHello?.(msg.assist ?? null);
                    return;
                case "pong":
                    this.opts.onPong?.(msg.serverTime ?? 0);
                    return;
                case "error": {
                    const err = msg;
                    this.opts.onServerError?.(err.error ?? "error", err.code);
                    return;
                }
                default:
                    this.opts.onMessage(msg);
            }
        };
        ws.onerror = () => {
            /* onclose follows */
        };
        ws.onclose = (ev) => {
            if (this.ws !== ws)
                return;
            this.ws = null;
            const wasConnected = this.connected;
            this.connected = false;
            this.stopHeartbeat();
            // 1008 is the server closing because Live Assist is no longer allowed.
            // Retrying would only hammer a 402.
            const code = ev?.code;
            if (code === 1008)
                this.closedByUs = true;
            if (wasConnected)
                this.opts.onClose?.(code);
            if (!this.closedByUs)
                this.scheduleReconnect();
        };
    }
    scheduleReconnect() {
        if (this.reconnectTimer || this.closedByUs)
            return;
        const max = this.opts.maxBackoffMs ?? 30_000;
        const delay = Math.min(max, 500 * 2 ** Math.min(this.attempts, 8)) * (0.75 + Math.random() * 0.5);
        this.attempts++;
        this.reconnectTimer = setTimeout(() => {
            this.reconnectTimer = null;
            this.connect();
        }, delay);
    }
    startHeartbeat() {
        this.stopHeartbeat();
        const ms = this.opts.heartbeatMs ?? 20_000;
        this.heartbeat = setInterval(() => this.rawAny({ type: "ping" }), ms);
    }
    stopHeartbeat() {
        if (this.heartbeat)
            clearInterval(this.heartbeat);
        this.heartbeat = null;
    }
    raw(m) {
        this.rawAny(m);
    }
    rawAny(m) {
        try {
            this.ws?.send(JSON.stringify(m));
        }
        catch {
            /* dropped; state is re-sent on reconnect */
        }
    }
    /** Send now, or queue (bounded) until the socket opens. */
    send(m) {
        if (this.connected && this.ws)
            this.raw(m);
        else if (this.queue.length < 50)
            this.queue.push(m);
    }
    close() {
        this.closedByUs = true;
        if (this.reconnectTimer)
            clearTimeout(this.reconnectTimer);
        this.reconnectTimer = null;
        this.stopHeartbeat();
        const ws = this.ws;
        this.ws = null;
        this.connected = false;
        try {
            ws?.close(1000, "ended");
        }
        catch {
            /* ignore */
        }
    }
}

/**
 * Live Assist chrome rendered in a closed shadow root: one consent modal per
 * capability, the persistent banner listing active capabilities (one Stop per
 * capability), and the help-code widget. Host CSS cannot reach any of it.
 */
const LABELS = {
    cobrowse: "Support access",
    voice: "Voice call",
    annotate: "Drawing",
    control: "Remote control",
    record: "Recording",
};
const CSS = `
:host{all:initial}
*{box-sizing:border-box;font:13px/1.4 system-ui,-apple-system,Segoe UI,Roboto,sans-serif;color:#e5e7eb}
.banner{position:fixed;top:0;left:0;right:0;z-index:2147483647;display:flex;align-items:center;gap:10px;padding:8px 14px;background:#111827;border-bottom:1px solid #4c1d95;box-shadow:0 2px 12px rgba(0,0,0,.35)}
.banner .dot{width:8px;height:8px;border-radius:50%;background:#a855f7;box-shadow:0 0 0 4px rgba(168,85,247,.25)}
.banner .who{font-weight:600}
.banner .caps{display:flex;gap:6px;flex-wrap:wrap;margin-left:auto}
.banner.notice{background:#3b1111;border-bottom-color:#7f1d1d}
.banner.notice .msg{flex:1}
.chip{display:inline-flex;align-items:center;gap:6px;background:#1f2937;border:1px solid #374151;border-radius:999px;padding:3px 6px 3px 10px}
.chip .timer{font-variant-numeric:tabular-nums;color:#fbbf24}
.chip .lvl{width:26px;height:6px;background:#374151;border-radius:3px;overflow:hidden}
.chip .lvl i{display:block;height:100%;width:0;background:#22c55e}
button{cursor:pointer;border:0;border-radius:6px;padding:4px 9px;background:#374151;color:#e5e7eb}
button:hover{background:#4b5563}
button:focus-visible{outline:2px solid #a855f7;outline-offset:1px}
button.stop{background:#7f1d1d}
button.stop:hover{background:#991b1b}
button.primary{background:#6d28d9}
button.primary:hover{background:#7c3aed}
.backdrop{position:fixed;inset:0;z-index:2147483647;background:rgba(0,0,0,.45);display:flex;align-items:center;justify-content:center}
.modal{width:min(440px,calc(100vw - 32px));background:#111827;border:1px solid #374151;border-radius:12px;padding:18px 18px 14px;box-shadow:0 20px 60px rgba(0,0,0,.5)}
.modal h2{margin:0 0 12px;font-size:15px}
.modal p{margin:0 0 14px;color:#d1d5db}
.modal .who-line{display:flex;align-items:center;gap:10px;margin:0 0 12px;padding:8px 10px;background:#1f2937;border:1px solid #374151;border-radius:9px}
.modal .who-avatar{flex:none;display:inline-grid;place-items:center;width:32px;height:32px;border-radius:50%;background:#6d28d9;color:#fff;font-weight:700;font-size:12px}
.modal .who-line strong{display:block;font-size:14px;font-weight:700;color:#fff}
.modal .who-line small{color:#9ca3af}
.modal ul.caps{margin:0 0 14px;padding:0 0 0 18px;color:#e5e7eb}
.modal ul.caps li{margin:3px 0}
.modal .device{display:grid;gap:5px;margin:0 0 14px;color:#d1d5db}
.modal .device select{width:100%;border:1px solid #4b5563;border-radius:7px;background:#0f172a;color:#f9fafb;padding:8px 9px}
.modal .row{display:flex;gap:8px;justify-content:flex-end}
.modal .note{color:#9ca3af;font-size:12px;margin-top:10px;font-variant-numeric:tabular-nums}
.code{position:fixed;right:16px;bottom:16px;z-index:2147483647;background:#111827;border:1px solid #374151;border-radius:12px;padding:12px 14px;box-shadow:0 10px 30px rgba(0,0,0,.4);min-width:220px}
.code .num{font-size:26px;letter-spacing:4px;font-weight:700;font-variant-numeric:tabular-nums;color:#fff;margin:4px 0}
.code small{color:#9ca3af}
.code .x{position:absolute;top:6px;right:6px;padding:2px 6px}
.chat{position:fixed;right:16px;bottom:16px;z-index:2147483646;width:min(340px,calc(100vw - 32px));background:#111827;border:1px solid #374151;border-radius:12px;box-shadow:0 16px 40px rgba(0,0,0,.45);overflow:hidden}
.chat-head{display:flex;align-items:center;gap:8px;padding:10px 12px;border-bottom:1px solid #374151}
.chat-head strong{font-size:14px}
.chat-head button{margin-left:auto}
.chat-lines{display:flex;flex-direction:column;gap:8px;max-height:240px;overflow:auto;padding:12px}
.chat-line{max-width:86%;padding:7px 9px;border-radius:9px;background:#1f2937;white-space:pre-wrap;overflow-wrap:anywhere}
.chat-line.user{align-self:flex-end;background:#5b21b6}
.chat-send{display:flex;gap:7px;padding:10px 12px;border-top:1px solid #374151}
.chat-send input{min-width:0;flex:1;border:1px solid #4b5563;border-radius:7px;background:#0f172a;color:#f9fafb;padding:7px 9px}
.chat-min{position:fixed;right:16px;bottom:16px;z-index:2147483646;border-radius:999px;background:#6d28d9;padding:9px 13px;box-shadow:0 8px 24px rgba(0,0,0,.4)}
.chat-min .unread{display:inline-grid;place-items:center;min-width:18px;height:18px;margin-left:6px;border-radius:999px;background:#ef4444;color:white;font-size:11px;font-weight:700}
.sr{position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0 0 0 0)}
`;
class LiveUI {
    cb;
    doc;
    host = null;
    root = null;
    banner = null;
    notice = null;
    modal = null;
    codeEl = null;
    chatEl = null;
    chatLines = null;
    chatMin = null;
    chatMinimized = false;
    /** Minimized because the user asked for it, not because chat opened that way. */
    chatDismissed = false;
    chatUnread = 0;
    currentRequest = null;
    countdown = null;
    lastFocus = null;
    voiceDeviceId = "default";
    constructor(cb, doc = document) {
        this.cb = cb;
        this.doc = doc;
    }
    ensure() {
        if (this.root)
            return this.root;
        const host = this.doc.createElement("div");
        host.setAttribute("data-backstory", "live");
        host.style.cssText = "all:initial;position:fixed;top:0;left:0;width:0;height:0;z-index:2147483647";
        const root = host.attachShadow({ mode: "closed" });
        const style = this.doc.createElement("style");
        style.textContent = CSS;
        root.appendChild(style);
        (this.doc.body ?? this.doc.documentElement).appendChild(host);
        this.host = host;
        this.root = root;
        return root;
    }
    // ---- consent modal ------------------------------------------------------
    /** Show a consent prompt; only one is visible at a time (the newest wins). */
    showPrompt(req) {
        const root = this.ensure();
        this.closePrompt();
        this.currentRequest = req;
        this.lastFocus = this.doc.activeElement;
        const back = this.doc.createElement("div");
        back.className = "backdrop";
        const m = this.doc.createElement("div");
        m.className = "modal";
        m.setAttribute("role", "dialog");
        m.setAttribute("aria-modal", "true");
        m.setAttribute("aria-labelledby", "backstory-live-title");
        m.setAttribute("aria-describedby", "backstory-live-desc");
        m.setAttribute("data-capability", req.capability);
        const h = this.doc.createElement("h2");
        h.id = "backstory-live-title";
        h.textContent = `${LABELS[req.capability]} request`;
        // Who is asking comes before what they are asking for: the name and team
        // are the part of the prompt the user has to judge.
        const who = this.doc.createElement("div");
        who.className = "who-line";
        const avatar = this.doc.createElement("span");
        avatar.className = "who-avatar";
        avatar.setAttribute("aria-hidden", "true");
        avatar.textContent = initials(req.agent.name);
        const whoText = this.doc.createElement("span");
        const name = this.doc.createElement("strong");
        name.setAttribute("data-agent", "");
        name.textContent = req.agent.name || "A support agent";
        const org = this.doc.createElement("small");
        org.textContent = req.agent.org || "support";
        whoText.append(name, org);
        who.append(avatar, whoText);
        // What the grant covers, as a list. Support access is a fixed bundle, so
        // the user reads two items instead of a paragraph.
        let desc;
        if (req.capability === "cobrowse") {
            const list = this.doc.createElement("ul");
            list.className = "caps";
            for (const item of ["Annotations", "Remote control"]) {
                const li = this.doc.createElement("li");
                li.textContent = item;
                list.appendChild(li);
            }
            desc = list;
        }
        else {
            desc = this.doc.createElement("p");
            desc.textContent = req.wording;
        }
        desc.id = "backstory-live-desc";
        const row = this.doc.createElement("div");
        row.className = "row";
        const decline = this.doc.createElement("button");
        decline.textContent = "Not now";
        decline.setAttribute("data-action", "decline");
        decline.addEventListener("click", () => this.cb.onRespond(req.requestId, false));
        const accept = this.doc.createElement("button");
        accept.className = "primary";
        accept.textContent = req.capability === "cobrowse" ? "Allow support" : "Allow";
        accept.setAttribute("data-action", "accept");
        accept.addEventListener("click", () => this.cb.onRespond(req.requestId, true));
        row.append(decline, accept);
        let device = null;
        if (req.capability === "voice") {
            device = this.doc.createElement("label");
            device.className = "device";
            device.textContent = "Microphone";
            const select = this.doc.createElement("select");
            select.setAttribute("data-voice-device", "");
            select.setAttribute("autocomplete", "off");
            const fallback = this.doc.createElement("option");
            fallback.value = "default";
            fallback.textContent = "Default microphone";
            select.appendChild(fallback);
            select.value = this.voiceDeviceId;
            select.addEventListener("change", () => {
                this.voiceDeviceId = select.value || "default";
            });
            device.appendChild(select);
            const mediaDevices = this.doc.defaultView?.navigator.mediaDevices;
            void mediaDevices?.enumerateDevices?.().then((devices) => {
                const microphones = devices.filter((d) => d.kind === "audioinput");
                select.textContent = "";
                if (!microphones.some((d) => d.deviceId === "default"))
                    select.appendChild(fallback);
                microphones.forEach((d, i) => {
                    const option = this.doc.createElement("option");
                    option.value = d.deviceId;
                    option.textContent = d.deviceId === "default" ? "Default microphone" : d.label || `Microphone ${i + 1}`;
                    select.appendChild(option);
                });
                select.value = [...select.options].some((o) => o.value === this.voiceDeviceId) ? this.voiceDeviceId : "default";
                this.voiceDeviceId = select.value;
            }).catch(() => undefined);
        }
        // An unanswered prompt declines itself; say when, so ignoring it is an
        // informed choice rather than a dialog that vanishes for no visible reason.
        const note = this.doc.createElement("div");
        note.className = "note";
        note.setAttribute("data-expiry", "");
        const tick = () => {
            const left = Math.max(0, Math.ceil((req.expiresAt - Date.now()) / 1000));
            note.textContent = `Closes in ${left}s`;
            if (left <= 0)
                this.stopCountdown();
        };
        tick();
        this.stopCountdown();
        this.countdown = setInterval(tick, 1000);
        m.append(h, who, desc);
        if (device)
            m.appendChild(device);
        m.append(row, note);
        back.appendChild(m);
        // Focus trap: Tab cycles between the two buttons; Esc declines.
        m.addEventListener("keydown", (e) => {
            if (e.key === "Escape") {
                e.preventDefault();
                this.cb.onRespond(req.requestId, false);
            }
            else if (e.key === "Tab") {
                e.preventDefault();
                (this.root?.activeElement === decline ? accept : decline).focus();
            }
        });
        root.appendChild(back);
        this.modal = back;
        decline.focus();
    }
    closePrompt(requestId) {
        if (!this.modal)
            return;
        if (requestId && this.currentRequest && this.currentRequest.requestId !== requestId)
            return;
        this.stopCountdown();
        this.modal.remove();
        this.modal = null;
        this.currentRequest = null;
        try {
            this.lastFocus?.focus?.();
        }
        catch {
            /* ignore */
        }
        this.lastFocus = null;
    }
    promptVisibleFor() {
        return this.currentRequest?.requestId ?? null;
    }
    selectedVoiceDevice() {
        return this.voiceDeviceId;
    }
    stopCountdown() {
        if (!this.countdown)
            return;
        clearInterval(this.countdown);
        this.countdown = null;
    }
    // ---- banner ---------------------------------------------------------------
    /** Render (or remove) the persistent banner for the active capabilities. */
    renderBanner(active, agentName, voice) {
        if (active.length === 0) {
            this.banner?.remove();
            this.banner = null;
            return;
        }
        const root = this.ensure();
        if (!this.banner) {
            this.banner = this.doc.createElement("div");
            this.banner.className = "banner";
            this.banner.setAttribute("role", "status");
            this.banner.setAttribute("aria-live", "polite");
            this.banner.setAttribute("data-backstory", "live-banner");
            root.appendChild(this.banner);
        }
        const b = this.banner;
        b.textContent = "";
        const dot = this.doc.createElement("span");
        dot.className = "dot";
        dot.setAttribute("aria-hidden", "true");
        const who = this.doc.createElement("span");
        who.className = "who";
        who.textContent = `${agentName || "A support agent"} is assisting you`;
        const caps = this.doc.createElement("div");
        caps.className = "caps";
        // Annotation and control are included in the support grant. Present one
        // clear support chip instead of exposing internal wire capabilities.
        const visible = active.filter((cap) => cap === "voice" || cap === "cobrowse");
        for (const cap of visible) {
            const chip = this.doc.createElement("span");
            chip.className = "chip";
            chip.setAttribute("data-cap", cap);
            const label = this.doc.createElement("span");
            label.textContent = LABELS[cap];
            chip.appendChild(label);
            if (cap === "voice" && voice) {
                const lvl = this.doc.createElement("span");
                lvl.className = "lvl";
                lvl.setAttribute("aria-hidden", "true");
                const bar = this.doc.createElement("i");
                bar.style.width = `${Math.round(Math.min(1, voice.level) * 100)}%`;
                lvl.appendChild(bar);
                chip.appendChild(lvl);
                const mute = this.doc.createElement("button");
                mute.textContent = voice.muted ? "Unmute" : "Mute";
                mute.setAttribute("data-action", "mute");
                mute.setAttribute("aria-pressed", String(voice.muted));
                mute.addEventListener("click", () => this.cb.onMute(!voice.muted));
                chip.appendChild(mute);
            }
            const stop = this.doc.createElement("button");
            stop.className = "stop";
            stop.textContent = cap === "voice" ? "Hang up" : "Stop";
            stop.setAttribute("data-action", "stop");
            stop.setAttribute("aria-label", `Stop ${LABELS[cap].toLowerCase()}`);
            stop.addEventListener("click", () => this.cb.onStop(cap));
            chip.appendChild(stop);
            caps.appendChild(chip);
        }
        b.append(dot, who, caps);
    }
    /** A full-width line after Live Assist stops, until the visitor dismisses it. */
    showNotice(text) {
        const root = this.ensure();
        this.banner?.remove();
        this.banner = null;
        this.notice?.remove();
        const bar = this.doc.createElement("div");
        bar.className = "banner notice";
        bar.setAttribute("role", "status");
        bar.setAttribute("aria-live", "polite");
        bar.setAttribute("data-backstory", "live-notice");
        const msg = this.doc.createElement("span");
        msg.className = "msg";
        msg.textContent = text;
        const dismiss = this.doc.createElement("button");
        dismiss.textContent = "Dismiss";
        dismiss.setAttribute("data-action", "dismiss-notice");
        dismiss.addEventListener("click", () => {
            bar.remove();
            if (this.notice === bar)
                this.notice = null;
        });
        bar.append(msg, dismiss);
        root.appendChild(bar);
        this.notice = bar;
    }
    // ---- help code --------------------------------------------------------------
    showCode(code, message) {
        const root = this.ensure();
        this.codeEl?.remove();
        const box = this.doc.createElement("div");
        box.className = "code";
        box.setAttribute("role", "dialog");
        box.setAttribute("aria-label", "Support code");
        box.setAttribute("data-backstory", "live-code");
        const x = this.doc.createElement("button");
        x.className = "x";
        x.textContent = "×";
        x.setAttribute("aria-label", "Close");
        x.addEventListener("click", () => this.cb.onCloseCode());
        const small = this.doc.createElement("small");
        small.textContent = message ?? "Read this code to the support agent";
        const num = this.doc.createElement("div");
        num.className = "num";
        num.setAttribute("data-code", code);
        num.textContent = code.replace(/(\d{3})(\d{3})/, "$1 $2");
        const hint = this.doc.createElement("small");
        hint.textContent = "They can see this page. Anything more has to be asked for.";
        box.append(x, small, num, hint);
        root.appendChild(box);
        this.codeEl = box;
    }
    hideCode() {
        this.codeEl?.remove();
        this.codeEl = null;
    }
    // ---- chat -------------------------------------------------------------------
    /** Show an agent message to the end user, opening chat unless they minimized it. */
    receiveChat(text, agentName = "Support") {
        // Once support is actively talking, the temporary lookup code no longer
        // needs to occupy the same lower-right corner.
        this.hideCode();
        this.ensureChat(agentName);
        this.appendChat("agent", text);
        // Chat starts minimized on the grant, so only a minimize the user asked for
        // keeps the first message behind a badge.
        if (this.chatMinimized && this.chatDismissed) {
            this.chatUnread += 1;
            this.renderChatMin(agentName);
        }
        else {
            this.showChat(agentName);
        }
    }
    /**
     * Put chat within reach as soon as support access is granted: the pill alone,
     * so the user knows they can talk without a panel taking over their page.
     */
    offerChat(agentName = "Support") {
        if (this.chatEl)
            return;
        this.hideCode();
        this.ensureChat(agentName);
        this.chatMinimized = true;
        if (this.chatEl)
            this.chatEl.hidden = true;
        this.renderChatMin(agentName);
    }
    ensureChat(agentName) {
        if (this.chatEl)
            return;
        const root = this.ensure();
        const box = this.doc.createElement("section");
        box.className = "chat";
        box.setAttribute("role", "region");
        box.setAttribute("aria-label", "Chat with support");
        box.setAttribute("data-backstory", "live-chat");
        const head = this.doc.createElement("div");
        head.className = "chat-head";
        const title = this.doc.createElement("strong");
        title.textContent = `Chat with ${agentName}`;
        const minimize = this.doc.createElement("button");
        minimize.textContent = "Minimize";
        minimize.setAttribute("aria-label", "Minimize support chat");
        minimize.addEventListener("click", () => {
            this.chatMinimized = true;
            this.chatDismissed = true;
            box.hidden = true;
            this.renderChatMin(agentName);
        });
        head.append(title, minimize);
        const lines = this.doc.createElement("div");
        lines.className = "chat-lines";
        lines.setAttribute("aria-live", "polite");
        const form = this.doc.createElement("form");
        form.className = "chat-send";
        const input = this.doc.createElement("input");
        input.placeholder = "Message support…";
        input.setAttribute("aria-label", "Message support");
        input.setAttribute("autocomplete", "off");
        const send = this.doc.createElement("button");
        send.className = "primary";
        send.type = "submit";
        send.textContent = "Send";
        form.append(input, send);
        form.addEventListener("submit", (e) => {
            e.preventDefault();
            const text = input.value.trim();
            if (!text)
                return;
            this.appendChat("user", text);
            this.cb.onChat(text);
            input.value = "";
        });
        box.append(head, lines, form);
        root.appendChild(box);
        this.chatEl = box;
        this.chatLines = lines;
    }
    appendChat(from, text) {
        if (!this.chatLines)
            return;
        const line = this.doc.createElement("div");
        line.className = `chat-line ${from}`;
        line.setAttribute("data-from", from);
        line.textContent = text;
        this.chatLines.appendChild(line);
        this.chatLines.scrollTop = this.chatLines.scrollHeight;
    }
    showChat(agentName) {
        if (!this.chatEl)
            return;
        this.chatMinimized = false;
        this.chatDismissed = false;
        this.chatUnread = 0;
        this.chatEl.hidden = false;
        this.chatMin?.remove();
        this.chatMin = null;
        const title = this.chatEl.querySelector(".chat-head strong");
        if (title)
            title.textContent = `Chat with ${agentName}`;
    }
    renderChatMin(agentName) {
        const root = this.ensure();
        if (!this.chatMin) {
            this.chatMin = this.doc.createElement("button");
            this.chatMin.className = "chat-min";
            this.chatMin.setAttribute("aria-label", "Open support chat");
            this.chatMin.addEventListener("click", () => this.showChat(agentName));
            root.appendChild(this.chatMin);
        }
        this.chatMin.textContent = "Support chat";
        if (this.chatUnread > 0) {
            const badge = this.doc.createElement("span");
            badge.className = "unread";
            badge.setAttribute("aria-label", `${this.chatUnread} unread message${this.chatUnread === 1 ? "" : "s"}`);
            badge.textContent = String(this.chatUnread);
            this.chatMin.appendChild(badge);
        }
    }
    closeChat() {
        this.chatEl?.remove();
        this.chatMin?.remove();
        this.chatEl = null;
        this.chatLines = null;
        this.chatMin = null;
        this.chatMinimized = false;
        this.chatDismissed = false;
        this.chatUnread = 0;
    }
    /** Test hook: the shadow root (closed roots are otherwise unreachable). */
    get shadow() {
        return this.root;
    }
    destroy() {
        this.closePrompt();
        this.hideCode();
        this.closeChat();
        this.banner?.remove();
        this.banner = null;
        this.notice?.remove();
        this.notice = null;
        this.host?.remove();
        this.host = null;
        this.root = null;
    }
}
function initials(name) {
    const parts = (name || "").trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0)
        return "?";
    return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : "")).toUpperCase();
}

/**
 * Hand every RTCPeerConnection LiveKit creates to the WebRTC collector, so a bad
 * support call is diagnosable with exactly the same metrics and detectors as the
 * customer's own calls (PLAN §5.7). Best effort: LiveKit's engine internals are
 * not public API, so every access is guarded and failure is silent.
 */
function instrumentLiveKitPeerConnections(room, attach) {
    try {
        const engine = room.engine;
        if (!engine)
            return;
        const candidates = [];
        // livekit-client 2.x: engine.pcManager.{publisher,subscriber}.pc; older: engine.{publisher,subscriber}.pc
        const mgr = engine.pcManager;
        for (const holder of [mgr?.publisher, mgr?.subscriber, engine.publisher, engine.subscriber]) {
            const pc = holder?.pc;
            if (pc)
                candidates.push(pc);
        }
        for (const pc of candidates) {
            if (pc && typeof pc.getStats === "function")
                attach(pc);
        }
    }
    catch {
        /* internals moved; the call still works, we just lose our own metrics */
    }
}
const DEFAULT_LIVEKIT_CLIENT_URL = "https://cdn.jsdelivr.net/npm/livekit-client@2/dist/livekit-client.esm.mjs";
async function loadLiveKit(url = DEFAULT_LIVEKIT_CLIENT_URL, importer = (u) => import(/* @vite-ignore */ u)) {
    const g = globalThis;
    if (g.__BACKSTORY_LIVEKIT__?.Room)
        return g.__BACKSTORY_LIVEKIT__;
    if (g.LivekitClient?.Room)
        return g.LivekitClient;
    try {
        const m = (await importer(url));
        const mod = m?.Room ? m : m?.default?.Room ? m.default : null;
        return mod ?? null;
    }
    catch {
        return null;
    }
}
class VoiceCall {
    opts;
    room = null;
    audioEls = [];
    levelTimer = null;
    muted = false;
    connected = false;
    constructor(opts = {}) {
        this.opts = opts;
    }
    /** Join the room audio-only. Resolves false when LiveKit is unavailable or the join fails. */
    async join(payload, microphoneId = "default") {
        const lk = await loadLiveKit(this.opts.clientUrl, this.opts.importer);
        if (!lk) {
            this.opts.log?.("[backstory] live: livekit-client is not available; voice declined");
            return false;
        }
        try {
            const room = new lk.Room({ adaptiveStream: false, dynacast: false, ...(payload.e2ee ? { e2ee: { enabled: true } } : {}) });
            this.room = room;
            const doc = this.opts.doc ?? (typeof document !== "undefined" ? document : null);
            room.on(lk.RoomEvent?.TrackSubscribed ?? "trackSubscribed", (track) => {
                if (track?.kind && track.kind !== (lk.Track?.Kind?.Audio ?? "audio"))
                    return;
                const el = track?.attach?.();
                if (el && doc) {
                    el.setAttribute("data-backstory", "voice");
                    el.style.display = "none";
                    (doc.body ?? doc.documentElement).appendChild(el);
                    this.audioEls.push(el);
                }
            });
            room.on(lk.RoomEvent?.Disconnected ?? "disconnected", () => this.end(false));
            await room.connect(payload.livekitUrl, payload.token);
            if (microphoneId !== "default")
                await room.switchActiveDevice?.("audioinput", microphoneId, true);
            await room.localParticipant.setMicrophoneEnabled(true);
            this.connected = true;
            this.muted = false;
            if (this.opts.attachPeerConnection)
                instrumentLiveKitPeerConnections(room, this.opts.attachPeerConnection);
            this.levelTimer = setInterval(() => this.opts.onLevel?.(room.localParticipant.audioLevel ?? 0), 250);
            return true;
        }
        catch (e) {
            this.opts.log?.("[backstory] live: voice join failed", e);
            this.end(false);
            return false;
        }
    }
    async setMuted(muted) {
        this.muted = muted;
        try {
            await this.room?.localParticipant.setMicrophoneEnabled(!muted);
        }
        catch {
            /* ignore */
        }
    }
    /** Leave the room and remove audio elements. */
    end(notify = true) {
        if (this.levelTimer)
            clearInterval(this.levelTimer);
        this.levelTimer = null;
        const room = this.room;
        this.room = null;
        for (const el of this.audioEls.splice(0))
            el.remove();
        const was = this.connected;
        this.connected = false;
        try {
            void room?.disconnect();
        }
        catch {
            /* ignore */
        }
        if (was && notify)
            this.opts.onEnded?.();
    }
}

/** Floor between snapshots taken for a waiting console. */
const SNAPSHOT_MIN_INTERVAL_MS = 4000;
/** Generates a 6-digit help code with crypto randomness. */
function generateHelpCode() {
    const buf = new Uint32Array(1);
    if (typeof crypto !== "undefined" && crypto.getRandomValues)
        crypto.getRandomValues(buf);
    else
        buf[0] = Math.floor(Math.random() * 2 ** 32);
    return String(buf[0] % 1_000_000).padStart(6, "0");
}
/**
 * Live Assist controller (PLAN §14): connects the live socket on demand, runs
 * the consent ladder as the user's source of truth, renders the chrome, draws
 * annotations, executes agent commands under policy, and records every consent
 * and agent action into the session so the replay shows what happened.
 */
class LiveAssist {
    cfg;
    opts;
    r;
    win;
    doc;
    now;
    socket = null;
    ladder;
    ui;
    annotations;
    control;
    voice = null;
    voiceLevel = 0;
    code = null;
    off = [];
    minuteTick = null;
    pausedUntil = 0;
    lastStateMinute = -1;
    lastSnapshotAt = -Infinity;
    stopped = false;
    /** Set once the included minutes run out, so the socket does not come back. */
    limited = null;
    constructor(recorder, cfg, opts = {}) {
        this.cfg = cfg;
        this.opts = opts;
        this.r = recorder;
        this.win = opts.win ?? window;
        this.doc = opts.doc ?? document;
        this.now = opts.now ?? (() => Date.now());
        this.ladder = new ConsentLadder((m) => this.send(m), {
            disabled: this.disabledCaps(),
            now: this.now,
            requestTimeoutMs: opts.requestTimeoutMs,
        });
        this.ui = new LiveUI({
            onRespond: (id, granted) => this.ladder.respond(id, granted, granted ? undefined : "declined"),
            onStop: (cap) => this.ladder.revoke(cap, "user"),
            onMute: (muted) => void this.voice?.setMuted(muted).then(() => this.renderBanner()),
            onCloseCode: () => this.hideCode(),
            onChat: (text) => {
                if (!this.ladder.consent.cobrowse)
                    return;
                this.send({ type: "chat", text });
                this.r.emit({ type: "assist", t: this.now(), actor: "user", kind: "action", payload: { chat: true, length: text.length } });
            },
        }, this.doc);
        this.annotations = new AnnotationLayer(this.r.map, this.doc, this.win, this.now);
        this.control = new ControlExecutor({ map: this.r.map, emit: (e) => this.r.emit(e), now: this.now, win: this.win, doc: this.doc });
        this.off.push(this.ladder.on((e) => this.onLadder(e)));
        // Navigation: pending prompts decline, control ends.
        this.off.push(bus.tap((e) => {
            if (e.type === "navigation" && e.kind !== "load")
                this.ladder.onNavigation();
        }));
        // Esc clears drawings and revokes control; real user activity pauses control (user priority).
        const onKey = (e) => {
            if (!e.isTrusted)
                return;
            if (e.key === "Escape")
                this._escape();
            this.userActivity();
        };
        const onPointer = (e) => {
            if (e.isTrusted)
                this.userActivity();
        };
        this.doc.addEventListener("keydown", onKey, true);
        this.doc.addEventListener("pointerdown", onPointer, true);
        this.doc.addEventListener("pointermove", onPointer, true);
        // Keep the agent's viewport reference current. Recording emits its own
        // viewport event, while this state update immediately resizes the console
        // stage used to normalize annotations.
        const onResize = () => this.send(this.stateMessage());
        this.win.addEventListener("resize", onResize);
        this.off.push(() => this.doc.removeEventListener("keydown", onKey, true), () => this.doc.removeEventListener("pointerdown", onPointer, true), () => this.doc.removeEventListener("pointermove", onPointer, true), () => this.win.removeEventListener("resize", onResize));
        // Tenant policy can only narrow: re-read disabled caps when remote flags change (cheap poll).
        const poll = setInterval(() => this.ladder.setDisabled(this.disabledCaps()), 10_000);
        this.off.push(() => clearInterval(poll));
        const onUnload = () => {
            if (this.ladder.anyActive)
                this.send({ type: "ended", reason: "tab_closed" });
        };
        this.win.addEventListener("pagehide", onUnload);
        this.off.push(() => this.win.removeEventListener("pagehide", onUnload));
    }
    // ---- policy ----------------------------------------------------------------
    disabledCaps() {
        // The wire contract retains "record" for stored historical assists, but
        // new assists cannot request it until the product exposes recording.
        const out = new Set(["record"]);
        const live = this.cfg.live;
        if (live.remoteControl === "off")
            out.add("control");
        if (!live.voice)
            out.add("voice");
        if (!live.annotations)
            out.add("annotate");
        const flags = this.r.getRemoteConfig?.()?.flags ?? {};
        for (const [k, v] of Object.entries(flags)) {
            const m = /^liveDisable:(cobrowse|voice|annotate|control|record)$/.exec(k);
            if (m && v)
                out.add(m[1]);
        }
        const limits = this.r.getLimits?.();
        if (limits && !limits.remoteControl.allowed) {
            out.add("control");
            this.r.noteLimit?.("remoteControl", limits.remoteControl.reason);
        }
        if (limits && !limits.supportCalling.allowed) {
            out.add("voice");
            this.r.noteLimit?.("supportCalling", limits.supportCalling.reason);
        }
        return [...out];
    }
    userActivity() {
        if (!this.ladder.consent.control)
            return;
        if (this.cfg.live.pauseOnActivity === "revoke")
            this.ladder.revoke("control", "activity");
        else
            this.pausedUntil = this.now() + CONTROL_POLICY.userPriorityResumeMs;
    }
    // ---- socket ----------------------------------------------------------------
    /** Open the live socket (idempotent). Called by showCode/requestHelp or the agent-waiting nudge. */
    connect() {
        if (this.socket || this.stopped || this.limited)
            return;
        const url = deriveLiveUrl(apiBaseUrl(this.cfg.apiUrl, this.cfg.ingestUrl), this.cfg.projectKey, this.r.getSessionId(), this.r.getVisitId(), this.opts.version ?? "0.1.0");
        this.socket = new LiveSocket({
            url,
            WebSocketImpl: this.opts.WebSocketImpl,
            onMessage: (m) => this.onMessage(m),
            onHello: (assist) => {
                // An agent may already be waiting; the server-side assist record carries their identity.
                const a = assist?.agent;
                if (a && !this.ladder.agent)
                    this.ladder.agent = a;
            },
            onServerError: (err, code) => {
                if (code === "limit_reached")
                    this.stopForLimits("minutes");
                else if (code === "feature_not_included" || code === "subscription_ended")
                    this.stopForLimits("unavailable");
                else
                    console.warn("[backstory] live: server rejected a message:", err);
            },
            stateProvider: () => this.stateMessage(),
            onClose: (code) => {
                if (code === 1008)
                    this.stopForLimits(this.limited ?? "unavailable");
            },
            log: (msg, ...a) => console.warn(msg, ...a),
        });
        this.socket.connect();
    }
    get connected() {
        return !!this.socket?.connected;
    }
    send(m) {
        this.socket?.send(m);
    }
    /**
     * Take a checkpoint for a console that has nothing to render. Consoles retry
     * while they wait, so a snapshot already on its way is not taken twice: a
     * snapshot is the most expensive thing the SDK sends.
     */
    sendSnapshot() {
        const at = this.now();
        if (at - this.lastSnapshotAt < SNAPSHOT_MIN_INTERVAL_MS)
            return;
        this.lastSnapshotAt = at;
        void this.r.checkpoint?.("manual");
    }
    stateMessage() {
        return { type: "state", consent: { ...this.ladder.consent }, url: this.win.location.href, viewport: { w: this.win.innerWidth, h: this.win.innerHeight } };
    }
    onMessage(m) {
        switch (m.type) {
            case "request": {
                this.record("consent_request", m.agent, m.capability, undefined, { requestId: m.requestId });
                this.ladder.request(m.requestId, m.capability, m.agent, m.wording);
                return;
            }
            case "revoke":
                this.ladder.revoke(m.capability, "user");
                return;
            case "annotation":
                if (this.ladder.consent.annotate) {
                    this.annotations.add(m.shape);
                    this.record("annotation", this.ladder.agent, "annotate", undefined, { kind: m.shape.kind, id: m.shape.id });
                }
                return;
            case "annotation_clear":
                this.annotations.clear();
                this.record("annotation", this.ladder.agent, "annotate", undefined, { kind: "clear" });
                return;
            case "snapshot_request":
                this.sendSnapshot();
                return;
            case "control": {
                const actor = `agent:${this.ladder.agent?.id ?? "unknown"}`;
                if (!this.ladder.consent.control) {
                    this.send({ type: "control_result", commandId: m.commandId, ok: false, error: "blocked_by_policy" });
                    return;
                }
                if (this.now() < this.pausedUntil) {
                    this.send({ type: "control_result", commandId: m.commandId, ok: false, error: "user_priority" });
                    return;
                }
                const res = this.control.execute(m.command, actor);
                this.record("action", this.ladder.agent, "control", undefined, { kind: m.command.kind, ok: res.ok, error: res.error });
                this.send({ type: "control_result", commandId: m.commandId, ok: res.ok, error: res.error });
                return;
            }
            case "voice_token":
                void this.joinVoice(m);
                return;
            case "chat":
                if (!this.ladder.consent.cobrowse)
                    return;
                this.r.emit({ type: "assist", t: this.now(), actor: `agent:${this.ladder.agent?.id ?? "unknown"}`, kind: "action", payload: { chat: true, length: m.text.length } });
                this.ui.receiveChat(m.text, this.ladder.agent?.name ?? "Support");
                return;
            case "end":
                if (m.reason === "minutes" || m.reason === "unavailable")
                    this.stopForLimits(m.reason);
                else
                    this.endSession("agent");
                return;
        }
    }
    async joinVoice(m) {
        if (!this.ladder.consent.voice)
            return;
        this.voice?.end(false);
        this.voice = new VoiceCall({
            importer: this.opts.livekitImporter,
            clientUrl: this.cfg.live.livekitClientUrl,
            doc: this.doc,
            // Dogfood the WebRTC collector: our own support calls get the same
            // metrics, detectors, and Media panel as the customer's calls (PLAN §5.7).
            attachPeerConnection: (pc) => {
                try {
                    globalThis.Backstory?.webrtc?.attach?.(pc);
                }
                catch {
                    /* collector not loaded (webrtc disabled); voice still works */
                }
            },
            onLevel: (l) => {
                this.voiceLevel = l;
                this.renderBanner();
            },
            onEnded: () => {
                if (this.ladder.consent.voice)
                    this.ladder.revoke("voice", "user");
            },
            log: (msg, ...a) => console.warn(msg, ...a),
        });
        const ok = await this.voice.join(m, this.ui.selectedVoiceDevice());
        if (!ok) {
            this.voice = null;
            // Keep the user's grant visible (and therefore revocable) and tell the
            // agent what failed. Silently revoking here made both sides' voice
            // controls disappear immediately when LiveKit was unavailable.
            this.send({ type: "voice_status", connected: false, error: "The user could not connect to the audio room." });
        }
        else {
            this.send({ type: "voice_status", connected: true });
            this.record("action", this.ladder.agent, "voice", undefined, { kind: "call_started" });
        }
        this.renderBanner();
    }
    // ---- ladder events -> UI, recording, side effects ----------------------------
    onLadder(e) {
        switch (e.type) {
            case "prompt":
                this.ui.showPrompt(e.request);
                break;
            case "prompt_closed":
                this.ui.closePrompt(e.requestId);
                break;
            case "granted":
                this.record("consent_response", e.agent, e.capability, true);
                if (e.capability === "cobrowse") {
                    this.send(this.stateMessage());
                    this.ui.offerChat(e.agent?.name ?? this.ladder.agent?.name ?? "Support");
                }
                break;
            case "revoked":
                this.record("consent_response", this.ladder.agent, e.capability, false, { by: e.by });
                if (e.capability === "annotate" || e.capability === "cobrowse")
                    this.annotations.clear();
                if (e.capability === "cobrowse")
                    this.ui.closeChat();
                if (e.capability === "voice") {
                    if (this.voice)
                        this.record("action", this.ladder.agent, "voice", undefined, { kind: "call_ended", by: e.by });
                    this.voice?.end(false);
                    this.voice = null;
                }
                break;
            case "changed":
                this.send(this.stateMessage());
                break;
        }
        // Declined prompts (not granted) are also recorded so the replay shows the ask and the answer.
        if (e.type === "prompt_closed") {
            this.ladder.pendingRequests.some((p) => p.requestId === e.requestId);
        }
        this.renderBanner();
        this.syncTimers();
    }
    record(kind, agent, capability, granted, payload) {
        this.r.emit({ type: "assist", t: this.now(), actor: `agent:${agent?.id ?? "unknown"}`, kind, capability, granted, payload });
    }
    renderBanner() {
        const voice = this.ladder.consent.voice && this.voice ? { muted: this.voice.muted, level: this.voiceLevel } : null;
        this.ui.renderBanner(this.ladder.active, this.ladder.agent?.name ?? "", voice);
    }
    syncTimers() {
        // Metering support: a state message on every minute boundary while anything is active.
        const needMinute = this.ladder.anyActive;
        if (needMinute && !this.minuteTick) {
            this.minuteTick = setInterval(() => {
                const minute = Math.floor(this.now() / 60_000);
                if (minute !== this.lastStateMinute) {
                    this.lastStateMinute = minute;
                    this.send(this.stateMessage());
                }
            }, 5_000);
        }
        if (!needMinute && this.minuteTick) {
            clearInterval(this.minuteTick);
            this.minuteTick = null;
        }
    }
    // ---- public API ----------------------------------------------------------------
    /** Show a 6-digit code the user reads to the agent; connects the socket. */
    showCode() {
        if (this.limited)
            return this.code ?? "";
        this.code = this.code ?? generateHelpCode();
        this.connect();
        this.send({ type: "help_request", code: this.code });
        this.r.emit({ type: "custom", t: this.now(), name: "backstory.help_requested", props: { viaCode: true } });
        this.ui.showCode(this.code);
        return this.code;
    }
    /** Ask for help with an optional message; connects the socket. */
    requestHelp(message) {
        if (this.limited)
            return this.code ?? "";
        this.code = this.code ?? generateHelpCode();
        this.connect();
        this.send({ type: "help_request", code: this.code, message });
        this.r.emit({ type: "custom", t: this.now(), name: "backstory.help_requested", props: { viaCode: false, hasMessage: !!message } });
        this.ui.showCode(this.code, message ? "Help requested — a support agent will be with you shortly" : undefined);
        return this.code;
    }
    hideCode() {
        this.ui.hideCode();
    }
    /**
     * Called by the core when the ingest ack carries `flags.liveAgentWaiting`.
     * The agent is already watching, so take a fresh snapshot: without one their
     * console has nothing to render until the next scheduled checkpoint.
     */
    agentWaiting() {
        this.connect();
        // The socket may already exist because the user displayed a help code.
        // Agent arrival still needs a new checkpoint: an attached console only
        // receives live traffic and cannot recover a DOM from later mutations.
        this.sendSnapshot();
    }
    getState() {
        return {
            connected: this.connected,
            consent: { ...this.ladder.consent },
            agent: this.ladder.agent,
            code: this.code,
            controlRemainingMs: 0,
            voice: this.voice ? { muted: this.voice.muted, level: this.voiceLevel } : null,
        };
    }
    /** End the assist session from the user's side. */
    end() {
        this.endSession("user");
    }
    /**
     * The included minutes ran out, or Live Assist is no longer part of the
     * account. Hang up the call and the other tools, tell the visitor, and
     * record one last line in the session.
     */
    stopForLimits(reason) {
        if (this.limited || this.stopped)
            return;
        this.limited = reason;
        const hadVoice = !!this.voice;
        const hadTools = hadVoice || this.ladder.anyActive;
        if (this.ladder.anyActive || this.ladder.pendingRequests.length)
            this.ladder.reset("user");
        this.annotations.clear();
        this.voice?.end(false);
        this.voice = null;
        const sentence = reason === "minutes"
            ? "Live Assist ended because the included minutes ran out."
            : "Live Assist ended because it is no longer included for this account.";
        const notice = hadVoice
            ? `${sentence} The call has ended too.`
            : hadTools
                ? `${sentence} The tools that were on have stopped.`
                : sentence;
        this.r.emit({ type: "assist", t: this.now(), actor: "system", kind: "action", payload: { kind: reason === "minutes" ? "minutes_ran_out" : "unavailable" } });
        this.r.emit({ type: "console", t: this.now(), level: "warn", args: [sentence] });
        this.ui.hideCode();
        this.ui.closeChat();
        this.ui.closePrompt();
        this.renderBanner();
        this.syncTimers();
        this.ui.showNotice(notice);
        this.socket?.close();
        this.code = null;
    }
    endSession(reason) {
        if (this.ladder.anyActive || this.ladder.pendingRequests.length)
            this.ladder.reset("user");
        this.annotations.clear();
        this.voice?.end(false);
        this.voice = null;
        this.send({ type: "ended", reason });
        this.ui.hideCode();
        this.ui.closeChat();
        this.code = null;
        this.renderBanner();
        this.syncTimers();
    }
    /** Tear everything down (recorder stop). */
    destroy() {
        if (this.stopped)
            return;
        this.stopped = true;
        if (!this.limited)
            this.endSession("user");
        this.socket?.close();
        this.socket = null;
        for (const f of this.off)
            f();
        this.off = [];
        this.annotations.destroy();
        this.ui.destroy();
    }
    /** Test hooks. */
    get _ladder() {
        return this.ladder;
    }
    get _ui() {
        return this.ui;
    }
    get _annotations() {
        return this.annotations;
    }
    /** Feed a message as if it came from the socket (tests). */
    _receive(m) {
        this.onMessage(m);
    }
    /** Simulate trusted user activity / Esc (tests; jsdom cannot mint trusted events). */
    _userActivity() {
        this.userActivity();
    }
    _escape() {
        this.annotations.clear();
        if (this.ladder.consent.cobrowse)
            this.ladder.revoke("cobrowse", "user");
    }
}
function attachLive(recorder, cfg, opts = {}) {
    return new LiveAssist(recorder, cfg, opts);
}

// Lazily loaded Live Assist module (dist/backstory-live.js). Registers on
// window.__BACKSTORY_MOD__.live for the script-tag bundle; imported statically by the
// ESM build. Consent ladder, annotations, remote control, voice, help code.

var liveModule = /*#__PURE__*/Object.freeze({
    __proto__: null,
    AnnotationLayer: AnnotationLayer,
    CAPABILITIES: CAPABILITIES,
    ConsentLadder: ConsentLadder,
    ControlExecutor: ControlExecutor,
    LiveAssist: LiveAssist,
    LiveSocket: LiveSocket,
    LiveUI: LiveUI,
    REQUEST_TIMEOUT_MS: REQUEST_TIMEOUT_MS,
    VoiceCall: VoiceCall,
    apiBaseUrl: apiBaseUrl,
    attachLive: attachLive,
    deriveLiveUrl: deriveLiveUrl,
    generateHelpCode: generateHelpCode,
    loadLiveKit: loadLiveKit,
    policyFor: policyFor,
    setNativeValue: setNativeValue,
    substituteWording: substituteWording
});

function toMap(report) {
    if (report instanceof Map)
        return report;
    const m = new Map();
    report.forEach((v, k) => m.set(k, v));
    return m;
}
const num = (v) => (typeof v === "number" && Number.isFinite(v) ? v : undefined);
const round = (v, d = 1) => (v === undefined ? undefined : Math.round(v * 10 ** d) / 10 ** d);
/** Counters reset on ICE restart; a negative delta means "restarted", not "went backwards". */
const delta = (now, before) => {
    if (now === undefined)
        return undefined;
    if (before === undefined)
        return undefined;
    const d = now - before;
    return d >= 0 ? d : undefined;
};
function mediaOf(s) {
    const k = s.kind ?? s.mediaType;
    return k === "audio" || k === "video" ? k : undefined;
}
/**
 * Derived freeze detection for browsers without freezeCount (Safari). A freeze
 * is a window where decoded frames stalled while the track was live: we cannot
 * see individual frame durations from stats, so we count a window with zero
 * decoded frames but arriving packets as one freeze lasting the window.
 */
function derivedFreeze(decodedDelta, packetsDelta, windowMs) {
    if (decodedDelta === undefined || packetsDelta === undefined)
        return undefined;
    if (decodedDelta === 0 && packetsDelta > 0)
        return { freezes: 1, freezeMs: windowMs };
    return { freezes: 0, freezeMs: 0 };
}
/** Compute one WebRtcStatsEvent from two reports. Returns undefined until primed. */
function computeSample(prev, curr, windowMs, ctx, t) {
    if (!prev || windowMs <= 0)
        return undefined;
    const now = toMap(curr);
    const before = toMap(prev);
    const tracks = [];
    const codecMime = (id) => {
        if (!id)
            return undefined;
        const c = now.get(id);
        return typeof c?.mimeType === "string" ? c.mimeType : undefined;
    };
    // remote-inbound-rtp reports what the far end observed about our outbound
    // stream: the only place outbound loss and RTT per stream are visible.
    const remoteInboundByLocal = new Map();
    now.forEach((s) => {
        if (s.type === "remote-inbound-rtp" && s.localId)
            remoteInboundByLocal.set(s.localId, s);
    });
    const secs = windowMs / 1000;
    now.forEach((s, id) => {
        const inbound = s.type === "inbound-rtp";
        const outbound = s.type === "outbound-rtp";
        if (!inbound && !outbound)
            return;
        const media = mediaOf(s);
        if (!media)
            return;
        const p = before.get(id);
        const direction = inbound ? "inbound" : "outbound";
        const out = { media, direction };
        if (s.trackIdentifier)
            out.trackId = s.trackIdentifier;
        const bytes = delta(inbound ? s.bytesReceived : s.bytesSent, inbound ? p?.bytesReceived : p?.bytesSent);
        if (bytes !== undefined && secs > 0)
            out.kbps = round((bytes * 8) / 1000 / secs);
        const packets = delta(inbound ? s.packetsReceived : s.packetsSent, inbound ? p?.packetsReceived : p?.packetsSent);
        if (inbound) {
            const lost = delta(s.packetsLost, p?.packetsLost);
            if (lost !== undefined && packets !== undefined && lost + packets > 0)
                out.lossPct = round((lost / (lost + packets)) * 100, 2);
            if (num(s.jitter) !== undefined)
                out.jitterMs = round(num(s.jitter) * 1000);
        }
        else {
            // Outbound loss comes from the receiver report.
            const ri = remoteInboundByLocal.get(id);
            const rp = ri ? before.get(ri.id) : undefined;
            const lost = ri ? delta(ri.packetsLost, rp?.packetsLost) : undefined;
            if (lost !== undefined && packets !== undefined && lost + packets > 0)
                out.lossPct = round((lost / (lost + packets)) * 100, 2);
            else if (ri && num(ri.fractionLost) !== undefined)
                out.lossPct = round(num(ri.fractionLost) * 100, 2);
            if (ri && num(ri.jitter) !== undefined)
                out.jitterMs = round(num(ri.jitter) * 1000);
        }
        if (media === "video") {
            if (num(s.framesPerSecond) !== undefined)
                out.fps = round(num(s.framesPerSecond));
            if (num(s.frameWidth) !== undefined)
                out.w = num(s.frameWidth);
            if (num(s.frameHeight) !== undefined)
                out.h = num(s.frameHeight);
            const freezes = delta(s.freezeCount, p?.freezeCount);
            const freezeSec = delta(s.totalFreezesDuration, p?.totalFreezesDuration);
            if (freezes !== undefined) {
                out.freezes = freezes;
                if (freezeSec !== undefined)
                    out.freezeMs = round(freezeSec * 1000);
            }
            else if (inbound) {
                const d = derivedFreeze(delta(s.framesDecoded, p?.framesDecoded), packets, windowMs);
                if (d) {
                    out.freezes = d.freezes;
                    out.freezeMs = d.freezeMs;
                }
            }
            const decoded = delta(s.framesDecoded, p?.framesDecoded);
            const dropped = delta(s.framesDropped, p?.framesDropped);
            if (decoded !== undefined && dropped !== undefined && decoded + dropped > 0)
                out.frameDropPct = round((dropped / (decoded + dropped)) * 100, 2);
            const kf = delta(inbound ? s.keyFramesDecoded : s.keyFramesEncoded, inbound ? p?.keyFramesDecoded : p?.keyFramesEncoded);
            if (kf !== undefined)
                out.keyFrames = kf;
        }
        else {
            const concealed = delta(s.concealedSamples, p?.concealedSamples);
            const total = delta(s.totalSamplesReceived, p?.totalSamplesReceived);
            if (concealed !== undefined && total !== undefined && total > 0)
                out.concealmentPct = round((concealed / total) * 100, 2);
            if (num(s.audioLevel) !== undefined)
                out.audioLevel = round(num(s.audioLevel), 3);
        }
        const jbDelay = delta(s.jitterBufferDelay, p?.jitterBufferDelay);
        const jbCount = delta(s.jitterBufferEmittedCount, p?.jitterBufferEmittedCount);
        if (jbDelay !== undefined && jbCount !== undefined && jbCount > 0)
            out.jitterBufferMs = round((jbDelay / jbCount) * 1000);
        const nack = delta(s.nackCount, p?.nackCount);
        if (nack !== undefined)
            out.nackCount = nack;
        const pli = delta(s.pliCount, p?.pliCount);
        if (pli !== undefined)
            out.pliCount = pli;
        if (outbound) {
            const reason = s.qualityLimitationReason;
            if (reason === "none" || reason === "cpu" || reason === "bandwidth" || reason === "other")
                out.qualityLimitation = reason;
            const d = s.qualityLimitationDurations;
            const pd = p?.qualityLimitationDurations;
            if (d && pd && secs > 0) {
                // Durations are seconds in the spec; some builds report milliseconds.
                const scale = (v) => (v > windowMs ? v / 1000 : v);
                const cpu = delta(scale(d.cpu ?? 0), scale(pd.cpu ?? 0));
                const bw = delta(scale(d.bandwidth ?? 0), scale(pd.bandwidth ?? 0));
                if (cpu !== undefined)
                    out.limitedByCpuPct = round(Math.min(100, (cpu / secs) * 100), 1);
                if (bw !== undefined)
                    out.limitedByBandwidthPct = round(Math.min(100, (bw / secs) * 100), 1);
            }
        }
        const mime = codecMime(s.codecId) ?? (typeof s.mimeType === "string" ? s.mimeType : undefined);
        if (mime)
            out.codec = mime;
        // A live track that moved no packets at all in the window: the freeze or
        // silence the user is complaining about, and the one-way media signal.
        if (packets !== undefined && packets === 0)
            out.stalled = true;
        tracks.push(out);
    });
    const ev = { t, type: "webrtc_stats", pc: ctx.pc, windowMs: Math.round(windowMs), connectionState: ctx.connectionState, tracks };
    if (ctx.iceState)
        ev.iceState = ctx.iceState;
    // Selected candidate pair: latency, available bandwidth, and the media path.
    let pair;
    now.forEach((s) => {
        if (s.type !== "candidate-pair")
            return;
        if (s.selected === true || s.nominated === true || s.state === "succeeded") {
            if (!pair || s.selected === true)
                pair = s;
        }
    });
    if (pair) {
        const rtt = num(pair.currentRoundTripTime);
        if (rtt !== undefined)
            ev.rttMs = round(rtt * 1000);
        const ob = num(pair.availableOutgoingBitrate);
        if (ob !== undefined)
            ev.availableOutgoingKbps = round(ob / 1000);
        const ib = num(pair.availableIncomingBitrate);
        if (ib !== undefined)
            ev.availableIncomingKbps = round(ib / 1000);
        const lc = pair.localCandidateId ? now.get(pair.localCandidateId) : undefined;
        const rc = pair.remoteCandidateId ? now.get(pair.remoteCandidateId) : undefined;
        const ct = (v) => (v === "host" || v === "srflx" || v === "prflx" || v === "relay" ? v : undefined);
        ev.localCandidate = ct(lc?.candidateType);
        ev.remoteCandidate = ct(rc?.candidateType);
        if (ev.localCandidate || ev.remoteCandidate)
            ev.relayed = ev.localCandidate === "relay" || ev.remoteCandidate === "relay";
        if (lc?.networkType)
            ev.networkType = lc.networkType;
        if (!ev.localCandidate)
            delete ev.localCandidate;
        if (!ev.remoteCandidate)
            delete ev.remoteCandidate;
    }
    return ev;
}
function digest(ev) {
    const d = { windowMs: ev.windowMs, freezeMs: 0, freezes: 0, relayed: !!ev.relayed, stalled: [], flowing: [] };
    d.rttMs = ev.rttMs;
    let loss;
    let jitter;
    let conceal;
    let fps;
    let minH;
    let cpu;
    for (const t of ev.tracks) {
        if (t.lossPct !== undefined)
            loss = Math.max(loss ?? 0, t.lossPct);
        if (t.jitterMs !== undefined)
            jitter = Math.max(jitter ?? 0, t.jitterMs);
        if (t.concealmentPct !== undefined)
            conceal = Math.max(conceal ?? 0, t.concealmentPct);
        if (t.freezeMs !== undefined)
            d.freezeMs += t.freezeMs;
        if (t.freezes !== undefined)
            d.freezes += t.freezes;
        if (t.media === "video" && t.direction === "inbound" && t.fps !== undefined)
            fps = t.fps;
        if (t.media === "video" && t.h !== undefined)
            minH = Math.min(minH ?? Infinity, t.h);
        if (t.limitedByCpuPct !== undefined)
            cpu = Math.max(cpu ?? 0, t.limitedByCpuPct);
        if (t.stalled)
            d.stalled.push({ media: t.media, direction: t.direction });
        else if ((t.kbps ?? 0) > 0)
            d.flowing.push({ media: t.media, direction: t.direction });
    }
    d.lossPct = loss;
    d.jitterMs = jitter;
    d.concealmentPct = conceal;
    d.fps = fps;
    d.minHeight = minH === Infinity ? undefined : minH;
    d.cpuLimitedPct = cpu;
    return d;
}
// --- end webrtc ---

/** DOMException name -> MediaErrorKind. The spec fixes these names, which is why we key on them. */
const ERROR_MAP = {
    NotAllowedError: "permission_denied",
    PermissionDeniedError: "permission_denied",
    SecurityError: "security",
    NotFoundError: "device_not_found",
    DevicesNotFoundError: "device_not_found",
    NotReadableError: "device_in_use",
    TrackStartError: "device_in_use",
    OverconstrainedError: "constraints_unsatisfied",
    ConstraintNotSatisfiedError: "constraints_unsatisfied",
    AbortError: "aborted",
    TypeError: "type_error",
};
function mediaErrorKind(err) {
    const e = err;
    const name = typeof e?.name === "string" ? e.name : undefined;
    const kind = (name && ERROR_MAP[name]) || "unknown";
    return { kind, name, constraint: typeof e?.constraint === "string" && e.constraint ? e.constraint : undefined };
}
function requested(constraints) {
    const c = constraints;
    return { audio: !!c?.audio, video: !!c?.video };
}
/** Mute/unmute/ended listeners for one track, with the mute start time kept per track. */
function watchTrack(pc, track, direction, obs, now, cleanups) {
    const info = { id: track.id, media: track.kind === "audio" ? "audio" : "video", direction, label: track.label || undefined, track };
    let mutedAt;
    const onMute = () => {
        mutedAt = now();
        obs.onTrackMuted(pc, info);
    };
    const onUnmute = () => {
        const ms = mutedAt !== undefined ? now() - mutedAt : undefined;
        mutedAt = undefined;
        obs.onTrackUnmuted(pc, { ...info, ...(ms !== undefined ? { mutedMs: ms } : {}) });
    };
    const onEnded = () => obs.onTrackEnded(pc, info);
    track.addEventListener("mute", onMute);
    track.addEventListener("unmute", onUnmute);
    track.addEventListener("ended", onEnded);
    cleanups.push(() => {
        track.removeEventListener("mute", onMute);
        track.removeEventListener("unmute", onUnmute);
        track.removeEventListener("ended", onEnded);
    });
    // A track that arrives already muted never fires `mute`.
    if (track.muted)
        mutedAt = now();
    return info;
}
/** Attach listeners and method wrappers to one peer connection instance. */
function instrument(pc, obs, now) {
    const cleanups = [];
    const seen = new WeakSet();
    const watch = (track, direction) => {
        if (!track || seen.has(track))
            return;
        seen.add(track);
        const info = watchTrack(pc, track, direction, obs, now, cleanups);
        obs.onTrack(pc, info);
    };
    const on = (type, fn) => {
        pc.addEventListener(type, fn);
        cleanups.push(() => pc.removeEventListener(type, fn));
    };
    on("connectionstatechange", () => obs.onConnectionState(pc, String(pc.connectionState ?? "")));
    on("iceconnectionstatechange", () => obs.onIceState(pc, String(pc.iceConnectionState ?? "")));
    on("icegatheringstatechange", () => obs.onIceGathering(pc, String(pc.iceGatheringState ?? "")));
    on("signalingstatechange", () => obs.onSignalingState(pc, String(pc.signalingState ?? "")));
    on("negotiationneeded", () => obs.onNegotiationNeeded(pc));
    on("icecandidateerror", (e) => {
        const ev = e;
        obs.onIceCandidateError(pc, { errorCode: ev.errorCode, errorText: ev.errorText, url: ev.url, address: ev.address });
    });
    on("track", (e) => {
        const ev = e;
        watch(ev.track, "inbound");
    });
    on("datachannel", (e) => {
        const ch = e.channel;
        if (!ch)
            return;
        obs.onDataChannel(pc, ch.label, ch.readyState);
        const report = () => obs.onDataChannel(pc, ch.label, ch.readyState);
        ch.addEventListener("open", report);
        ch.addEventListener("close", report);
        cleanups.push(() => {
            ch.removeEventListener("open", report);
            ch.removeEventListener("close", report);
        });
    });
    // Method wrappers. Each one keeps the original's return value untouched.
    const wrap = (name, fn) => {
        const orig = pc[name];
        if (typeof orig !== "function")
            return;
        const bound = orig.bind(pc);
        pc[name] = fn(bound);
        cleanups.push(() => {
            // Delete the own property so the prototype method shows through again.
            delete pc[name];
        });
    };
    wrap("addTrack", (orig) => (...a) => {
        const r = orig(...a);
        watch(a[0], "outbound");
        return r;
    });
    wrap("addTransceiver", (orig) => (...a) => {
        const r = orig(...a);
        if (a[0] && typeof a[0] === "object" && "kind" in a[0])
            watch(a[0], "outbound");
        return r;
    });
    wrap("setLocalDescription", (orig) => (...a) => {
        const p = orig(...a);
        return Promise.resolve(p).then((v) => {
            const d = (a[0] ?? pc.localDescription);
            if (d)
                obs.onSdp(pc, "local", String(d.type ?? ""), d.sdp);
            // Outbound tracks may have been added by the app before we attached.
            for (const s of pc.getSenders?.() ?? [])
                watch(s.track ?? null, "outbound");
            return v;
        });
    });
    wrap("setRemoteDescription", (orig) => (...a) => {
        const p = orig(...a);
        return Promise.resolve(p).then((v) => {
            const d = a[0];
            if (d)
                obs.onSdp(pc, "remote", String(d.type ?? ""), d.sdp);
            return v;
        });
    });
    wrap("restartIce", (orig) => (...a) => {
        obs.onIceRestart(pc);
        return orig(...a);
    });
    wrap("close", (orig) => (...a) => {
        const r = orig(...a);
        obs.onClosed(pc);
        return r;
    });
    // Tracks that already exist (the `attach()` escape hatch and re-instrumentation).
    for (const s of pc.getSenders?.() ?? [])
        watch(s.track ?? null, "outbound");
    for (const r of pc.getReceivers?.() ?? [])
        watch(r.track ?? null, "inbound");
    return () => {
        for (const c of cleanups.splice(0)) {
            try {
                c();
            }
            catch {
                /* teardown never throws */
            }
        }
    };
}
function iceConfig(cfg) {
    const c = cfg;
    const servers = Array.isArray(c?.iceServers) ? c.iceServers : [];
    let hasTurn = false;
    for (const s of servers) {
        const urls = Array.isArray(s?.urls) ? s.urls : s?.urls ? [s.urls] : [];
        if (urls.some((u) => typeof u === "string" && /^turns?:/i.test(u)))
            hasTurn = true;
    }
    return { iceServerCount: servers.length, hasTurn, bundlePolicy: c?.bundlePolicy, rtcpMuxPolicy: c?.rtcpMuxPolicy };
}
/**
 * Wrap the global RTCPeerConnection. The wrapper is a subclass so `instanceof`,
 * the prototype chain, and static members all keep working, and a page that has
 * already wrapped the constructor is left alone rather than double-patched.
 */
function patchPeerConnection(win, obs, now, onInstance) {
    const Orig = win.RTCPeerConnection;
    if (typeof Orig !== "function")
        return () => { };
    const marker = "__rwPatched";
    if (Orig[marker])
        return () => { };
    class Patched extends Orig {
        constructor(...args) {
            super(...args);
            try {
                const self = this;
                obs.onCreated(self, iceConfig(args[0]));
                const detach = instrument(self, obs, now);
                onInstance(self, detach);
            }
            catch (e) {
                console.warn("[backstory] webrtc instrumentation failed", e);
            }
        }
    }
    // Carry over statics (generateCertificate) and the marker.
    for (const k of Object.getOwnPropertyNames(Orig)) {
        if (k === "prototype" || k === "name" || k === "length")
            continue;
        try {
            const d = Object.getOwnPropertyDescriptor(Orig, k);
            if (d)
                Object.defineProperty(Patched, k, d);
        }
        catch {
            /* some statics are not redefinable */
        }
    }
    Patched[marker] = true;
    win.RTCPeerConnection = Patched;
    // Vendor aliases some libraries still reach for.
    const aliases = ["webkitRTCPeerConnection", "mozRTCPeerConnection"];
    const prevAliases = [];
    for (const a of aliases) {
        const w = win;
        if (w[a] === Orig) {
            prevAliases.push([a, w[a]]);
            w[a] = Patched;
        }
    }
    return () => {
        win.RTCPeerConnection = Orig;
        for (const [a, v] of prevAliases)
            win[a] = v;
    };
}
/** Patch getUserMedia, getDisplayMedia, enumerateDevices, and the devicechange event. */
function patchMediaDevices(win, obs, doc) {
    const nav = win.navigator;
    const md = nav?.mediaDevices;
    const undo = [];
    const counts = (devices) => ({
        audioIn: devices.filter((d) => d.kind === "audioinput").length,
        audioOut: devices.filter((d) => d.kind === "audiooutput").length,
        videoIn: devices.filter((d) => d.kind === "videoinput").length,
    });
    if (md) {
        const wrapGet = (name, source) => {
            const orig = md[name];
            if (typeof orig !== "function")
                return;
            const bound = orig.bind(md);
            md[name] = (constraints) => {
                const req = name === "getDisplayMedia" ? { audio: !!constraints?.audio, video: true } : requested(constraints);
                return bound(constraints).then((stream) => {
                    try {
                        obs.onAcquired(source, req, stream);
                    }
                    catch {
                        /* never break the app's call */
                    }
                    return stream;
                }, (err) => {
                    try {
                        obs.onError(source, req, err);
                    }
                    catch {
                        /* never swallow the app's rejection reason */
                    }
                    throw err;
                });
            };
            undo.push(() => {
                md[name] = orig;
            });
        };
        wrapGet("getUserMedia", "camera_mic");
        wrapGet("getDisplayMedia", "display");
        const origEnum = md.enumerateDevices?.bind(md);
        if (origEnum) {
            md.enumerateDevices = () => origEnum().then((devices) => {
                try {
                    const c = counts(devices);
                    // Labels are only populated once permission has been granted, which
                    // is a cheap and reliable permission probe.
                    obs.onDeviceInventory({ ...c, permissionKnown: devices.some((d) => !!d.label) }, devices);
                }
                catch {
                    /* ignore */
                }
                return devices;
            });
            undo.push(() => {
                md.enumerateDevices = origEnum;
            });
        }
        const onChange = () => {
            md.enumerateDevices?.()
                .then((d) => obs.onDeviceChange(counts(d), d))
                .catch(() => { });
        };
        md.addEventListener?.("devicechange", onChange);
        undo.push(() => md.removeEventListener?.("devicechange", onChange));
    }
    // `setSinkId` is the only browser API that reveals an explicit speaker
    // choice. The OS master output and hardware volume remain inaccessible.
    const mediaProto = win.HTMLMediaElement?.prototype;
    const originalSetSinkId = mediaProto?.setSinkId;
    if (mediaProto && typeof originalSetSinkId === "function") {
        mediaProto.setSinkId = function (deviceId) {
            return originalSetSinkId.call(this, deviceId).then(() => {
                try {
                    obs.onOutputDeviceChange(deviceId);
                }
                catch {
                    /* never break playback */
                }
            });
        };
        undo.push(() => {
            mediaProto.setSinkId = originalSetSinkId;
        });
    }
    // Legacy callback API, still used by older telehealth embeds.
    const legacy = nav;
    if (legacy && typeof legacy.getUserMedia === "function") {
        const orig = legacy.getUserMedia;
        legacy.getUserMedia = (c, ok, fail) => {
            const req = requested(c);
            orig.call(nav, c, (s) => {
                try {
                    obs.onAcquired("camera_mic", req, s);
                }
                catch {
                    /* ignore */
                }
                ok(s);
            }, (e) => {
                try {
                    obs.onError("camera_mic", req, e);
                }
                catch {
                    /* ignore */
                }
                fail(e);
            });
        };
        undo.push(() => {
            legacy.getUserMedia = orig;
        });
    }
    return () => {
        for (const u of undo.splice(0)) {
            try {
                u();
            }
            catch {
                /* ignore */
            }
        }
    };
}
// --- end webrtc ---

/**
 * Per-session salt so a device or address hash cannot be correlated across
 * sessions or tenants. Regenerated whenever the collector starts.
 */
let salt = "";
function setSalt(s) {
    salt = s;
}
function randomSalt() {
    const c = globalThis.crypto;
    if (c?.getRandomValues) {
        const b = new Uint8Array(16);
        c.getRandomValues(b);
        let out = "";
        for (const x of b)
            out += x.toString(16).padStart(2, "0");
        return out;
    }
    return Math.random().toString(36).slice(2) + Date.now().toString(36);
}
/**
 * FNV-1a over the salted input. Synchronous by design: `getStats()` deltas and
 * ICE errors are emitted inline, and crypto.subtle.digest is async only. A
 * 64-bit FNV is not a cryptographic commitment, but the goal is unlinkability
 * of an opaque identifier, not resistance to a determined preimage search on a
 * value we never store in plaintext anywhere.
 */
function hashValue(v) {
    if (!v)
        return undefined;
    const s = salt + " " + v;
    let h1 = 0x811c9dc5;
    let h2 = 0x01000193;
    for (let i = 0; i < s.length; i++) {
        const c = s.charCodeAt(i);
        h1 = (h1 ^ c) >>> 0;
        h1 = Math.imul(h1, 0x01000193) >>> 0;
        h2 = (h2 + c) >>> 0;
        h2 = Math.imul(h2, 0x85ebca6b) >>> 0;
    }
    return (h1.toString(16).padStart(8, "0") + h2.toString(16).padStart(8, "0")).slice(0, 12);
}
/**
 * Browser-supplied device names are recorded as-is. Empty labels (common
 * before the user has granted capture permission) are omitted.
 */
function deviceLabel(label) {
    const t = label?.trim();
    return t ? t : undefined;
}
/** Host only: a TURN URL can carry credentials in the userinfo component. */
function iceServerHost(url) {
    if (!url)
        return undefined;
    // turn:user:pass@host:3478?transport=udp -> host:3478
    const noScheme = url.replace(/^[a-z]+:/i, "");
    const afterAuth = noScheme.includes("@") ? noScheme.slice(noScheme.lastIndexOf("@") + 1) : noScheme;
    return afterAuth.split("?")[0] || undefined;
}
const IPV4 = /\b\d{1,3}(?:\.\d{1,3}){3}\b/;
const IPV6 = /\b(?:[0-9a-f]{0,4}:){2,7}[0-9a-f]{0,4}\b/i;
/** True when the string looks like it contains a network address. Used by tests and as a belt-and-braces guard. */
function looksLikeAddress(s) {
    return IPV4.test(s) || IPV6.test(s);
}
/**
 * SDP redaction. Dropped entirely while privacy mode is on. Elsewhere every line that can
 * carry an address or an ICE credential is removed before the remainder is run
 * through the PII detectors.
 */
const SDP_DROP = /^(a=candidate|c=|a=rtcp:|a=ice-ufrag|a=ice-pwd|a=fingerprint|o=|a=identity)/i;
function redactSdp(sdp, mode, capture) {
    if (!sdp || normalizeMode(mode) === "strict" || !capture)
        return undefined;
    const kept = [];
    for (const line of sdp.split(/\r\n|\n/)) {
        if (!line || SDP_DROP.test(line))
            continue;
        if (looksLikeAddress(line))
            continue;
        kept.push(line);
    }
    const [masked] = maskPII(kept.join("\n"));
    return masked;
}
// --- end webrtc ---

// --- webrtc ---
// Client-side WebRTC quality triggers. These exist so a conditional-recording
// session is promoted, and a monitor-mode burst is flushed, at the moment a call
// goes bad rather than minutes later when the server finishes detecting it.
// Thresholds are the shared DEFAULT_THRESHOLDS from the protocol so the client
// and the server agree on what "bad" means.
const TRIGGER_PREFIX = "webrtc:";
function triggerName(k) {
    return TRIGGER_PREFIX + k;
}
/** Media errors that mean "the call could not start", split by cause. */
function mediaErrorTrigger(e) {
    if (e === "permission_denied")
        return "media_permission_denied";
    if (e === "device_not_found" || e === "device_in_use")
        return "device_unavailable";
    return undefined;
}
/**
 * Per-connection trigger state. Sustained conditions (latency, loss,
 * concealment, low resolution) must hold for their whole window before firing,
 * and each kind fires at most once per connection so one bad call does not
 * produce a hundred identical bursts.
 */
class TriggerEngine {
    emit;
    fired = new Set();
    sustain = new Map();
    oneWay = new Map();
    reconnects = [];
    freezeMsTotal = 0;
    sampledMsTotal = 0;
    cpuLimitedMsTotal = 0;
    th;
    constructor(emit, thresholds) {
        this.emit = emit;
        this.th = { ...DEFAULT_THRESHOLDS, ...(thresholds ?? {}) };
    }
    setThresholds(t) {
        this.th = { ...DEFAULT_THRESHOLDS, ...(t ?? {}) };
    }
    has(kind) {
        return this.fired.has(kind);
    }
    fire(kind, detail = {}) {
        if (this.fired.has(kind))
            return;
        this.fired.add(kind);
        this.emit(kind, detail);
    }
    /** Hold a condition open until it has been true for `windowMs`, then fire once. */
    sustained(kind, active, windowMs, now, detail) {
        if (!active) {
            this.sustain.delete(kind);
            return;
        }
        const s = this.sustain.get(kind);
        if (!s) {
            this.sustain.set(kind, { since: now });
            return;
        }
        if (s.since !== undefined && now - s.since >= windowMs)
            this.fire(kind, { ...detail, sustainedMs: now - s.since });
    }
    /** Never reached `connected` in time. */
    onSetupTimeout(now, startedAt, state) {
        if (state === "connected" || state === "completed")
            return;
        if (now - startedAt >= this.th.webrtcConnectFailure.setupTimeoutMs)
            this.fire("connect_failure", { state, setupMs: now - startedAt });
    }
    onConnectionState(state, now) {
        if (state === "failed")
            this.fire("connect_failure", { state });
        if (state === "disconnected")
            this.countReconnect(now);
    }
    onIceRestart(now) {
        this.countReconnect(now);
    }
    onCandidatePairChange(now) {
        this.countReconnect(now);
    }
    countReconnect(now) {
        const { events, windowMs } = this.th.webrtcReconnectStorm;
        this.reconnects.push(now);
        this.reconnects = this.reconnects.filter((t) => now - t <= windowMs);
        if (this.reconnects.length >= events)
            this.fire("reconnect_storm", { events: this.reconnects.length, windowMs });
    }
    onMediaError(e) {
        const k = mediaErrorTrigger(e);
        if (k)
            this.fire(k, { error: e });
    }
    /** A remote track muting for long enough is a visible freeze even without stats. */
    onTrackMuted(media, direction, mutedMs) {
        if (direction !== "inbound")
            return;
        if (mutedMs >= this.th.webrtcFreeze.singleFreezeMs)
            this.fire("freeze", { media, mutedMs, source: "track_mute" });
    }
    /**
     * One-way media: one direction of a negotiated sendrecv stream has moved zero
     * packets for the whole window while the other direction is flowing. A
     * send-only or receive-only transceiver is not one-way media, it is the app's
     * design, so the caller must pass `bidirectional`.
     */
    onSample(d, now, bidirectional, callDurationMs) {
        const th = this.th;
        const windowMs = Math.max(0, d.windowMs);
        this.freezeMsTotal += Math.max(0, d.freezeMs);
        this.sampledMsTotal += windowMs;
        this.cpuLimitedMsTotal += windowMs * Math.max(0, d.cpuLimitedPct ?? 0) / 100;
        this.sustained("high_latency", (d.rttMs ?? 0) >= th.webrtcHighLatency.rttMs, th.webrtcHighLatency.sustainedMs, now, { rttMs: d.rttMs });
        this.sustained("packet_loss", (d.lossPct ?? 0) >= th.webrtcPacketLoss.lossPct, th.webrtcPacketLoss.sustainedMs, now, { lossPct: d.lossPct });
        this.sustained("audio_dropout", (d.concealmentPct ?? 0) >= th.webrtcAudioDropout.concealmentPct, th.webrtcAudioDropout.sustainedMs, now, {
            concealmentPct: d.concealmentPct,
        });
        this.sustained("low_resolution", d.minHeight !== undefined && d.minHeight > 0 && d.minHeight < th.webrtcLowResolution.minHeight, th.webrtcLowResolution.sustainedMs, now, {
            height: d.minHeight,
        });
        const freezeSharePct = (this.freezeMsTotal / Math.max(callDurationMs, 1)) * 100;
        if (d.freezeMs >= th.webrtcFreeze.singleFreezeMs || freezeSharePct >= th.webrtcFreeze.callSharePct) {
            this.fire("freeze", { freezeMs: d.freezeMs, freezeMsTotal: this.freezeMsTotal, freezeSharePct, freezes: d.freezes });
        }
        if (d.relayed)
            this.fire("relay_fallback", { relayed: true });
        const cpuLimitedSharePct = this.sampledMsTotal > 0 ? (this.cpuLimitedMsTotal / this.sampledMsTotal) * 100 : 0;
        if (cpuLimitedSharePct >= th.webrtcCpuLimited.callSharePct) {
            this.fire("cpu_limited", { cpuLimitedPct: d.cpuLimitedPct, cpuLimitedSharePct });
        }
        // One-way media, tracked per media kind and direction.
        const flowing = new Set(d.flowing.map((f) => f.media + ":" + f.direction));
        for (const s of d.stalled) {
            if (!bidirectional(s.media))
                continue;
            const other = s.direction === "inbound" ? "outbound" : "inbound";
            if (!flowing.has(s.media + ":" + other))
                continue;
            const key = s.media + ":" + s.direction;
            const since = this.oneWay.get(key);
            if (since === undefined)
                this.oneWay.set(key, now);
            else if (now - since >= th.webrtcOneWayMedia.windowMs)
                this.fire("one_way_media", { media: s.media, silentDirection: s.direction, forMs: now - since });
        }
        // Clear directions that recovered.
        for (const key of [...this.oneWay.keys()]) {
            if (!d.stalled.some((s) => s.media + ":" + s.direction === key))
                this.oneWay.delete(key);
        }
    }
    /** Called when the connection closes: a short call with poor quality was abandoned. */
    onClose(durationMs, qualityScore) {
        const th = this.th.webrtcCallAbandoned;
        if (durationMs <= th.maxDurationMs && qualityScore < th.qualityScoreBelow)
            this.fire("call_abandoned", { durationMs, qualityScore });
    }
    reset() {
        this.fired.clear();
        this.sustain.clear();
        this.oneWay.clear();
        this.reconnects = [];
        this.freezeMsTotal = 0;
        this.sampledMsTotal = 0;
        this.cpuLimitedMsTotal = 0;
    }
}
// --- end webrtc ---

/** True only when the project asked, the visitor agreed, and minutes remain. */
function visualActive(p) {
    const asked = p.configEnabled || p.orgEnabled;
    const strictOk = p.privacy !== "strict" || p.whenStrict || p.orgEnabled;
    return asked && strictOk && p.consented && p.canvasAllowed;
}
/** Fit a frame inside a square of `maxEdge` without cropping. */
function fitEdge(srcW, srcH, maxEdge) {
    if (srcW <= 0 || srcH <= 0 || maxEdge <= 0)
        return { w: 0, h: 0 };
    const edge = Math.max(srcW, srcH);
    const scale = edge > maxEdge ? maxEdge / edge : 1;
    return { w: Math.max(1, Math.round(srcW * scale)), h: Math.max(1, Math.round(srcH * scale)) };
}
/** The video element is showing a remote track, not the visitor's own camera. */
function showsRemoteVideo(video, local) {
    const stream = video.srcObject;
    if (!stream || typeof stream.getVideoTracks !== "function")
        return null;
    for (const track of stream.getVideoTracks()) {
        if (track.kind !== "video" || track.readyState === "ended")
            continue;
        if (local.has(track))
            continue;
        return track;
    }
    return null;
}
const BURST_MS = 15000;
/**
 * Samples inbound video elements. Nothing is captured until `allow()` and
 * `policy()` both say the project and the visitor have agreed.
 */
class VisualSampler {
    o;
    timer = null;
    local = new WeakSet();
    last = new WeakMap();
    inflight = new WeakSet();
    burstUntil = 0;
    consented = false;
    surface = null;
    constructor(o) {
        this.o = o;
    }
    start() {
        if (this.timer)
            return;
        this.timer = setInterval(() => this.tick(), 250);
    }
    stop() {
        if (this.timer)
            clearInterval(this.timer);
        this.timer = null;
        this.surface = null;
    }
    /** The visitor agreed to stored pictures of the call. */
    allow() {
        this.consented = true;
    }
    /** A camera or outbound track. Pictures of it never leave the page. */
    noteLocal(track) {
        if (track && track.kind === "video")
            this.local.add(track);
    }
    /** Take pictures faster for a few seconds after a quality problem. */
    burst() {
        this.burstUntil = this.o.now() + BURST_MS;
    }
    tick() {
        const policy = this.o.policy();
        if (!visualActive({ ...policy, consented: this.consented }))
            return;
        const interval = this.o.now() < this.burstUntil ? this.o.burstIntervalMs : this.o.intervalMs;
        const videos = this.o.doc.querySelectorAll("video");
        videos.forEach((video) => {
            if (!showsRemoteVideo(video, this.local))
                return;
            if (video.readyState < 2 || !video.videoWidth || !video.videoHeight)
                return;
            const prev = this.last.get(video) ?? 0;
            if (this.o.now() - prev < interval || this.inflight.has(video))
                return;
            this.last.set(video, this.o.now());
            this.capture(video);
        });
    }
    capture(video) {
        const size = fitEdge(video.videoWidth, video.videoHeight, this.o.maxEdge);
        if (!size.w || !size.h)
            return;
        const id = this.o.nodeId(video);
        if (!id)
            return;
        const canvas = this.surface ?? this.o.doc.createElement("canvas");
        this.surface = canvas;
        canvas.width = size.w;
        canvas.height = size.h;
        const ctx = canvas.getContext("2d");
        if (!ctx)
            return;
        try {
            ctx.drawImage(video, 0, 0, size.w, size.h);
        }
        catch {
            return;
        }
        if (typeof canvas.toBlob !== "function")
            return;
        this.inflight.add(video);
        const now = this.o.now();
        canvas.toBlob((blob) => {
            this.inflight.delete(video);
            if (!blob)
                return;
            void blob.arrayBuffer().then((buf) => {
                const data = b64(new Uint8Array(buf));
                if (!data)
                    return;
                const ev = {
                    type: "canvas_frame",
                    t: now,
                    id,
                    codec: "jpeg",
                    keyframe: true,
                    w: size.w,
                    h: size.h,
                    data,
                    ts: now,
                    ...(this.o.phi ? { phi: true } : {}),
                };
                this.o.emit(ev);
            });
        }, "image/jpeg", 0.6);
    }
}
function b64(bytes) {
    let s = "";
    for (let i = 0; i < bytes.length; i += 0x8000)
        s += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + 0x8000)));
    return typeof btoa === "function" ? btoa(s) : "";
}

// --- webrtc ---
// WebRTC collector (PLAN §5.7): patches RTCPeerConnection and mediaDevices,
// samples getStats() on a governor-driven ladder, and fires client-side quality
// triggers so a conditional-recording session is promoted the moment a call goes
// bad. Call quality is metrics and state. Pictures of the remote video leave
// the page only when the project turns visual sampling on and the visitor agrees.
const CONNECTION_STATES = new Set(["new", "connecting", "connected", "disconnected", "failed", "closed"]);
class WebRtcCollector {
    r;
    full;
    o;
    win;
    now;
    conns = new Map();
    order = [];
    seq = 0;
    undo = [];
    timer = null;
    stopped = false;
    cfg;
    lastLevel;
    /** Stamped onto every later call event. Null means this session is not in a group. */
    group = null;
    visual = null;
    /** Set when the visitor agrees before the sampler has started. */
    picturesAllowed = false;
    constructor(r, full, o = {}) {
        this.r = r;
        this.full = full;
        this.o = o;
        this.win = (o.win ?? (typeof window !== "undefined" ? window : globalThis));
        this.now = o.now ?? (() => Date.now());
        this.cfg = full.webrtc ?? { ...DEFAULT_WEBRTC_CONFIG };
        this.lastLevel = r.level ?? "HEALTHY";
        setSalt(randomSalt());
    }
    start() {
        if (this.stopped)
            return;
        const obs = this.observer();
        this.undo.push(patchPeerConnection(this.win, obs, this.now, (pc, detach) => this.track(pc, detach)), patchMediaDevices(this.win, this.mediaObserver(), this.o.doc));
        // Device inventory once at start: a call that fails because there is no
        // camera is only explainable if we know there was never a camera.
        const md = this.win.navigator?.mediaDevices;
        md?.enumerateDevices?.().catch(() => { });
        this.timer = setInterval(() => this.poll(), this.o.pollMs ?? 1000);
        this.r.getWebRtc = () => this.debugList();
        this.ensureVisual();
    }
    stop() {
        this.stopped = true;
        if (this.timer)
            clearInterval(this.timer);
        this.timer = null;
        this.visual?.stop();
        this.visual = null;
        for (const u of this.undo.splice(0)) {
            try {
                u();
            }
            catch {
                /* ignore */
            }
        }
        for (const c of this.conns.values()) {
            if (c.setupTimer)
                clearTimeout(c.setupTimer);
            try {
                c.detach();
            }
            catch {
                /* ignore */
            }
        }
        this.conns.clear();
        this.order = [];
        this.r.getWebRtc = () => [];
    }
    /**
     * Link this session to the other sessions in the same conversation. Pass null
     * to stop stamping new events. Ids must be application identifiers, not names.
     */
    setGroup(input) {
        const next = sanitizeCallGroup(input);
        if (input && !next) {
            console.warn("[backstory] webrtc group ignored: callGroupId and participantId must be 1–80 letters, numbers, or . _ : -");
            return;
        }
        this.group = next;
    }
    /** Instrument a connection the app created before the module loaded. */
    attach(pc) {
        if (this.stopped || this.conns.has(pc))
            return;
        const obs = this.observer();
        obs.onCreated(pc, { iceServerCount: 0, hasTurn: false });
        const detach = instrument(pc, obs, this.now);
        this.track(pc, detach);
    }
    debugList() {
        const out = [];
        for (const pc of this.order) {
            const c = this.conns.get(pc);
            if (!c)
                continue;
            out.push({
                pc: c.id,
                state: c.closed ? "closed" : c.lastState,
                relayed: !!c.lastDigest?.relayed,
                rttMs: c.lastDigest?.rttMs,
                lossPct: c.lastDigest?.lossPct,
                fps: c.lastDigest?.fps,
                quality: this.quality(c),
                tracks: c.tracks.size,
                sampled: c.sampled,
            });
        }
        return out;
    }
    // ---- internals -----------------------------------------------------------
    emit(e) {
        if (this.stopped)
            return;
        if (this.group && (e.type === "webrtc" || e.type === "webrtc_stats")) {
            const stamped = e;
            stamped.callGroupId = this.group.callGroupId;
            stamped.participantId = this.group.participantId;
            if (this.group.role)
                stamped.participantRole = this.group.role;
            if (this.group.topology)
                stamped.topology = this.group.topology;
            if (this.group.remoteParticipantId)
                stamped.remoteParticipantId = this.group.remoteParticipantId;
        }
        this.r.emit(e);
    }
    track(pc, detach) {
        const id = "pc" + ++this.seq;
        const sampled = this.conns.size < this.cfg.maxPeerConnections;
        const conn = {
            pc,
            id,
            createdAt: this.now(),
            closed: false,
            detach,
            sampled,
            prev: null,
            prevAt: 0,
            lastEmitAt: 0,
            triggers: new TriggerEngine((kind, detail) => this.onTrigger(conn, kind, detail), this.o.thresholds),
            tracks: new Map(),
            worst: { freezeMs: 0 },
            lastState: pc.connectionState ?? "new",
            res: new Map(),
            codecs: new Map(),
            trackEnabled: new Map(),
            trackDevices: new Map(),
            setupTimer: null,
        };
        this.conns.set(pc, conn);
        this.order.push(pc);
        // Connect-failure watchdog: a call that never reaches `connected`.
        const timeout = Math.max(1000, (this.o.thresholds?.webrtcConnectFailure?.setupTimeoutMs ?? 15000));
        conn.setupTimer = setTimeout(() => {
            if (!conn.closed)
                conn.triggers.onSetupTimeout(this.now(), conn.createdAt, String(pc.connectionState ?? conn.lastState));
        }, timeout);
    }
    connOf(pc) {
        return this.conns.get(pc);
    }
    base(c) {
        return { t: this.now(), type: "webrtc", ...(c ? { pc: c.id } : {}) };
    }
    observer() {
        const self = this;
        return {
            onCreated(pc, cfg) {
                // `track()` assigns the id, so on the constructor path the connection is
                // not registered yet: emit with the id it is about to receive.
                const id = "pc" + (self.seq + 1);
                self.emit({ t: self.now(), type: "webrtc", pc: self.conns.get(pc)?.id ?? id, kind: "pc_created", iceServerCount: cfg.iceServerCount, hasTurn: cfg.hasTurn, ...(cfg.bundlePolicy ? { bundlePolicy: cfg.bundlePolicy } : {}), ...(cfg.rtcpMuxPolicy ? { rtcpMuxPolicy: cfg.rtcpMuxPolicy } : {}) });
            },
            onConnectionState(pc, state) {
                const c = self.connOf(pc);
                if (!CONNECTION_STATES.has(state))
                    return;
                const s = state;
                const sinceMs = c ? self.now() - c.createdAt : undefined;
                if (c) {
                    c.lastState = s;
                    if (s === "connected" && c.connectedAt === undefined) {
                        c.connectedAt = self.now();
                        if (c.setupTimer)
                            clearTimeout(c.setupTimer);
                        c.setupTimer = null;
                    }
                    c.triggers.onConnectionState(s, self.now());
                }
                self.emit({ ...self.base(c), kind: "connection_state", state: s, ...(sinceMs !== undefined ? { sinceMs } : {}) });
            },
            onIceState(pc, state) {
                const c = self.connOf(pc);
                if (c)
                    c.lastIce = state;
                self.emit({ ...self.base(c), kind: "ice_state", state: state });
            },
            onIceGathering(pc, state) {
                self.emit({ ...self.base(self.connOf(pc)), kind: "ice_gathering", state });
            },
            onSignalingState(pc, state) {
                self.emit({ ...self.base(self.connOf(pc)), kind: "signaling_state", state });
            },
            onIceCandidateError(pc, e) {
                self.emit({
                    ...self.base(self.connOf(pc)),
                    kind: "ice_candidate_error",
                    errorCode: e.errorCode ?? 0,
                    ...(e.errorText ? { errorText: e.errorText } : {}),
                    ...(iceServerHost(e.url) ? { url: iceServerHost(e.url) } : {}),
                    ...(hashValue(e.address) ? { addressHash: hashValue(e.address) } : {}),
                });
            },
            onTrack(pc, t) {
                const c = self.connOf(pc);
                if (c) {
                    c.tracks.set(t.id, t);
                    if (t.track) {
                        c.trackEnabled.set(t.id, t.track.enabled);
                        const deviceId = t.track.getSettings?.().deviceId;
                        if (deviceId)
                            c.trackDevices.set(t.id, deviceId);
                        if (t.direction !== "inbound")
                            self.visual?.noteLocal(t.track);
                    }
                }
                const label = deviceLabel(t.label);
                self.emit({ ...self.base(c), kind: "track_added", trackId: t.id, media: t.media, direction: t.direction, ...(label ? { label } : {}) });
            },
            onTrackMuted(pc, t) {
                self.emit({ ...self.base(self.connOf(pc)), kind: "track_muted", trackId: t.id, media: t.media, direction: t.direction });
            },
            onTrackUnmuted(pc, t) {
                const c = self.connOf(pc);
                const mutedMs = t.mutedMs;
                if (c && mutedMs !== undefined)
                    c.triggers.onTrackMuted(t.media, t.direction, mutedMs);
                self.emit({ ...self.base(c), kind: "track_unmuted", trackId: t.id, media: t.media, direction: t.direction, ...(mutedMs !== undefined ? { mutedMs } : {}) });
            },
            onTrackEnded(pc, t) {
                self.emit({ ...self.base(self.connOf(pc)), kind: "track_ended", trackId: t.id, media: t.media, direction: t.direction });
            },
            onIceRestart(pc) {
                const c = self.connOf(pc);
                if (c)
                    c.triggers.onIceRestart(self.now());
                self.emit({ ...self.base(c), kind: "ice_restart" });
            },
            onClosed(pc) {
                const c = self.connOf(pc);
                if (!c || c.closed)
                    return;
                c.closed = true;
                if (c.setupTimer)
                    clearTimeout(c.setupTimer);
                const durationMs = self.now() - c.createdAt;
                c.triggers.onClose(durationMs, self.quality(c) ?? 100);
                self.emit({ ...self.base(c), kind: "pc_closed", durationMs });
                try {
                    c.detach();
                }
                catch {
                    /* ignore */
                }
            },
            onDataChannel(pc, label, state) {
                self.emit({ ...self.base(self.connOf(pc)), kind: "data_channel", label, state });
            },
            onSdp(pc, direction, sdpType, sdp) {
                const redacted = redactSdp(sdp, self.full.privacy.mode, self.cfg.captureSdp);
                if (!redacted)
                    return;
                self.emit({ ...self.base(self.connOf(pc)), kind: "sdp", direction, sdpType, redacted: true, sdp: redacted });
            },
            onNegotiationNeeded() {
                /* No event in the contract; negotiation is visible through signaling state. */
            },
        };
    }
    mediaObserver() {
        const self = this;
        return {
            onAcquired(source, req, stream) {
                const audio = stream.getAudioTracks?.()[0];
                const video = stream.getVideoTracks?.()[0];
                for (const track of stream.getVideoTracks?.() ?? [])
                    self.visual?.noteLocal(track);
                const vs = video?.getSettings?.();
                self.emit({
                    t: self.now(),
                    type: "webrtc",
                    kind: "media_acquired",
                    source,
                    audio: !!audio,
                    video: !!video,
                    ...(hashValue(audio?.getSettings?.().deviceId) ? { audioDeviceHash: hashValue(audio?.getSettings?.().deviceId) } : {}),
                    ...(hashValue(vs?.deviceId) ? { videoDeviceHash: hashValue(vs?.deviceId) } : {}),
                    ...(deviceLabel(audio?.label) ? { audioLabel: deviceLabel(audio?.label) } : {}),
                    ...(deviceLabel(video?.label) ? { videoLabel: deviceLabel(video?.label) } : {}),
                    ...(vs?.width ? { w: vs.width } : {}),
                    ...(vs?.height ? { h: vs.height } : {}),
                    ...(vs?.frameRate ? { fps: Math.round(vs.frameRate) } : {}),
                    ...(audio?.muted || video?.muted ? { arrivedMuted: true } : {}),
                });
            },
            onError(source, req, err) {
                const { kind, name, constraint } = mediaErrorKind(err);
                self.emit({
                    t: self.now(),
                    type: "webrtc",
                    kind: "media_error",
                    source,
                    error: kind,
                    ...(name ? { name } : {}),
                    requestedAudio: req.audio,
                    requestedVideo: req.video,
                    ...(constraint ? { constraint } : {}),
                });
                // Device failures are session-level, not connection-level: fire through
                // a standalone engine so a call that never existed still promotes.
                const t = new TriggerEngine((k, d) => self.onTrigger(undefined, k, d), self.o.thresholds);
                t.onMediaError(kind);
            },
            onDeviceChange(counts, devices) {
                self.emit({ t: self.now(), type: "webrtc", kind: "device_change", ...counts, devices: self.deviceSnapshots(devices) });
            },
            onDeviceInventory(counts, devices) {
                self.emit({ t: self.now(), type: "webrtc", kind: "device_inventory", ...counts, devices: self.deviceSnapshots(devices) });
            },
            onOutputDeviceChange(deviceId) {
                const deviceHash = hashValue(deviceId || "default");
                if (deviceHash)
                    self.emit({ t: self.now(), type: "webrtc", kind: "output_device_change", deviceHash, ...(deviceId === "" || deviceId === "default" ? { isDefault: true } : {}) });
            },
        };
    }
    deviceSnapshots(devices) {
        return devices.flatMap((d) => {
            if (!d.deviceId || !["audioinput", "audiooutput", "videoinput"].includes(d.kind))
                return [];
            const idHash = hashValue(d.deviceId);
            if (!idHash)
                return [];
            const label = deviceLabel(d.label);
            return [{
                    kind: d.kind,
                    idHash,
                    ...(label ? { label } : {}),
                    ...(d.deviceId === "default" ? { isDefault: true } : {}),
                }];
        });
    }
    /** Fire a trigger: a custom event for the transcript plus a recording promotion. */
    onTrigger(c, kind, detail) {
        const name = triggerName(kind);
        this.emit({ t: this.now(), type: "custom", name: "backstory.webrtc_trigger", props: { kind, trigger: name, ...(c ? { pc: c.id } : {}), ...detail } });
        this.r.forceRecord?.(name);
        this.visual?.burst();
    }
    quality(c) {
        const w = c.worst;
        if (w.rttMs === undefined && w.lossPct === undefined && w.freezeMs === 0 && w.concealmentPct === undefined)
            return undefined;
        return callQualityScore({ rttMs: w.rttMs, lossPct: w.lossPct, jitterMs: w.jitterMs, freezeMs: w.freezeMs, concealmentPct: w.concealmentPct, durationMs: this.now() - c.createdAt });
    }
    /** True when both directions of this media kind were negotiated (sendrecv). */
    bidirectional(c, media) {
        const tvs = c.pc.getTransceivers?.() ?? [];
        for (const t of tvs) {
            const dir = t.currentDirection ?? t.direction;
            const kind = t.receiver?.track?.kind ?? t.sender?.track?.kind;
            if (kind === media && dir === "sendrecv")
                return true;
        }
        // Fall back to observed tracks when transceivers are unavailable.
        let inbound = false;
        let outbound = false;
        for (const t of c.tracks.values()) {
            if (t.media !== media)
                continue;
            if (t.direction === "inbound")
                inbound = true;
            else
                outbound = true;
        }
        return inbound && outbound;
    }
    ladder() {
        const lvl = this.r.level ?? "HEALTHY";
        const row = WEBRTC_SAMPLE_POLICY[lvl] ?? WEBRTC_SAMPLE_POLICY.HEALTHY;
        // A project may ask for a slower cadence than the ladder allows, never faster.
        return { ...row, intervalSec: Math.max(row.intervalSec, lvl === "HEALTHY" ? this.cfg.sampleIntervalSec : row.intervalSec) };
    }
    /** The visitor agreed. Pictures still wait for the project switch and remaining minutes. */
    allowPictures() {
        this.picturesAllowed = true;
        this.visual?.allow();
    }
    visualPolicy() {
        return {
            configEnabled: !!this.cfg.visual?.enabled,
            orgEnabled: this.r.getRemoteConfig?.()?.flags?.webrtcVisual === true,
            privacy: this.full.privacy.mode,
            whenStrict: !!this.cfg.visual?.whenStrict,
            consented: this.picturesAllowed,
            // Minutes remaining, not the page-canvas switch. Call pictures are the
            // same pool, and a project can store them without recording canvases.
            canvasAllowed: (this.r.limitsReady?.() ?? false) && (this.r.getLimits?.().canvas.allowed ?? false),
        };
    }
    ensureVisual() {
        if (this.visual || this.stopped)
            return;
        const v = this.cfg.visual;
        if (!v?.enabled && this.r.getRemoteConfig?.()?.flags?.webrtcVisual !== true)
            return;
        const doc = this.o.doc ?? (typeof document !== "undefined" ? document : null);
        if (!doc)
            return;
        this.visual = new VisualSampler({
            doc,
            now: this.now,
            emit: (e) => this.emit(e),
            nodeId: (el) => this.r.map?.assign(el) ?? 0,
            policy: () => this.visualPolicy(),
            intervalMs: v?.intervalMs ?? 3000,
            burstIntervalMs: v?.burstIntervalMs ?? 1000,
            maxEdge: v?.maxEdge ?? 320,
            phi: this.full.privacy.mode === "strict",
        });
        if (this.picturesAllowed)
            this.visual.allow();
        this.visual.start();
    }
    poll() {
        if (this.stopped)
            return;
        this.ensureVisual();
        const level = this.r.level ?? "HEALTHY";
        if (level !== this.lastLevel)
            this.lastLevel = level;
        const row = this.ladder();
        if (!row.statsEnabled)
            return;
        const now = this.now();
        for (const pc of this.order) {
            const c = this.conns.get(pc);
            if (!c || c.closed || !c.sampled)
                continue;
            if (c.lastState === "closed")
                continue;
            // MediaStreamTrack has no event for application mute (`enabled`) or for
            // a source switch caused by applyConstraints. Poll these alongside stats
            // so the timeline distinguishes user/app actions from transport failure.
            for (const t of c.tracks.values()) {
                const track = t.track;
                if (!track)
                    continue;
                const enabled = track.enabled;
                if (c.trackEnabled.get(t.id) !== enabled) {
                    c.trackEnabled.set(t.id, enabled);
                    this.emit({ ...this.base(c), kind: "track_enabled", trackId: t.id, media: t.media, direction: t.direction, enabled });
                }
                const deviceId = track.getSettings?.().deviceId;
                if (deviceId && c.trackDevices.get(t.id) !== deviceId) {
                    c.trackDevices.set(t.id, deviceId);
                    const deviceHash = hashValue(deviceId);
                    if (deviceHash) {
                        const label = deviceLabel(track.label);
                        this.emit({ ...this.base(c), kind: "track_device_change", trackId: t.id, media: t.media, direction: t.direction, deviceHash, ...(label ? { label } : {}) });
                    }
                }
            }
            const p = pc.getStats?.();
            if (!p || typeof p.then !== "function")
                continue;
            p
                .then((report) => {
                if (this.stopped || c.closed)
                    return;
                const windowMs = c.prevAt ? now - c.prevAt : 0;
                const ev = computeSample(c.prev, report, windowMs, { connectionState: c.lastState, iceState: c.lastIce, pc: c.id, primed: !!c.prev }, now);
                c.prev = report;
                c.prevAt = now;
                if (!ev)
                    return;
                this.onSample(c, ev, now, row);
            })
                .catch(() => {
                /* getStats rejects on a closing connection */
            });
        }
    }
    onSample(c, ev, now, row) {
        const d = digest(ev);
        c.lastDigest = d;
        // Roll the worst-case values that feed the quality score.
        if (d.rttMs !== undefined)
            c.worst.rttMs = Math.max(c.worst.rttMs ?? 0, d.rttMs);
        if (d.lossPct !== undefined)
            c.worst.lossPct = Math.max(c.worst.lossPct ?? 0, d.lossPct);
        if (d.jitterMs !== undefined)
            c.worst.jitterMs = Math.max(c.worst.jitterMs ?? 0, d.jitterMs);
        if (d.concealmentPct !== undefined)
            c.worst.concealmentPct = Math.max(c.worst.concealmentPct ?? 0, d.concealmentPct);
        c.worst.freezeMs += d.freezeMs;
        const before = this.triggerCount(c);
        c.triggers.onSample(d, now, (m) => this.bidirectional(c, m), now - c.createdAt);
        const crossed = this.triggerCount(c) > before;
        // Resolution and codec changes are events, not samples: emit on change only.
        for (const t of ev.tracks) {
            if (t.media === "video" && t.w && t.h && t.trackId) {
                const key = `${t.w}x${t.h}`;
                if (c.res.get(t.trackId) !== key) {
                    c.res.set(t.trackId, key);
                    this.emit({ ...this.base(c), kind: "resolution_change", trackId: t.trackId, direction: t.direction, w: t.w, h: t.h, ...(t.fps ? { fps: t.fps } : {}) });
                }
            }
            if (t.codec) {
                const key = t.media + ":" + t.direction;
                if (c.codecs.get(key) !== t.codec) {
                    c.codecs.set(key, t.codec);
                    this.emit({ ...this.base(c), kind: "codec_change", media: t.media, direction: t.direction, mimeType: t.codec });
                }
            }
        }
        // Candidate pair change: the media path moved, often a visible glitch.
        if (ev.localCandidate && ev.remoteCandidate) {
            const key = `${ev.localCandidate}/${ev.remoteCandidate}/${ev.networkType ?? ""}`;
            if (c.lastPairKey !== key) {
                // Initial selection describes the established path; only a subsequent
                // switch counts toward reconnect-storm detection.
                if (c.lastPairKey)
                    c.triggers.onCandidatePairChange(now);
                this.emit({
                    ...this.base(c),
                    kind: "candidate_pair_change",
                    localType: ev.localCandidate,
                    remoteType: ev.remoteCandidate,
                    ...(ev.networkType ? { networkType: ev.networkType } : {}),
                    relayed: !!ev.relayed,
                });
            }
            c.lastPairKey = key;
        }
        const due = now - c.lastEmitAt >= row.intervalSec * 1000;
        const allowed = row.onThresholdOnly ? crossed && this.cfg.emitOnThresholdCross : due || (crossed && this.cfg.emitOnThresholdCross);
        if (!allowed)
            return;
        c.lastEmitAt = now;
        ev.level = this.r.level ?? "HEALTHY";
        this.emit(ev);
    }
    triggerCount(c) {
        let n = 0;
        for (const k of ["connect_failure", "one_way_media", "freeze", "audio_dropout", "high_latency", "packet_loss", "relay_fallback", "cpu_limited", "reconnect_storm", "low_resolution", "call_abandoned"]) {
            if (c.triggers.has(k))
                n++;
        }
        return n;
    }
}
/** Attach the collector to a running recorder. Mirrors `attachCanvas`. */
function attachWebRtc(recorder, cfg, opts = {}) {
    const c = new WebRtcCollector(recorder, cfg, opts);
    c.start();
    return c;
}
// --- end webrtc ---

// --- webrtc ---
// Lazily loaded WebRTC module (dist/backstory-webrtc.js). Registers on
// window.__BACKSTORY_MOD__.webrtc for the script-tag bundle; imported statically by the
// ESM build. Kept out of the core so the budget is unaffected when a project
// does not make calls.

var webrtcModule = /*#__PURE__*/Object.freeze({
    __proto__: null,
    TRIGGER_PREFIX: TRIGGER_PREFIX,
    TriggerEngine: TriggerEngine,
    WebRtcCollector: WebRtcCollector,
    attachWebRtc: attachWebRtc,
    computeSample: computeSample,
    deviceLabel: deviceLabel,
    digest: digest,
    hashValue: hashValue,
    instrument: instrument,
    looksLikeAddress: looksLikeAddress,
    mediaErrorKind: mediaErrorKind,
    mediaErrorTrigger: mediaErrorTrigger,
    patchMediaDevices: patchMediaDevices,
    patchPeerConnection: patchPeerConnection,
    redactSdp: redactSdp,
    triggerName: triggerName
});

export { BLOCK_ATTR, Backstory, MASK_ATTR, UNMASK_ATTR, blockImages, classify, Backstory as default, detectCard, detectEmail, detectIBAN, detectPhone, detectSSN, isAlwaysMasked, isUnmasked, luhnValid, maskCards, maskPII, maskShownValue, maskString, maskText, normalizeMode, shouldMaskInput };
//# sourceMappingURL=backstory.esm.js.map
